import { ApiError, type HubBrandDto, type HubPersonDto, type HubRecordsDto, type WorkspaceSiteDto } from "@bafe/api-client";
import { roleHasPermission } from "@bafe/types";
import { Select } from "@bafe/ui";
import { DotsThreeIcon, DownloadSimpleIcon, EyeIcon, EyeSlashIcon, FileIcon, TrashIcon } from "@phosphor-icons/react";
import { Add, ChevronDown, ChevronRight, Grid as BoardIcon } from "reicon-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { jsPDF as JsPdf } from "jspdf";
import { Fragment, useEffect, useId, useMemo, useRef, useState, type ComponentProps, type FormEvent, type MouseEvent, type ReactNode, type RefObject } from "react";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router";

import { DOCUMENT_KINDS, bankOf, documentFromForm, documentKindLabel, organisationRecord, parentOf, partyOf, type BankDetails, type EducationRow, type EmergencyContact, type EmployeeDocument, type EmployeeProfile, type ParentDetails, type PartyDetails } from "./employee.js";
import { api } from "../../lib/api";
import { presentError } from "../../lib/errors";
import { queryKeys } from "../../lib/query";
import { activeMembership, useSession } from "../../lib/session.js";
import { closeSiblingSelectMenus } from "../../lib/select-menu.js";
import { HubFieldSelect } from "./hub-field-select.js";
import { applyBoardMoveOptimistic, createBoardMovePersister } from "./task-board-persist.js";
import { HubColorPalette, HubColorPopover } from "./hub-color-palette.js";
import {
	defaultTagColorId,
	hubToneClass,
	hubToneStyle,
	isHubStoredColor,
	resolveAssigneeColorValue,
	resolveTagColorValue,
} from "./hub-task-colors.js";
import { TaskBoardKanban } from "./task-board-kanban.js";
import { TaskDetailComments } from "./task-detail-comments.js";
import { boardSortKey, columnTasks, positionBetween } from "./task-board-order.js";
import { hubCalendarItemsForDay, isoDay, monthCells } from "./hub-calendar-items.js";
import {
	defaultWorkDueFromSalesOrder,
	defaultWorkTitleFromSalesOrder,
	findSalesOrderById,
	salesOrderDetailPath,
	salesOrderIdOf,
	workMatchesSalesOrder,
	workRecordWithSalesOrder,
	workTemplateAlreadyLinked,
	workUnlinkedForNewLink,
	worksForSalesOrder,
} from "./hub-work-sales-order.js";
import { ConsumablesHub } from "./hub-operation-consumables.js";
import { InventoryHub } from "./hub-operation-inventory.js";
import type { InventoryRow, LookupRow } from "./hub-operation-types.js";
import {
	assigneeFieldFromNames,
	assigneeNamesOf,
	DEFAULT_WORK_STATUS,
	JOB_STATUSES,
	LEGACY_WORK_STATUS,
	personDisplayName,
	WORK_TYPE_OPTIONS,
	workStatusLabel,
	workTypeLabel,
	workTypesOf,
} from "./hub-operation-shared.js";
import { CustomerSitesHub, CustomerSitesSection, ProjectsHub, WorkProjectContext } from "./hub-operation-sites.js";
import { compareLocale, defaultColumnVisibility } from "./hub-operation-list-table.js";
import {
	HubOperationBar,
	HubOperationColumnMenu,
	HubOperationFilterClear,
	HubOperationFilterMenu,
	HubOperationGroupHeader,
	HubOperationListTable,
	HubOperationTableEmptyRow,
	HubOperationTablePagination,
	hubOperationGroupedHasRows,
	HubOperationFormField,
	HubOperationFormRow,
	HubOperationFormStack,
	HubOperationModal,
	HubOperationScreen,
	HubOperationSearchField,
	HubOperationSearchToggle,
	HubOperationToolbar,
	hubTableSortIndicator,
	toggleHubSort,
	useHubOperationSearchFocus,
} from "./hub-operation-shell.js";
import { useHubOperationPagination } from "./hub-operation-pagination.js";
import {
	WorkAssigneesCell,
	WorkAssigneesField,
	workAssignablePeople,
	workTypeFilterCounts,
	WorkTypeBadges,
	WorkTypeSelect,
	useWorkAssigneeColorMap,
} from "./hub-operation-work.js";
import {
	docTotalFromLines,
	draftSubtotal,
	emptyLineDraft,
	lineDraftsFromDoc,
	lineQuantity,
	lineRowTotal,
	lineUnitPrice,
	linesFromDrafts,
	normalizePurchaseLine,
	purchaseLinesFromSingleLineForm,
	purchaseStatusAfterSend,
	type PurchaseLine,
	type PurchaseLineDraft,
	validateSalesOrderInput,
} from "./hub-purchase-lines.js";
import { DocDetailItemTableHead, DocEditItemTableHead } from "./hub-purchase-document-table.js";
import { SalesOrderLinesEditor } from "./SalesOrderLinesEditor.js";
import {
	buildSalesOrderRecordFromQuotation,
	linkedQuotationForSalesOrder,
	linkedSalesOrderForQuotation,
	quotationConversionBlockedReason,
	quotationDeletionBlockedReason,
	quotationDetailPath,
	quotationIdOf,
	quotationRecordWithSalesOrderLink,
	findQuotationById,
} from "./hub-quotation-sales-order.js";
import {
	buildInvoiceRecordFromSalesOrder,
	invoiceDetailPath,
	linkedInvoiceForSalesOrder,
	linkedSalesOrderForInvoice,
	salesOrderDeletionBlockedReason,
	salesOrderInvoiceBlockedReason,
	salesOrderRecordWithInvoiceLink,
} from "./hub-sales-order-invoice.js";
import {
	buildReceiptRecordFromInvoice,
	findInvoiceForReceipt,
	findReceiptByKey,
	formatReceiptAmount,
	invoiceBalanceDue,
	invoiceDeletionBlockedReason,
	invoiceDetailPathFromInvoice,
	invoiceGrandTotal,
	invoiceStatusAfterReceipts,
	invoiceTaxAmount,
	paidTotalForInvoice,
	receiptAmount,
	receiptDetailPath,
	receiptRecordFromExisting,
	receiptsForInvoice,
	suggestReceiptRef,
	validateReceiptPaymentInput,
	type HubReceiptRow,
} from "./hub-invoice-receipt.js";

const logo = `${import.meta.env.BASE_URL}favicon.svg`;

function when(value: string): string {
	if (!value) return "";
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return value;
	return date.toLocaleString("en-GB", { timeZone: "Asia/Kuala_Lumpur", dateStyle: "medium", timeStyle: "short" });
}

function stamp(value: string): string {
	if (!value) return "";
	const raw = value.includes("T") ? value : value.replace(" ", "T");
	const date = new Date(/Z$|[+-]\d{2}:\d{2}$/.test(raw) ? raw : `${raw}+08:00`);
	if (Number.isNaN(date.getTime())) return value;
	return new Intl.DateTimeFormat("en-GB", {
		timeZone: "Asia/Kuala_Lumpur",
		day: "2-digit",
		month: "short",
		year: "numeric",
		hour: "2-digit",
		minute: "2-digit",
		hour12: false,
	}).format(date);
}

function money(amount: number): string {
	return `RM${amount.toLocaleString("en-MY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const TASK_STATUSES = ["new", "pending", "reviewed", "completed"] as const;
const TASK_LABEL: Record<string, string> = { new: "New", pending: "Pending", reviewed: "Need Review", completed: "Completed" };
type HubTask = HubRecordsDto["tasks"][number];
type TaskChecklistItem = { id: string; title: string; done: boolean };
type TaskAttachmentItem = { id: string; name: string; type: string; size: number; data: string };

const TASK_ATTACHMENT_MAX_BYTES = 5 * 1024 * 1024;
const TASK_ATTACHMENT_MAX_COUNT = 12;
const DEAL_STAGES = [
	["new", "New"],
	["qualified", "Qualified"],
	["proposal", "Proposal"],
	["won", "Won"],
	["lost", "Lost"],
] as const;
const ORG_KINDS = [
	["customer", "Customer"],
	["vendor", "Vendor"],
	["employee", "Employee"],
] as const;
const MAINTENANCE_PURPOSES = ["Cleaning", "Pest control", "Landscaping", "Servicing", "Inspection", "Other"] as const;
const MAINTENANCE_STATUSES = [
	["scheduled", "Scheduled"],
	["progress", "In progress"],
	["done", "Done"],
] as const;
const REPAIR_STATUSES = [
	["reported", "Reported"],
	["progress", "In progress"],
	["done", "Done"],
] as const;
const PAYMENT_TYPES = [
	["cash", "Cash"],
	["transfer", "Transfer"],
	["cheque", "Cheque"],
	["card", "Card"],
] as const;
const ASSET_TYPES = ["Computer equipment", "Office equipment", "Motor vehicles", "Furniture and fittings", "Tools", "Other"] as const;
const DEPRECIATION_METHODS = [
	["none", "No depreciation"],
	["straight", "Straight line"],
	["diminishing", "Diminishing value"],
] as const;
const AVERAGING_METHODS = [
	["full-month", "Full month"],
	["actual-days", "Actual days"],
] as const;
const ACTIVITY_LABEL: Record<string, string> = {
	login: "Signed in",
	logout: "Signed out",
	workspace: "Switched workspace",
	"lead.api": "Website lead",
	"lead.create": "Added lead",
	"lead.edit": "Edited lead",
	"lead.delete": "Deleted lead",
	"task.create": "Added task",
	"task.edit": "Edited task",
	"task.status": "Changed task",
	"task.delete": "Deleted task",
	"user.access": "Changed access",
	"user.create": "Added user",
	"expense.create": "Added expense",
	"expense.edit": "Edited expense",
	"expense.delete": "Deleted expense",
};
const LEAD_SOURCE: Record<string, string> = {
	website: "Website",
	social: "Social Media",
	"walk-in": "Walk-in",
	call: "Call",
	associates: "Known Associates",
};
const EXPENSE_CATEGORY: Record<string, string> = {
	fuel: "Fuel",
	toll: "Toll",
	parking: "Parking",
	meals: "Meals",
	supplies: "Supplies",
	tools: "Tools",
	transport: "Transport",
	utilities: "Utilities",
	other: "Other",
};

const LABEL: Record<string, string> = {
	annual: "Annual",
	medical: "Medical",
	hospitalisation: "Hospitalisation",
	maternity: "Maternity",
	paternity: "Paternity",
	marriage: "Marriage",
	pending: "Pending",
	approved: "Approved",
	rejected: "Rejected",
	open: "Open",
	paid: "Paid",
	overdue: "Overdue",
	draft: "Draft",
	sent: "Sent",
	received: "Received",
	applied: "Applied",
	accepted: "Accepted",
	declined: "Declined",
	confirmed: "Confirmed",
	fulfilled: "Fulfilled",
	unit: "Unit",
	roll: "Roll",
	job: "Job",
	hour: "Hour",
	cash: "Cash",
	transfer: "Transfer",
	cheque: "Cheque",
	fuel: "Fuel",
	meals: "Meals",
	parking: "Parking",
	travel: "Travel",
	tools: "Tools",
	present: "Present",
	late: "Late",
	absent: "Absent",
	leave: "Leave",
	day: "Day",
	night: "Night",
	office: "Office",
	asset: "Asset",
	liability: "Liability",
	equity: "Equity",
	income: "Income",
	expense: "Expense",
	ledger: "Ledger",
	ppe: "PPE",
	secretary: "Secretary",
	hire: "Hire",
	loan: "Loan",
	filed: "Filed",
	original: "Original",
	revised: "Revised",
	management: "Management",
	supporting: "Supporting",
	unqualified: "Unqualified",
	answered: "Answered",
	closed: "Closed",
	grant: "Grant",
	insurance: "Insurance",
	roadtax: "Road tax",
	mon: "Mon",
	tue: "Tue",
	wed: "Wed",
	thu: "Thu",
	fri: "Fri",
	sat: "Sat",
	sun: "Sun",
	in: "In",
	out: "Out",
};

function label(value: string): string {
	return LABEL[value] ?? (value ? value.charAt(0).toUpperCase() + value.slice(1) : "");
}

type Line = PurchaseLine;
type PurchaseDoc = {
	id?: number;
	party: string;
	doc_at: string;
	ref_no: string;
	status: string;
	mark?: string;
	extra?: string;
	note: string;
	lines?: Line[];
	salesOrderId?: number | string;
	quotationId?: number | string;
	invoiceId?: number | string;
};
type LeaveRow = { person: string; kind: string; from: string; to: string; days: number; status: string; reason: string };
type LeavePolicy = { kind: string; days: number; paid: boolean; approval: boolean };
type AttendanceRow = { person: string; day: string; shift: string; inAt: string; outAt: string; status: string; note: string };
type Slot = { person: string; days: string[]; shift: string };
type Shift = { name: string; start: string; end: string };
type ClaimRow = { person: string; day: string; kind: string; amount: string; status: string; note: string };
type ClaimPolicy = { kind: string; limit: string; approval: boolean };
type Employee = { name: string; role: string; basic: string; allowance: string; active: boolean };
type PayLine = { person: string; role: string; basic: string; allowance: string; epf: string; socso: string; eis: string; net: string };
type PayRun = { month: string; status: string; note: string; lines: PayLine[] };
type Account = { id: string; code: string; name: string; type: string };
type JournalLine = { account: string; description: string; debit: string; credit: string };
type Journal = { day: string; ref: string; note: string; lines: JournalLine[] };
type Bank = { id: string; name: string; opening: number };
type Move = { day: string; kind: string; bank: string; party: string; amount: string; note: string; method: string };
type Transfer = { day: string; from: string; to: string; amount: string; note: string };
type FixedAsset = {
	id?: number;
	number: string;
	name: string;
	purchased: string;
	cost: string;
	assetType: string;
	description: string;
	serial: string;
	warranty: string;
	method: string;
	rate: string;
	averaging: string;
	residual: string;
	status: string;
	registerNumber?: string;
	openedYears?: number;
};
type TaxDoc = { year: string; kind: string; day: string; note: string };
type EaForm = { year: string; employee: string; gross: string; epf: string; pcb: string };
type FormC = { year: string; chargeable: string; rate: string; status: string };
type FormE = { year: string; employees: string; remuneration: string; status: string };
type Cp204 = { year: string; estimate: string; revision: string; note: string };
type Computation = { year: string; profit: string; addback: string; deduction: string; rate: string };
type AuditDoc = { year: string; kind: string; title: string; day: string; note: string; file: string };
type AuditYear = { id?: number; start: string; end: string };
type AuditFile = { id?: number; yearId: string; folder: string; name: string; type: string; data: string; stamp?: string };
type AuditLetter = { year: string; title: string; party: string; day: string; note: string };
type AuditReport = { year: string; opinion: string; day: string; note: string };
type AuditQuery = { year: string; subject: string; status: string; day: string; note: string };
type LetterTemplate = { id?: number | string; title: string; type: string; updated_at: string; body: string };
type SavedLetter = { id?: number | string; title: string; template: string; recipient: string; saved_at: string; docx?: string; pdf?: string; docx_data?: string; pdf_data?: string };
type DocVersion = { n: number; uploaded_at: string; uploaded_by: string; file: string; note: string };
type DocumentItem = { id?: string; kind: string; subtype: string; title: string; party: string; draft?: boolean; versions: DocVersion[] };
type VehicleFile = { kind: string; filename: string; expires_at: string; note: string; uploaded_by: string; created_at?: string };
type Vehicle = { id?: number; plate: string; make: string; model: string; year: string; colour: string; note: string; created_at?: string; files: VehicleFile[] };

type OrgRow = {
	id?: number;
	name: string;
	kind: string;
	phone?: string;
	email?: string;
	note?: string;
	created_at?: string;
	profile?: Partial<EmployeeProfile>;
	party?: PartyDetails;
	documents?: EmployeeDocument[];
};
type CalEvent = { id?: number; title: string; day: string; start?: string; end?: string; note?: string; allDay?: boolean };

type Books = {
	deals: Array<{ title: string; party: string; amount: string; stage: string; expected: string; note: string }>;
	items: Array<{ name: string; sku: string; unit: string; price: string; note: string }>;
	inventory: InventoryRow[];
	inventoryCategories: LookupRow[];
	inventoryBrands: LookupRow[];
	consumables: Array<{ id?: number; code: string; category: string; name: string; description?: string; brand?: string; specification?: string; unit: string; qty: string; minQty: string; unitPrice: string; vendorId?: string; location: "internal" | "external"; customerId?: string; siteId?: string }>;
	consumableMovements: Array<{ id?: number; consumableId: number; type: "in" | "out" | "transfer"; qty: string; unit: string; at: string; note?: string; fromLocation?: string; toLocation?: string; customerId?: string; siteId?: string }>;
	consumableSettings: Array<{ id?: number; categories?: string[]; units?: string[]; conversions?: Array<{ fromUnit: string; toUnit: string; factor: string; consumableId?: number }> }>;
	customerSites: Array<{ id?: number; name: string; customerId: string; address?: string; state?: string; contactPerson?: string; phone?: string; status?: string; notes?: string }>;
	projects: Array<{ id?: number; name: string; note: string; customerId?: string; siteId?: string }>;
	work: Array<{ id?: number; title: string; projectId: string; status: string; due: string; start?: string; salesOrderId?: number | string; workTypes?: string[]; assignee?: string }>;
	receipts: Array<{ id?: number; party: string; doc_at: string; ref_no: string; against: string; method: string; amount: string; note: string; invoiceId?: number | string }>;
	purchases: Record<string, PurchaseDoc[]>;
	leave: LeaveRow[];
	leavePolicy: LeavePolicy[];
	attendance: AttendanceRow[];
	attendanceSlots: Slot[];
	attendanceSettings: { lateMinutes: number; shifts: Shift[] };
	claims: ClaimRow[];
	claimPolicy: ClaimPolicy[];
	employees: Employee[];
	payrollSettings: { payDay: number; epf: number; socso: string; eis: string };
	payrollRuns: PayRun[];
	accounts: Account[];
	journals: Journal[];
	banks: Bank[];
	moves: Move[];
	organisations: OrgRow[];
	events: CalEvent[];
	transfers: Transfer[];
	internal: Array<{ id?: number; date: string; remarks: string; payee: string; ref: string; amount: string; via: string; payment: string }>;
	assets: FixedAsset[];
	phones: Array<{ id?: number; line?: string; telco: string; plan: string; phone: string; type: string; user: string }>;
	credentials: Array<{ id?: number; name: string; userId: string; password: string; link: string }>;
	maintenance: Array<{ id?: number; purpose: string; place: string; date: string; status: string; note: string }>;
	repairs: Array<{ id?: number; item: string; required: string; place: string; date: string; status: string; note: string }>;
	register: Array<{ id?: number; number?: string; name: string; assetId: string; financeNumber?: string; note: string; assetType?: string; purchased?: string; cost?: string; serial?: string; warranty?: string; description?: string }>;
	taxDocuments: TaxDoc[];
	eaForms: EaForm[];
	formC: FormC[];
	formE: FormE[];
	cp204: Cp204[];
	computations: Computation[];
	auditDocuments: AuditDoc[];
	auditYears: AuditYear[];
	auditFiles: AuditFile[];
	auditLetters: AuditLetter[];
	auditReports: AuditReport[];
	auditQueries: AuditQuery[];
	letterTemplates: LetterTemplate[];
	savedLetters: SavedLetter[];
	documents: DocumentItem[];
	vehicles: Vehicle[];
};

const EMPTY_BOOKS: Books = {
	deals: [],
	items: [],
	inventory: [],
	inventoryCategories: [],
	inventoryBrands: [],
	consumables: [],
	consumableMovements: [],
	consumableSettings: [],
	customerSites: [],
	projects: [],
	work: [],
	receipts: [],
	purchases: {},
	leave: [],
	leavePolicy: [],
	attendance: [],
	attendanceSlots: [],
	attendanceSettings: { lateMinutes: 0, shifts: [] },
	claims: [],
	claimPolicy: [],
	employees: [],
	payrollSettings: { payDay: 0, epf: 0, socso: "", eis: "" },
	payrollRuns: [],
	accounts: [],
	journals: [],
	banks: [],
	moves: [],
	organisations: [],
	events: [],
	transfers: [],
	internal: [],
	assets: [],
	phones: [],
	credentials: [],
	maintenance: [],
	repairs: [],
	register: [],
	taxDocuments: [],
	eaForms: [],
	formC: [],
	formE: [],
	cp204: [],
	computations: [],
	auditDocuments: [],
	auditYears: [],
	auditFiles: [],
	auditLetters: [],
	auditReports: [],
	auditQueries: [],
	letterTemplates: [],
	savedLetters: [],
	documents: [],
	vehicles: [],
};

function booksOf(data: HubRecordsDto): Books {
	const raw = data.library?.books;
	if (!raw || typeof raw !== "object" || Array.isArray(raw)) return EMPTY_BOOKS;
	return { ...EMPTY_BOOKS, ...(raw as Partial<Books>) };
}

function yesNo(value: boolean): string {
	return value ? "Yes" : "No";
}

function Bar({ sub, children }: { sub: string; children?: ReactNode }) {
	return (
		<div className="row">
			<p className="sub">{sub}</p>
			{children ? <div className="actions">{children}</div> : null}
		</div>
	);
}

function Modal({
	open,
	title,
	error,
	pending,
	action = "Save",
	hideSubmit = false,
	className,
	extra,
	renderHeader,
	dialogLabelId,
	modalFooter,
	onClose,
	onSubmit,
	children,
}: {
	open: boolean;
	title: string;
	error: string;
	pending: boolean;
	action?: string;
	hideSubmit?: boolean;
	className?: string;
	extra?: ReactNode;
	renderHeader?: () => ReactNode;
	dialogLabelId?: string;
	modalFooter?: ReactNode;
	onClose: () => void;
	onSubmit: (form: FormData) => void;
	children: ReactNode;
}) {
	const ref = useRef<HTMLDialogElement>(null);
	useEffect(() => {
		const dialog = ref.current;
		if (!open || !dialog) return;
		if (!dialog.open) dialog.showModal();
	}, [open]);
	if (!open) return null;
	return (
		<dialog
			ref={ref}
			className={className ? `modal ${className}` : "modal"}
			aria-labelledby={dialogLabelId}
			aria-label={!dialogLabelId && !renderHeader ? title : undefined}
			onClose={onClose}
			onClick={(event) => {
				if (event.target === ref.current) onClose();
			}}
		>
			<form
				className="grid"
				onSubmit={(event: FormEvent<HTMLFormElement>) => {
					event.preventDefault();
					onSubmit(new FormData(event.currentTarget));
				}}
			>
				{renderHeader ? renderHeader() : <div className="row tight"><h1>{title}</h1></div>}
				{error ? <p className="err">{error}</p> : null}
				<div className="modal-body">{children}</div>
				{modalFooter ? <div className="modal-footer">{modalFooter}</div> : null}
				<p className="actions">
					{extra}
					<button className="btn ghost" type="button" onClick={onClose}>{hideSubmit ? "Close" : "Cancel"}</button>
					{hideSubmit ? null : <button className="btn" type="submit" disabled={pending}>{action}</button>}
				</p>
			</form>
		</dialog>
	);
}

function useHubSave(run: (form: FormData) => Promise<unknown>) {
	const client = useQueryClient();
	const [open, setOpen] = useState(false);
	const [error, setError] = useState("");
	const [pending, setPending] = useState(false);
	async function save(form: FormData) {
		setPending(true);
		setError("");
		try {
			await run(form);
			await client.invalidateQueries({ queryKey: queryKeys.hubRecords });
			setOpen(false);
		} catch (err) {
			setError(presentError(err).title);
		} finally {
			setPending(false);
		}
	}
	return { open, setOpen, error, pending, save };
}

function textOf(form: FormData, name: string) {
	return String(form.get(name) ?? "").trim();
}

function csvCell(value: string) {
	return `"${value.replaceAll("\"", "\"\"")}"`;
}

function Chips({ items, current, onPick }: { items: Array<[string, string]>; current: string; onPick: (value: string) => void }) {
	return (
		<div className="filters">
			{items.map(([value, label]) => (
				<button key={value || "all"} type="button" className={current === value ? "chip active" : "chip"} onClick={() => onPick(value)}>
					{label}
				</button>
			))}
		</div>
	);
}

function DocTypeTabs({
	items,
	current,
	onPick,
	label,
}: {
	items: Array<[string, string]>;
	current: string;
	onPick: (value: string) => void;
	label: string;
}) {
	return (
		<div className="doc-type-tabs" role="tablist" aria-label={label}>
			{items.map(([value, text]) => {
				const active = current === value;
				return (
					<button
						key={value || "all"}
						type="button"
						role="tab"
						aria-selected={active}
						className={active ? "doc-type-tab active" : "doc-type-tab"}
						onClick={() => onPick(value)}
					>
						{text}
					</button>
				);
			})}
		</div>
	);
}

function Empty({ children }: { children: string }) {
	return <p className="empty">{children}</p>;
}

function Grid({ columns, rows, onRow, onRowMenu, className }: { columns: string[]; rows: string[][]; onRow?: (index: number) => void; onRowMenu?: (index: number, event: MouseEvent<HTMLTableRowElement>) => void; className?: string }) {
	if (rows.length === 0) return null;
	return (
		<div className="table-wrap">
			<table className={className}>
				<thead>
					<tr>
						{columns.map((column) => (
							<th key={column}>{column}</th>
						))}
					</tr>
				</thead>
				<tbody>
					{rows.map((row, index) => (
						<tr
							key={index}
							className={onRow || onRowMenu ? "clickable" : undefined}
							onClick={onRow ? () => onRow(index) : undefined}
							onContextMenu={onRowMenu ? (event) => onRowMenu(index, event) : undefined}
						>
							{row.map((cell, cellIndex) => (
								<td key={cellIndex}>{cell || "—"}</td>
							))}
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}

function useHubBundle(mine = false) {
	const records = useQuery({
		queryKey: [...queryKeys.hubRecords, mine ? "mine" : "all"],
		queryFn: () => api.getHubRecords(mine),
	});
	const sites = useQuery({ queryKey: queryKeys.siteRecords, queryFn: () => api.getSiteRecords() });
	return {
		records,
		sites: sites.data?.sites ?? [],
	};
}

export function HubPage({ id }: { id: string }) {
	const location = useLocation();
	const mineOnly = id === "tasks" && location.pathname.includes("/my/tasks");
	const bundle = useHubBundle(mineOnly);
	if (bundle.records.isLoading) return <p className="empty">Loading records.</p>;
	if (bundle.records.isError && !bundle.records.data) {
		return <p className="err">{presentError(bundle.records.error).title}</p>;
	}
	if (!bundle.records.data) return null;
	return <Screen id={id} data={bundle.records.data} sites={bundle.sites} mineOnly={mineOnly} />;
}

function Screen({
	id,
	data,
	sites,
	mineOnly = false,
}: {
	id: string;
	data: HubRecordsDto;
	sites: WorkspaceSiteDto[];
	mineOnly?: boolean;
}) {
	const books = booksOf(data);
	const [params, setParams] = useSearchParams();
	const tab = params.get("tab") ?? "";
	const pick = (value: string) => {
		const next = new URLSearchParams(params);
		if (value) next.set("tab", value);
		else next.delete("tab");
		setParams(next);
	};

	if (id === "tasks") return <Tasks data={data} mineOnly={mineOnly} />;
	if (id === "leads") return <Leads data={data} sites={sites} />;
	if (id === "calendar") return <Calendar data={data} books={books} />;
	if (id === "organisation") return <Organisations books={books} />;
	if (id === "inventory") return <InventoryHub books={books} columnPrefs={data.inventoryColumnPrefs} />;
	if (id === "consumables") return <ConsumablesHub books={books} />;
	if (id === "customer-sites") return <CustomerSitesHub books={books} status={tab} onStatus={pick} />;
	if (id === "projects") return <ProjectsHub books={books} />;
	if (id === "work") {
		const workParam = params.get("work") ?? "";
		const openWorkId = /^\d+$/.test(workParam) ? Number(workParam) : null;
		const workType = params.get("type") ?? "";
		const pickWorkType = (value: string) => {
			const next = new URLSearchParams(params);
			if (value) next.set("type", value);
			else next.delete("type");
			setParams(next);
		};
		return (
			<Work
				books={books}
				people={data.people}
				assigneeColors={data.assigneeColors ?? {}}
				status={tab}
				workType={workType}
				project={params.get("project") ?? ""}
				onStatus={pick}
				onWorkType={pickWorkType}
				openWorkId={openWorkId}
			/>
		);
	}
	if (id === "deals") return <Deals books={books} />;
	if (id === "items") return <Items books={books} />;
	if (id === "receipts") return <Receipts books={books} brands={data.brands} />;
	if (id === "quotations" || id === "sales-orders" || id === "invoices" || id === "bills" || id === "purchase-orders" || id === "credits") {
		return <Purchase id={id} books={books} brands={data.brands} tab={tab} onTab={pick} />;
	}
	if (id === "expenses") return <Expenses data={data} />;
	if (id === "leave") return <Leave books={books} tab={tab || "all"} onTab={pick} />;
	if (id === "attendance") return <Attendance books={books} tab={tab || "all"} onTab={pick} />;
	if (id === "claims") return <Claims books={books} tab={tab || "all"} onTab={pick} />;
	if (id === "payroll") return <Payroll books={books} tab={tab || "employees"} onTab={pick} />;
	if (id === "internal") return <Internal books={books} />;
	if (id === "transactions") return <Transactions books={books} />;
	if (id === "journals") return <Journals books={books} />;
	if (id === "accounts") return <Accounts books={books} />;
	if (id === "tax") return <Tax books={books} tab={tab || "documents"} onTab={pick} />;
	if (id === "audit") return <Audit books={books} tab={tab || "documents"} onTab={pick} />;
	if (id === "assets") return <Assets books={books} />;
	if (id === "maintenance") return <Maintenance books={books} />;
	if (id === "letters") return <Letters books={books} tab={tab || "template"} onTab={pick} />;
	if (id === "repairs") return <Repairs books={books} />;
	if (id === "register") return <Register books={books} />;
	if (id === "credentials") return <Credentials books={books} />;
	if (id === "documents") return <Documents books={books} />;
	if (id === "vehicles") return <Vehicles books={books} />;
	if (id === "phones") return <Phones books={books} />;
	if (id === "reports") return <Reports data={data} />;
	if (id === "activity") return <Activity data={data} />;
	if (id === "integration") return <Coming sub="Connect sites, forms, and other tools." />;
	return <Coming sub="This page is not built yet." />;
}

function isInstallTag(name: string) {
	return name.trim().toLowerCase() === "install";
}

function namesOf(value: string) {
	const seen = new Set<string>();
	const names: string[] = [];
	for (const item of value.split(",")) {
		const name = item.trim();
		if (!name) continue;
		const key = name.toLowerCase();
		if (seen.has(key)) continue;
		seen.add(key);
		names.push(name);
	}
	return names;
}

function taskAssigneeList(task: { assignee: string }) {
	return namesOf(task.assignee);
}

function resolveAssigneePicks(raw: string, options: string[]): string[] {
	const seen = new Set<string>();
	const picks: string[] = [];
	for (const part of namesOf(raw)) {
		const resolved = resolveAssigneePick(part, options);
		if (!resolved) continue;
		const key = resolved.toLowerCase();
		if (seen.has(key)) continue;
		seen.add(key);
		picks.push(resolved);
	}
	return picks;
}

function assigneeSummaryLabel(picked: string[]) {
	if (picked.length === 0) return "Unassigned";
	if (picked.length === 1) return personLabel(picked[0]!);
	return `${personLabel(picked[0]!)} +${picked.length - 1}`;
}

function formatAssigneeLabels(assignee: string) {
	const list = namesOf(assignee);
	if (!list.length) return "";
	return list.map((name) => personLabel(name)).join(", ");
}

function parseStamp(value: string): Date | null {
	if (!value) return null;
	const raw = value.includes("T") ? value.trim() : value.trim().replace(" ", "T");
	const normalized = raw
		.replace(/([+-])(\d{2})$/, "$1$2:00")
		.replace(/([+-])(\d{2})(\d{2})$/, "$1$2:$3");
	const date = new Date(/Z$|[+-]\d{2}:\d{2}$/.test(normalized) ? normalized : `${normalized}+08:00`);
	return Number.isNaN(date.getTime()) ? null : date;
}

function createdLabel(value: string): string {
	const date = parseStamp(value);
	if (!date) return "";
	return new Intl.DateTimeFormat("en-GB", {
		timeZone: "Asia/Kuala_Lumpur",
		day: "numeric",
		month: "short",
		year: "numeric",
	}).format(date);
}

function dayLabel(value: string): string {
	if (!value) return "";
	if (value.includes("T") || value.includes(" ")) return createdLabel(value);
	const date = new Date(`${value}T12:00:00`);
	if (Number.isNaN(date.getTime())) return value;
	return date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

/** Kanban card face — day and month only; full date stays in labels and tooltips. */
function dayLabelShort(value: string): string {
	if (!value) return "";
	if (value.includes("T") || value.includes(" ")) {
		const full = createdLabel(value);
		return full.replace(/\s+\d{4}$/, "");
	}
	const date = new Date(`${value}T12:00:00`);
	if (Number.isNaN(date.getTime())) return value;
	return date.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

function todayIso(): string {
	return new Intl.DateTimeFormat("en-CA", {
		timeZone: "Asia/Kuala_Lumpur",
		year: "numeric",
		month: "2-digit",
		day: "2-digit",
	}).format(new Date());
}

function isOverdue(dueOn: string, status: string): boolean {
	if (!dueOn.trim()) return false;
	if (taskStatusOf(status) === "completed") return false;
	return dueOn < todayIso();
}

function personLabel(name: string): string {
	return name.trim().split(/\s+/).map((part) => {
		if (!part || part.includes("@") || part.startsWith("(")) return part;
		return part.charAt(0).toUpperCase() + part.slice(1);
	}).join(" ");
}

function assigneeOptionMatch(value: string, items: Array<[string, string]>): [string, string] | undefined {
	const key = value.trim().toLowerCase();
	if (!key) return items.find(([item]) => !item);
	return items.find(([item]) => item.trim().toLowerCase() === key);
}

function resolveAssigneePick(raw: string, options: string[]): string {
	const key = raw.trim().toLowerCase();
	if (!key) return "";
	return options.find((name) => name.trim().toLowerCase() === key) ?? "";
}

function parseChecklist(value: unknown): TaskChecklistItem[] {
	const raw = typeof value === "string" ? (() => {
		try {
			return JSON.parse(value) as unknown;
		} catch {
			return [];
		}
	})() : value;
	if (!Array.isArray(raw)) return [];
	const items: TaskChecklistItem[] = [];
	for (const [index, item] of raw.entries()) {
		if (!item || typeof item !== "object") continue;
		const row = item as { id?: unknown; title?: unknown; done?: unknown };
		const title = String(row.title ?? "").trim();
		if (!title) continue;
		const id = String(row.id ?? `item-${index + 1}`).trim().slice(0, 80);
		items.push({
			id: id || `item-${index + 1}`,
			title: title.slice(0, 200),
			done: row.done === true,
		});
	}
	return items.slice(0, 50);
}

function checklistId(): string {
	return `c${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
}

function attachmentId(): string {
	return `a${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
}

function attachmentDataUrl(data: string, type: string): string {
	const raw = data.trim();
	if (!raw) return "";
	if (raw.startsWith("data:") || raw.startsWith("blob:") || /^https?:\/\//i.test(raw)) return raw;
	const mime = type.trim() || "application/octet-stream";
	return `data:${mime};base64,${raw}`;
}

function taskAttachmentIsImage(row: TaskAttachmentItem): boolean {
	if (!row.data) return false;
	return row.type.startsWith("image/")
		|| /\.(jpe?g|png|gif|webp|avif)$/i.test(row.name)
		|| row.data.startsWith("/9j/")
		|| row.data.startsWith("iVBOR");
}

function taskAttachmentPreviewUrl(row: TaskAttachmentItem): string {
	const mime = row.type.startsWith("image/") ? row.type : "image/jpeg";
	return attachmentDataUrl(row.data, mime);
}

function taskCoverImageUrl(attachments: TaskAttachmentItem[]): string {
	for (const row of attachments) {
		if (!taskAttachmentIsImage(row)) continue;
		return taskAttachmentPreviewUrl(row);
	}
	return "";
}

function parseAttachments(value: unknown): TaskAttachmentItem[] {
	const raw = typeof value === "string" ? (() => {
		try {
			return JSON.parse(value) as unknown;
		} catch {
			return [];
		}
	})() : value;
	if (!Array.isArray(raw)) return [];
	const items: TaskAttachmentItem[] = [];
	for (const [index, item] of raw.entries()) {
		if (!item || typeof item !== "object") continue;
		const row = item as { id?: unknown; name?: unknown; type?: unknown; size?: unknown; data?: unknown };
		const name = String(row.name ?? "").trim();
		const data = String(row.data ?? "").trim();
		if (!name || !data) continue;
		const size = Number(row.size);
		const id = String(row.id ?? `file-${index + 1}`).trim().slice(0, 80);
		items.push({
			id: id || `file-${index + 1}`,
			name: name.slice(0, 255),
			type: String(row.type ?? "application/octet-stream").trim().slice(0, 127) || "application/octet-stream",
			size: Number.isFinite(size) && size >= 0 ? Math.min(Math.floor(size), TASK_ATTACHMENT_MAX_BYTES) : 0,
			data,
		});
	}
	return items.slice(0, TASK_ATTACHMENT_MAX_COUNT);
}

async function fileToTaskAttachment(file: File): Promise<TaskAttachmentItem | null> {
	if (file.size === 0 || file.size > TASK_ATTACHMENT_MAX_BYTES) return null;
	const bytes = new Uint8Array(await file.arrayBuffer());
	let binary = "";
	for (let index = 0; index < bytes.length; index += 8192) {
		const slice = bytes.subarray(index, index + 8192);
		binary += String.fromCharCode(...slice);
	}
	return {
		id: attachmentId(),
		name: file.name.slice(0, 255),
		type: file.type || "application/octet-stream",
		size: file.size,
		data: btoa(binary),
	};
}

function formatFileSize(bytes: number): string {
	if (!Number.isFinite(bytes) || bytes <= 0) return "";
	if (bytes < 1024) return `${bytes} B`;
	if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(bytes < 10_240 ? 1 : 0)} KB`;
	return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function taskStatusOf(status: string): (typeof TASK_STATUSES)[number] {
	const key = status.trim().toLowerCase();
	const aliases: Record<string, (typeof TASK_STATUSES)[number]> = {
		complete: "completed",
		done: "completed",
		finished: "completed",
		review: "reviewed",
		"need review": "reviewed",
	};
	if (aliases[key]) return aliases[key];
	return TASK_STATUSES.includes(key as (typeof TASK_STATUSES)[number]) ? key as (typeof TASK_STATUSES)[number] : "new";
}

type FilterChoice = readonly [string, string] | readonly [string, string, number];
type TaskGroupBy = "status" | "assignee" | "tag" | "";

function TaskTagIcon({ size = 16 }: { size?: number }) {
	return (
		<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
			<path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
			<circle cx="7" cy="7" r="1.5" fill="currentColor" stroke="none" />
		</svg>
	);
}

function FilterMenu({
	label,
	value,
	ariaLabel,
	items,
	onPick,
	active = Boolean(value),
	counted = false,
	iconOnly = false,
}: {
	label: string;
	value: string;
	ariaLabel: string;
	items: FilterChoice[];
	onPick: (next: string) => void;
	active?: boolean;
	counted?: boolean;
	iconOnly?: boolean;
}) {
	const current = items.find(([id]) => id === value)?.[1]
		?? (value === "__none__" ? "Unassigned" : value || items[0]?.[1] || label);
	const titleText = value && current !== "All" ? `${ariaLabel}: ${current}` : ariaLabel;
	return (
		<details
			className={[
				"select",
				"task-filter-menu",
				iconOnly ? "task-view-tag-filter" : "",
				active ? "is-on" : "",
			].filter(Boolean).join(" ")}
			onToggle={(event) => closeSiblingSelectMenus(event.currentTarget)}
			onPointerDown={(event) => event.stopPropagation()}
		>
			<summary aria-label={ariaLabel} title={titleText}>
				{iconOnly ? (
					<>
						<TaskTagIcon size={16} />
						<span className="sr-only">{value && current !== "All" ? current : "All"}</span>
					</>
				) : (
					<>
						<span className="task-filter-k">{label}</span>
						<span className="task-filter-v">{current}</span>
						<ChevronDown size={12} aria-hidden="true" />
					</>
				)}
			</summary>
			<div className="select-pop" role="listbox">
				{items.map((item) => {
					const [id, text, count] = item;
					return (
						<button
							key={id || "all"}
							type="button"
							role="option"
							aria-selected={id === value}
							aria-label={counted && count != null ? `${text} · ${count}` : text}
							className={id === value ? "select-item active" : "select-item"}
							onClick={(event) => {
								onPick(id);
								event.currentTarget.closest("details")?.removeAttribute("open");
							}}
						>
							<span>{text}</span>
							{count != null ? <span className="task-filter-n" aria-hidden="true">{count}</span> : null}
						</button>
					);
				})}
			</div>
		</details>
	);
}

function groupTasks(tasks: HubTask[], groupBy: TaskGroupBy): Array<{ key: string; label: string; tasks: HubTask[] }> {
	if (!groupBy) return [{ key: "all", label: "", tasks }];
	const buckets = new Map<string, { key: string; label: string; tasks: HubTask[] }>();
	const take = (key: string, label: string) => {
		const current = buckets.get(key) ?? { key, label, tasks: [] };
		if (!buckets.has(key)) buckets.set(key, current);
		return current;
	};
	if (groupBy === "status") {
		for (const status of TASK_STATUSES) take(status, TASK_LABEL[status] ?? status);
		for (const task of tasks) take(taskStatusOf(task.status), TASK_LABEL[taskStatusOf(task.status)] ?? task.status).tasks.push(task);
		return [...buckets.values()].filter((group) => group.tasks.length);
	}
	if (groupBy === "assignee") {
		for (const task of tasks) {
			const assignees = taskAssigneeList(task);
			if (assignees.length === 0) take("__none__", "Unassigned").tasks.push(task);
			else for (const name of assignees) take(name, personLabel(name)).tasks.push(task);
		}
		return [...buckets.values()].sort((left, right) => Number(left.key === "__none__") - Number(right.key === "__none__") || left.label.localeCompare(right.label));
	}
	for (const task of tasks) {
		const name = namesOf(task.tags)[0] ?? "";
		if (name) take(name, name).tasks.push(task);
		else take("__none__", "No tag").tasks.push(task);
	}
	return [...buckets.values()].sort((left, right) => Number(left.key === "__none__") - Number(right.key === "__none__") || left.label.localeCompare(right.label));
}

function TaskTableColGroup() {
	return (
		<colgroup>
			<col className="task-col-task" />
			<col className="task-col-assignee" />
			<col />
			<col />
			<col />
			<col />
		</colgroup>
	);
}

function TaskTableHead() {
	return (
		<thead>
			<tr>
				<th>Task</th>
				<th>Assigned to</th>
				<th>Created</th>
				<th>Deadline</th>
				<th>Status</th>
				<th>Tags</th>
			</tr>
		</thead>
	);
}

function TaskTableRows({
	tasks,
	groupKey,
	tagColorByLowerName,
	assigneeColorByLowerName,
	onOpen,
	onMenu,
}: {
	tasks: HubTask[];
	groupKey: string;
	tagColorByLowerName: ReadonlyMap<string, string | undefined>;
	assigneeColorByLowerName: ReadonlyMap<string, string | undefined>;
	onOpen: (task: HubTask) => void;
	onMenu: (event: MouseEvent<HTMLTableRowElement>, task: HubTask) => void;
}) {
	return tasks.map((task) => {
		const overdue = isOverdue(task.dueOn, taskStatusOf(task.status));
		const tagNames = namesOf(task.tags);
		const assignees = taskAssigneeList(task);
		const assigneeTitle = assignees.length ? formatAssigneeLabels(task.assignee) : "";
		return (
			<tr
				key={task.id || `${groupKey}-${task.title}-${task.dueOn}`}
				className={`clickable${overdue ? " is-overdue" : ""}`}
				onClick={() => onOpen(task)}
				onContextMenu={(event) => onMenu(event, task)}
			>
				<td data-label="Task">{task.title || "—"}</td>
				<td data-label="Assigned to">
					{assignees.length > 0 ? (
						<div className="task-card-assignees" title={assigneeTitle || undefined}>
							{assignees.map((name) => (
								<span
									key={name.toLowerCase()}
									className={`task-card-assignee-name ${hubToneClass(
										resolveAssigneeColorValue(name, assigneeColorByLowerName),
										name,
									)}`}
									style={hubToneStyle(
										resolveAssigneeColorValue(name, assigneeColorByLowerName),
										name,
									)}
								>
									{personLabel(name)}
								</span>
							))}
						</div>
					) : (
						"Unassigned"
					)}
				</td>
				<td data-label="Created">{createdLabel(task.createdAt) || "—"}</td>
				<td data-label="Deadline">
					{task.dueOn ? dayLabel(task.dueOn) : "No deadline"}
					{overdue ? <span className="task-overdue">Overdue</span> : null}
				</td>
				<td data-label="Status">{TASK_LABEL[taskStatusOf(task.status)]}</td>
				<td data-label="Tags">
					{tagNames.length ? (
						<span className="task-table-tag-chips">
							{tagNames.map((name) => (
								<span
									key={name}
									className={`task-card-tag ${hubToneClass(resolveTagColorValue(name, tagColorByLowerName), name)}`}
									style={hubToneStyle(resolveTagColorValue(name, tagColorByLowerName), name)}
								>
									{name}
								</span>
							))}
						</span>
					) : (
						"—"
					)}
				</td>
			</tr>
		);
	});
}

function TaskTableShell({
	tasks,
	groupKey,
	tagColorByLowerName,
	assigneeColorByLowerName,
	onOpen,
	onMenu,
}: {
	tasks: HubTask[];
	groupKey: string;
	tagColorByLowerName: ReadonlyMap<string, string | undefined>;
	assigneeColorByLowerName: ReadonlyMap<string, string | undefined>;
	onOpen: (task: HubTask) => void;
	onMenu: (event: MouseEvent<HTMLTableRowElement>, task: HubTask) => void;
}) {
	return (
		<div className="table-wrap">
			<table className="task-table">
				<TaskTableColGroup />
				<TaskTableHead />
				<tbody>
					<TaskTableRows
						tasks={tasks}
						groupKey={groupKey}
						tagColorByLowerName={tagColorByLowerName}
						assigneeColorByLowerName={assigneeColorByLowerName}
						onOpen={onOpen}
						onMenu={onMenu}
					/>
				</tbody>
			</table>
		</div>
	);
}

function TaskTableGroupBlock({
	groupKey,
	label,
	tasks,
	collapsed,
	onToggle,
	tagColorByLowerName,
	assigneeColorByLowerName,
	onOpen,
	onMenu,
}: {
	groupKey: string;
	label: string;
	tasks: HubTask[];
	collapsed: boolean;
	onToggle: () => void;
	tagColorByLowerName: ReadonlyMap<string, string | undefined>;
	assigneeColorByLowerName: ReadonlyMap<string, string | undefined>;
	onOpen: (task: HubTask) => void;
	onMenu: (event: MouseEvent<HTMLTableRowElement>, task: HubTask) => void;
}) {
	const panelId = useId();
	return (
		<section className="task-table-group">
			<button
				type="button"
				className={`task-table-group-toggle${collapsed ? " is-collapsed" : ""}`}
				aria-expanded={!collapsed}
				aria-controls={panelId}
				onClick={onToggle}
			>
				<ChevronDown size={14} className="task-table-group-chevron" aria-hidden="true" />
				<span className="task-group-head">
					<span className="task-group-name">{label}</span>
					<span className="task-group-n">{tasks.length}</span>
				</span>
			</button>
			<div id={panelId} className={`task-table-group-body${collapsed ? " is-collapsed" : ""}`}>
				<div className="task-table-group-inner">
					<TaskTableShell
						tasks={tasks}
						groupKey={groupKey}
						tagColorByLowerName={tagColorByLowerName}
						assigneeColorByLowerName={assigneeColorByLowerName}
						onOpen={onOpen}
						onMenu={onMenu}
					/>
				</div>
			</div>
		</section>
	);
}

function TaskTable({
	tasks,
	groupBy,
	tagColorByLowerName,
	assigneeColorByLowerName,
	onOpen,
	onMenu,
}: {
	tasks: HubTask[];
	groupBy: TaskGroupBy;
	tagColorByLowerName: ReadonlyMap<string, string | undefined>;
	assigneeColorByLowerName: ReadonlyMap<string, string | undefined>;
	onOpen: (task: HubTask) => void;
	onMenu: (event: MouseEvent<HTMLTableRowElement>, task: HubTask) => void;
}) {
	const groups = groupTasks(tasks, groupBy);
	const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});
	useEffect(() => {
		setCollapsedGroups({});
	}, [groupBy]);
	if (!groupBy) {
		return (
			<TaskTableShell
				tasks={tasks}
				groupKey="all"
				tagColorByLowerName={tagColorByLowerName}
				assigneeColorByLowerName={assigneeColorByLowerName}
				onOpen={onOpen}
				onMenu={onMenu}
			/>
		);
	}
	return (
		<div className="task-table-groups">
			{groups.map((group) => (
				<TaskTableGroupBlock
					key={group.key}
					groupKey={group.key}
					label={group.label}
					tasks={group.tasks}
					collapsed={Boolean(collapsedGroups[group.key])}
					onToggle={() =>
						setCollapsedGroups((current) => ({ ...current, [group.key]: !(current[group.key] ?? false) }))
					}
					tagColorByLowerName={tagColorByLowerName}
					assigneeColorByLowerName={assigneeColorByLowerName}
					onOpen={onOpen}
					onMenu={onMenu}
				/>
			))}
		</div>
	);
}

function TaskChecklistItemRow({
	item,
	editing,
	onStartEdit,
	onChangeTitle,
	onCommitTitle,
	onToggleDone,
	onRemove,
}: {
	item: TaskChecklistItem;
	editing: boolean;
	onStartEdit: () => void;
	onChangeTitle: (title: string) => void;
	onCommitTitle: (title: string) => void;
	onToggleDone: () => void;
	onRemove: () => void;
}) {
	return (
		<li className={`task-check-item${item.done ? " is-done" : ""}`}>
			<label className="task-check-item-check">
				<input
					type="checkbox"
					checked={item.done}
					onChange={onToggleDone}
				/>
				<span className="sr-only">{item.done ? "Completed" : "Incomplete"}</span>
			</label>
			{editing ? (
				<input
					aria-label="Checklist item"
					className={`task-check-item-name is-editing${item.done ? " is-done" : ""}`}
					value={item.title}
					autoFocus
					onChange={(event) => onChangeTitle(event.target.value)}
					onBlur={(event) => onCommitTitle(event.target.value)}
					onKeyDown={(event) => {
						if (event.key === "Enter") {
							event.preventDefault();
							event.currentTarget.blur();
						}
					}}
				/>
			) : (
				<button
					type="button"
					className={`task-check-item-label${item.done ? " is-done" : ""}`}
					onClick={onStartEdit}
				>
					{item.title}
				</button>
			)}
			<button type="button" className="task-check-del" aria-label={`Remove ${item.title}`} onClick={onRemove}>
				×
			</button>
		</li>
	);
}

function TaskAutoResizeTextarea({
	id,
	name,
	className,
	placeholder,
	defaultValue,
	readOnly,
	"aria-label": ariaLabel,
}: {
	id: string;
	name: string;
	className?: string;
	placeholder?: string;
	defaultValue: string;
	readOnly?: boolean;
	"aria-label"?: string;
}) {
	const ref = useRef<HTMLTextAreaElement>(null);
	const resize = () => {
		const el = ref.current;
		if (!el) return;
		el.style.height = "0px";
		el.style.height = `${Math.max(36, el.scrollHeight)}px`;
	};
	useEffect(() => {
		resize();
	}, [defaultValue]);
	return (
		<textarea
			ref={ref}
			id={id}
			className={className}
			name={name}
			placeholder={placeholder}
			defaultValue={defaultValue}
			aria-label={ariaLabel}
			readOnly={readOnly}
			rows={1}
			onInput={resize}
		/>
	);
}

function TaskSelect({
	id,
	name,
	label,
	value,
	items,
	onPick,
	colorToneClass,
	colorToneStyle,
	colorPickerOpen,
	onColorPickerToggle,
	renderColorPicker,
}: {
	id: string;
	name: string;
	label: string;
	value: string;
	items: Array<[string, string]>;
	onPick: (next: string) => void;
	colorToneClass?: string;
	colorToneStyle?: Record<string, string>;
	colorPickerOpen?: boolean;
	onColorPickerToggle?: () => void;
	renderColorPicker?: () => ReactNode;
}) {
	const detailsRef = useRef<HTMLDetailsElement>(null);
	const active = name === "assignee"
		? assigneeOptionMatch(value, items)
		: items.find(([item]) => item === value);
	const current = active?.[1] ?? (name === "assignee" && value ? personLabel(value) : items[0]?.[1] ?? "");
	const fieldValue = active?.[0] ?? value;
	useEffect(() => {
		const details = detailsRef.current;
		if (!details) return;
		const dialog = details.closest("dialog.modal");
		const scrollRoot = dialog?.querySelector(".modal-body");
		if (!scrollRoot) return;
		const onScroll = () => {
			if (details.open) details.open = false;
		};
		scrollRoot.addEventListener("scroll", onScroll, { passive: true });
		return () => scrollRoot.removeEventListener("scroll", onScroll);
	}, []);
	return (
		<div className={colorToneClass ? "task-field is-color-anchor" : "task-field"}>
			<label id={`${id}-label`}>{label}</label>
			<input type="hidden" name={name} value={fieldValue} />
			<details
				ref={detailsRef}
				className="select task-select"
				onToggle={(event) => closeSiblingSelectMenus(event.currentTarget)}
				onPointerDown={(event) => event.stopPropagation()}
			>
				<summary aria-labelledby={`${id}-label`} aria-label={label}>
					{colorToneClass && value && onColorPickerToggle ? (
						<button
							type="button"
							className={`task-select-color-name ${colorToneClass}`}
							style={colorToneStyle}
							aria-expanded={colorPickerOpen}
							aria-label={`${current}, choose color`}
							onClick={(event) => {
								event.preventDefault();
								event.stopPropagation();
								onColorPickerToggle();
							}}
						>
							{current}
						</button>
					) : (
						<span>{current}</span>
					)}
					<ChevronDown size={12} aria-hidden="true" />
				</summary>
				<div className="select-pop" role="listbox">
					{items.map(([item, text]) => (
						<button
							key={item || "empty"}
							type="button"
							role="option"
							aria-selected={item === fieldValue}
							className={item === fieldValue ? "select-item active" : "select-item"}
							onClick={(event) => {
								onPick(item);
								event.currentTarget.closest("details")?.removeAttribute("open");
							}}
						>
							{text}
						</button>
					))}
				</div>
			</details>
			{colorPickerOpen && renderColorPicker ? renderColorPicker() : null}
		</div>
	);
}

function TaskAssigneesField({
	id,
	label,
	picked,
	assignable,
	onChange,
	assigneeColorByLowerName,
	colorPickerOpen,
	colorPickerUsername,
	onColorPickerToggle,
	renderColorPicker,
}: {
	id: string;
	label: string;
	picked: string[];
	assignable: string[];
	onChange: (next: string[]) => void;
	assigneeColorByLowerName: ReadonlyMap<string, string | undefined>;
	colorPickerOpen: boolean;
	colorPickerUsername: string;
	onColorPickerToggle: (username: string) => void;
	renderColorPicker: (username: string) => ReactNode;
}) {
	const detailsRef = useRef<HTMLDetailsElement>(null);
	const [assignMenuOpen, setAssignMenuOpen] = useState(false);
	const summaryName = assigneeSummaryLabel(picked);
	const summaryColorUser = picked.length === 1 ? picked[0]! : "";
	const colorToneClass = summaryColorUser
		? hubToneClass(resolveAssigneeColorValue(summaryColorUser, assigneeColorByLowerName), summaryColorUser)
		: undefined;
	const colorToneStyle = summaryColorUser
		? hubToneStyle(resolveAssigneeColorValue(summaryColorUser, assigneeColorByLowerName), summaryColorUser)
		: undefined;
	const pickerUsername = colorPickerUsername.trim();
	const pickerOpen = colorPickerOpen && Boolean(pickerUsername);
	const pickerKey = pickerUsername.toLowerCase();
	const isPickerFor = (name: string) => pickerOpen && name.trim().toLowerCase() === pickerKey;
	useEffect(() => {
		const details = detailsRef.current;
		if (!details) return;
		const dialog = details.closest("dialog.modal");
		const scrollRoot = dialog?.querySelector(".modal-body");
		if (!scrollRoot) return;
		const onScroll = () => {
			if (details.open) details.open = false;
		};
		scrollRoot.addEventListener("scroll", onScroll, { passive: true });
		return () => scrollRoot.removeEventListener("scroll", onScroll);
	}, []);
	const togglePerson = (name: string) => {
		const key = name.toLowerCase();
		const on = picked.some((row) => row.toLowerCase() === key);
		onChange(on ? picked.filter((row) => row.toLowerCase() !== key) : [...picked, name]);
	};
	const openColorPicker = (name: string, event: { preventDefault: () => void; stopPropagation: () => void }) => {
		event.preventDefault();
		event.stopPropagation();
		onColorPickerToggle(name);
	};
	return (
		<div className={colorToneClass || pickerOpen ? "task-field is-color-anchor" : "task-field"}>
			<label id={`${id}-label`}>{label}</label>
			<details
				ref={detailsRef}
				className="select task-select task-assignees-select"
				onToggle={(event) => {
					closeSiblingSelectMenus(event.currentTarget);
					setAssignMenuOpen(event.currentTarget.open);
				}}
				onPointerDown={(event) => event.stopPropagation()}
			>
				<summary aria-labelledby={`${id}-label`} aria-label={label}>
					{colorToneClass && summaryColorUser ? (
						<button
							type="button"
							className={`task-select-color-name ${colorToneClass}${isPickerFor(summaryColorUser) && !assignMenuOpen ? " is-open" : ""}`}
							style={colorToneStyle}
							aria-expanded={isPickerFor(summaryColorUser)}
							aria-label={`${summaryName}, choose color`}
							onClick={(event) => openColorPicker(summaryColorUser, event)}
						>
							{summaryName}
						</button>
					) : (
						<span>{summaryName}</span>
					)}
					<ChevronDown size={12} aria-hidden="true" />
				</summary>
				<div className="select-pop task-assignees-pop" role="listbox" aria-multiselectable="true">
					{picked.length > 0 ? (
						<div className="task-select-pop-divider">
							<button
								type="button"
								className="select-item task-assignees-clear"
								onClick={() => onChange([])}
							>
								Clear all
							</button>
						</div>
					) : null}
					{assignable.map((name) => {
						const on = picked.some((row) => row.toLowerCase() === name.toLowerCase());
						const personTone = hubToneClass(resolveAssigneeColorValue(name, assigneeColorByLowerName), name);
						const personToneStyle = hubToneStyle(resolveAssigneeColorValue(name, assigneeColorByLowerName), name);
						const colorOpen = isPickerFor(name) && assignMenuOpen;
						return (
							<label
								key={name}
								className={[
									"select-item task-assignees-option",
									on ? "active" : "",
									colorOpen ? "is-color-editing" : "",
								].filter(Boolean).join(" ")}
							>
								<input
									type="checkbox"
									checked={on}
									onChange={() => togglePerson(name)}
								/>
								<button
									type="button"
									className={`task-assignees-color-name task-select-color-name ${personTone}${colorOpen ? " is-open" : ""}`}
									style={personToneStyle}
									aria-expanded={colorOpen}
									aria-label={`${personLabel(name)}, choose color`}
									onClick={(event) => openColorPicker(name, event)}
								>
									{personLabel(name)}
								</button>
								{colorOpen ? renderColorPicker(name) : null}
							</label>
						);
					})}
				</div>
			</details>
			{pickerOpen && !assignMenuOpen ? renderColorPicker(pickerUsername) : null}
		</div>
	);
}

function TaskAttachmentsField({
	id,
	label,
	attachments,
	attachmentError,
	attachDragOver,
	setAttachDragOver,
	attachmentInputRef,
	onAddFiles,
	onDownload,
	onRemove,
}: {
	id: string;
	label: string;
	attachments: TaskAttachmentItem[];
	attachmentError: string;
	attachDragOver: boolean;
	setAttachDragOver: (next: boolean) => void;
	attachmentInputRef: RefObject<HTMLInputElement | null>;
	onAddFiles: (files: FileList | File[] | null | undefined) => void;
	onDownload: (row: TaskAttachmentItem) => void;
	onRemove: (fileId: string) => void | Promise<void>;
}) {
	const detailsRef = useRef<HTMLDetailsElement>(null);
	const prevCountRef = useRef(attachments.length);
	const countLabel = attachments.length === 0
		? "None"
		: attachments.length === 1
			? "1 file"
			: `${attachments.length} files`;
	useEffect(() => {
		const details = detailsRef.current;
		if (!details) return;
		const dialog = details.closest("dialog.modal");
		const scrollRoot = dialog?.querySelector(".modal-body");
		if (!scrollRoot) return;
		const onScroll = () => {
			if (details.open) details.open = false;
		};
		scrollRoot.addEventListener("scroll", onScroll, { passive: true });
		return () => scrollRoot.removeEventListener("scroll", onScroll);
	}, []);
	useEffect(() => {
		if (attachments.length > prevCountRef.current) {
			const details = detailsRef.current;
			if (details) details.open = false;
		}
		prevCountRef.current = attachments.length;
	}, [attachments.length]);
	return (
		<div
			className={`task-field task-field-attachments${attachDragOver ? " is-dragover" : ""}`}
			onDragEnter={(event) => {
				event.preventDefault();
				setAttachDragOver(true);
			}}
			onDragOver={(event) => {
				event.preventDefault();
				event.dataTransfer.dropEffect = "copy";
				setAttachDragOver(true);
			}}
			onDragLeave={(event) => {
				if (event.currentTarget.contains(event.relatedTarget as Node)) return;
				setAttachDragOver(false);
			}}
			onDrop={(event) => {
				event.preventDefault();
				setAttachDragOver(false);
				onAddFiles(event.dataTransfer.files);
			}}
		>
			<label id={`${id}-label`}>{label}</label>
			<details
				ref={detailsRef}
				className="select task-select task-attachments-select"
				onToggle={(event) => closeSiblingSelectMenus(event.currentTarget)}
				onPointerDown={(event) => event.stopPropagation()}
			>
				<summary aria-labelledby={`${id}-label`} aria-label={label}>
					<span className="task-attachments-trigger">
						<svg className="task-attachments-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
							<path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
						</svg>
						<span className="task-attachments-count">{countLabel}</span>
					</span>
					<ChevronDown size={12} aria-hidden="true" />
				</summary>
				<div className="select-pop task-attachments-pop" role="region" aria-label="Attachment files">
					{attachmentError ? <p className="err task-attach-err">{attachmentError}</p> : null}
					{attachments.length > 0 ? (
						<ul className="task-attachments-list">
							{attachments.map((row) => (
								<li key={row.id} className="task-attachments-item">
									<span className="task-attachments-file-icon" aria-hidden="true">
										{taskAttachmentIsImage(row) ? (
											<img
												className="task-attachments-file-thumb"
												src={taskAttachmentPreviewUrl(row)}
												alt=""
												loading="lazy"
											/>
										) : (
											<FileIcon size={14} weight="regular" />
										)}
									</span>
									<span className="task-attachments-item-text">
										<span className="task-attachments-item-name" title={row.name}>{row.name}</span>
										{row.size ? (
											<span className="task-attachments-item-meta">{formatFileSize(row.size)}</span>
										) : null}
									</span>
									<button
										type="button"
										className="task-attachments-icon-btn"
										aria-label={`Download ${row.name}`}
										title={`Download ${row.name}`}
										onClick={() => onDownload(row)}
									>
										<DownloadSimpleIcon size={15} weight="bold" aria-hidden="true" />
									</button>
									<button
										type="button"
										className="task-attachments-icon-btn is-remove"
										aria-label={`Remove ${row.name}`}
										title={`Remove ${row.name}`}
										onClick={() => void onRemove(row.id)}
									>
										<TrashIcon size={15} weight="regular" aria-hidden="true" />
									</button>
								</li>
							))}
						</ul>
					) : null}
					<button
						type="button"
						className="task-attachments-add"
						onClick={() => attachmentInputRef.current?.click()}
					>
						+ Add file
					</button>
				</div>
			</details>
			<input
				ref={attachmentInputRef}
				id={`${id}-file`}
				className="task-attach-file-input"
				type="file"
				multiple
				aria-label="Add attachment"
				onChange={(event) => {
					onAddFiles(event.target.files);
					event.target.value = "";
				}}
			/>
			<input type="hidden" name="attachments" value={JSON.stringify(attachments)} />
		</div>
	);
}

const PAYMENT_METHODS: Array<[string, string]> = [["transfer", "Transfer"], ["cash", "Cash"], ["cheque", "Cheque"]];
const INVOICE_MARKS: Array<[string, string]> = [["commercial", "Commercial"], ["proforma", "Proforma"]];
const PURCHASE_TERMS: Array<[string, string]> = [["net-30", "Net 30"], ["net-15", "Net 15"], ["net-7", "Net 7"], ["due-receipt", "Due on receipt"]];

function useCompactTaskBoard() {
	const [compact, setCompact] = useState(() => {
		if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
		return window.matchMedia("(max-width: 720px)").matches;
	});
	useEffect(() => {
		if (typeof window.matchMedia !== "function") return;
		const media = window.matchMedia("(max-width: 720px)");
		const sync = () => setCompact(media.matches);
		sync();
		media.addEventListener("change", sync);
		return () => media.removeEventListener("change", sync);
	}, []);
	return compact;
}

function TaskDetailMoreMenu({
	taskId,
	taskTitle,
	onDelete,
}: {
	taskId: string;
	taskTitle: string;
	onDelete: (id: string, title: string) => void | Promise<void>;
}) {
	const [open, setOpen] = useState(false);
	const rootRef = useRef<HTMLDivElement>(null);
	useEffect(() => {
		if (!open) return;
		const close = () => setOpen(false);
		const onPointerDown = (event: PointerEvent) => {
			const root = rootRef.current;
			if (root?.contains(event.target as Node)) return;
			close();
		};
		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key !== "Escape") return;
			event.preventDefault();
			event.stopPropagation();
			close();
		};
		window.addEventListener("pointerdown", onPointerDown);
		window.addEventListener("keydown", onKeyDown, true);
		return () => {
			window.removeEventListener("pointerdown", onPointerDown);
			window.removeEventListener("keydown", onKeyDown, true);
		};
	}, [open]);
	return (
		<div className="task-detail-more-menu" ref={rootRef}>
			<button
				type="button"
				className="task-detail-more-trigger"
				aria-label="Task actions"
				aria-haspopup="menu"
				aria-expanded={open}
				onClick={() => setOpen((value) => !value)}
			>
				<DotsThreeIcon size={18} weight="bold" aria-hidden="true" />
			</button>
			{open ? (
				<div
					className="task-detail-more-pop"
					role="menu"
					aria-label="Task actions"
					onPointerDown={(event) => event.stopPropagation()}
				>
					<button
						type="button"
						className="task-detail-more-danger"
						role="menuitem"
						onClick={() => {
							setOpen(false);
							void onDelete(taskId, taskTitle);
						}}
					>
						<TrashIcon size={16} weight="regular" aria-hidden="true" />
						Delete Task
					</button>
				</div>
			) : null}
		</div>
	);
}

function Tasks({
	data,
	mineOnly = false,
	calendarOverlayTaskId = undefined,
	onCalendarOverlayClose,
}: {
	data: HubRecordsDto;
	mineOnly?: boolean;
	calendarOverlayTaskId?: string | null;
	onCalendarOverlayClose?: () => void;
}) {
	const calendarOverlay = onCalendarOverlayClose != null;
	const client = useQueryClient();
	const session = useSession();
	const organisationId = session.data?.activeOrganisationId ?? session.data?.memberships[0]?.organisationId ?? null;
	const canManageHubTasks = useMemo(() => {
		if (data.taskAccess?.manage != null) return data.taskAccess.manage;
		const membership = session.data ? activeMembership(session.data, organisationId) : null;
		return membership ? roleHasPermission(membership.role, "hub:manage") : false;
	}, [data.taskAccess?.manage, organisationId, session.data]);
	const myAssigneeNames = useMemo(
		() => new Set((data.taskAccess?.myAssigneeNames ?? []).map((name) => name.trim().toLowerCase())),
		[data.taskAccess?.myAssigneeNames],
	);
	const currentUserId = session.data?.user.id ?? "";
	const compact = useCompactTaskBoard();
	const [view, setView] = useState<"board" | "table">("board");
	const [tag, setTag] = useState("");
	const [assignee, setAssignee] = useState("");
	const [groupBy, setGroupBy] = useState<TaskGroupBy>("status");
	const [column, setColumn] = useState<(typeof TASK_STATUSES)[number]>("new");
	const [editing, setEditing] = useState<HubTask | null>(null);
	const [pickedAssignees, setPickedAssignees] = useState<string[]>([]);
	const [pickedStatus, setPickedStatus] = useState<(typeof TASK_STATUSES)[number]>("new");
	const [dueOn, setDueOn] = useState("");
	const [pickedTags, setPickedTags] = useState<string[]>([]);
	const [newTag, setNewTag] = useState("");
	type TaskColorPicker =
		| { kind: "tag"; name: string }
		| { kind: "assignee"; username: string };
	const [colorPicker, setColorPicker] = useState<TaskColorPicker | null>(null);
	const [items, setItems] = useState<TaskChecklistItem[]>([]);
	const [attachments, setAttachments] = useState<TaskAttachmentItem[]>([]);
	const [attachmentError, setAttachmentError] = useState("");
	const [attachDragOver, setAttachDragOver] = useState(false);
	const attachmentInputRef = useRef<HTMLInputElement>(null);
	const [draft, setDraft] = useState("");
	const [hideCheckedItems, setHideCheckedItems] = useState(false);
	const [checklistCollapsed, setChecklistCollapsed] = useState(false);
	const checklistAddInputRef = useRef<HTMLInputElement>(null);
	const [editingChecklistId, setEditingChecklistId] = useState<string | null>(null);
	const [menu, setMenu] = useState<{ id: string; title: string; x: number; y: number } | null>(null);
	const [menuError, setMenuError] = useState("");
	const boardDragActive = useRef(false);
	const [boardDragging, setBoardDragging] = useState(false);
	const [statusTabDropHover, setStatusTabDropHover] = useState<(typeof TASK_STATUSES)[number] | null>(null);
	useEffect(() => {
		if (!boardDragging || !compact) {
			setStatusTabDropHover(null);
			return;
		}
		const syncTabHover = (x: number, y: number) => {
			const stack = document.elementsFromPoint?.(x, y)
				?? (document.elementFromPoint(x, y)
					? [document.elementFromPoint(x, y)!]
					: []);
			for (const node of stack) {
				if (!(node instanceof HTMLElement)) continue;
				const tab = node.closest("[data-task-status-tab]");
				if (!(tab instanceof HTMLElement)) continue;
				const status = tab.dataset.taskStatusTab ?? "";
				if (TASK_STATUSES.includes(status as (typeof TASK_STATUSES)[number])) {
					const next = status as (typeof TASK_STATUSES)[number];
					// Do not switch the visible column here. On a narrow board the
					// other columns are display:none, so hiding the source mid-drag
					// makes dnd-kit remeasure and nest setState (React error 185).
					// The board reveals the target column after the card moves.
					setStatusTabDropHover((current) => (current === next ? current : next));
					return;
				}
			}
			setStatusTabDropHover((current) => (current === null ? current : null));
		};
		const onPointerMove = (event: PointerEvent) => syncTabHover(event.clientX, event.clientY);
		const onTouchMove = (event: TouchEvent) => {
			const touch = event.touches[0];
			if (touch) syncTabHover(touch.clientX, touch.clientY);
		};
		window.addEventListener("pointermove", onPointerMove, { passive: true });
		window.addEventListener("touchmove", onTouchMove, { passive: true });
		return () => {
			window.removeEventListener("pointermove", onPointerMove);
			window.removeEventListener("touchmove", onTouchMove);
		};
	}, [boardDragging, compact]);
	useEffect(() => {
		if (!compact || view !== "board" || boardDragging) return;
		const body = document.querySelector(".task-board .task-col.is-active .task-col-body");
		if (body instanceof HTMLElement) body.scrollTop = 0;
	}, [column, compact, view, boardDragging]);
	const recordsQueryKey = mineOnly ? [...queryKeys.hubRecords, "mine"] : queryKeys.hubRecords;
	const boardMovePersister = useMemo(
		() => createBoardMovePersister({
			client,
			queryKey: recordsQueryKey,
			onError: (message) => setMenuError(message),
			patchTask: async (id, input) => {
				await api.updateHubTask(id, input);
			},
		}),
		[client, recordsQueryKey],
	);
	const form = useHubSave(async (fields) => {
		const created = textOf(fields, "newTag");
		const tags = [...pickedTags];
		if (created && !tags.some((name) => name.toLowerCase() === created.toLowerCase())) tags.push(created);
		if (tags.some(isInstallTag)) throw new ApiError(422, { code: "validation_failed", message: "Install work is on the Work tab.", requestId: "" });
		const assigneeUserIds = [...new Set(
			pickedAssignees
				.map((name) => data.people.find((person) => person.username === name)?.userId)
				.filter((id): id is string => Boolean(id)),
		)];
		const next = {
			title: textOf(fields, "title"),
			description: textOf(fields, "description"),
			assignee: pickedAssignees[0] ?? "",
			assignees: pickedAssignees,
			...(assigneeUserIds.length ? { assigneeUserIds } : {}),
			dueOn: textOf(fields, "dueOn"),
			tag: tags[0] ?? "",
			tags,
			status: textOf(fields, "status") || "new",
			checklist: parseChecklist(fields.get("checklist")),
			checklistTitle: textOf(fields, "checklistTitle"),
			attachments: parseAttachments(fields.get("attachments")),
		};
		if (!canManageHubTasks) {
			throw new ApiError(403, { code: "forbidden", message: "You can update progress on tasks assigned to you.", requestId: "" });
		}
		if (editing?.id) await api.updateHubTask(editing.id, next);
		else await api.createHubTask(next);
		setEditing(null);
		setPickedTags([]);
		setNewTag("");
		setItems([]);
		setAttachments([]);
		setAttachmentError("");
		setDraft("");
	});
	const tags = useMemo(() => {
		const names = new Set<string>();
		for (const row of data.tags ?? []) if (row.name.trim() && !isInstallTag(row.name)) names.add(row.name.trim());
		for (const task of data.tasks) {
			for (const name of namesOf(task.tags)) if (!isInstallTag(name)) names.add(name);
		}
		return [...names];
	}, [data.tags, data.tasks]);
	const tagColorByLowerName = useMemo(() => {
		const map = new Map<string, string>();
		for (const row of data.tags ?? []) {
			const name = row.name.trim();
			if (!name || isInstallTag(name)) continue;
			const stored = row.color?.trim();
			map.set(name.toLowerCase(), stored && isHubStoredColor(stored) ? stored : defaultTagColorId(name));
		}
		return map;
	}, [data.tags]);
	const assigneeColorByLowerName = useMemo(() => {
		const map = new Map<string, string>();
		for (const [key, value] of Object.entries(data.assigneeColors ?? {})) {
			const norm = key.trim().toLowerCase();
			const stored = value?.trim();
			if (!norm || !stored || !isHubStoredColor(stored)) continue;
			map.set(norm, stored);
		}
		return map;
	}, [data.assigneeColors]);
	const people = useMemo(() => {
		const names = new Set(data.people.map((person) => person.username));
		for (const task of data.tasks) {
			for (const name of taskAssigneeList(task)) names.add(name);
		}
		return [...names];
	}, [data.people, data.tasks]);
	const assignedToCurrentUser = (task: { assignee: string }) =>
		taskAssigneeList(task).some((name) => myAssigneeNames.has(name.toLowerCase()));
	const listed = data.tasks.filter((task) => !namesOf(task.tags).some(isInstallTag));
	const pool = listed.filter((task) => {
		const assignees = taskAssigneeList(task);
		if (assignee === "__none__") return assignees.length === 0;
		if (assignee && !assignees.some((name) => name.toLowerCase() === assignee.toLowerCase())) return false;
		return true;
	});
	const visible = pool.filter((task) => !tag || namesOf(task.tags).includes(tag));
	const filtered = tag || assignee;
	const tagChoices = tags
		.map((name) => [name, name, pool.filter((task) => namesOf(task.tags).includes(name)).length] as [string, string, number])
		.filter(([name, , count]) => count > 0 || name === tag);
	const peopleChoices = people
		.map((name) => [name, personLabel(name), listed.filter((task) => taskAssigneeList(task).some((row) => row.toLowerCase() === name.toLowerCase())).length] as [string, string, number])
		.filter(([name, , count]) => count > 0 || name === assignee);
	const unassignedCount = listed.filter((task) => taskAssigneeList(task).length === 0).length;
	const doneCount = items.filter((item) => item.done).length;
	const checklistPct = items.length ? Math.round((doneCount / items.length) * 100) : 0;
	const visibleChecklistItems = hideCheckedItems ? items.filter((item) => !item.done) : items;
	const sparseTaskDetail = items.length === 0 && attachments.length === 0;
	const taskTagPool = useMemo(
		() => [...new Set([...tags, ...pickedTags])].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" })),
		[tags, pickedTags],
	);
	const tagQueryNorm = newTag.trim().toLowerCase();
	const visibleTaskTags = tagQueryNorm
		? taskTagPool.filter((name) => name.toLowerCase().includes(tagQueryNorm))
		: taskTagPool;
	const assignable = useMemo(() => {
		const seen = new Set<string>();
		const names: string[] = [];
		for (const person of data.people) {
			const key = person.username.trim().toLowerCase();
			if (!key || seen.has(key)) continue;
			seen.add(key);
			names.push(person.username);
		}
		names.sort((left, right) => left.localeCompare(right));
		return names;
	}, [data.people]);
	const openTask = (task: HubTask) => {
		if (boardDragActive.current) return;
		openEdit(task);
	};
	const taskBoardMeta = (task: HubTask) => {
		const status = taskStatusOf(task.status);
		const tagList = namesOf(task.tags);
		const checklist = parseChecklist(task.checklist);
		const attachments = parseAttachments(task.attachments);
		const checklistDone = checklist.filter((item) => item.done).length;
		const overdue = isOverdue(task.dueOn, status);
		return {
			status,
			tagList,
			checklistDone,
			checklistTotal: checklist.length,
			attachmentCount: attachments.length,
			coverImageUrl: taskCoverImageUrl(attachments),
			overdue,
			dueLabel: task.dueOn ? dayLabel(task.dueOn) : "",
			dueLabelCompact: task.dueOn ? dayLabelShort(task.dueOn) : "",
			createdLabel: task.createdAt ? createdLabel(task.createdAt) : "",
			assigneeLabel: formatAssigneeLabels(task.assignee),
			assigneeKey: taskAssigneeList(task)[0] ?? "",
			cardHint: [
				tagList.length ? tagList.join(", ") : "",
				task.dueOn ? `Due ${dayLabel(task.dueOn)}${overdue ? ", overdue" : ""}` : "",
				taskAssigneeList(task).length ? `Assigned to ${formatAssigneeLabels(task.assignee)}` : "",
				checklist.length ? `Checklist ${checklistDone} of ${checklist.length}` : "",
				attachments.length ? `${attachments.length} attachment${attachments.length === 1 ? "" : "s"}` : "",
			].filter(Boolean).join(". "),
		};
	};
	const openAdd = () => {
		setEditing(null);
		setPickedAssignees([]);
		setPickedStatus("new");
		setDueOn("");
		setPickedTags([]);
		setNewTag("");
		setItems([]);
		setAttachments([]);
		setAttachmentError("");
		setDraft("");
		setHideCheckedItems(false);
		setEditingChecklistId(null);
		setChecklistCollapsed(false);
		form.setOpen(true);
	};
	const openEdit = (task: HubTask) => {
		setEditing(task);
		setPickedAssignees(resolveAssigneePicks(task.assignee ?? "", assignable));
		setPickedStatus(taskStatusOf(task.status));
		setDueOn(task.dueOn ?? "");
		setPickedTags(namesOf(task.tags).filter((name) => !isInstallTag(name)));
		setNewTag("");
		const checklist = parseChecklist(task.checklist);
		setItems(checklist);
		setAttachments(parseAttachments(task.attachments));
		setAttachmentError("");
		setDraft("");
		setHideCheckedItems(false);
		setEditingChecklistId(null);
		setChecklistCollapsed(false);
		form.setOpen(true);
	};
	const resetTaskForm = () => {
		setColorPicker(null);
		setEditing(null);
		setPickedAssignees([]);
		setPickedStatus("new");
		setDueOn("");
		setPickedTags([]);
		setNewTag("");
		setItems([]);
		setAttachments([]);
		setAttachmentError("");
		setDraft("");
		setHideCheckedItems(false);
		setEditingChecklistId(null);
		setChecklistCollapsed(false);
	};
	const closeForm = () => {
		form.setOpen(false);
		resetTaskForm();
		onCalendarOverlayClose?.();
	};
	const openMenu = (event: MouseEvent, task: HubTask) => {
		event.preventDefault();
		if (!canManageHubTasks || !task.id) return;
		setMenu({
			id: task.id,
			title: task.title,
			x: Math.min(event.clientX, window.innerWidth - 160),
			y: Math.min(event.clientY, window.innerHeight - 48),
		});
	};
	useEffect(() => {
		if (!menu) return;
		const close = () => setMenu(null);
		const onKey = (event: KeyboardEvent) => {
			if (event.key === "Escape") close();
		};
		window.addEventListener("click", close);
		window.addEventListener("keydown", onKey);
		return () => {
			window.removeEventListener("click", close);
			window.removeEventListener("keydown", onKey);
		};
	}, [menu]);
	useEffect(() => {
		if (!form.open) return;
		const frame = window.requestAnimationFrame(() => {
			const title = document.getElementById("task-title");
			if (!(title instanceof HTMLInputElement)) return;
			title.focus({ preventScroll: true });
			const end = title.value.length;
			title.setSelectionRange(end, end);
		});
		return () => window.cancelAnimationFrame(frame);
	}, [form.open, editing?.id]);
	useEffect(() => {
		if (!form.open || !editing?.assignee) return;
		setPickedAssignees(resolveAssigneePicks(editing.assignee, assignable));
	}, [form.open, editing?.id, editing?.assignee, assignable]);
	useEffect(() => {
		if (!calendarOverlay) return;
		const id = calendarOverlayTaskId?.trim() ?? "";
		if (!id) {
			if (form.open) {
				form.setOpen(false);
				resetTaskForm();
			}
			return;
		}
		const task = data.tasks.find((row) => row.id === id);
		if (!task) {
			onCalendarOverlayClose?.();
			return;
		}
		if (editing?.id === id && form.open) return;
		openEdit(task);
	}, [calendarOverlay, calendarOverlayTaskId, data.tasks, editing?.id, form.open, onCalendarOverlayClose]);
	const patchTask = async (id: string, input: Parameters<typeof api.updateHubTask>[1]) => {
		setMenuError("");
		try {
			await api.updateHubTask(id, input);
			await client.invalidateQueries({ queryKey: queryKeys.hubRecords });
		} catch (err) {
			setMenuError(presentError(err).title);
			await client.invalidateQueries({ queryKey: queryKeys.hubRecords });
		}
	};
	const commitBoardMove = (
		taskId: string,
		status: (typeof TASK_STATUSES)[number],
		boardPosition: number,
	) => {
		const moving = visible.find((row) => row.id === taskId);
		if (!(canManageHubTasks || mineOnly || (moving ? assignedToCurrentUser(moving) : false))) return;
		if (!Number.isFinite(boardPosition)) return;
		const task = visible.find((row) => row.id === taskId);
		if (!task?.id) return;
		applyBoardMoveOptimistic(client, task.id, status, boardPosition, recordsQueryKey);
		void boardMovePersister.enqueue({ taskId: task.id, status, boardPosition });
	};
	const shiftTaskStatus = (taskId: string, direction: -1 | 1) => {
		const task = visible.find((row) => row.id === taskId);
		if (!task?.id) return;
		const current = taskStatusOf(task.status);
		const index = TASK_STATUSES.indexOf(current);
		const nextIndex = index + direction;
		if (nextIndex < 0 || nextIndex >= TASK_STATUSES.length) return;
		const nextStatus = TASK_STATUSES[nextIndex]!;
		const column = columnTasks(visible, nextStatus);
		const last = column[column.length - 1];
		const boardPosition = positionBetween(last ? boardSortKey(last) : undefined, undefined);
		commitBoardMove(taskId, nextStatus, boardPosition);
		if (compact) setColumn(nextStatus);
	};
	const canProgressTask = (task: { assignee: string } | null) =>
		Boolean(task && (canManageHubTasks || mineOnly || assignedToCurrentUser(task)));
	const persistItems = async (next: TaskChecklistItem[]) => {
		if (editing && !canProgressTask(editing)) return;
		setItems(next);
		if (editing?.id) {
			setEditing({ ...editing, checklist: next });
			await patchTask(editing.id, { checklist: next });
		}
	};
	const persistAttachments = async (next: TaskAttachmentItem[]) => {
		if (!canManageHubTasks) return;
		setAttachments(next);
		if (editing?.id) {
			setEditing({ ...editing, attachments: next });
			await patchTask(editing.id, { attachments: next });
		}
	};
	const addAttachmentFiles = (files: FileList | File[] | null | undefined) => {
		if (!files?.length) return;
		void (async () => {
			setAttachmentError("");
			let next = attachments;
			for (const file of Array.from(files)) {
				if (next.length >= TASK_ATTACHMENT_MAX_COUNT) {
					setAttachmentError(`You can attach up to ${TASK_ATTACHMENT_MAX_COUNT} files.`);
					break;
				}
				if (file.size > TASK_ATTACHMENT_MAX_BYTES) {
					setAttachmentError("Each file can be up to 5 MB.");
					continue;
				}
				const row = await fileToTaskAttachment(file);
				if (!row) {
					setAttachmentError("That file could not be added.");
					continue;
				}
				next = [...next, row];
			}
			if (next !== attachments) await persistAttachments(next);
		})();
	};
	const removeAttachment = async (id: string) => {
		await persistAttachments(attachments.filter((row) => row.id !== id));
	};
	const downloadAttachment = (row: TaskAttachmentItem) => {
		const url = attachmentUrl(row.data, row.type);
		const link = document.createElement("a");
		link.href = url;
		link.download = row.name;
		link.rel = "noopener";
		link.click();
		window.setTimeout(() => URL.revokeObjectURL(url), 0);
	};
	const addItem = () => {
		const title = draft.trim();
		if (!title) return;
		setChecklistCollapsed(false);
		void persistItems([...items, { id: checklistId(), title, done: false }]);
		setDraft("");
	};
	const addTag = () => {
		const name = newTag.trim();
		if (!name || isInstallTag(name) || pickedTags.some((row) => row.toLowerCase() === name.toLowerCase())) {
			setNewTag("");
			return;
		}
		setPickedTags([...pickedTags, name]);
		setNewTag("");
	};
	const detachTag = (name: string) => {
		setPickedTags((current) => current.filter((row) => row !== name));
	};
	const removeTag = async (name: string) => {
		if (!window.confirm(`Remove the ${name} tag from every task?`)) return;
		setMenuError("");
		try {
			await api.deleteHubTag(name);
			setPickedTags((current) => current.filter((row) => row !== name));
			if (tag === name) setTag("");
			await client.invalidateQueries({ queryKey: queryKeys.hubRecords });
		} catch (err) {
			setMenuError(presentError(err).title);
		}
	};
	const saveTagColor = async (name: string, color: string) => {
		setMenuError("");
		try {
			await api.updateHubTag({ name, color });
			await client.invalidateQueries({ queryKey: queryKeys.hubRecords });
		} catch (err) {
			setMenuError(presentError(err).title);
		}
	};
	const saveAssigneeColor = async (username: string, color: string) => {
		setMenuError("");
		try {
			await api.updateHubAssigneeColor({ username, color });
			await client.invalidateQueries({ queryKey: queryKeys.hubRecords });
		} catch (err) {
			setMenuError(presentError(err).title);
		}
	};
	const removeTask = async (id: string, title: string) => {
		setMenu(null);
		if (!window.confirm(`Remove ${title}?`)) return;
		setMenuError("");
		try {
			await api.deleteHubTask(id);
			await client.invalidateQueries({ queryKey: queryKeys.hubRecords });
			if (editing?.id === id) closeForm();
		} catch (err) {
			setMenuError(presentError(err).title);
		}
	};
	const clearFilters = () => {
		setTag("");
		setAssignee("");
	};

	return (
		<>
			{calendarOverlay ? null : (
		<div className="task-screen">
			<Bar sub={`${visible.length} ${visible.length === 1 ? "task" : "tasks"}${filtered ? " · filtered" : ""}`}>
				<nav className="seg task-view" aria-label="Task view">
					<button
						type="button"
						className={view === "board" ? "active" : ""}
						aria-pressed={view === "board"}
						aria-label="Board"
						title="Board"
						onClick={() => setView("board")}
					>
						<BoardIcon size={16} aria-hidden="true" />
					</button>
					<button
						type="button"
						className={view === "table" ? "active" : ""}
						aria-pressed={view === "table"}
						aria-label="List"
						title="List"
						onClick={() => setView("table")}
					>
						<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
							<path d="M4 7h16" />
							<path d="M4 12h12" />
							<path d="M4 17h8" />
						</svg>
					</button>
					<FilterMenu
						iconOnly
						label="Tag"
						ariaLabel="Filter labels"
						value={tag}
						onPick={setTag}
						active={Boolean(tag)}
						counted
						items={[["", "All", pool.length], ...tagChoices]}
					/>
				</nav>
				{canManageHubTasks ? (
				<button className="btn task-add-btn" type="button" onClick={openAdd}>
					<Add size={16} aria-hidden="true" />
					Add task
				</button>
				) : null}
			</Bar>
			<div className="task-filters">
				<FilterMenu
					label="Assigned"
					ariaLabel="Assigned user"
					value={assignee}
					onPick={setAssignee}
					items={[
						["", "All", listed.length],
						...(unassignedCount || assignee === "__none__" ? [["__none__", "Unassigned", unassignedCount] as [string, string, number]] : []),
						...peopleChoices,
					]}
				/>
				{view === "table" ? (
					<FilterMenu
						label="Group"
						ariaLabel="Group list"
						value={groupBy}
						onPick={(next) => setGroupBy(next as TaskGroupBy)}
						active={false}
						items={[
							["status", "Status"],
							["assignee", "Assigned"],
							["tag", "Tag"],
							["", "None"],
						]}
					/>
				) : null}
				{filtered ? (
					<button className="task-filter-clear" type="button" onClick={clearFilters} aria-label="Clear filters">
						Clear
					</button>
				) : null}
			</div>
			{menu ? (
				<div className="task-menu" role="menu" style={{ left: menu.x, top: menu.y }} onClick={(event) => event.stopPropagation()}>
					<button className="select-item" type="button" role="menuitem" onClick={() => void removeTask(menu.id, menu.title)}>Delete</button>
				</div>
			) : null}
			{view === "board" ? (
				<>
					<nav
						className={[
							"task-status-tabs",
							boardDragging && compact ? "is-drag-targets" : "",
						].filter(Boolean).join(" ")}
						aria-label="Task status"
					>
						{TASK_STATUSES.map((status) => {
							const count = visible.filter((task) => taskStatusOf(task.status) === status).length;
							return (
								<button
									key={status}
									type="button"
									className={[
										column === status ? "active" : "",
										statusTabDropHover === status ? "is-drop-hover" : "",
									].filter(Boolean).join(" ")}
									aria-pressed={column === status}
									data-task-status-tab={status}
									onClick={() => setColumn(status)}
								>
									{TASK_LABEL[status]}
									<span>{count}</span>
								</button>
							);
						})}
					</nav>
					<TaskBoardKanban
						tasks={visible}
						compact={compact}
						boardDraggable={canManageHubTasks || mineOnly}
						mobileColumn={column}
						columnLabel={TASK_LABEL as Record<(typeof TASK_STATUSES)[number], string>}
						onOpenTask={openTask}
						onOpenMenu={openMenu}
						onMoveTask={(taskId, status, boardPosition) => {
							void commitBoardMove(taskId, status, boardPosition);
						}}
						onDragActivityChange={(active) => {
							boardDragActive.current = active;
							setBoardDragging(active);
						}}
						onMobileColumnFocus={setColumn}
						onSwipeTaskStatus={shiftTaskStatus}
						taskMeta={taskBoardMeta}
						tagColorByLowerName={tagColorByLowerName}
						assigneeColorByLowerName={assigneeColorByLowerName}
					/>
				</>
			) : visible.length === 0 ? (
				<Empty>{filtered ? "No tasks match those filters." : mineOnly ? "No tasks assigned to you." : "No tasks yet."}</Empty>
			) : (
				<div className="task-list-scroll">
					<TaskTable
						tasks={visible}
						groupBy={groupBy}
						tagColorByLowerName={tagColorByLowerName}
						assigneeColorByLowerName={assigneeColorByLowerName}
						onOpen={openEdit}
						onMenu={(event, task) => openMenu(event, task)}
					/>
				</div>
			)}
		</div>
			)}
			<Modal
				className={sparseTaskDetail ? "task-detail task-detail-sparse" : "task-detail"}
				open={form.open}
				title={editing ? "Edit task" : "Add task"}
				dialogLabelId="task-detail-dialog-title"
				error={form.error}
				pending={form.pending}
				hideSubmit={!canManageHubTasks}
				onClose={closeForm}
				onSubmit={form.save}
				renderHeader={() => (
					<div className="task-detail-header-wrap">
						<div className="task-detail-header-bar">
							<h1 id="task-detail-dialog-title" className="task-detail-modal-kicker">
								{editing ? "Edit task" : "Add task"}
							</h1>
							<div className="task-detail-header-actions">
								{editing?.id && canManageHubTasks ? (
									<TaskDetailMoreMenu
										taskId={editing.id}
										taskTitle={editing.title}
										onDelete={removeTask}
									/>
								) : null}
								<button className="task-modal-close" type="button" aria-label="Close" onClick={closeForm}>
									×
								</button>
							</div>
						</div>
						{editing?.createdAt ? (
							<p className="task-detail-created-meta">
								Created {createdLabel(editing.createdAt)}
							</p>
						) : null}
						<div className="task-detail-header-title">
							<span className="task-detail-card-icon" aria-hidden="true">
								<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
									<rect x="3" y="3" width="18" height="18" rx="2" />
									<path d="M8 10h8" />
									<path d="M8 14h5" />
								</svg>
							</span>
							<input
								id="task-title"
								className="task-detail-title"
								name="title"
								placeholder="Task name"
								required
								readOnly={!canManageHubTasks}
								defaultValue={editing?.title ?? ""}
								aria-label="Task"
							/>
						</div>
					</div>
				)}
			>
				<Fragment key={editing?.id ?? "new"}>
					<div className="task-detail-primary">
						<section className="task-detail-properties" aria-label="Task properties">
							<div className="task-detail-properties-row task-detail-properties-row-assign">
								<TaskAssigneesField
									id="task-assignee"
									label="Assign"
									picked={pickedAssignees}
									assignable={assignable}
									onChange={canManageHubTasks ? setPickedAssignees : () => undefined}
									assigneeColorByLowerName={assigneeColorByLowerName}
									colorPickerOpen={colorPicker?.kind === "assignee"}
									colorPickerUsername={colorPicker?.kind === "assignee" ? colorPicker.username : ""}
									onColorPickerToggle={(username) => {
										setColorPicker((current) => (
											current?.kind === "assignee" && current.username === username
												? null
												: { kind: "assignee", username }
										));
									}}
									renderColorPicker={(username) => (
										<HubColorPopover
											ariaLabel={`Assign colour for ${personLabel(username)}`}
											onDismiss={() => setColorPicker(null)}
										>
											<HubColorPalette
												compact
												ariaLabel={`Assign colour for ${personLabel(username)}`}
												value={resolveAssigneeColorValue(username, assigneeColorByLowerName)}
												onChange={(color) => {
													void saveAssigneeColor(username, color);
												}}
											/>
										</HubColorPopover>
									)}
								/>
								<TaskSelect
									id="task-status"
									name="status"
									label="Status"
									value={pickedStatus}
									onPick={(next) => {
										const status = taskStatusOf(next);
										setPickedStatus(status);
										if (editing?.id && !canManageHubTasks && canProgressTask(editing)) {
											void patchTask(editing.id, { status });
										}
									}}
									items={TASK_STATUSES.map((status) => [status, TASK_LABEL[status]] as [string, string])}
								/>
							</div>
							<div className="task-detail-properties-row task-detail-properties-row-meta">
								<div className="task-field task-field-due">
									<label htmlFor="task-due">Deadline</label>
									<div className="task-date">
										<input
											id="task-due"
											name="dueOn"
											type="date"
											value={dueOn}
											readOnly={!canManageHubTasks}
											onChange={(event) => {
												if (!canManageHubTasks) return;
												setDueOn(event.target.value);
											}}
										/>
										<button
											className="btn ghost task-date-clear"
											type="button"
											aria-label="Remove deadline"
											disabled={!dueOn}
											onClick={() => setDueOn("")}
										>
											×
										</button>
									</div>
								</div>
								<TaskAttachmentsField
									id="task-attachments"
									label="Attachments"
									attachments={attachments}
									attachmentError={attachmentError}
									attachDragOver={attachDragOver}
									setAttachDragOver={setAttachDragOver}
									attachmentInputRef={attachmentInputRef}
									onAddFiles={addAttachmentFiles}
									onDownload={downloadAttachment}
									onRemove={removeAttachment}
								/>
							</div>
						</section>
						<section className="task-detail-block task-detail-block-desc task-section">
							<div className="task-detail-block-icon" aria-hidden="true">
								<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
									<path d="M4 6h16" />
									<path d="M4 12h10" />
									<path d="M4 18h7" />
								</svg>
							</div>
							<h2 className="task-detail-block-heading task-detail-block-heading-with-icon">Description</h2>
							<div className="task-detail-block-body">
								<TaskAutoResizeTextarea
									id="task-description"
									className="task-detail-desc"
									name="description"
									placeholder="Add a more detailed description…"
									aria-label="Description"
									readOnly={!canManageHubTasks}
									defaultValue={editing?.description ?? ""}
								/>
							</div>
						</section>
						<section className="task-detail-block task-check task-check-section" aria-labelledby="task-checklist-heading">
							<button
								type="button"
								className={`task-detail-block-icon task-check-section-toggle${checklistCollapsed ? " is-collapsed" : ""}`}
								aria-expanded={!checklistCollapsed}
								aria-controls="task-checklist-panel"
								aria-label={checklistCollapsed ? "Expand checklist" : "Collapse checklist"}
								onClick={() => setChecklistCollapsed((current) => !current)}
							>
								<span className="task-check-section-glyph task-check-section-glyph-icon" aria-hidden="true">
									<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
										<path d="M9 11l3 3L22 4" />
										<path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
									</svg>
								</span>
								<span className="task-check-section-glyph task-check-section-glyph-chevron" aria-hidden="true">
									{checklistCollapsed ? (
										<ChevronRight size={14} strokeWidth={2} />
									) : (
										<ChevronDown size={14} strokeWidth={2} />
									)}
								</span>
							</button>
							<header className="task-check-head task-check-section-head task-detail-block-heading-with-icon">
								<h3 id="task-checklist-heading" className="task-detail-block-heading task-check-section-title">
									Checklist
								</h3>
								{items.length > 0 ? (
									<span className="task-section-meta task-check-section-meta">{doneCount}/{items.length}</span>
								) : null}
								{items.length > 0 && doneCount > 0 ? (
									<button
										type="button"
										className="task-check-hide-btn"
										aria-label={
											hideCheckedItems
												? `Show checked items (${doneCount})`
												: "Hide checked items"
										}
										onClick={() => setHideCheckedItems((current) => !current)}
									>
										{hideCheckedItems
											? compact
												? `Show (${doneCount})`
												: `Show checked items (${doneCount})`
											: compact
												? "Hide checked"
												: "Hide checked items"}
									</button>
								) : null}
							</header>
							<div className="task-detail-block-body">
								<input type="hidden" name="checklistTitle" value={editing?.checklistTitle ?? ""} />
								<div id="task-checklist-panel" className={`task-check-rows${checklistCollapsed ? " is-collapsed" : ""}`}>
									{items.length > 0 ? (
										<div className="task-check-progress-wrap">
											<div
												className={`task-check-progress${checklistPct >= 100 ? " is-complete" : ""}`}
												role="progressbar"
												aria-valuenow={checklistPct}
												aria-valuemin={0}
												aria-valuemax={100}
												aria-label="Checklist progress"
											>
												<span className="task-check-pct">{checklistPct}%</span>
												<div className="task-check-bar" aria-hidden="true">
													{checklistPct > 0 ? <span style={{ width: `${checklistPct}%` }} /> : null}
												</div>
											</div>
										</div>
									) : null}
									<ul className="task-check-list">
										{visibleChecklistItems.map((item) => (
											<TaskChecklistItemRow
												key={item.id}
												item={item}
												editing={editingChecklistId === item.id}
												onStartEdit={() => setEditingChecklistId(item.id)}
												onChangeTitle={(title) => setItems(items.map((row) => (row.id === item.id ? { ...row, title } : row)))}
												onCommitTitle={(raw) => {
													setEditingChecklistId(null);
													const title = raw.trim();
													if (!title) {
														void persistItems(items.filter((row) => row.id !== item.id));
														return;
													}
													void persistItems(items.map((row) => (row.id === item.id ? { ...row, title } : row)));
												}}
												onToggleDone={() => void persistItems(items.map((row) => (row.id === item.id ? { ...row, done: !row.done } : row)))}
												onRemove={() => void persistItems(items.filter((row) => row.id !== item.id))}
											/>
										))}
									</ul>
									<div className="task-check-add">
										<span className="task-check-add-gutter" aria-hidden="true" />
										<div className="task-check-add-field">
											<input
												ref={checklistAddInputRef}
												aria-label="New checklist item"
												placeholder="Add an item"
												value={draft}
												onChange={(event) => setDraft(event.target.value)}
												onKeyDown={(event) => {
													if (event.key === "Enter") {
														event.preventDefault();
														addItem();
													}
												}}
											/>
											{draft.trim() ? (
												<button className="btn ghost task-check-add-btn" type="button" onClick={addItem}>Add</button>
											) : null}
										</div>
									</div>
								</div>
								<input type="hidden" name="checklist" value={JSON.stringify(items)} />
							</div>
						</section>
						<section className="task-detail-block task-tags-section" aria-labelledby="task-tags-label">
							<div className="task-detail-block-icon" aria-hidden="true">
								<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
									<path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
									<circle cx="7" cy="7" r="1.5" fill="currentColor" stroke="none" />
								</svg>
							</div>
							<h2 id="task-tags-label" className="task-detail-block-heading task-detail-block-heading-with-icon">Tags</h2>
							<div className="task-detail-block-body task-tags-body">
								<div className="task-tags-stack">
									{visibleTaskTags.length > 0 ? (
										<div className="task-tag-scroll">
											<div className="task-tag-picks" role="group" aria-labelledby="task-tags-label">
												{visibleTaskTags.map((name) => {
													const tagColor = resolveTagColorValue(name, tagColorByLowerName);
													const tagTone = hubToneClass(tagColor, name);
													const tagToneStyle = hubToneStyle(tagColor, name);
													const onTask = pickedTags.includes(name);
													const colorOpen = colorPicker?.kind === "tag" && colorPicker.name === name;
													const openTagColorPicker = (event: { preventDefault: () => void; stopPropagation: () => void }) => {
														event.preventDefault();
														event.stopPropagation();
														setColorPicker((current) => (
															current?.kind === "tag" && current.name === name
																? null
																: { kind: "tag", name }
														));
													};
													return (
												<label
													key={name}
													className={[
														"task-tag-pick-item",
														onTask ? "is-on" : "",
														colorOpen ? "is-color-editing" : "",
														tagTone,
													].filter(Boolean).join(" ")}
													style={tagToneStyle}
												>
													<input
														type="checkbox"
														aria-label={`Tag ${name}`}
														checked={onTask}
														onChange={() => setPickedTags(
															onTask ? pickedTags.filter((row) => row !== name) : [...pickedTags, name],
														)}
													/>
													<button
														type="button"
														className={`task-tag-color-trigger ${tagTone}${colorOpen ? " is-open" : ""}`}
														style={tagToneStyle}
														aria-label={`Choose color for ${name}`}
														aria-expanded={colorOpen}
														onClick={openTagColorPicker}
													/>
													<span className="task-tag-name">{name}</span>
													{colorOpen ? (
														<HubColorPopover
															ariaLabel={`Color for ${name}`}
															onDismiss={() => setColorPicker(null)}
														>
															<HubColorPalette
																compact
																heading={name}
																ariaLabel={`Color for ${name}`}
																value={tagColor}
																onChange={(color) => {
																	void saveTagColor(name, color);
																}}
															/>
														</HubColorPopover>
													) : null}
													{onTask ? (
														<button
															type="button"
															className="task-tag-off"
															aria-label={`Remove ${name} from this task`}
															onClick={(event) => {
																event.preventDefault();
																event.stopPropagation();
																detachTag(name);
															}}
														>
															×
														</button>
													) : (
														<button
															type="button"
															className="task-tag-del"
															aria-label={`Delete ${name} from every task`}
															title={`Delete ${name} from every task`}
															onClick={(event) => {
																event.preventDefault();
																event.stopPropagation();
																void removeTag(name);
															}}
														>
															<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
																<path d="M3 6h18" />
																<path d="M8 6V4h8v2" />
																<path d="M19 6l-1 14H6L5 6" />
															</svg>
														</button>
													)}
												</label>
													);
												})}
											</div>
										</div>
									) : null}
									<div className="task-tag-add">
										<input
											id="task-new-tag"
											className="task-tag-add-input"
											name="newTag"
											aria-label="Search or add tag"
											placeholder="Search or add a tag"
											value={newTag}
											onChange={(event) => setNewTag(event.target.value)}
											onKeyDown={(event) => {
												if (event.key === "Enter") {
													event.preventDefault();
													addTag();
												}
											}}
										/>
										{newTag.trim() ? (
											<button className="btn ghost" type="button" onClick={addTag}>Add</button>
										) : null}
									</div>
								</div>
								{visibleTaskTags.length === 0 && tagQueryNorm ? (
									<p className="hint task-tag-empty">No tags match. Press Enter to add “{newTag.trim()}”.</p>
								) : null}
							</div>
						</section>
						{editing?.id ? (
							<TaskDetailComments
								taskId={editing.id}
								comments={editing.comments ?? []}
								currentUserId={currentUserId}
								canComment={canProgressTask(editing)}
								onCommentsChange={(next) => {
									setEditing((current) => (current ? { ...current, comments: next } : current));
									void client.invalidateQueries({ queryKey: queryKeys.hubRecords });
								}}
							/>
						) : null}
					</div>
				</Fragment>
			</Modal>
			{menuError ? <p className="err">{menuError}</p> : null}
		</>
	);
}

function sitesForSource(sites: WorkspaceSiteDto[], source: string, selected = ""): WorkspaceSiteDto[] {
	const matched = sites.filter((item) => item.source === source);
	const rows = source === "website" ? matched : matched.length ? matched : sites;
	if (selected && !rows.some((item) => item.domain === selected)) {
		const extra = sites.find((item) => item.domain === selected);
		if (extra) return [extra, ...rows];
	}
	return rows;
}

function serviceChoices(sites: WorkspaceSiteDto[], domain: string): string[] {
	const current = sites.find((item) => item.domain === domain);
	const own = (current?.services ?? "").split(",").map((item) => item.trim()).filter(Boolean);
	if (own.length) return own;
	return [...new Set(sites.flatMap((item) => item.services.split(",").map((part) => part.trim()).filter(Boolean)))];
}

function Leads({ data, sites }: { data: HubRecordsDto; sites: WorkspaceSiteDto[] }) {
	const client = useQueryClient();
	const [site, setSite] = useState("");
	const [open, setOpen] = useState(false);
	const [editing, setEditing] = useState<(typeof data.leads)[number] | null>(null);
	const [error, setError] = useState("");
	const [pending, setPending] = useState(false);
	const rows = site ? data.leads.filter((lead) => lead.site === site) : data.leads;

	async function save(form: FormData) {
		const input = {
			source: textOf(form, "source"),
			site: textOf(form, "site"),
			name: textOf(form, "name"),
			phone: textOf(form, "phone"),
			email: textOf(form, "email"),
			location: textOf(form, "location"),
			service: textOf(form, "service"),
			page: textOf(form, "page"),
		};
		setPending(true);
		setError("");
		try {
			if (editing) await api.updateHubLead(editing.id, input);
			else await api.createHubLead(input);
			await client.invalidateQueries({ queryKey: queryKeys.hubRecords });
			setOpen(false);
			setEditing(null);
		} catch (err) {
			setError(presentError(err).title);
		} finally {
			setPending(false);
		}
	}

	async function remove(id: string) {
		if (!window.confirm("Delete this lead?")) return;
		try {
			await api.deleteHubLead(id);
			await client.invalidateQueries({ queryKey: queryKeys.hubRecords });
		} catch (err) {
			setError(presentError(err).title);
		}
	}

	function exportCsv() {
		const header = ["Source", "Time", "Site", "Page", "Name", "Phone", "Email", "Location", "Service"];
		const body = rows.map((lead) =>
			[LEAD_SOURCE[lead.source] ?? lead.source, stamp(lead.createdAt), lead.site, lead.page, lead.name, lead.phone, lead.email, lead.location, lead.service].map(csvCell).join(","),
		);
		const blob = new Blob([[header.join(","), ...body].join("\n")], { type: "text/csv" });
		const url = URL.createObjectURL(blob);
		const link = document.createElement("a");
		link.href = url;
		link.download = "leads.csv";
		link.click();
		URL.revokeObjectURL(url);
	}

	const current = site || "All sites";
	function pickSite(value: string, event: { currentTarget: HTMLButtonElement }) {
		setSite(value);
		event.currentTarget.closest("details")?.removeAttribute("open");
	}
	function toggleRow(event: { currentTarget: HTMLTableRowElement; target: EventTarget }) {
		const target = event.target;
		if (!(target instanceof Element) || target.closest("a, button, details, summary")) return;
		event.currentTarget.classList.toggle("is-open");
	}

	return (
		<div>
			<Bar sub={rows.length ? `1–${rows.length} of ${rows.length}` : "0 records"}>
				<details className="select" onToggle={(event) => closeSiblingSelectMenus(event.currentTarget)}>
					<summary>
						<span>{current}</span>
						<ChevronDown size={12} aria-hidden="true" />
					</summary>
					<div className="select-pop">
						<button type="button" className={site ? "select-item" : "select-item active"} onClick={(event) => pickSite("", event)}>
							All sites{site ? null : <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5" /></svg>}
						</button>
						{sites.map((item) => (
							<button type="button" key={item.domain} className={item.domain === site ? "select-item active" : "select-item"} onClick={(event) => pickSite(item.domain, event)}>
								{item.domain}
								{item.domain === site ? <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5" /></svg> : null}
							</button>
						))}
					</div>
				</details>
				<button className="btn ghost icon" type="button" onClick={exportCsv} aria-label="Export CSV" title="Export CSV">
					<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M8 17h8M8 13h8M8 9h2" /><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" /><path d="M14 3v5h5" /></svg>
				</button>
				{sites.length ? <button className="btn" type="button" onClick={() => { setEditing(null); setError(""); setOpen(true); }}>Add</button> : null}
				<a className="btn" href="/site/sites">Sites</a>
			</Bar>
			{error && !open ? <p className="err">{error}</p> : null}
			{rows.length === 0 ? <Empty>No leads yet.</Empty> : (
				<div className="table-wrap">
					<table className="fit">
						<thead>
							<tr>
								<th>Source</th><th>Time</th><th className="clip">Site</th><th className="clip clip-name">Name</th><th>Phone</th><th className="clip">Email</th><th className="clip">Location</th><th className="tight"></th>
							</tr>
						</thead>
						<tbody>
							{rows.map((lead) => {
								const siteTitle = lead.page ? `${lead.site} ${lead.page}` : lead.site;
								return (
									<Fragment key={lead.id}>
										<tr className="lead-row" onClick={toggleRow}>
											<td data-label="Source">{LEAD_SOURCE[lead.source] ?? lead.source}</td>
											<td data-label="Time"><span className="time">{stamp(lead.createdAt)}</span></td>
											<td className="clip" data-label="Site" title={siteTitle}>{lead.site}{lead.page ? <span className="quiet"> {lead.page}</span> : null}</td>
											<td className="clip clip-name" data-label="Name" title={lead.name}>{lead.name}</td>
											<td data-label="Phone"><a href={`https://wa.me/${lead.phone.replace(/[^\d]/g, "")}`}>{lead.phone}</a></td>
											<td className="clip" data-label="Email" title={lead.email}><a href={`mailto:${lead.email}`}>{lead.email}</a></td>
											<td className="clip" data-label="Location" title={lead.location}>{lead.location}</td>
											<td className="tight" data-label="">
												<details className="select menu">
													<summary aria-label="Lead actions">
														<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="12" cy="5" r="1.8" /><circle cx="12" cy="12" r="1.8" /><circle cx="12" cy="19" r="1.8" /></svg>
													</summary>
													<div className="select-pop">
														<button className="select-item" type="button" onClick={() => { setEditing(lead); setError(""); setOpen(true); }}>Edit</button>
														<button className="select-item" type="button" onClick={() => void remove(lead.id)}>Delete</button>
													</div>
												</details>
											</td>
										</tr>
										<tr className="lead-detail">
											<td colSpan={8}><div className="lead-detail-inner"><span>Service</span>{lead.service || "—"}</div></td>
										</tr>
									</Fragment>
								);
							})}
						</tbody>
					</table>
				</div>
			)}
			<LeadDialog key={editing?.id ?? "new"} sites={sites} site={site} lead={editing} open={open} error={error} pending={pending} onClose={() => { setOpen(false); setEditing(null); }} onSubmit={(form) => void save(form)} />
		</div>
	);
}

function LeadDialog({
	sites,
	site,
	lead,
	open,
	error,
	pending,
	onClose,
	onSubmit,
}: {
	sites: WorkspaceSiteDto[];
	site: string;
	lead: HubRecordsDto["leads"][number] | null;
	open: boolean;
	error: string;
	pending: boolean;
	onClose: () => void;
	onSubmit: (form: FormData) => void;
}) {
	const [source, setSource] = useState(lead?.source || "website");
	const [domain, setDomain] = useState(lead?.site || site || sites[0]?.domain || "");
	const choices = sitesForSource(sites, source, lead?.site || site);
	const services = serviceChoices(sites, domain);
	const serviceValue = services.includes(lead?.service ?? "") ? (lead?.service ?? "") : (services[0] ?? "");
	function changeSource(next: string) {
		setSource(next);
		const nextSites = sitesForSource(sites, next, domain);
		if (!nextSites.some((item) => item.domain === domain)) setDomain(nextSites[0]?.domain ?? "");
	}
	return (
		<Modal open={open} title={lead ? "Edit lead" : "Add lead"} error={error} pending={pending} onClose={onClose} onSubmit={onSubmit}>
			<div className="grid two">
				<div>
					<label htmlFor="lead-source">Source</label>
					<Select id="lead-source" name="source" value={source} onChange={(event) => changeSource(event.target.value)}>
						{Object.entries(LEAD_SOURCE).map(([value, name]) => <option key={value} value={value}>{name}</option>)}
					</Select>
				</div>
				<div>
					<label htmlFor="lead-site">Site</label>
					<Select id="lead-site" name="site" value={domain} required onChange={(event) => setDomain(event.target.value)}>
						{choices.map((item) => <option key={item.domain} value={item.domain}>{source === "website" ? item.domain : item.name || item.domain}</option>)}
					</Select>
				</div>
				<div>
					<label htmlFor="lead-name">Name</label>
					<input id="lead-name" name="name" defaultValue={lead?.name ?? ""} required />
				</div>
				<div>
					<label htmlFor="lead-phone">Phone</label>
					<input id="lead-phone" name="phone" placeholder="012-345 6789" defaultValue={lead?.phone ?? ""} required />
				</div>
				<div>
					<label htmlFor="lead-email">Email</label>
					<input id="lead-email" name="email" type="email" defaultValue={lead?.email ?? ""} required />
				</div>
				<div>
					<label htmlFor="lead-location">Location</label>
					<input id="lead-location" name="location" defaultValue={lead?.location ?? ""} required />
				</div>
				<div>
					<label htmlFor="lead-service">Service</label>
					<Select id="lead-service" name="service" key={domain} defaultValue={serviceValue} required>
						{services.map((item) => <option key={item} value={item}>{item}</option>)}
					</Select>
				</div>
				<div hidden={source !== "website"}>
					<label htmlFor="lead-page">Page</label>
					<input id="lead-page" name="page" defaultValue={lead?.page || "/"} />
				</div>
			</div>
		</Modal>
	);
}

function Calendar({ data, books }: { data: HubRecordsDto; books: Books }) {
	const [cursor, setCursor] = useState(() => new Date());
	const [view, setView] = useState<"month" | "agenda">("month");
	const [sources, setSources] = useState({ event: true, task: true, work: true });
	const [open, setOpen] = useState(false);
	const [day, setDay] = useState("");
	const [taskDetailId, setTaskDetailId] = useState<string | null>(null);
	const [workDetailId, setWorkDetailId] = useState<number | null>(null);
	const form = useHubSave(async (fields) => {
		await api.appendHubBook({
			list: "events",
			record: {
				title: textOf(fields, "title"),
				day: textOf(fields, "day"),
				start: textOf(fields, "start"),
				end: textOf(fields, "end"),
				note: textOf(fields, "note"),
				allDay: textOf(fields, "allDay") === "1",
			},
		});
	});
	const year = cursor.getFullYear();
	const month = cursor.getMonth();
	const label = cursor.toLocaleString("en-GB", { month: "long", year: "numeric" });
	const today = isoDay(new Date());
	const cells = useMemo(() => monthCells(cursor), [cursor]);
	const selected = isoDay(cursor);
	const calendarItemOptions = useMemo(
		() => ({
			events: books.events,
			tasks: data.tasks,
			work: books.work,
			showEvents: sources.event,
			showTasks: sources.task,
			showWork: sources.work,
		}),
		[books.events, books.work, data.tasks, sources.event, sources.task, sources.work],
	);
	const itemsOn = (value: string) => hubCalendarItemsForDay(value, calendarItemOptions);
	const openCalendarItem = (item: ReturnType<typeof hubCalendarItemsForDay>[number]) => {
		if (item.kind === "task") {
			setWorkDetailId(null);
			setTaskDetailId(item.taskId);
			return;
		}
		if (item.kind === "work") {
			setTaskDetailId(null);
			setWorkDetailId(item.workId);
		}
	};
	const calendarItemLabel = (item: ReturnType<typeof hubCalendarItemsForDay>[number]) =>
		`${item.start ? `${item.start} ` : ""}${item.title}`;
	return (
		<div className="cal-page">
			<aside className="cal-side">
				<div>
					<div className="mini-top">
						<span>{label}</span>
						<div className="mini-nav">
							<button type="button" aria-label="Previous" onClick={() => setCursor(new Date(year, month - 1, 1))}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="m15 18-6-6 6-6" /></svg></button>
							<button type="button" aria-label="Next" onClick={() => setCursor(new Date(year, month + 1, 1))}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="m9 18 6-6-6-6" /></svg></button>
						</div>
					</div>
					<div className="mini-grid">
						{["M", "T", "W", "T", "F", "S", "S"].map((name, index) => <div className="dow" key={`${name}${index}`}>{name}</div>)}
						{cells.map((value) => (
							<button type="button" key={value} className={[value.slice(0, 7) !== selected.slice(0, 7) ? "out" : "", value === today ? "today" : "", value === selected ? "on" : ""].filter(Boolean).join(" ")} onClick={() => setCursor(new Date(`${value}T12:00:00`))}>{Number(value.slice(8))}</button>
						))}
					</div>
				</div>
				<div>
					<p className="sub">Calendars</p>
					<button type="button" className={sources.event ? "cal-src on" : "cal-src"} onClick={() => setSources({ ...sources, event: !sources.event })}><span className="dot dot-event" />Events</button>
					<button type="button" className={sources.task ? "cal-src on" : "cal-src"} onClick={() => setSources({ ...sources, task: !sources.task })}><span className="dot dot-task" />Tasks</button>
					<button type="button" className={sources.work ? "cal-src on" : "cal-src"} onClick={() => setSources({ ...sources, work: !sources.work })}><span className="dot dot-work" />Work</button>
				</div>
			</aside>
			<div>
				<div className="cal-bar">
					<div className="actions">
						<button className="btn ghost" type="button" onClick={() => setCursor(new Date())}>Today</button>
						<div className="navs">
							<button className="cal-step" type="button" aria-label="Previous" onClick={() => setCursor(new Date(year, month - 1, 1))}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="m15 18-6-6 6-6" /></svg></button>
							<button className="cal-step" type="button" aria-label="Next" onClick={() => setCursor(new Date(year, month + 1, 1))}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="m9 18 6-6-6-6" /></svg></button>
						</div>
						<h1>{label}</h1>
					</div>
					<div className="actions">
						<nav className="seg">
							<button type="button" className={view === "month" ? "active" : ""} onClick={() => setView("month")}>Month</button>
							<button type="button" className={view === "agenda" ? "active" : ""} onClick={() => setView("agenda")}>Agenda</button>
						</nav>
						<button className="btn" type="button" onClick={() => { setDay(selected); setOpen(true); }}>Add</button>
					</div>
				</div>
				{view === "month" ? (
					<div className="cal-month">
						<div className="cal-month-head">{["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((name) => <div className="cal-dow" key={name}>{name}</div>)}</div>
						<div className="cal-month-body">
							{cells.map((value) => {
								const rows = itemsOn(value);
								return (
									<div className={["cal-cell", value.slice(0, 7) !== selected.slice(0, 7) ? "out" : "", value === today ? "today" : ""].filter(Boolean).join(" ")} key={`cell-${value}`}>
										<div className="cal-cell-top">
											<button type="button" className="num" onClick={() => setCursor(new Date(`${value}T12:00:00`))}>{Number(value.slice(8))}</button>
											<button type="button" className="cal-add" aria-label="Add event" onClick={() => { setDay(value); setOpen(true); }}>+</button>
										</div>
										{rows.slice(0, 3).map((item) => (
											item.kind === "event" ? (
												<span className={`ev ev-${item.kind}`} key={item.key}>
													{calendarItemLabel(item)}
												</span>
											) : (
												<button
													type="button"
													className={`ev ev-${item.kind}`}
													key={item.key}
													onClick={() => openCalendarItem(item)}
												>
													{calendarItemLabel(item)}
												</button>
											)
										))}
										{rows.length > 3 ? <span className="ev-more">+{rows.length - 3} more</span> : null}
									</div>
								);
							})}
						</div>
					</div>
				) : (
					<div className="cal-agenda">
						{cells.filter((value) => itemsOn(value).length).map((value) => (
							<section key={value}>
								<h2>{value}</h2>
								{itemsOn(value).map((item) => (
									item.kind === "event" ? (
										<div className="cal-line" key={item.key}>
											<div className="when">{item.start || "All day"}</div>
											<div><div>{item.title}</div><div className="who">Events</div></div>
										</div>
									) : (
										<button
											type="button"
											className={`cal-line cal-line-${item.kind}`}
											key={item.key}
											onClick={() => openCalendarItem(item)}
										>
											<div className="when">{item.start || "All day"}</div>
											<div><div>{item.title}</div><div className="who">{item.kind === "work" ? "Work" : "Tasks"}</div></div>
										</button>
									)
								))}
							</section>
						))}
						{cells.every((value) => itemsOn(value).length === 0) ? <Empty>Nothing scheduled in this period.</Empty> : null}
					</div>
				)}
			</div>
			<Modal open={open || form.open} title="Add event" error={form.error} pending={form.pending} onClose={() => { setOpen(false); form.setOpen(false); }} onSubmit={(fields) => { setOpen(false); void form.save(fields); }}>
				<label htmlFor="ev-title">Event</label>
				<input id="ev-title" name="title" placeholder="Site visit" required />
				<label htmlFor="ev-day">Date</label>
				<input id="ev-day" name="day" type="date" defaultValue={day || selected} required />
				<label className="check"><input type="checkbox" name="allDay" value="1" defaultChecked /> All day</label>
				<div className="grid two">
					<div><label htmlFor="ev-start">Start</label><input id="ev-start" name="start" type="time" /></div>
					<div><label htmlFor="ev-end">End</label><input id="ev-end" name="end" type="time" /></div>
				</div>
				<label htmlFor="ev-note">Note</label>
				<textarea id="ev-note" name="note" rows={3} placeholder="Optional" />
			</Modal>
			<Tasks
				data={data}
				calendarOverlayTaskId={taskDetailId}
				onCalendarOverlayClose={() => setTaskDetailId(null)}
			/>
			<Work
				books={books}
				people={data.people}
				assigneeColors={data.assigneeColors ?? {}}
				status=""
				project=""
				onStatus={() => {}}
				calendarOverlayWorkId={workDetailId}
				onCalendarOverlayClose={() => setWorkDetailId(null)}
			/>
		</div>
	);
}

function Organisations({ books }: { books: Books }) {
	const [kind, setKind] = useState("");
	const [formKind, setFormKind] = useState(kind || "customer");
	const [filing, setFiling] = useState<OrgRow | null>(null);
	const form = useHubSave(async (fields) => {
		await api.appendHubBook({ list: "organisations", record: organisationRecord(fields) });
	});
	const docs = useHubSave(async (fields) => {
		if (!filing?.id) return;
		const document = documentFromForm(fields);
		const record: Record<string, unknown> = { ...filing, documents: [...(filing.documents ?? []), document] };
		delete record.id;
		await api.updateHubBook({ list: "organisations", id: filing.id, record });
	});
	const client = useQueryClient();
	const [idDir, setIdDir] = useState<"" | "asc" | "desc">("");
	const rows = books.organisations;
	const visible = kind ? rows.filter((row) => row.kind === kind) : rows;
	async function arrangeEmployees(direction: "asc" | "desc") {
		const employees = rows.filter((item) => item.kind === "employee" && typeof item.id === "number");
		const ordered = [...employees].sort((a, b) => {
			const left = a.profile?.employeeId ?? "";
			const right = b.profile?.employeeId ?? "";
			if (left === right) return 0;
			if (!left) return 1;
			if (!right) return -1;
			const order = left.localeCompare(right, undefined, { numeric: true });
			return direction === "asc" ? order : -order;
		});
		const ids = ordered.flatMap((item) => (typeof item.id === "number" ? [item.id] : []));
		if (!ids.length) return;
		setIdDir(direction);
		await api.reorderHubBook({ list: "organisations", ids });
		await client.invalidateQueries({ queryKey: queryKeys.hubRecords });
	}
	function openAdd() {
		setFormKind(kind || "customer");
		form.setOpen(true);
	}
	return (
		<div>
			<Bar sub={`${visible.length} ${visible.length === 1 ? "record" : "records"}${kind ? " · filtered" : ""}`}>
				<button className="btn" type="button" onClick={openAdd}>Add</button>
			</Bar>
			<Chips
				current={kind}
				onPick={setKind}
				items={[["", `All · ${rows.length}`], ...ORG_KINDS.map(([value, name]) => [value, `${name} · ${rows.filter((row) => row.kind === value).length}`] as [string, string])]}
			/>
			{visible.length === 0 ? <Empty>{kind ? `No ${ORG_KINDS.find(([value]) => value === kind)?.[1].toLowerCase()}s yet.` : "No organisations yet."}</Empty> : (
				<div className="table-wrap">
					<table className="org">
						<colgroup>
							<col className="chev" />
							<col className="id" />
							<col className="name" />
							<col className="type" />
							<col className="phone" />
							<col className="email" />
						</colgroup>
						<thead><tr><th className="tight"></th><th className="id-col"><span className="th-sort">ID <span className="th-arrows"><button type="button" className={idDir === "asc" ? "th-arrow active" : "th-arrow"} aria-label="Arrange employee ID up" onClick={() => void arrangeEmployees("asc")}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m18 15-6-6-6 6" /></svg></button><button type="button" className={idDir === "desc" ? "th-arrow active" : "th-arrow"} aria-label="Arrange employee ID down" onClick={() => void arrangeEmployees("desc")}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m6 9 6 6 6-6" /></svg></button></span></span></th><th>Name</th><th>Type</th><th>Phone</th><th>Email</th></tr></thead>
						<tbody>
							{visible.map((row) => (
								<Fragment key={row.id ?? row.name}>
									<tr className="lead-row" onClick={(event) => event.currentTarget.classList.toggle("is-open")}>
										<td className="tight"><details className="expand"><summary aria-label="Show details"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="m6 9 6 6 6-6" /></svg></summary></details></td>
										<td className="id-col" data-label="ID">{(row.kind === "employee" ? row.profile?.employeeId : row.party?.code) || "—"}</td>
										<td data-label="Name">{row.name}</td>
										<td data-label="Type">{ORG_KINDS.find(([value]) => value === row.kind)?.[1] ?? row.kind}</td>
										<td data-label="Phone">{row.phone || "—"}</td>
										<td data-label="Email">{row.email || "—"}</td>
									</tr>
									<tr className="lead-detail">
										<td colSpan={6}>
											<EmployeeFile books={books} row={row} onAddDocument={() => { setFiling(row); docs.setOpen(true); }} />
										</td>
									</tr>
								</Fragment>
							))}
						</tbody>
					</table>
				</div>
			)}
			<Modal open={form.open} title={formKind === "employee" ? "Add employee" : formKind === "vendor" ? "Add vendor" : "Add customer"} error={form.error} pending={form.pending} onClose={() => form.setOpen(false)} onSubmit={(fields) => void form.save(fields)}>
				<div className="org-identity form">
					<div>
						<label htmlFor="org-code">ID</label>
						<input id="org-code" name={formKind === "employee" ? "employeeId" : "code"} />
					</div>
					<div>
						<label htmlFor="org-name">Name</label>
						<input id="org-name" name="name" placeholder={formKind === "employee" ? "Company or person" : "Company"} required />
					</div>
					<div>
						<label htmlFor="org-kind">Type</label>
						<Select id="org-kind" name="kind" value={formKind} onChange={(event) => setFormKind(event.target.value)}>{ORG_KINDS.map(([value, name]) => <option key={value} value={value}>{name}</option>)}</Select>
					</div>
					<div>
						<label htmlFor="org-phone">Phone</label>
						<input id="org-phone" name="phone" placeholder="012-345 6789" />
					</div>
					<div>
						<label htmlFor="org-email">Email</label>
						<input id="org-email" name="email" type="email" placeholder="name@email.com" />
					</div>
				</div>
				{formKind === "employee" ? null : (
					<>
						<label htmlFor="org-contact">Contact</label>
						<input id="org-contact" name="contact" placeholder="Person at the company" />
					</>
				)}
				{formKind === "employee" ? <EmployeeFields idPrefix="org" /> : <PartyFields idPrefix="org" vendor={formKind === "vendor"} />}
				<label htmlFor="org-note">Note</label>
				<textarea id="org-note" name="note" rows={3} placeholder="Optional" />
			</Modal>
			<Modal open={docs.open} title="Add document" action="Add" error={docs.error} pending={docs.pending} onClose={() => docs.setOpen(false)} onSubmit={(fields) => void docs.save(fields)}>
				<label htmlFor="doc-title">Document</label>
				<input id="doc-title" name="docTitle" placeholder="SPM certificate" required />
				<label htmlFor="doc-kind">Kind</label>
				<Select id="doc-kind" name="docKind" defaultValue="certificate">{DOCUMENT_KINDS.map(([value, name]) => <option key={value} value={value}>{name}</option>)}</Select>
				<label htmlFor="doc-note">Note</label>
				<textarea id="doc-note" name="docNote" rows={2} placeholder="Optional" />
			</Modal>
		</div>
	);
}

function EmployeeFields({ idPrefix, profile, locked }: { idPrefix: string; profile?: Partial<EmployeeProfile>; locked?: boolean }) {
	const savedEducation = profile?.education ?? [];
	const savedEmergency = profile?.emergency ?? [];
	const [educationCount, setEducationCount] = useState(Math.max(1, savedEducation.length));
	const [emergencyCount, setEmergencyCount] = useState(Math.max(1, savedEmergency.length));
	const mother = parentOf(profile?.mother);
	const father = parentOf(profile?.father);
	const bank = bankOf(profile?.bank);
	return (
		<>
			<div className="grid two">
				<div><label htmlFor={`${idPrefix}-nric`}>NRIC</label><input id={`${idPrefix}-nric`} name="nric" defaultValue={profile?.nric ?? ""} readOnly={locked} /></div>
				<div><label htmlFor={`${idPrefix}-birth`}>Date of birth</label><input id={`${idPrefix}-birth`} name="birth" placeholder="DD/MM/YYYY" defaultValue={profile?.birth ?? ""} readOnly={locked} /></div>
				<div><label htmlFor={`${idPrefix}-gender`}>Gender</label><input id={`${idPrefix}-gender`} name="gender" defaultValue={profile?.gender ?? ""} readOnly={locked} /></div>
				<div><label htmlFor={`${idPrefix}-religion`}>Religion</label><input id={`${idPrefix}-religion`} name="religion" defaultValue={profile?.religion ?? ""} readOnly={locked} /></div>
				<div><label htmlFor={`${idPrefix}-nationality`}>Nationality</label><input id={`${idPrefix}-nationality`} name="nationality" defaultValue={profile?.nationality ?? ""} readOnly={locked} /></div>
				<div><label htmlFor={`${idPrefix}-race`}>Race</label><input id={`${idPrefix}-race`} name="race" defaultValue={profile?.race ?? ""} readOnly={locked} /></div>
				<div><label htmlFor={`${idPrefix}-languages`}>Languages</label><input id={`${idPrefix}-languages`} name="languages" defaultValue={profile?.languages ?? ""} readOnly={locked} /></div>
				<div><label htmlFor={`${idPrefix}-marital`}>Marital status</label><input id={`${idPrefix}-marital`} name="marital" defaultValue={profile?.marital ?? ""} readOnly={locked} /></div>
				<div><label htmlFor={`${idPrefix}-birth-place`}>Place of birth</label><input id={`${idPrefix}-birth-place`} name="birthPlace" defaultValue={profile?.birthPlace ?? ""} readOnly={locked} /></div>
			</div>
			<label htmlFor={`${idPrefix}-address`}>Address</label>
			<input id={`${idPrefix}-address`} name="address" defaultValue={profile?.address ?? ""} readOnly={locked} />
			<div className="grid two">
				<div><label htmlFor={`${idPrefix}-city`}>City</label><input id={`${idPrefix}-city`} name="city" defaultValue={profile?.city ?? ""} readOnly={locked} /></div>
				<div><label htmlFor={`${idPrefix}-state`}>State</label><input id={`${idPrefix}-state`} name="state" defaultValue={profile?.state ?? ""} readOnly={locked} /></div>
				<div><label htmlFor={`${idPrefix}-postcode`}>Postcode</label><input id={`${idPrefix}-postcode`} name="postcode" defaultValue={profile?.postcode ?? ""} readOnly={locked} /></div>
			</div>
			<h2>Mother</h2>
			<ParentFields idPrefix={idPrefix} who="mother" parent={mother} locked={locked} />
			<h2>Father</h2>
			<ParentFields idPrefix={idPrefix} who="father" parent={father} locked={locked} />
			<h2>Education</h2>
			{Array.from({ length: educationCount }, (_, index) => (
				<EducationFields key={index} idPrefix={idPrefix} index={index} row={savedEducation[index]} locked={locked} />
			))}
			{locked ? null : <button className="btn ghost" type="button" onClick={() => setEducationCount((count) => count + 1)}>Add education</button>}
			<h2>Bank</h2>
			<BankFields idPrefix={idPrefix} bank={bank} locked={locked} />
			<h2>Emergency contact</h2>
			{Array.from({ length: emergencyCount }, (_, index) => (
				<EmergencyFields key={index} idPrefix={idPrefix} index={index} row={savedEmergency[index]} locked={locked} />
			))}
			{locked ? null : <button className="btn ghost" type="button" onClick={() => setEmergencyCount((count) => count + 1)}>Add contact</button>}
		</>
	);
}

function ParentFields({ idPrefix, who, parent, locked }: { idPrefix: string; who: "mother" | "father"; parent: ParentDetails; locked?: boolean }) {
	return (
		<div className="grid three">
			<div><label htmlFor={`${idPrefix}-${who}-name`}>Name</label><input id={`${idPrefix}-${who}-name`} name={`${who}Name`} defaultValue={parent.name} readOnly={locked} /></div>
			<div><label htmlFor={`${idPrefix}-${who}-religion`}>Religion</label><input id={`${idPrefix}-${who}-religion`} name={`${who}Religion`} defaultValue={parent.religion} readOnly={locked} /></div>
			<div><label htmlFor={`${idPrefix}-${who}-birth`}>Date of birth</label><input id={`${idPrefix}-${who}-birth`} name={`${who}Birth`} placeholder="DD/MM/YYYY" defaultValue={parent.birth} readOnly={locked} /></div>
			<div><label htmlFor={`${idPrefix}-${who}-nationality`}>Nationality</label><input id={`${idPrefix}-${who}-nationality`} name={`${who}Nationality`} defaultValue={parent.nationality} readOnly={locked} /></div>
		</div>
	);
}

function EducationFields({ idPrefix, index, row, locked }: { idPrefix: string; index: number; row?: EducationRow; locked?: boolean }) {
	return (
		<div className="grid three">
			<div><label htmlFor={`${idPrefix}-edu-name-${index}`}>Name</label><input id={`${idPrefix}-edu-name-${index}`} name={`eduName${index}`} defaultValue={row?.name ?? ""} readOnly={locked} /></div>
			<div><label htmlFor={`${idPrefix}-edu-cert-${index}`}>Certificate</label><input id={`${idPrefix}-edu-cert-${index}`} name={`eduCert${index}`} defaultValue={row?.certificate ?? ""} readOnly={locked} /></div>
			<div><label htmlFor={`${idPrefix}-edu-discipline-${index}`}>Discipline</label><input id={`${idPrefix}-edu-discipline-${index}`} name={`eduDiscipline${index}`} defaultValue={row?.discipline ?? ""} readOnly={locked} /></div>
		</div>
	);
}

function BankFields({ idPrefix, bank, locked }: { idPrefix: string; bank: BankDetails; locked?: boolean }) {
	return (
		<div className="grid three">
			<div><label htmlFor={`${idPrefix}-bank-type`}>Bank</label><input id={`${idPrefix}-bank-type`} name="bankType" defaultValue={bank.type} readOnly={locked} /></div>
			<div><label htmlFor={`${idPrefix}-bank-number`}>Account number</label><input id={`${idPrefix}-bank-number`} name="bankNumber" defaultValue={bank.number} readOnly={locked} /></div>
			<div><label htmlFor={`${idPrefix}-bank-holder`}>Account holder</label><input id={`${idPrefix}-bank-holder`} name="bankHolder" defaultValue={bank.holder} readOnly={locked} /></div>
		</div>
	);
}

function EmergencyFields({ idPrefix, index, row, locked }: { idPrefix: string; index: number; row?: EmergencyContact; locked?: boolean }) {
	return (
		<div className="grid three">
			<div><label htmlFor={`${idPrefix}-emergency-name-${index}`}>Name</label><input id={`${idPrefix}-emergency-name-${index}`} name={`emergencyName${index}`} defaultValue={row?.name ?? ""} readOnly={locked} /></div>
			<div><label htmlFor={`${idPrefix}-emergency-relation-${index}`}>Relation</label><input id={`${idPrefix}-emergency-relation-${index}`} name={`emergencyRelation${index}`} defaultValue={row?.relation ?? ""} readOnly={locked} /></div>
			<div><label htmlFor={`${idPrefix}-emergency-phone-${index}`}>Contact number</label><input id={`${idPrefix}-emergency-phone-${index}`} name={`emergencyPhone${index}`} defaultValue={row?.phone ?? ""} readOnly={locked} /></div>
		</div>
	);
}

function PartyFields({ idPrefix, vendor, party, locked }: { idPrefix: string; vendor: boolean; party?: PartyDetails; locked?: boolean }) {
	const details = partyOf(party);
	return (
		<>
			{vendor ? (
				<>
					<label htmlFor={`${idPrefix}-registration`}>Registration</label>
					<input id={`${idPrefix}-registration`} name="registration" defaultValue={details.registration} readOnly={locked} />
				</>
			) : null}
			<label htmlFor={`${idPrefix}-address`}>Address</label>
			<input id={`${idPrefix}-address`} name="address" defaultValue={details.address} readOnly={locked} />
			<div className="grid two">
				<div><label htmlFor={`${idPrefix}-city`}>City</label><input id={`${idPrefix}-city`} name="city" defaultValue={details.city} readOnly={locked} /></div>
				<div><label htmlFor={`${idPrefix}-state`}>State</label><input id={`${idPrefix}-state`} name="state" defaultValue={details.state} readOnly={locked} /></div>
				<div><label htmlFor={`${idPrefix}-postcode`}>Postcode</label><input id={`${idPrefix}-postcode`} name="postcode" defaultValue={details.postcode} readOnly={locked} /></div>
			</div>
			{vendor ? (
				<>
					<h2>Bank</h2>
					<BankFields idPrefix={idPrefix} bank={details.bank} locked={locked} />
				</>
			) : null}
		</>
	);
}

function EmployeeFile({ books, row, onAddDocument }: { books: Books; row: OrgRow; onAddDocument: () => void }) {
	const [editing, setEditing] = useState(false);
	const prefix = `file-${row.id ?? row.name}`;
	const save = useHubSave(async (fields) => {
		if (!row.id) return;
		await api.updateHubBook({
			list: "organisations",
			id: row.id,
			record: organisationRecord(fields, { created_at: row.created_at, documents: row.documents }),
		});
		setEditing(false);
	});
	const employee = row.kind === "employee";
	return (
		<div className={editing ? "emp-file is-editing" : "emp-file"}>
			<form key={editing ? "edit" : `${row.name}-${row.phone}-${row.email}-${row.note}-${row.profile?.employeeId ?? ""}-${row.party?.code ?? ""}-${row.profile?.education?.length ?? 0}-${row.profile?.emergency?.length ?? 0}`} className="grid" onSubmit={(event) => { event.preventDefault(); void save.save(new FormData(event.currentTarget)); }}>
				<div className="org-identity">
					<div />
					<div>
						<label htmlFor={`${prefix}-code`}>ID</label>
						<input id={`${prefix}-code`} name={employee ? "employeeId" : "code"} defaultValue={employee ? (row.profile?.employeeId ?? "") : (row.party?.code ?? "")} readOnly={!editing} />
					</div>
					<div>
						<label htmlFor={`${prefix}-name`}>Name</label>
						<input id={`${prefix}-name`} name="name" defaultValue={row.name} readOnly={!editing} required />
					</div>
					<div>
						<label htmlFor={`${prefix}-kind`}>Type</label>
						<Select id={`${prefix}-kind`} name="kind" defaultValue={row.kind} disabled={!editing}>{ORG_KINDS.map(([value, name]) => <option key={value} value={value}>{name}</option>)}</Select>
					</div>
					<div>
						<label htmlFor={`${prefix}-phone`}>Phone</label>
						<input id={`${prefix}-phone`} name="phone" defaultValue={row.phone ?? ""} readOnly={!editing} />
					</div>
					<div>
						<label htmlFor={`${prefix}-email`}>Email</label>
						<input id={`${prefix}-email`} name="email" type="email" defaultValue={row.email ?? ""} readOnly={!editing} />
					</div>
				</div>
				<div className="org-rest">
				{employee ? null : (
					<>
						<label htmlFor={`${prefix}-contact`}>Contact</label>
						<input id={`${prefix}-contact`} name="contact" defaultValue={row.party?.contact ?? ""} readOnly={!editing} />
					</>
				)}
				{employee ? <EmployeeFields idPrefix={prefix} profile={row.profile} locked={!editing} /> : <PartyFields idPrefix={prefix} vendor={row.kind === "vendor"} party={row.party} locked={!editing} />}
				<label htmlFor={`${prefix}-note`}>Note</label>
				<textarea id={`${prefix}-note`} name="note" rows={2} defaultValue={row.note ?? ""} readOnly={!editing} />
				{save.error ? <p className="err">{save.error}</p> : null}
				<div className="emp-actions">
					{row.id && !editing ? <button className="btn ghost" type="button" onClick={() => setEditing(true)}>Edit</button> : null}
					{editing ? <button className="btn" type="submit" disabled={save.pending}>Save</button> : null}
					{editing ? <button className="btn ghost" type="button" onClick={() => setEditing(false)}>Cancel</button> : null}
					{employee && row.id && !editing ? <button className="btn ghost" type="button" onClick={onAddDocument}>Add document</button> : null}
				</div>
				</div>
			</form>
			{row.kind === "customer" && row.id ? (
				<div className="emp-docs">
					<h2>Sites</h2>
					<CustomerSitesSection books={books} customerId={String(row.id)} />
				</div>
			) : null}
			{employee ? (
				<div className="emp-docs">
					<h2>Documents</h2>
					{(row.documents ?? []).length === 0 ? <p className="quiet">No documents yet.</p> : row.documents?.map((item) => (
						<div className="emp-doc" key={`${item.kind}-${item.title}`}>
							<span>{item.title}</span>
							<span className="pill">{documentKindLabel(item.kind)}</span>
							{item.note ? <span className="quiet">{item.note}</span> : null}
						</div>
					))}
				</div>
			) : null}
		</div>
	);
}

type WorkRow = Books["work"][number];

type WorkColumnId = "title" | "type" | "project" | "site" | "customer" | "assignee" | "status" | "due";
const WORK_TABLE_COLUMNS: Array<{ id: WorkColumnId; label: string; sortable?: boolean }> = [
	{ id: "title", label: "Work", sortable: true },
	{ id: "type", label: "Type", sortable: false },
	{ id: "project", label: "Project", sortable: true },
	{ id: "site", label: "Site", sortable: true },
	{ id: "customer", label: "Customer", sortable: true },
	{ id: "assignee", label: "Assigned to", sortable: true },
	{ id: "status", label: "Status", sortable: true },
	{ id: "due", label: "Date", sortable: true },
];

function workMatchesSearch(
	row: WorkRow,
	query: string,
	projectName: (id: string) => string,
	siteName: (projectId: string) => string,
	customerNameForProject: (projectId: string) => string,
): boolean {
	const q = query.trim().toLowerCase();
	if (!q) return true;
	return [
		row.title,
		workStatusLabel(row.status),
		row.projectId ? projectName(row.projectId) : "",
		row.projectId ? siteName(row.projectId) : "",
		row.projectId ? customerNameForProject(row.projectId) : "",
		...workTypesOf(row).map((type) => workTypeLabel(type)),
		...assigneeNamesOf(row.assignee).map((name) => personDisplayName(name)),
	].some((part) => part.toLowerCase().includes(q));
}

type WorkGroupBy = "type" | "";

function workGroupedRows(rows: WorkRow[], groupBy: WorkGroupBy): Array<[string, WorkRow[]]> {
	if (!groupBy) return [["", rows]];
	const map = new Map<string, WorkRow[]>();
	for (const row of rows) {
		const types = workTypesOf(row);
		if (types.length === 0) {
			const list = map.get("No type") ?? [];
			list.push(row);
			map.set("No type", list);
			continue;
		}
		for (const slug of types) {
			const label = workTypeLabel(slug) || slug;
			const list = map.get(label) ?? [];
			list.push(row);
			map.set(label, list);
		}
	}
	const order = new Map<string, number>(WORK_TYPE_OPTIONS.map(([, label], index) => [label, index]));
	return [...map.entries()].sort(([a], [b]) => {
		if (a === "No type") return 1;
		if (b === "No type") return -1;
		const ai = order.get(a) ?? 999;
		const bi = order.get(b) ?? 999;
		return ai - bi || a.localeCompare(b);
	});
}

function Work({
	books,
	people,
	assigneeColors,
	status,
	workType = "",
	project,
	onStatus,
	onWorkType,
	openWorkId = null,
	calendarOverlayWorkId = undefined,
	onCalendarOverlayClose,
}: {
	books: Books;
	people: HubPersonDto[];
	assigneeColors: Record<string, string | undefined>;
	status: string;
	workType?: string;
	project: string;
	onStatus: (value: string) => void;
	onWorkType?: (value: string) => void;
	openWorkId?: number | null;
	calendarOverlayWorkId?: number | null;
	onCalendarOverlayClose?: () => void;
}) {
	const calendarOverlay = onCalendarOverlayClose != null;
	const client = useQueryClient();
	const searchRef = useRef<HTMLInputElement>(null);
	const [editing, setEditing] = useState<WorkRow | null>(null);
	const [formReadOnly, setFormReadOnly] = useState(false);
	const [deletePending, setDeletePending] = useState(false);
	const [formProjectId, setFormProjectId] = useState(project);
	const [formWorkType, setFormWorkType] = useState("");
	const [formAssignees, setFormAssignees] = useState<string[]>([]);
	const [searchOpen, setSearchOpen] = useState(false);
	const [searchQuery, setSearchQuery] = useState("");
	const [sortKey, setSortKey] = useState<WorkColumnId>("title");
	const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
	const [columnVisibility, setColumnVisibility] = useState(() => defaultColumnVisibility(WORK_TABLE_COLUMNS));
	const [groupBy, setGroupBy] = useState<WorkGroupBy>("");
	const [collapsedGroups, setCollapsedGroups] = useState<string[]>([]);
	useHubOperationSearchFocus(searchOpen, searchRef);
	const assigneeColorByLowerName = useWorkAssigneeColorMap(assigneeColors);
	const assignable = useMemo(() => workAssignablePeople(people, books.work), [people, books.work]);
	const projectName = (id: string) => books.projects.find((row) => String(row.id ?? "") === id)?.name ?? "";
	const siteName = (projectId: string) => {
		const proj = books.projects.find((row) => String(row.id ?? "") === projectId);
		if (!proj?.siteId) return "";
		return books.customerSites.find((row) => String(row.id ?? "") === proj.siteId)?.name ?? "";
	};
	const customerNameForProject = (projectId: string) => {
		const proj = books.projects.find((row) => String(row.id ?? "") === projectId);
		if (!proj) return "";
		const customerId = proj.customerId
			?? books.customerSites.find((row) => String(row.id ?? "") === proj.siteId)?.customerId
			?? "";
		return books.organisations.find((row) => String(row.id ?? "") === customerId)?.name ?? "";
	};
	const rows = books.work.filter((row) => !project || row.projectId === project);
	const searchMatched = useMemo(
		() => rows.filter((row) => workMatchesSearch(row, searchQuery, projectName, siteName, customerNameForProject)),
		[rows, searchQuery],
	);
	const visible = useMemo(() => {
		return searchMatched.filter((row) => {
			if (status && row.status !== status) return false;
			if (workType && !workTypesOf(row).includes(workType)) return false;
			return true;
		});
	}, [searchMatched, status, workType]);
	const sorted = useMemo(() => {
		const copy = [...visible];
		copy.sort((a, b) => {
			let av = "";
			let bv = "";
			switch (sortKey) {
				case "project":
					av = a.projectId ? projectName(a.projectId) : "";
					bv = b.projectId ? projectName(b.projectId) : "";
					break;
				case "site":
					av = a.projectId ? siteName(a.projectId) : "";
					bv = b.projectId ? siteName(b.projectId) : "";
					break;
				case "customer":
					av = a.projectId ? customerNameForProject(a.projectId) : "";
					bv = b.projectId ? customerNameForProject(b.projectId) : "";
					break;
				case "assignee":
					av = assigneeNamesOf(a.assignee).map((name) => personDisplayName(name)).join(", ");
					bv = assigneeNamesOf(b.assignee).map((name) => personDisplayName(name)).join(", ");
					break;
				case "status":
					av = workStatusLabel(a.status);
					bv = workStatusLabel(b.status);
					break;
				case "title":
					av = a.title;
					bv = b.title;
					break;
				case "due":
					av = a.due ?? "";
					bv = b.due ?? "";
					break;
				default:
					av = "";
					bv = "";
			}
			return compareLocale(av, bv, sortDir);
		});
		return copy;
	}, [visible, sortKey, sortDir]);
	const workPaginationResetKey = `${searchQuery}|${status}|${workType}|${groupBy}|${sortKey}|${sortDir}|${project}`;
	const workPagination = useHubOperationPagination(sorted.length, workPaginationResetKey);
	const pagedSorted = useMemo(() => workPagination.sliceRows(sorted), [sorted, workPagination.sliceRows]);
	const groupedRows = useMemo(() => workGroupedRows(pagedSorted, groupBy), [pagedSorted, groupBy]);
	const statusCounts = useMemo(() => {
		const counts = new Map<string, number>();
		for (const row of searchMatched) counts.set(row.status, (counts.get(row.status) ?? 0) + 1);
		return counts;
	}, [searchMatched]);
	const currentProject = projectName(project);
	const salesOrders = books.purchases["sales-orders"] ?? [];
	const linkedSalesOrder = editing?.id != null
		? findSalesOrderById(salesOrders, salesOrderIdOf(editing.salesOrderId) ?? -1)
		: undefined;
	const form = useHubSave(async (fields) => {
		const salesOrderRaw = textOf(fields, "salesOrder");
		const salesOrderId = salesOrderRaw ? Number(salesOrderRaw) : null;
		const statusRaw = textOf(fields, "status");
		const record: Record<string, unknown> = {
			title: textOf(fields, "title"),
			projectId: textOf(fields, "project"),
			status: statusRaw || editing?.status || DEFAULT_WORK_STATUS,
			due: textOf(fields, "due"),
			start: textOf(fields, "start"),
			workTypes: formWorkType ? [formWorkType] : [],
			assignee: assigneeFieldFromNames(formAssignees),
		};
		if (salesOrderId != null && Number.isFinite(salesOrderId)) record.salesOrderId = salesOrderId;
		if (editing?.id != null) {
			const next = { ...editing, ...record } as WorkRow;
			if (salesOrderId == null || !Number.isFinite(salesOrderId)) delete next.salesOrderId;
			await api.updateHubBook({
				list: "work",
				id: editing.id,
				record: next,
			});
		} else {
			await api.appendHubBook({ list: "work", record });
		}
		setEditing(null);
	});
	const openAdd = () => {
		setEditing(null);
		setFormReadOnly(false);
		setFormProjectId(project);
		setFormWorkType("");
		setFormAssignees([]);
		form.setOpen(true);
	};
	const openView = (row: WorkRow) => {
		setEditing(row);
		setFormReadOnly(true);
		setFormProjectId(row.projectId ?? project);
		setFormWorkType(workTypesOf(row)[0] ?? "");
		setFormAssignees(resolveAssigneePicks(row.assignee ?? "", assignable));
		form.setOpen(true);
	};
	const openEdit = (row: WorkRow) => {
		setEditing(row);
		setFormReadOnly(false);
		setFormProjectId(row.projectId ?? project);
		setFormWorkType(workTypesOf(row)[0] ?? "");
		setFormAssignees(resolveAssigneePicks(row.assignee ?? "", assignable));
		form.setOpen(true);
	};
	const closeForm = () => {
		form.setOpen(false);
		setEditing(null);
		setFormReadOnly(false);
		onCalendarOverlayClose?.();
	};
	useEffect(() => {
		if (!calendarOverlay) return;
		const id = calendarOverlayWorkId;
		if (id == null) {
			if (form.open) {
				form.setOpen(false);
				setEditing(null);
			}
			return;
		}
		const row = books.work.find((item) => item.id === id);
		if (!row) {
			onCalendarOverlayClose?.();
			return;
		}
		if (editing?.id === id && form.open) return;
		openView(row);
	}, [books.work, calendarOverlay, calendarOverlayWorkId, editing?.id, form.open, onCalendarOverlayClose]);
	useEffect(() => {
		if (calendarOverlay || openWorkId == null) return;
		const row = books.work.find((item) => item.id === openWorkId);
		if (!row) return;
		if (editing?.id === openWorkId && form.open) return;
		openView(row);
	}, [books.work, calendarOverlay, editing?.id, form.open, openWorkId]);
	async function deleteWorkRow(row: WorkRow) {
		if (row.id == null) return;
		if (!window.confirm(`Remove "${row.title}"?`)) return;
		setDeletePending(true);
		try {
			await api.deleteHubBook({ list: "work", id: row.id });
			await client.invalidateQueries({ queryKey: queryKeys.hubRecords });
			if (editing?.id === row.id) closeForm();
		} finally {
			setDeletePending(false);
		}
	}
	async function removeWork() {
		if (editing?.id == null) return;
		if (!window.confirm(`Remove "${editing.title}"?`)) return;
		await deleteWorkRow(editing);
	}
	const searchActive = searchQuery.trim().length > 0;
	const listFiltered = Boolean(status || workType);
	const filtered = searchActive || listFiltered;
	const visibleColumns = WORK_TABLE_COLUMNS.filter((column) => columnVisibility[column.id]);
	const colSpan = visibleColumns.length + 1;

	function renderWorkRow(row: WorkRow, rowKey: string) {
		return (
			<tr key={rowKey}>
				{visibleColumns.map((column) => {
					switch (column.id) {
						case "title":
							return (
								<td key={column.id} data-label={column.label}>
									<button className="row-link" type="button" onClick={() => openView(row)}>{row.title}</button>
								</td>
							);
						case "type":
							return <td key={column.id} data-label={column.label}><WorkTypeBadges types={workTypesOf(row)} /></td>;
						case "project":
							return (
								<td key={column.id} data-label={column.label}>
									{row.projectId && projectName(row.projectId) ? <Link className="row-link" to={`/hub/projects/${row.projectId}`}>{projectName(row.projectId)}</Link> : "—"}
								</td>
							);
						case "site":
							return <td key={column.id} data-label={column.label}>{row.projectId ? (siteName(row.projectId) || "—") : "—"}</td>;
						case "customer":
							return <td key={column.id} data-label={column.label}>{row.projectId ? (customerNameForProject(row.projectId) || "—") : "—"}</td>;
						case "assignee":
							return (
								<td key={column.id} data-label={column.label}>
									<WorkAssigneesCell assignee={row.assignee} assigneeColorByLowerName={assigneeColorByLowerName} />
								</td>
							);
						case "status":
							return <td key={column.id} data-label={column.label}>{workStatusLabel(row.status)}</td>;
						case "due":
							return <td key={column.id} data-label={column.label}>{row.due || "—"}</td>;
						default:
							return <td key={column.id}>—</td>;
					}
				})}
				<td className="tight" onClick={(event) => event.stopPropagation()}>
					{row.id != null ? (
						<details className="select menu">
							<summary aria-label="Actions"><svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="12" cy="5" r="1.8" /><circle cx="12" cy="12" r="1.8" /><circle cx="12" cy="19" r="1.8" /></svg></summary>
							<div className="select-pop">
								<button className="select-item" type="button" onClick={() => openView(row)}>View</button>
								<button className="select-item" type="button" onClick={() => openEdit(row)}>Edit</button>
								<button className="select-item" type="button" disabled={deletePending} onClick={() => void deleteWorkRow(row)}>Delete</button>
							</div>
						</details>
					) : null}
				</td>
			</tr>
		);
	}

	function toggleWorkSort(key: WorkColumnId) {
		const next = toggleHubSort(sortKey, sortDir, key);
		setSortKey(next.sortKey as WorkColumnId);
		setSortDir(next.sortDir);
	}

	function clearWorkFilters() {
		onStatus("");
		onWorkType?.("");
	}

	return (
		<>
			{calendarOverlay ? null : (
				<HubOperationScreen>
					<HubOperationBar sub={currentProject ? `${sorted.length} for ${currentProject}` : `${sorted.length} ${sorted.length === 1 ? "work item" : "work items"}${filtered ? " · filtered" : ""}`}>
						<HubOperationToolbar
							searchSlot={(
								<HubOperationSearchField
									id="hub-work-search"
									ariaLabel="Search work"
									placeholder="Search work…"
									searchOpen={searchOpen}
									searchQuery={searchQuery}
									searchRef={searchRef}
									onQueryChange={setSearchQuery}
									onEscape={() => { setSearchQuery(""); setSearchOpen(false); }}
								/>
							)}
							tools={(
								<>
									<HubOperationSearchToggle
										searchOpen={searchOpen}
										searchActive={searchActive}
										ariaLabel="Search work"
										onToggle={() => {
											if (searchOpen) setSearchQuery("");
											setSearchOpen(!searchOpen);
										}}
									/>
									<HubOperationColumnMenu
										columns={WORK_TABLE_COLUMNS}
										visibility={columnVisibility}
										onToggle={(id, checked) => setColumnVisibility((current) => ({ ...current, [id]: checked }))}
									/>
								</>
							)}
							primaryAction={(
								<>
									{project ? <Link className="btn ghost" to={status ? `/hub/work?tab=${status}` : "/hub/work"}>All work</Link> : null}
									<button className="btn" type="button" onClick={openAdd}>New work</button>
								</>
							)}
						/>
					</HubOperationBar>
					<div className="task-filters">
						<HubOperationFilterMenu
							label="Status"
							ariaLabel="Work status"
							value={status}
							onPick={onStatus}
							active={Boolean(status)}
							counted
							items={[
								["", "All", searchMatched.length],
								...JOB_STATUSES.map(([id, text]) => [id, text, statusCounts.get(id) ?? 0] as [string, string, number]),
							]}
						/>
						{onWorkType ? (
							<HubOperationFilterMenu
								label="Type"
								ariaLabel="Work type"
								value={workType}
								onPick={onWorkType}
								active={Boolean(workType)}
								counted
								items={workTypeFilterCounts(searchMatched)}
							/>
						) : null}
						<HubOperationFilterMenu
							label="Group"
							ariaLabel="Group list"
							value={groupBy}
							onPick={(next) => {
								setGroupBy(next as WorkGroupBy);
								setCollapsedGroups([]);
							}}
							active={Boolean(groupBy)}
							items={[
								["", "All"],
								["type", "Work type"],
							]}
						/>
						{listFiltered ? <HubOperationFilterClear onClear={clearWorkFilters} /> : null}
					</div>
					<HubOperationListTable>
					<div className="table-wrap hub-inventory-table-wrap">
						<table className="hub-inventory-table">
							<thead>
								<tr>
									{visibleColumns.map((column) => (
										<th key={column.id}>
											{column.sortable ? (
												<button type="button" className="th-sort plain" onClick={() => toggleWorkSort(column.id)}>
													{column.label}
													{hubTableSortIndicator(sortKey, column.id, sortDir)}
												</button>
											) : column.label}
										</th>
									))}
									<th className="tight"></th>
								</tr>
							</thead>
							<tbody>
								{!hubOperationGroupedHasRows(groupedRows) ? (
									<HubOperationTableEmptyRow colSpan={colSpan}>
										{filtered || rows.length > 0 ? "No work matches your filters." : "No work yet."}
									</HubOperationTableEmptyRow>
								) : groupedRows.flatMap(([groupLabel, groupItems]) => {
									const collapsed = collapsedGroups.includes(groupLabel);
									const header = groupBy ? (
										<HubOperationGroupHeader
											key={`grp-${groupLabel}`}
											label={groupLabel}
											collapsed={collapsed}
											colSpan={colSpan}
											onToggle={() => setCollapsedGroups((current) => (
												current.includes(groupLabel) ? current.filter((entry) => entry !== groupLabel) : [...current, groupLabel]
											))}
										/>
									) : null;
									return [
										...(header ? [header] : []),
										...(groupBy && collapsed ? [] : groupItems.map((row) => renderWorkRow(row, `${groupLabel}-${String(row.id ?? row.title)}`))),
									];
								})}
							</tbody>
						</table>
					</div>
					<HubOperationTablePagination
						total={sorted.length}
						page={workPagination.page}
						pageSize={workPagination.pageSize}
						totalPages={workPagination.totalPages}
						onPage={workPagination.setPage}
						onPageSize={workPagination.setPageSize}
						totalNoun="work items"
					/>
					</HubOperationListTable>
				</HubOperationScreen>
			)}
			<HubOperationModal
				hubForm
				open={form.open}
				title={formReadOnly ? "View work" : editing ? "Edit work" : "New work"}
				error={form.error}
				pending={form.pending || deletePending}
				readOnly={formReadOnly}
				onEdit={editing ? () => setFormReadOnly(false) : undefined}
				onClose={closeForm}
				onSubmit={(fields) => void form.save(fields)}
			>
				<HubOperationFormStack>
					<HubOperationFormField label="Work" htmlFor="work-title">
						<input id="work-title" name="title" placeholder="Install CCTV at site" defaultValue={editing?.title ?? ""} required readOnly={formReadOnly} />
					</HubOperationFormField>
					<HubOperationFormRow>
						<HubOperationFormField label="Project" htmlFor="work-project">
							<Select
								key={`${project || "all"}-${editing?.id ?? "new"}`}
								id="work-project"
								name="project"
								value={formProjectId}
								onChange={(event) => setFormProjectId(event.target.value)}
								required={!editing && !formReadOnly}
								disabled={formReadOnly}
							>
								{editing ? <option value="">No project</option> : <option value="">Choose project</option>}
								{books.projects.map((row) => <option key={row.id ?? row.name} value={String(row.id ?? "")}>{row.name}</option>)}
							</Select>
						</HubOperationFormField>
						<HubOperationFormField label="Status" htmlFor="work-status">
							{editing?.status === LEGACY_WORK_STATUS ? (
								<p className="quiet" id="work-status-legacy">Currently New. Choose In progress or Done below, or leave blank to keep New.</p>
							) : null}
							<Select
								id="work-status"
								name="status"
								defaultValue={
									editing?.status === LEGACY_WORK_STATUS
										? ""
										: editing?.status ?? (status && status !== LEGACY_WORK_STATUS ? status : DEFAULT_WORK_STATUS)
								}
								aria-describedby={editing?.status === LEGACY_WORK_STATUS ? "work-status-legacy" : undefined}
								disabled={formReadOnly}
							>
								{editing?.status === LEGACY_WORK_STATUS ? <option value="">Keep New</option> : null}
								{JOB_STATUSES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
							</Select>
						</HubOperationFormField>
					</HubOperationFormRow>
					<HubOperationFormField label="Work type" htmlFor="work-type">
						{formReadOnly ? (
							<p><WorkTypeBadges types={formWorkType ? [formWorkType] : workTypesOf(editing ?? {})} /></p>
						) : (
							<WorkTypeSelect id="work-type" value={formWorkType} onChange={setFormWorkType} />
						)}
					</HubOperationFormField>
					{formProjectId ? <WorkProjectContext books={books} projectId={formProjectId} /> : null}
					<HubOperationFormRow>
						<HubOperationFormField label="Date" htmlFor="work-due">
							<input id="work-due" name="due" type="date" defaultValue={editing?.due?.slice(0, 10) ?? ""} readOnly={formReadOnly} />
						</HubOperationFormField>
						<HubOperationFormField label="Time" htmlFor="work-start">
							<input id="work-start" name="start" type="time" defaultValue={editing?.start ?? ""} readOnly={formReadOnly} />
						</HubOperationFormField>
					</HubOperationFormRow>
					<WorkAssigneesField
						id="work-assignees"
						label="Assigned to"
						picked={formAssignees}
						assignable={assignable}
						onChange={setFormAssignees}
						disabled={formReadOnly}
						assigneeColorByLowerName={assigneeColorByLowerName}
					/>
					<HubOperationFormField label="Sales order" htmlFor="work-sales-order">
						<Select
							key={`work-so-${editing?.id ?? "new"}`}
							id="work-sales-order"
							name="salesOrder"
							defaultValue={editing?.salesOrderId != null ? String(editing.salesOrderId) : ""}
							disabled={formReadOnly}
						>
							<option value="">No sales order</option>
							{salesOrders.filter((row) => row.id != null).map((row) => (
								<option key={row.id} value={String(row.id)}>{row.ref_no} · {row.party}</option>
							))}
						</Select>
						{linkedSalesOrder ? (
							<p className="quiet">
								<Link className="row-link" to={salesOrderDetailPath(linkedSalesOrder)}>Open {linkedSalesOrder.ref_no}</Link>
							</p>
						) : null}
					</HubOperationFormField>
					{editing?.id != null && !formReadOnly ? (
						<div className="flex justify-start">
							<button className="btn ghost" type="button" disabled={deletePending} onClick={() => void removeWork()}>Delete</button>
						</div>
					) : null}
				</HubOperationFormStack>
			</HubOperationModal>
		</>
	);
}

function Deals({ books }: { books: Books }) {
	const rows = books.deals;
	const form = useHubSave(async (fields) => {
		await api.appendHubBook({
			list: "deals",
			record: {
				title: textOf(fields, "title"),
				party: textOf(fields, "party"),
				amount: textOf(fields, "amount"),
				stage: textOf(fields, "stage") || "new",
				expected: textOf(fields, "expected"),
				note: textOf(fields, "note"),
			},
		});
	});
	return (
		<div>
			<Bar sub={`${rows.length} ${rows.length === 1 ? "deal" : "deals"}`}>
				<button className="btn" type="button" onClick={() => form.setOpen(true)}>New</button>
			</Bar>
			<Modal open={form.open} title="New deal" error={form.error} pending={form.pending} onClose={() => form.setOpen(false)} onSubmit={form.save}>
				<label htmlFor="deal-title">Deal</label>
				<input id="deal-title" name="title" required />
				<div className="grid two">
					<div><label htmlFor="deal-party">Customer</label><input id="deal-party" name="party" required /></div>
					<div><label htmlFor="deal-amount">Amount</label><input id="deal-amount" name="amount" inputMode="decimal" required /></div>
					<div>
						<label htmlFor="deal-stage">Stage</label>
						<Select id="deal-stage" name="stage">{DEAL_STAGES.map(([value, name]) => <option key={value} value={value}>{name}</option>)}</Select>
					</div>
					<div><label htmlFor="deal-expected">Expected</label><input id="deal-expected" name="expected" type="date" /></div>
				</div>
				<label htmlFor="deal-note">Note</label>
				<textarea id="deal-note" name="note" rows={3} />
			</Modal>
			<div className="board deal-board">
				{DEAL_STAGES.map(([stage, name]) => {
					const cards = rows.filter((row) => row.stage === stage);
					return (
						<section className="col" key={stage}>
							<h2>{name} · {cards.length}</h2>
							{cards.map((row) => (
								<article className="card" key={row.title}>
									<div className="card-title">{row.title}</div>
									<div className="card-meta">{row.party} · {money(Number(row.amount) || 0)}</div>
									<div className="card-meta">{row.expected || "No expected date"}</div>
									{row.note ? <div className="card-meta">{row.note}</div> : null}
								</article>
							))}
						</section>
					);
				})}
			</div>
		</div>
	);
}

function Items({ books }: { books: Books }) {
	const rows = books.items;
	const form = useHubSave(async (fields) => {
		await api.appendHubBook({
			list: "items",
			record: { name: textOf(fields, "name"), sku: textOf(fields, "sku"), unit: textOf(fields, "unit") || "unit", price: textOf(fields, "price"), note: textOf(fields, "note") },
		});
	});
	return (
		<div>
			<Bar sub={`${rows.length} ${rows.length === 1 ? "item" : "items"}`}>
				<button className="btn" type="button" onClick={() => form.setOpen(true)}>New</button>
			</Bar>
			<Modal open={form.open} title="New item" error={form.error} pending={form.pending} onClose={() => form.setOpen(false)} onSubmit={form.save}>
				<label htmlFor="item-name">Item</label>
				<input id="item-name" name="name" required />
				<div className="grid two">
					<div><label htmlFor="item-sku">SKU</label><input id="item-sku" name="sku" required /></div>
					<div>
						<label htmlFor="item-unit">Unit</label>
						<Select id="item-unit" name="unit"><option value="unit">Unit</option><option value="roll">Roll</option><option value="set">Set</option><option value="job">Job</option><option value="hour">Hour</option></Select>
					</div>
					<div><label htmlFor="item-price">Price</label><input id="item-price" name="price" inputMode="decimal" required /></div>
				</div>
				<label htmlFor="item-note">Note</label>
				<textarea id="item-note" name="note" rows={3} />
			</Modal>
			{rows.length === 0 ? <Empty>No items yet.</Empty> : (
				<Grid columns={["Item", "SKU", "Unit", "Price", "Note"]} rows={rows.map((row) => [row.name, row.sku, label(row.unit), money(Number(row.price) || 0), row.note])} />
			)}
		</div>
	);
}

function Receipts({ books, brands }: { books: Books; brands: HubBrandDto[] }) {
	const navigate = useNavigate();
	const { docId } = useParams();
	const listPath = "/hub/receipts";
	const all = books.receipts;
	const invoices = books.purchases.invoices ?? [];
	const [query, setQuery] = useState("");
	const [searching, setSearching] = useState(false);
	const [sort, setSort] = useState<DocSort | "">("");
	const [dir, setDir] = useState<"asc" | "desc">("asc");
	const searchRef = useRef<HTMLInputElement>(null);
	const needle = query.trim().toLowerCase();
	const found = needle
		? all.filter((row) => {
			const invoice = findInvoiceForReceipt(row, invoices);
			const invoiceRef = invoice?.ref_no ?? row.against ?? "";
			return [row.party, row.ref_no, row.note, row.against, invoiceRef, label(row.method)].some((value) => (value ?? "").toLowerCase().includes(needle));
		})
		: all;
	const receiptSortValue = (row: HubReceiptRow): string | number => {
		const invoice = findInvoiceForReceipt(row, invoices);
		if (sort === "amount") return receiptAmount(row);
		if (sort === "ref") return row.ref_no;
		if (sort === "party") return row.party;
		if (sort === "extra") return invoice?.ref_no ?? row.against ?? "";
		if (sort === "status") return row.method;
		return row.doc_at;
	};
	const sign = dir === "asc" ? 1 : -1;
	const rows = sort
		? [...found].sort((a, b) => {
			const left = receiptSortValue(a);
			const right = receiptSortValue(b);
			if (typeof left === "number" && typeof right === "number") return (left - right) * sign;
			return String(left).localeCompare(String(right), "en", { numeric: true }) * sign;
		})
		: found;
	const totalReceived = all.reduce((sum, row) => sum + receiptAmount(row), 0);
	function sortBy(column: DocSort) {
		if (sort === column) setDir(dir === "asc" ? "desc" : "asc");
		else {
			setSort(column);
			setDir("asc");
		}
	}
	useEffect(() => {
		if (searching) searchRef.current?.focus();
	}, [searching]);
	if (docId) {
		const selected = findReceiptByKey(all, docId);
		if (!selected) {
			return (
				<div className="doc-screen">
					<button className="doc-detail-back" type="button" onClick={() => navigate(listPath)}>Back</button>
					<Empty>Receipt not found.</Empty>
				</div>
			);
		}
		return <ReceiptDetail receipt={selected} books={books} brands={brands} listPath={listPath} />;
	}
	return (
		<div className="doc-screen">
			<div className="doc-body">
				<section className="doc-summary" aria-label="Receipts summary">
					<dl>
						<div><dt>Total receipts</dt><dd>{all.length}</dd></div>
						<div><dt>Total received amount</dt><dd>{docMoney(totalReceived)}</dd></div>
					</dl>
				</section>
				<div className="doc-toolbar">
					<div className="doc-search-group">
						<div className={searching ? "doc-search-wrap is-open" : "doc-search-wrap"}>
							<div className="doc-search-field">
								<label className="sr" htmlFor="doc-search-receipts">Search receipts…</label>
								<input
									ref={searchRef}
									id="doc-search-receipts"
									className="doc-search-input"
									type="search"
									placeholder="Search receipts…"
									aria-label="Search receipts…"
									value={query}
									tabIndex={searching ? 0 : -1}
									onChange={(event) => setQuery(event.target.value)}
									onKeyDown={(event) => {
										if (event.key === "Escape") {
											setQuery("");
											setSearching(false);
										}
									}}
								/>
							</div>
						</div>
						<button
							className="doc-icon-btn doc-search-toggle"
							type="button"
							aria-label="Search receipts…"
							aria-expanded={searching}
							onClick={() => { if (searching) setQuery(""); setSearching(!searching); }}
						>
							<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>
						</button>
					</div>
				</div>
				<p className="doc-detail-muted doc-create-hint">Receipts are created from an invoice. Open an invoice and choose Create receipt.</p>
				<section className="doc-list" aria-label="Receipts">
					<div className="doc-head">
						<DocSortHead column="ref" label="Receipt no." sort={sort} dir={dir} onSort={sortBy} />
						<DocSortHead column="party" label="Customer" sort={sort} dir={dir} onSort={sortBy} />
						<DocSortHead column="extra" label="Invoice" sort={sort} dir={dir} onSort={sortBy} center />
						<DocSortHead column="amount" label="Amount" sort={sort} dir={dir} onSort={sortBy} center />
						<DocSortHead column="status" label="Method" sort={sort} dir={dir} onSort={sortBy} center />
					</div>
					{rows.length === 0 ? (
						<p className="doc-empty">{all.length === 0 ? "No receipts yet." : needle ? `No receipts match "${query.trim()}".` : "No receipts yet."}</p>
					) : (
						<div className="doc-rows">
							{rows.map((row, index) => {
								const invoice = findInvoiceForReceipt(row, invoices);
								const invoiceRef = invoice?.ref_no ?? row.against ?? "—";
								function openDoc() {
									navigate(receiptDetailPath(row));
								}
								return (
									<div
										className="doc-row clickable"
										key={`${row.ref_no}-${row.id ?? index}`}
										role="button"
										tabIndex={0}
										onClick={openDoc}
										onKeyDown={(event) => {
											if (event.key === "Enter" || event.key === " ") {
												event.preventDefault();
												openDoc();
											}
										}}
									>
										<div className="doc-ref">
											<span>{row.ref_no || "—"}</span>
											<small>{docDate(row.doc_at)}</small>
										</div>
										<div className="doc-party">
											<strong>{row.party || "—"}</strong>
											{row.note ? <small>{row.note}</small> : null}
										</div>
										<span className="doc-due" data-label="Invoice">{invoiceRef}</span>
										<span className="doc-amount">{docMoney(receiptAmount(row))}</span>
										<span className="doc-status"><span className="doc-pill idle">{label(row.method) || "—"}</span></span>
									</div>
								);
							})}
						</div>
					)}
				</section>
			</div>
		</div>
	);
}

function ReceiptDetail({
	receipt,
	books,
	brands,
	listPath,
}: {
	receipt: HubReceiptRow;
	books: Books;
	brands: HubBrandDto[];
	listPath: string;
}) {
	const navigate = useNavigate();
	const client = useQueryClient();
	const invoices = books.purchases.invoices ?? [];
	const invoice = findInvoiceForReceipt(receipt, invoices);
	const amount = receiptAmount(receipt);
	const customer = organisationByName(books, receipt.party, "customer") ?? organisationByName(books, receipt.party);
	const issuer = issuerOrganisation(books, brands, customer);
	const billed = docPartyBlock(customer, receipt.party);
	const from = docPartyBlock(issuer, brands[0]?.name ?? "");
	const refLabel = receipt.ref_no?.trim();
	const docNumber = refLabel
		? (/^\d+$/.test(refLabel) ? `#${refLabel}` : refLabel)
		: receipt.id != null
			? `#${receipt.id}`
			: "—";
	const sourceInvoiceRef = invoice?.ref_no?.trim() || receipt.against?.trim() || "—";
	const [editOpen, setEditOpen] = useState(false);
	const [copyNote, setCopyNote] = useState("");
	const [detailActionError, setDetailActionError] = useState("");
	useEffect(() => {
		setDetailActionError("");
		setCopyNote("");
	}, [receipt.id]);
	const editForm = useHubSave(async (fields) => {
		if (receipt.id == null || !invoice) throw new ApiError(400, { code: "validation_failed", message: "This receipt cannot be edited.", requestId: "" });
		const amountRaw = textOf(fields, "amount");
		const refNo = textOf(fields, "ref_no");
		const message = validateReceiptPaymentInput({
			amount: amountRaw,
			refNo,
			receipts: books.receipts,
			invoice,
			excludeReceiptId: receipt.id,
		});
		if (message) throw new ApiError(422, { code: "validation_failed", message, requestId: "" });
		await api.updateHubBook({
			list: "receipts",
			id: receipt.id,
			record: receiptRecordFromExisting(receipt, {
				doc_at: textOf(fields, "doc_at"),
				ref_no: refNo,
				method: textOf(fields, "method") || "transfer",
				amount: formatReceiptAmount(amountRaw),
				note: textOf(fields, "note"),
				party: invoice.party,
				against: invoice.ref_no ?? "",
				invoiceId: invoice.id,
			}),
		});
		const nextReceipts = books.receipts.map((row) => (
			row.id === receipt.id
				? { ...row, doc_at: textOf(fields, "doc_at"), ref_no: refNo, method: textOf(fields, "method"), amount: formatReceiptAmount(amountRaw), note: textOf(fields, "note") }
				: row
		));
		if (invoice.id != null) {
			const status = invoiceStatusAfterReceipts(invoice, nextReceipts);
			if (status !== invoice.status) {
				await api.updateHubBook({
					list: "purchases.invoices",
					id: invoice.id,
					record: { ...invoice, status },
				});
			}
		}
		setEditOpen(false);
		await client.invalidateQueries({ queryKey: queryKeys.hubRecords });
	});
	async function removeReceipt() {
		if (receipt.id == null) return;
		setDetailActionError("");
		if (!window.confirm("Remove this receipt?")) return;
		try {
			await api.deleteHubBook({ list: "receipts", id: receipt.id });
			if (invoice?.id != null) {
				const nextReceipts = books.receipts.filter((row) => row.id !== receipt.id);
				const status = invoiceStatusAfterReceipts(invoice, nextReceipts);
				if (status !== invoice.status) {
					await api.updateHubBook({
						list: "purchases.invoices",
						id: invoice.id,
						record: { ...invoice, status },
					});
				}
			}
			await client.invalidateQueries({ queryKey: queryKeys.hubRecords });
			navigate(listPath);
		} catch (err) {
			setCopyNote("");
			setDetailActionError(presentError(err).title);
		}
	}
	function pdfDoc() {
		const fileName = purchasePdfFileName("Receipt", docNumber);
		const methodLabel = label(receipt.method);
		void downloadPurchaseDocumentPdf({
			kind: "Receipt",
			docNumber,
			from,
			billed,
			statusLabel: "Receipt Status",
			status: "Recorded",
			tone: "ok",
			dateLabel: "Receipt Date",
			docDate: docLongDate(receipt.doc_at),
			dueLabel: "Source Invoice",
			dueDate: sourceInvoiceRef,
			lines: [{ description: `Payment · ${methodLabel}`, amount: String(amount) }],
			subtotal: amount,
			sst: 0,
			grand: amount,
			balanceDue: 0,
			note: receipt.note?.trim() ?? "",
		}, fileName);
	}
	function sendDoc() {
		const email = customer?.email?.trim() || (billed.contact.includes("@") ? billed.contact.split(" · ")[0]?.trim() : "");
		const subject = `Receipt ${receipt.ref_no}`;
		const body = `Receipt ${receipt.ref_no} for ${docMoney(amount)} against ${sourceInvoiceRef}.`;
		if (email) window.location.href = `mailto:${encodeURIComponent(email)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
	}
	async function copyLink() {
		try {
			await navigator.clipboard.writeText(window.location.href);
			setCopyNote("Link copied.");
		} catch {
			setCopyNote("Could not copy the link.");
		}
	}
	const paymentLineTitle = sourceInvoiceRef !== "—"
		? `Payment for ${sourceInvoiceRef}`
		: "Payment received";
	const itemLines: DocDetailItemLine[] = [{
		title: paymentLineTitle,
		details: [`${label(receipt.method)} · Receipt ${docNumber}`],
		qty: 1,
		rate: amount,
		amount,
	}];
	return (
		<DocDetailShell
			stack={(
				<>
					<DocDetailToolbarFrame
						onBack={() => navigate(listPath)}
						error={detailActionError || undefined}
						actions={(
							<DocDetailPurchaseActionBar
								onPdf={pdfDoc}
								onSend={sendDoc}
								onEdit={() => setEditOpen(true)}
								onDelete={removeReceipt}
								editDisabled={receipt.id == null || !invoice}
								deleteDisabled={receipt.id == null}
							/>
						)}
					/>
					<DocDetailDocumentCard ariaLabel={`Receipt ${receipt.ref_no}`}>
						<DocDetailIssuerHeader from={from} docKind="Receipt" docNumber={docNumber} />
						<DocDetailBilledBand
							billed={billed}
							meta={(
								<>
									<dt>Receipt Status</dt>
									<dd><span className="doc-pill ok">{label(receipt.method)}</span></dd>
									<dt>Receipt Date</dt>
									<dd>{docLongDate(receipt.doc_at)}</dd>
									<dt>Source Invoice</dt>
									<dd>
										{invoice ? (
											<Link className="row-link" to={invoiceDetailPathFromInvoice(invoice)}>{invoice.ref_no}</Link>
										) : sourceInvoiceRef !== "—" ? (
											sourceInvoiceRef
										) : (
											"—"
										)}
									</dd>
								</>
							)}
						/>
						<DocDetailBody>
							<DocDetailItemsSection lines={itemLines} />
							<DocDetailAmountSummary subtotal={amount} sst={0} grand={amount} balanceDue={0} />
							{receipt.note ? <p className="doc-detail-note">{receipt.note}</p> : null}
						</DocDetailBody>
					</DocDetailDocumentCard>
					<DocDetailBottomBar copyNote={copyNote} onCopyLink={copyLink} />
				</>
			)}
			after={(
				<Modal open={editOpen} title="Edit receipt" error={editForm.error} pending={editForm.pending} onClose={() => setEditOpen(false)} onSubmit={editForm.save}>
					<div className="grid two">
						<div><label htmlFor="rc-edit-date">Date</label><input id="rc-edit-date" name="doc_at" type="date" defaultValue={receipt.doc_at?.slice(0, 10) ?? todayIso()} required /></div>
						<div><label htmlFor="rc-edit-ref">Receipt no.</label><input id="rc-edit-ref" name="ref_no" defaultValue={receipt.ref_no} required /></div>
						<div><label htmlFor="rc-edit-amount">Amount</label><input id="rc-edit-amount" name="amount" inputMode="decimal" defaultValue={receipt.amount} required /></div>
						<HubFieldSelect id="rc-edit-method" name="method" label="Method" defaultValue={receipt.method || "transfer"} items={PAYMENT_METHODS} />
					</div>
					<label htmlFor="rc-edit-note">Note</label>
					<textarea id="rc-edit-note" name="note" rows={2} defaultValue={receipt.note ?? ""} />
				</Modal>
			)}
		/>
	);
}

const PURCHASE_COPY: Record<string, { title: string; one: string; many: string; party: string; ref: string; done: string; doneLabel: string; openLabel: string; tabs?: Array<[string, string]> }> = {
	quotations: { title: "Quotation", one: "quotation", many: "quotations", party: "Customer", ref: "Quote no.", done: "accepted", doneLabel: "Accepted", openLabel: "Open" },
	"sales-orders": { title: "Sales order", one: "sales order", many: "sales orders", party: "Customer", ref: "SO no.", done: "fulfilled", doneLabel: "Fulfilled", openLabel: "Open" },
	invoices: { title: "Invoice", one: "invoice", many: "invoices", party: "Customer", ref: "Invoice no.", done: "paid", doneLabel: "Paid", openLabel: "Unpaid", tabs: [["", "All"], ["proforma", "Proforma"], ["commercial", "Commercial"]] },
	bills: { title: "Bills", one: "bill", many: "bills", party: "Vendor", ref: "Bill no.", done: "paid", doneLabel: "Paid", openLabel: "Unpaid" },
	"purchase-orders": { title: "Purchase order", one: "purchase order", many: "purchase orders", party: "Vendor", ref: "PO no.", done: "received", doneLabel: "Received", openLabel: "Open" },
	credits: { title: "Credits", one: "credit", many: "credits", party: "Vendor", ref: "Credit no.", done: "applied", doneLabel: "Applied", openLabel: "Open" },
};

const DOC_TONE: Record<string, string> = {
	paid: "ok",
	accepted: "ok",
	fulfilled: "ok",
	received: "ok",
	applied: "ok",
	confirmed: "info",
	sent: "warn",
	open: "warn",
	overdue: "bad",
	declined: "bad",
};

type DocSort = "ref" | "party" | "extra" | "amount" | "status";

function docTotal(row: PurchaseDoc): number {
	return docTotalFromLines(row.lines);
}

function purchaseLinesForSave(listId: string, fields: FormData): PurchaseLine[] {
	if (listId === "sales-orders") {
		const raw = fields.get("so_lines_json");
		if (typeof raw !== "string" || !raw.trim()) return [];
		try {
			const drafts = JSON.parse(raw) as PurchaseLineDraft[];
			return linesFromDrafts(Array.isArray(drafts) ? drafts : []);
		} catch {
			return [];
		}
	}
	return purchaseLinesFromSingleLineForm(fields);
}

function purchaseLinesToRecords(lines: PurchaseLine[]) {
	return lines.map((line) => ({
		category: "other",
		description: line.description,
		amount: line.amount,
		qty: line.qty,
		unitPrice: line.unitPrice,
	}));
}

function assertSalesOrderValid(fields: FormData, lines: PurchaseLine[]) {
	const message = validateSalesOrderInput({
		party: textOf(fields, "party"),
		refNo: textOf(fields, "ref_no"),
		docAt: textOf(fields, "doc_at"),
		lines,
	});
	if (message) throw new ApiError(422, { code: "validation_failed", message, requestId: "" });
}

function docDate(value: string): string {
	const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
	return match ? `${match[3]}/${match[2]}/${match[1]}` : value;
}

function docMoney(amount: number): string {
	return `RM ${amount.toLocaleString("en-MY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function DocSortHead({ column, label: text, sort, dir, onSort, center }: { column: DocSort; label: string; sort: DocSort | ""; dir: "asc" | "desc"; onSort: (column: DocSort) => void; center?: boolean }) {
	const active = sort === column;
	return (
		<button
			type="button"
			className={center ? "doc-sort center" : "doc-sort"}
			aria-sort={active ? (dir === "asc" ? "ascending" : "descending") : undefined}
			onClick={() => onSort(column)}
		>
			{text}
			<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
				<path d="m7 9 5-5 5 5" opacity={active && dir === "desc" ? 0.3 : 1} />
				<path d="m7 15 5 5 5-5" opacity={active && dir === "asc" ? 0.3 : 1} />
			</svg>
		</button>
	);
}

type PurchaseMeta = (typeof PURCHASE_COPY)[string];

function docLongDate(value: string): string {
	if (!value) return "—";
	const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
	if (!match) return value;
	const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
	if (Number.isNaN(date.getTime())) return docDate(value);
	return new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", year: "numeric" }).format(date);
}

function findPurchaseDoc(rows: PurchaseDoc[], docId: string | undefined): PurchaseDoc | undefined {
	if (!docId) return undefined;
	const key = decodeURIComponent(docId);
	return rows.find((row) => String(row.id) === key || row.ref_no === key);
}

function purchaseDocDetailPath(listId: string, row: PurchaseDoc, tab: string): string {
	const key = encodeURIComponent(row.ref_no?.trim() || String(row.id ?? ""));
	return `/hub/${listId}/${key}${tab ? `?tab=${encodeURIComponent(tab)}` : ""}`;
}

function purchaseDocEditPath(listId: string, row: PurchaseDoc, tab: string): string {
	return `${purchaseDocDetailPath(listId, row, tab)}/edit`;
}

function purchaseTermsValue(docAt: string, dueAt: string): string {
	const start = /^(\d{4})-(\d{2})-(\d{2})/.exec(docAt);
	const end = /^(\d{4})-(\d{2})-(\d{2})/.exec(dueAt);
	if (!start || !end) return "net-30";
	const from = new Date(Number(start[1]), Number(start[2]) - 1, Number(start[3]));
	const to = new Date(Number(end[1]), Number(end[2]) - 1, Number(end[3]));
	const days = Math.round((to.getTime() - from.getTime()) / 86_400_000);
	if (days === 7) return "net-7";
	if (days === 15) return "net-15";
	if (days === 30) return "net-30";
	if (days === 0) return "due-receipt";
	return "net-30";
}

function purchaseDueFromTerms(docAt: string, terms: string): string {
	const start = /^(\d{4})-(\d{2})-(\d{2})/.exec(docAt);
	if (!start) return docAt.slice(0, 10);
	const from = new Date(Number(start[1]), Number(start[2]) - 1, Number(start[3]));
	const add = terms === "net-7" ? 7 : terms === "net-15" ? 15 : terms === "due-receipt" ? 0 : 30;
	from.setDate(from.getDate() + add);
	return from.toISOString().slice(0, 10);
}

function organisationByName(books: Books, name: string, kind?: string): OrgRow | undefined {
	const needle = name.trim().toLowerCase();
	if (!needle) return undefined;
	const exact = books.organisations.find((row) => row.name.trim().toLowerCase() === needle && (!kind || row.kind === kind));
	if (exact) return exact;
	return books.organisations.find((row) => {
		if (kind && row.kind !== kind) return false;
		const label = row.name.trim().toLowerCase();
		return label.includes(needle) || needle.includes(label);
	});
}

function purchasePartyKind(listId: string): "customer" | "vendor" {
	if (listId === "bills" || listId === "purchase-orders" || listId === "credits") return "vendor";
	return "customer";
}

async function syncPurchasePartyContact(books: Books, listId: string, fields: FormData) {
	const kind = purchasePartyKind(listId);
	const name = textOf(fields, "party");
	if (!name) return;
	const email = textOf(fields, "party_email");
	const phone = textOf(fields, "party_phone");
	const address = textOf(fields, "party_address");
	const city = textOf(fields, "party_city");
	const state = textOf(fields, "party_state");
	const postcode = textOf(fields, "party_postcode");
	const linkedId = textOf(fields, "party_org_id");
	const hasContact = Boolean(email || phone || address || city || state || postcode);
	let org = linkedId ? books.organisations.find((row) => String(row.id) === linkedId) : undefined;
	if (!org) org = organisationByName(books, name, kind);
	const patch = new FormData();
	patch.set("kind", kind);
	patch.set("name", name);
	patch.set("email", email);
	patch.set("phone", phone);
	patch.set("address", address);
	patch.set("city", city);
	patch.set("state", state);
	patch.set("postcode", postcode);
	if (org) {
		const party = partyOf(org.party);
		patch.set("code", party.code ?? "");
		patch.set("contact", party.contact ?? "");
		if (kind === "vendor") patch.set("registration", party.registration ?? "");
		const record = organisationRecord(patch, { created_at: org.created_at, documents: org.documents });
		if (org.id != null) await api.updateHubBook({ list: "organisations", id: org.id, record });
		return;
	}
	if (!hasContact) return;
	await api.appendHubBook({ list: "organisations", record: organisationRecord(patch) });
}

function suggestPurchaseRef(listId: string, rows: PurchaseDoc[]): string {
	const prefix =
		listId === "invoices" ? "INV-"
			: listId === "bills" ? "BILL-"
				: listId === "credits" ? "CR-"
					: listId === "quotations" ? "QT-"
						: listId === "sales-orders" ? "SO-"
							: listId === "purchase-orders" ? "PO-"
								: "REF-";
	let max = 0;
	for (const row of rows) {
		const ref = row.ref_no?.trim() ?? "";
		const match = new RegExp(`^${prefix.replace("-", "\\-")}(\\d+)$`, "i").exec(ref);
		if (match) max = Math.max(max, Number(match[1]));
	}
	return `${prefix}${max + 1}`;
}

function orgProfileScore(row: OrgRow): number {
	const party = partyOf(row.party);
	let score = 0;
	if (party.registration || party.code) score += 4;
	if (party.address) score += 3;
	if (party.city || party.state || party.postcode) score += 2;
	if (row.email) score += 2;
	if (row.phone || party.contact) score += 1;
	if (/^test$/i.test(row.name.trim())) score -= 20;
	return score;
}

function issuerOrganisation(books: Books, brands: HubBrandDto[], customer?: OrgRow): OrgRow | undefined {
	const brand = brands[0]?.name?.trim().toLowerCase();
	const candidates = books.organisations.filter((row) => row !== customer && row.kind !== "employee" && !/^test$/i.test(row.name.trim()));
	if (brand) {
		const fromBrand = candidates.find((row) => row.name.trim().toLowerCase() === brand || row.name.trim().toLowerCase().includes(brand));
		if (fromBrand) return fromBrand;
	}
	const vendors = candidates.filter((row) => row.kind === "vendor").sort((a, b) => orgProfileScore(b) - orgProfileScore(a));
	if (vendors[0]) return vendors[0];
	return [...candidates].sort((a, b) => orgProfileScore(b) - orgProfileScore(a))[0];
}

type DocPartyBlock = { heading: string; name: string; registration: string; address: string[]; contact: string };

function docPartyBlock(org: OrgRow | undefined, fallbackName: string): DocPartyBlock {
	if (!org) {
		const name = fallbackName.trim() || "—";
		return { heading: name, name, registration: "", address: [], contact: "—" };
	}
	const party = partyOf(org.party);
	return {
		heading: orgHeading(org, party),
		name: org.name,
		registration: party.registration?.trim() || party.code?.trim() || "",
		address: orgAddress(org, party).filter((line) => line !== "—"),
		contact: orgContact(org, party),
	};
}

function orgContact(row?: OrgRow, party?: PartyDetails): string {
	const email = row?.email?.trim();
	const phone = row?.phone?.trim() || party?.contact?.trim();
	return [email, phone].filter(Boolean).join(" · ") || "—";
}

function orgAddress(row?: OrgRow, party?: PartyDetails): string[] {
	const lines: string[] = [];
	const street = party?.address?.trim() || row?.note?.trim();
	if (street) lines.push(street);
	const town = [party?.postcode?.trim(), party?.city?.trim()].filter(Boolean).join(" ");
	const cityLine = [town, party?.state?.trim()].filter(Boolean).join(", ");
	if (cityLine) lines.push(cityLine);
	return lines.length ? lines : ["—"];
}

function orgHeading(row?: OrgRow, party?: PartyDetails): string {
	if (!row) return "—";
	const reg = party?.registration?.trim() || party?.code?.trim();
	return reg ? `${row.name} (${reg})` : row.name;
}

function purchaseStatusOptions(listId: string): string[] {
	if (listId === "bills") return ["open", "paid", "overdue"];
	if (listId === "purchase-orders") return ["draft", "sent", "received"];
	if (listId === "credits") return ["open", "applied"];
	if (listId === "quotations") return ["draft", "sent", "accepted", "declined"];
	if (listId === "sales-orders") return ["draft", "confirmed", "fulfilled"];
	return ["draft", "sent", "paid", "overdue"];
}

function purchaseDocKind(listId: string, meta: PurchaseMeta): string {
	if (listId === "bills") return "Bill";
	if (listId === "invoices") return "Invoice";
	if (listId === "credits") return "Credit";
	if (listId === "quotations") return "Quotation";
	if (listId === "sales-orders") return "Sales order";
	if (listId === "purchase-orders") return "Purchase order";
	return meta.title;
}

function purchaseTax(listId: string, subtotal: number, mark?: string): { sst: number; grand: number } {
	if (listId === "invoices") {
		const sst = invoiceTaxAmount(subtotal, mark);
		return { sst, grand: subtotal + sst };
	}
	const taxable = listId === "bills";
	const sst = taxable ? Math.round(subtotal * 0.08 * 100) / 100 : 0;
	return { sst, grand: subtotal + sst };
}

function purchaseLineParts(description: string): { title: string; details: string[] } {
	const chunks = (description || "—").split(/\n+/).map((part) => part.trim()).filter(Boolean);
	return { title: chunks[0] ?? "—", details: chunks.slice(1) };
}

function purchasePdfFileName(kind: string, docNumber: string): string {
	const ref = docNumber.trim();
	if (/^#\d+$/.test(ref)) return `${kind.toUpperCase()}-${ref}.pdf`;
	const id = ref.startsWith("#") ? ref.slice(1) : ref;
	return `${kind} ${id}.pdf`;
}

type PdfRgb = [number, number, number];

type PurchasePdfInput = {
	kind: string;
	docNumber: string;
	from: DocPartyBlock;
	billed: DocPartyBlock;
	statusLabel: string;
	status: string;
	tone: string;
	dateLabel: string;
	docDate: string;
	dueLabel: string;
	dueDate: string;
	lines: Line[];
	subtotal: number;
	sst: number;
	grand: number;
	balanceDue: number;
	note: string;
};

type PdfText = { size: number; bold?: boolean; color: PdfRgb; align?: "left" | "right" | "center"; space?: number };

const PDF_PAGE = { width: 595.28, height: 841.89, margin: 28 };
const PDF_INK: PdfRgb = [17, 24, 39];
const PDF_MUTED: PdfRgb = [107, 114, 128];
const PDF_LINE: PdfRgb = [229, 231, 235];
const PDF_PANEL: PdfRgb = [243, 244, 246];
const PDF_PILL: Record<string, { fill: PdfRgb; text: PdfRgb }> = {
	ok: { fill: [134, 239, 172], text: [20, 83, 45] },
	info: { fill: [191, 219, 254], text: [30, 58, 138] },
	warn: { fill: [251, 191, 36], text: [120, 53, 15] },
	bad: { fill: [252, 165, 165], text: [127, 29, 29] },
};
const PDF_PILL_IDLE = { fill: [229, 231, 235] as PdfRgb, text: [55, 65, 81] as PdfRgb };

function drawPurchaseDocument(pdf: JsPdf, input: PurchasePdfInput) {
	const left = PDF_PAGE.margin;
	const right = PDF_PAGE.width - PDF_PAGE.margin;
	const bottomLimit = PDF_PAGE.height - PDF_PAGE.margin;

	function font(size: number, bold = false) {
		pdf.setFont("helvetica", bold ? "bold" : "normal");
		pdf.setFontSize(size);
	}
	function width(text: string, style: PdfText): number {
		font(style.size, style.bold);
		return pdf.getTextWidth(text) + (style.space ?? 0) * Math.max(0, text.length - 1);
	}
	function write(text: string, x: number, y: number, style: PdfText): number {
		const w = width(text, style);
		pdf.setTextColor(...style.color);
		const startX = style.align === "right" ? x - w : style.align === "center" ? x - w / 2 : x;
		pdf.text(text, startX, y, style.space ? { charSpace: style.space } : {});
		return w;
	}
	function wrap(text: string, maxWidth: number, size: number, bold = false): string[] {
		font(size, bold);
		return pdf.splitTextToSize(text, maxWidth) as string[];
	}

	const titleStyle: PdfText = { size: 28, bold: true, color: PDF_INK };
	const numberStyle: PdfText = { size: 14, bold: true, color: PDF_INK };
	const docIdWidth = Math.max(width(input.kind, titleStyle), width(input.docNumber, numberStyle));
	const fromWidth = right - left - docIdWidth - 24;
	write(input.kind, right - docIdWidth, 53.2, titleStyle);
	write(input.docNumber, right - docIdWidth, 75.4, numberStyle);

	let y = 39.7;
	const fromName = wrap(input.from.name, fromWidth, 13, true);
	fromName.forEach((line, index) => {
		const w = write(line, left, y, { size: 13, bold: true, color: PDF_INK });
		if (index === fromName.length - 1 && input.from.registration) {
			write(`(${input.from.registration})`, left + w + 4, y - 1.7, { size: 8, color: PDF_MUTED });
		}
		if (index < fromName.length - 1) y += 15.6;
	});
	const fromLines = [...input.from.address, input.from.contact].filter((line) => line && line !== "—");
	y += 11.8;
	for (const line of fromLines.flatMap((item) => wrap(item, fromWidth, 8))) {
		write(line, left, y, { size: 8, color: PDF_MUTED });
		y += 13.6;
	}
	const headerBottom = Math.max(y - 13.6, 75.4);

	const boxTop = headerBottom + 28.4;
	const inner = left + 13;
	const billedWidth = 340;
	const billedName = wrap(input.billed.name, billedWidth, 11, true);
	const billedLines = [...input.billed.address, input.billed.contact]
		.filter((line) => line && line !== "—")
		.flatMap((item) => wrap(item, billedWidth, 8));
	const billedNameEnd = boxTop + 35.7 + (billedName.length - 1) * 13.2;
	const billedEnd = billedLines.length ? billedNameEnd + 11.4 + (billedLines.length - 1) * 13.6 : billedNameEnd;
	const boxBottom = Math.max(billedEnd, boxTop + 55.8) + 17.4;
	pdf.setDrawColor(...PDF_LINE);
	pdf.setLineWidth(1);
	pdf.setFillColor(...PDF_PANEL);
	pdf.roundedRect(left + 0.5, boxTop + 0.5, right - left - 1, boxBottom - boxTop - 1, 8, 8, "FD");
	write("BILLED TO", inner, boxTop + 20.2, { size: 8, bold: true, color: PDF_MUTED, space: 0.6 });
	let by = boxTop + 35.7;
	billedName.forEach((line, index) => {
		const w = write(line, inner, by, { size: 11, bold: true, color: PDF_INK });
		if (index === billedName.length - 1 && input.billed.registration) {
			write(`(${input.billed.registration})`, inner + w + 4, by - 1.1, { size: 8, color: PDF_MUTED });
		}
		if (index < billedName.length - 1) by += 13.2;
	});
	by += 11.4;
	for (const line of billedLines) {
		write(line, inner, by, { size: 8, color: PDF_MUTED });
		by += 13.6;
	}
	const metaRight = right - 13;
	const metaLabelRight = right - 100.5;
	const pill = PDF_PILL[input.tone] ?? PDF_PILL_IDLE;
	const pillText: PdfText = { size: 8, bold: true, color: pill.text };
	const pillWidth = width(input.status, pillText) + 16;
	pdf.setFillColor(...pill.fill);
	pdf.roundedRect(metaRight - pillWidth, boxTop + 13, pillWidth, 14.8, 4, 4, "F");
	write(input.status, metaRight - pillWidth + 8, boxTop + 23.2, pillText);
	write(input.statusLabel, metaLabelRight, boxTop + 23.5, { size: 9, color: PDF_MUTED, align: "right" });
	write(input.dateLabel, metaLabelRight, boxTop + 40.9, { size: 9, color: PDF_MUTED, align: "right" });
	write(input.docDate, metaRight, boxTop + 40.9, { size: 9, bold: true, color: PDF_INK, align: "right" });
	write(input.dueLabel, metaLabelRight, boxTop + 55.8, { size: 9, color: PDF_MUTED, align: "right" });
	write(input.dueDate, metaRight, boxTop + 55.8, { size: 9, bold: true, color: PDF_INK, align: "right" });
	const itemHeading = boxBottom + 26.8;
	write("Item", left, itemHeading, { size: 12, bold: true, color: PDF_INK });

	const tableWidth = right - left;
	const col = { index: left + 8, item: left + 40, qty: left + 378, rate: left + 442, amount: right - 13 };
	const itemWidth = col.qty - col.item - 10;
	const headStyle: PdfText = { size: 7, bold: true, color: PDF_MUTED, space: 0.4 };

	function tableHead(top: number): number {
		pdf.setFillColor(...PDF_PANEL);
		pdf.roundedRect(left + 0.5, top + 0.5, tableWidth - 1, 23.2, 7.5, 7.5, "F");
		pdf.rect(left + 0.5, top + 12, tableWidth - 1, 11.7, "F");
		pdf.setDrawColor(...PDF_LINE);
		pdf.line(left, top + 23.7, right, top + 23.7);
		const base = top + 13.3;
		write("ITEM NO.", col.index, base, headStyle);
		write("ITEM", col.item, base, headStyle);
		write("QTY", col.qty, base, { ...headStyle, align: "center" });
		write("RATE", col.rate, base, { ...headStyle, align: "center" });
		write("AMOUNT", col.amount, base, { ...headStyle, align: "center" });
		return top + 23.7;
	}
	function tableBorder(top: number, bottom: number) {
		pdf.setDrawColor(...PDF_LINE);
		pdf.setLineWidth(1);
		pdf.roundedRect(left + 0.5, top + 0.5, tableWidth - 1, bottom - top - 1, 8, 8, "S");
	}

	let segmentTop = itemHeading + 11.4;
	let rowTop = tableHead(segmentTop);
	input.lines.forEach((line, index) => {
		const normalized = normalizePurchaseLine(line);
		const qty = lineQuantity(normalized);
		const rate = lineUnitPrice(normalized);
		const amount = lineRowTotal(normalized);
		const { title, details } = purchaseLineParts(normalized.description);
		const titleLines = wrap(title, itemWidth, 8, true);
		const detailLines = details.flatMap((part) => wrap(part, itemWidth, 7));
		const itemBottom = 14.2 + (titleLines.length - 1) * 11 + (detailLines.length ? 11.9 + (detailLines.length - 1) * 9.8 : 0);
		const rowHeight = itemBottom + 11.4;
		if (rowTop + rowHeight > bottomLimit && rowTop > segmentTop + 23.7) {
			tableBorder(segmentTop, rowTop);
			pdf.addPage();
			segmentTop = PDF_PAGE.margin;
			rowTop = tableHead(segmentTop);
		} else if (index > 0) {
			pdf.setDrawColor(...PDF_LINE);
			pdf.line(left, rowTop, right, rowTop);
		}
		const base = rowTop + 14.2;
		write(String(index + 1), col.index, base, { size: 8, color: PDF_MUTED });
		titleLines.forEach((text, lineIndex) => write(text, col.item, base + lineIndex * 11, { size: 8, bold: true, color: PDF_INK }));
		let detailY = base + (titleLines.length - 1) * 11 + 11.9;
		for (const text of detailLines) {
			write(text, col.item, detailY, { size: 7, color: PDF_MUTED });
			detailY += 9.8;
		}
		write(`${qty} pcs`, col.qty, base, { size: 8, color: PDF_INK, align: "center" });
		write(docMoney(rate), col.rate, base, { size: 8, color: PDF_INK, align: "center" });
		write(docMoney(amount), col.amount, base, { size: 8, bold: true, color: PDF_INK, align: "right" });
		rowTop += rowHeight;
	});
	tableBorder(segmentTop, rowTop);

	let totalsTop = rowTop + 19;
	if (totalsTop + 82 > bottomLimit) {
		pdf.addPage();
		totalsTop = PDF_PAGE.margin;
	}
	const totalsLeft = right - 220;
	let ty = totalsTop + 11.1;
	write("Subtotal", totalsLeft, ty, { size: 9, color: PDF_MUTED });
	write(docMoney(input.subtotal), right, ty, { size: 9, color: PDF_INK, align: "right" });
	if (input.sst > 0) {
		ty += 15.9;
		write("SST (8%)", totalsLeft, ty, { size: 9, color: PDF_MUTED });
		write(docMoney(input.sst), right, ty, { size: 9, color: PDF_INK, align: "right" });
	}
	const divider = ty + 8.8;
	pdf.setDrawColor(...PDF_LINE);
	pdf.setLineWidth(1);
	pdf.line(totalsLeft, divider, right, divider);
	write("Total", totalsLeft, divider + 16.9, { size: 11, bold: true, color: PDF_INK });
	write(docMoney(input.grand), right, divider + 16.9, { size: 11, bold: true, color: PDF_INK, align: "right" });
	write("Balance Due", totalsLeft, divider + 39, { size: 11, bold: true, color: PDF_INK });
	write(docMoney(input.balanceDue), right, divider + 39, { size: 11, bold: true, color: PDF_INK, align: "right" });
	if (input.note) {
		let ny = totalsTop + 11.1;
		for (const text of wrap(input.note, totalsLeft - left - 32, 9)) {
			write(text, left, ny, { size: 9, color: PDF_MUTED });
			ny += 12.6;
		}
	}
}

async function downloadPurchaseDocumentPdf(input: PurchasePdfInput, fileName: string) {
	const { jsPDF } = await import("jspdf");
	const pdf = new jsPDF({ unit: "pt", format: "a4", orientation: "portrait" });
	drawPurchaseDocument(pdf, input);
	pdf.save(fileName);
}

function DocDetailIcon({ name }: { name: "wallet" | "download" | "send" | "pencil" | "trash" | "save" | "x" | "chevron" }) {
	const common = { viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round" as const, strokeLinejoin: "round" as const, "aria-hidden": true };
	if (name === "wallet") {
		return (
			<svg {...common}>
				<path d="M19 7V4a1 1 0 0 0-1-1H5a2 2 0 0 0 0 4h15a1 1 0 0 1 1 1v4h-3a2 2 0 0 0 0 4h3a1 1 0 0 0 1-1v-2a1 1 0 0 0-1-1" />
				<path d="M3 5v14a2 2 0 0 0 2 2h15a1 1 0 0 0 1-1v-4" />
			</svg>
		);
	}
	if (name === "download") {
		return (
			<svg {...common}>
				<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
				<path d="m7 10 5 5 5-5" />
				<path d="M12 15V3" />
			</svg>
		);
	}
	if (name === "send") {
		return (
			<svg {...common}>
				<path d="m22 2-7 20-4-9-9-4Z" />
				<path d="M22 2 11 13" />
			</svg>
		);
	}
	if (name === "pencil") {
		return (
			<svg {...common}>
				<path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
				<path d="m15 5 4 4" />
			</svg>
		);
	}
	if (name === "save") {
		return (
			<svg {...common}>
				<path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
				<path d="M17 21v-8H7v8" />
				<path d="M7 3v5h8" />
			</svg>
		);
	}
	if (name === "x") {
		return (
			<svg {...common}>
				<path d="M18 6 6 18" />
				<path d="m6 6 12 12" />
			</svg>
		);
	}
	if (name === "chevron") {
		return (
			<svg {...common}>
				<path d="m6 9 6 6 6-6" />
			</svg>
		);
	}
	return (
		<svg {...common}>
			<path d="M3 6h18" />
			<path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
			<path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
			<path d="M10 11v6" />
			<path d="M14 11v6" />
		</svg>
	);
}

function DocDetailBackChevron() {
	return (
		<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
			<path d="m15 18-6-6 6-6" />
		</svg>
	);
}

function DocDetailShell({ stack, after }: { stack: ReactNode; after?: ReactNode }) {
	return (
		<div className="doc-detail">
			<div className="doc-detail-stack">{stack}</div>
			{after}
		</div>
	);
}

function DocDetailToolbarFrame({
	onBack,
	error,
	actions,
}: {
	onBack: () => void;
	error?: string;
	actions: ReactNode;
}) {
	return (
		<>
			<div className="doc-detail-toolbar">
				<button className="doc-detail-back" type="button" onClick={onBack}>
					<DocDetailBackChevron />
					Back
				</button>
				<div className="doc-detail-actions">{actions}</div>
			</div>
			{error ? <p className="err" role="alert">{error}</p> : null}
		</>
	);
}

function DocDetailIconButton({
	icon,
	children,
	className,
	...rest
}: {
	icon: "wallet" | "download" | "send" | "pencil" | "trash" | "save" | "x" | "chevron";
	children: ReactNode;
	className?: string;
} & Omit<ComponentProps<"button">, "children" | "className" | "type">) {
	return (
		<button className={className ? `doc-detail-btn ${className}` : "doc-detail-btn"} type="button" {...rest}>
			<DocDetailIcon name={icon} />
			{children}
		</button>
	);
}

function DocDetailDocumentCard({ ariaLabel, children }: { ariaLabel: string; children: ReactNode }) {
	return (
		<article className="doc-detail-card" aria-label={ariaLabel}>
			{children}
		</article>
	);
}

function DocDetailIssuerHeader({
	from,
	docKind,
	docNumber,
}: {
	from: DocPartyBlock;
	docKind: string;
	docNumber: string;
}) {
	return (
		<header className="doc-detail-top">
			<div className="doc-detail-from">
				<strong>{from.heading}</strong>
				{from.address.map((line) => <div key={line} className="doc-detail-muted">{line}</div>)}
				<div className="doc-detail-muted">{from.contact}</div>
			</div>
			<div className="doc-detail-docid">
				<p className="doc-detail-kind">{docKind}</p>
				<p className="doc-detail-number">{docNumber}</p>
			</div>
		</header>
	);
}

function DocDetailBilledBand({ billed, meta }: { billed: DocPartyBlock; meta: ReactNode }) {
	return (
		<section className="doc-detail-band">
			<div className="doc-detail-billed">
				<h2>Billed to</h2>
				<strong>{billed.heading}</strong>
				{billed.address.map((line) => <div key={line} className="doc-detail-muted">{line}</div>)}
				<div className="doc-detail-muted">{billed.contact}</div>
			</div>
			<dl className="doc-detail-meta">{meta}</dl>
		</section>
	);
}

function DocDetailBody({ children }: { children: ReactNode }) {
	return <div className="doc-detail-body">{children}</div>;
}

function DocDetailBottomBar({
	copyNote,
	onCopyLink,
	trailing,
}: {
	copyNote: string;
	onCopyLink: () => void | Promise<void>;
	trailing?: ReactNode;
}) {
	return (
		<div className="doc-detail-bottom">
			<button className="doc-detail-btn wide" type="button" onClick={() => void onCopyLink()}>Copy Link</button>
			{copyNote ? <span className="doc-detail-muted">{copyNote}</span> : null}
			{trailing}
		</div>
	);
}

type DocDetailItemLine = {
	title: string;
	details: string[];
	qty: number;
	rate: number;
	amount: number;
};

function DocDetailPurchaseActionBar({
	onPdf,
	onSend,
	onEdit,
	onDelete,
	editDisabled,
	deleteDisabled,
	paymentAction,
}: {
	onPdf: () => void;
	onSend: () => void;
	onEdit: () => void;
	onDelete: () => void;
	editDisabled?: boolean;
	deleteDisabled?: boolean;
	paymentAction?: ReactNode;
}) {
	return (
		<>
			<div className="doc-detail-action-row">
				{paymentAction}
				<DocDetailIconButton icon="download" onClick={onPdf}>PDF</DocDetailIconButton>
				<DocDetailIconButton icon="send" onClick={onSend}>Send</DocDetailIconButton>
			</div>
			<div className="doc-detail-action-row">
				<DocDetailIconButton icon="pencil" disabled={editDisabled} onClick={onEdit}>Edit</DocDetailIconButton>
				<DocDetailIconButton icon="trash" disabled={deleteDisabled} onClick={() => void onDelete()}>Delete</DocDetailIconButton>
			</div>
		</>
	);
}

function DocDetailItemsSection({
	lines,
	sectionTitle = "Item",
}: {
	lines: DocDetailItemLine[];
	sectionTitle?: string;
}) {
	return (
		<>
			<h3 className="doc-detail-section">{sectionTitle}</h3>
			<div className="doc-detail-table-box">
				<table className="doc-detail-table">
					<thead>
						<DocDetailItemTableHead />
					</thead>
					<tbody>
						{lines.map((line, index) => (
							<tr key={index}>
								<td className="col-item-no">{index + 1}</td>
								<td className="doc-detail-item col-item-desc">
									<strong>{line.title}</strong>
									{line.details.length ? (
										<ul>
											{line.details.map((part) => <li key={part}>{part}</li>)}
										</ul>
									) : null}
								</td>
								<td className="num">{line.qty} pcs</td>
								<td className="num">{docMoney(line.rate)}</td>
								<td className="num">{docMoney(line.amount)}</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
		</>
	);
}

function DocDetailAmountSummary({
	subtotal,
	sst,
	grand,
	balanceDue,
}: {
	subtotal: number;
	sst: number;
	grand: number;
	balanceDue: number;
}) {
	return (
		<div className="doc-detail-totals-row">
			<div className="doc-detail-totals-spacer" aria-hidden="true" />
			<div className="doc-detail-totals">
				<div><span>Subtotal</span><strong>{docMoney(subtotal)}</strong></div>
				{sst > 0 ? <div><span>SST (8%)</span><strong>{docMoney(sst)}</strong></div> : null}
				<div className="grand"><span>Total</span><strong>{docMoney(grand)}</strong></div>
				<div className="balance"><span>Balance Due</span><strong>{docMoney(balanceDue)}</strong></div>
			</div>
		</div>
	);
}

function purchaseDetailItemLines(listId: string, lines: PurchaseLine[]): DocDetailItemLine[] {
	const source = lines.length ? lines : [{ description: "—", amount: "0" }];
	return source.map((line) => {
		const structuredLine = listId === "sales-orders" || listId === "invoices"
			? Boolean(line.qty?.trim() || line.unitPrice?.trim())
			: false;
		const qty = structuredLine ? lineQuantity(line) : 1;
		const rate = structuredLine ? lineUnitPrice(line) : Number(line.amount) || 0;
		const lineAmount = structuredLine ? lineRowTotal(line) : Number(line.amount) || 0;
		const chunks = (line.description || "—").split(/\n+/).map((part) => part.trim()).filter(Boolean);
		return {
			title: chunks[0] ?? "—",
			details: chunks.slice(1),
			qty,
			rate,
			amount: lineAmount,
		};
	});
}

function PurchaseEdit({
	listId,
	meta,
	row,
	books,
	tab,
}: {
	listId: string;
	meta: PurchaseMeta;
	row: PurchaseDoc;
	books: Books;
	tab: string;
}) {
	const navigate = useNavigate();
	const detailPath = purchaseDocDetailPath(listId, row, tab);
	const customer = organisationByName(books, row.party, "customer") ?? organisationByName(books, row.party);
	const billed = docPartyBlock(customer, row.party);
	const partyDetails = customer ? partyOf(customer.party) : undefined;
	const email = customer?.email?.trim() || (billed.contact.includes("@") ? billed.contact.split(" · ")[0]?.trim() : "") || "—";
	const phone = customer?.phone?.trim() || partyDetails?.contact?.trim() || (billed.contact.includes("·") ? billed.contact.split(" · ").slice(1).join(" · ").trim() : "") || "—";
	const addressText = billed.address.length ? billed.address.join(", ") : "—";
	const lines = row.lines?.length ? row.lines : [{ description: "—", amount: "0" }];
	const isSalesOrder = listId === "sales-orders";
	const docKind = purchaseDocKind(listId, meta);
	const dateLabel = listId === "bills" ? "Billing Date" : `${docKind} Date`;
	const dueLabel = listId === "credits" ? "Against" : "Due Date";
	const [partyName, setPartyName] = useState(row.party ?? "");
	const [amount, setAmount] = useState(String(lines[0]?.amount ?? ""));
	const [lineDrafts, setLineDrafts] = useState(() => (isSalesOrder ? lineDraftsFromDoc(row.lines) : [emptyLineDraft()]));
	const [docAt, setDocAt] = useState(row.doc_at?.slice(0, 10) ?? "");
	const [dueAt, setDueAt] = useState(listId === "credits" ? row.extra ?? "" : row.extra?.slice(0, 10) ?? "");
	const [terms, setTerms] = useState(() => listId === "credits" ? "" : purchaseTermsValue(row.doc_at ?? "", row.extra ?? ""));
	const amountNumber = isSalesOrder ? draftSubtotal(lineDrafts) : Number(amount) || 0;
	const invoiceMark = listId === "invoices" ? row.mark : undefined;
	const { sst, grand } = purchaseTax(listId, amountNumber, invoiceMark);
	const editForm = useHubSave(async (fields) => {
		if (row.id == null) throw new ApiError(400, { code: "validation_failed", message: "This record cannot be edited yet.", requestId: "" });
		const savedLines = isSalesOrder ? linesFromDrafts(lineDrafts) : purchaseLinesFromSingleLineForm(fields);
		if (isSalesOrder) assertSalesOrderValid(fields, savedLines);
		await api.updateHubBook({
			list: `purchases.${listId}`,
			id: row.id,
			record: {
				party: textOf(fields, "party"),
				doc_at: textOf(fields, "doc_at"),
				extra: textOf(fields, "extra"),
				ref_no: textOf(fields, "ref_no"),
				status: textOf(fields, "status"),
				mark: textOf(fields, "mark"),
				note: textOf(fields, "note"),
				lines: purchaseLinesToRecords(savedLines),
			},
		});
		navigate(detailPath);
	});
	function onTermsChange(value: string) {
		setTerms(value);
		if (listId !== "credits") setDueAt(purchaseDueFromTerms(docAt, value));
	}
	return (
		<div className="doc-edit">
			<form
				className="doc-edit-form"
				onSubmit={(event: FormEvent<HTMLFormElement>) => {
					event.preventDefault();
					void editForm.save(new FormData(event.currentTarget));
				}}
			>
				<div className="doc-edit-toolbar">
					<button className="doc-detail-back" type="button" onClick={() => navigate(detailPath)}>
						<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="m15 18-6-6 6-6" /></svg>
						Back
					</button>
					<div className="doc-edit-actions">
						<div className="doc-edit-save-group">
							<button className="doc-detail-btn doc-edit-save-main" type="submit" disabled={editForm.pending || row.id == null}>
								<DocDetailIcon name="save" />
								Save
							</button>
							<details className="doc-edit-save-menu">
								<summary aria-label="More save options">
									<DocDetailIcon name="chevron" />
								</summary>
								<div className="select-pop">
									<button type="submit" className="select-item">Save</button>
								</div>
							</details>
						</div>
						<button className="doc-detail-btn" type="button" onClick={() => navigate(detailPath)}>
							<DocDetailIcon name="x" />
							Cancel
						</button>
					</div>
				</div>
				{editForm.error ? <p className="err doc-edit-error">{editForm.error}</p> : null}
				<div className="doc-edit-stack">
					<section className="doc-edit-card" aria-labelledby="doc-edit-customer">
						<h2 id="doc-edit-customer" className="doc-edit-kicker">Customer</h2>
						<div className="doc-edit-inset">
							<label className="sr" htmlFor="edit-party">{meta.party}</label>
							<input id="edit-party" className="doc-edit-party" name="party" value={partyName} onChange={(event) => setPartyName(event.target.value)} required />
							<dl className="doc-edit-customer-meta">
								<div><dt>Address</dt><dd>{addressText}</dd></div>
								<div><dt>General Email</dt><dd>{email}</dd></div>
								<div><dt>Phone</dt><dd>{phone}</dd></div>
							</dl>
						</div>
					</section>
					<section className="doc-edit-card" aria-label="Billing dates">
						<div className="doc-edit-dates">
							<div className="doc-edit-field-block">
								<label htmlFor="edit-date">{dateLabel}</label>
								<input
									id="edit-date"
									className="doc-edit-control"
									name="doc_at"
									type="date"
									value={docAt}
									required
									onChange={(event) => {
										const value = event.target.value;
										setDocAt(value);
										if (listId !== "credits" && terms) setDueAt(purchaseDueFromTerms(value, terms));
									}}
								/>
							</div>
							{listId === "credits" ? (
								<div className="doc-edit-field-block">
									<label htmlFor="edit-extra">{dueLabel}</label>
									<input id="edit-extra" className="doc-edit-control" name="extra" type="text" value={dueAt} onChange={(event) => setDueAt(event.target.value)} />
								</div>
							) : (
								<>
									<div className="doc-edit-field-block">
										<HubFieldSelect
											menuInModal
											id="edit-terms"
											name="terms-ui"
											label="Terms"
											defaultValue={terms}
											items={PURCHASE_TERMS}
											onPick={onTermsChange}
										/>
									</div>
									<div className="doc-edit-field-block">
										<label htmlFor="edit-extra">{dueLabel}</label>
										<input id="edit-extra" className="doc-edit-control" name="extra" type="date" value={dueAt} onChange={(event) => setDueAt(event.target.value)} />
									</div>
								</>
							)}
						</div>
					</section>
					{isSalesOrder ? (
						<SalesOrderLinesEditor
							drafts={lineDrafts}
							onChange={setLineDrafts}
							showTax={sst > 0}
							taxAmount={sst}
							grandTotal={grand}
							formatMoney={docMoney}
							idPrefix="edit-so"
						/>
					) : (
						<section className="doc-edit-card" aria-labelledby="doc-edit-items">
							<div className="doc-edit-section-head">
								<h2 id="doc-edit-items" className="doc-edit-kicker">Items</h2>
								<button className="doc-detail-btn" type="button" disabled title="Additional lines are not available yet.">
									Add line
								</button>
							</div>
							<div className="doc-edit-table-box">
								<table className="doc-edit-table">
									<thead>
										<DocEditItemTableHead showTax rateLabel="Rate" />
									</thead>
									<tbody>
										<tr>
											<td className="center">1</td>
											<td>
												<label className="sr" htmlFor="edit-line">Item</label>
												<div className="doc-edit-chip doc-edit-chip-grow">
													<textarea id="edit-line" name="line" rows={3} defaultValue={lines[0]?.description ?? ""} required />
												</div>
											</td>
											<td className="center">
												<input className="doc-edit-qty-input" type="number" min={1} step={1} inputMode="numeric" defaultValue={1} aria-label="Quantity" />
											</td>
											<td className="num">
												<label className="sr" htmlFor="edit-amount">Unit price</label>
												<input id="edit-amount" name="amount" className="doc-edit-money" inputMode="decimal" value={amount} onChange={(event) => setAmount(event.target.value)} required />
											</td>
											<td className="num">
												<p className="doc-edit-tax">{sst > 0 ? `${docMoney(sst)} (8% SST)` : "—"}</p>
											</td>
											<td className="num">
												<p className="doc-edit-line-total">{docMoney(grand)}</p>
												<p className="doc-edit-line-sub">excl. {docMoney(amountNumber)}</p>
											</td>
										</tr>
									</tbody>
								</table>
							</div>
						</section>
					)}
					{isSalesOrder ? null : (
						<section className="doc-edit-card doc-edit-summary" aria-label={`${docKind} summary`}>
							<h2 className="doc-edit-kicker">{docKind} summary</h2>
							<dl className="doc-edit-summary-lines">
								<div><dt>Subtotal</dt><dd>{docMoney(amountNumber)}</dd></div>
								{sst > 0 ? <div><dt>Tax</dt><dd>{docMoney(sst)}</dd></div> : null}
								<div className="grand"><dt>Total</dt><dd>{docMoney(grand)}</dd></div>
							</dl>
						</section>
					)}
					<div className="doc-edit-notes-grid">
						<section className="doc-edit-card" aria-labelledby="doc-edit-notes-label">
							<h2 id="doc-edit-notes-label" className="doc-edit-kicker">Notes</h2>
							<label className="sr" htmlFor="edit-note">Notes shown on invoice</label>
							<textarea id="edit-note" className="doc-edit-note" name="note" rows={3} placeholder="Notes shown on invoice (optional)" defaultValue={row.note ?? ""} />
						</section>
						<section className="doc-edit-card" aria-labelledby="doc-edit-internal-label">
							<h2 id="doc-edit-internal-label" className="doc-edit-kicker">Internal Notes</h2>
							<label className="sr" htmlFor="edit-internal">Internal notes</label>
							<textarea id="edit-internal" className="doc-edit-note" rows={3} placeholder="Internal notes (optional)" aria-describedby="edit-internal-hint" />
							<p id="edit-internal-hint" className="doc-edit-hint">Internal notes are not stored yet.</p>
						</section>
					</div>
					<section className="doc-edit-card doc-edit-advanced" aria-label="Document details">
						<div><label htmlFor="edit-ref">{meta.ref}</label><input id="edit-ref" className="doc-edit-control" name="ref_no" defaultValue={row.ref_no} required /></div>
						<HubFieldSelect
							menuInModal
							id="edit-status"
							name="status"
							label="Status"
							defaultValue={row.status}
							items={purchaseStatusOptions(listId).map((value) => [value, label(value)] as [string, string])}
						/>
						{listId === "invoices" ? (
							<HubFieldSelect menuInModal id="edit-mark" name="mark" label="Type" defaultValue={row.mark ?? "commercial"} items={INVOICE_MARKS} />
						) : <input type="hidden" name="mark" value="" />}
					</section>
				</div>
			</form>
		</div>
	);
}

function purchaseDefaultMark(listId: string, tab: string): string {
	if (listId !== "invoices") return "";
	return tab === "proforma" || tab === "commercial" ? tab : "commercial";
}

function PurchaseCreateModal({
	listId,
	meta,
	tab,
	rows,
	books,
	open,
	error,
	pending,
	onClose,
	onSubmit,
}: {
	listId: string;
	meta: PurchaseMeta;
	tab: string;
	rows: PurchaseDoc[];
	books?: Books;
	open: boolean;
	error: string;
	pending: boolean;
	onClose: () => void;
	onSubmit: (form: FormData) => void;
}) {
	const docKind = purchaseDocKind(listId, meta);
	const dateLabel = listId === "bills" ? "Billing Date" : `${docKind} Date`;
	const dueLabel = listId === "credits" ? "Against" : "Due Date";
	const partyKind = purchasePartyKind(listId);
	const today = todayIso();
	const suggestedRef = suggestPurchaseRef(listId, rows);
	const [partyName, setPartyName] = useState("");
	const [partyEmail, setPartyEmail] = useState("");
	const [partyPhone, setPartyPhone] = useState("");
	const [partyAddress, setPartyAddress] = useState("");
	const [partyCity, setPartyCity] = useState("");
	const [partyState, setPartyState] = useState("");
	const [partyPostcode, setPartyPostcode] = useState("");
	const [amount, setAmount] = useState("");
	const [docAt, setDocAt] = useState(today);
	const [terms, setTerms] = useState("net-30");
	const [dueAt, setDueAt] = useState(() => purchaseDueFromTerms(today, "net-30"));
	const isSalesOrder = listId === "sales-orders";
	const [lineDrafts, setLineDrafts] = useState(() => [emptyLineDraft()]);
	useEffect(() => {
		if (open && isSalesOrder) setLineDrafts([emptyLineDraft()]);
	}, [open, isSalesOrder]);
	const amountNumber = isSalesOrder ? draftSubtotal(lineDrafts) : Number(amount) || 0;
	const defaultMark = purchaseDefaultMark(listId, tab);
	const { sst, grand } = purchaseTax(listId, amountNumber, listId === "invoices" ? defaultMark : undefined);
	function onTermsChange(value: string) {
		setTerms(value);
		if (listId !== "credits") setDueAt(purchaseDueFromTerms(docAt, value));
	}
	function handleSubmit(fields: FormData) {
		if (isSalesOrder) fields.set("so_lines_json", JSON.stringify(lineDrafts));
		onSubmit(fields);
	}
	return (
		<Modal
			open={open}
			title={`New ${meta.one}`}
			className="doc purchase-create"
			action="Save"
			error={error}
			pending={pending}
			onClose={onClose}
			onSubmit={handleSubmit}
		>
			<div className="doc-edit-stack doc-create-stack">
				<section className="doc-edit-card" aria-labelledby="doc-create-customer">
					<h2 id="doc-create-customer" className="doc-edit-kicker">{meta.party}</h2>
					<p className="doc-create-hint">Who is this {docKind.toLowerCase()} for? Contact details appear on the document and PDF.</p>
					<div className="doc-edit-inset doc-create-customer">
						<div className="doc-create-field">
							<label htmlFor="create-party">{meta.party} name</label>
							<input id="create-party" className="doc-edit-party" name="party" value={partyName} onChange={(event) => setPartyName(event.target.value)} placeholder={`e.g. ${partyKind === "customer" ? "Menara IQ" : "Maxxon Hardware"}`} required />
						</div>
						<div className="doc-create-contact-grid">
							<div className="doc-create-field doc-create-field-wide">
								<label htmlFor="create-party-address">Address</label>
								<textarea id="create-party-address" className="doc-edit-note" name="party_address" rows={2} placeholder="Street address" value={partyAddress} onChange={(event) => setPartyAddress(event.target.value)} />
							</div>
							<div className="doc-create-field">
								<label htmlFor="create-party-postcode">Postcode</label>
								<input id="create-party-postcode" className="doc-edit-control" name="party_postcode" inputMode="numeric" autoComplete="postal-code" value={partyPostcode} onChange={(event) => setPartyPostcode(event.target.value)} />
							</div>
							<div className="doc-create-field">
								<label htmlFor="create-party-city">City</label>
								<input id="create-party-city" className="doc-edit-control" name="party_city" autoComplete="address-level2" value={partyCity} onChange={(event) => setPartyCity(event.target.value)} />
							</div>
							<div className="doc-create-field">
								<label htmlFor="create-party-state">State</label>
								<input id="create-party-state" className="doc-edit-control" name="party_state" autoComplete="address-level1" value={partyState} onChange={(event) => setPartyState(event.target.value)} />
							</div>
							<div className="doc-create-field">
								<label htmlFor="create-party-email">General email</label>
								<input id="create-party-email" className="doc-edit-control" name="party_email" type="email" autoComplete="email" placeholder="billing@example.com" value={partyEmail} onChange={(event) => setPartyEmail(event.target.value)} />
							</div>
							<div className="doc-create-field">
								<label htmlFor="create-party-phone">Phone</label>
								<input id="create-party-phone" className="doc-edit-control" name="party_phone" type="tel" autoComplete="tel" placeholder="+60 …" value={partyPhone} onChange={(event) => setPartyPhone(event.target.value)} />
							</div>
						</div>
					</div>
				</section>
				<section className="doc-edit-card" aria-label="Billing dates">
					<div className="doc-edit-dates">
						<div className="doc-edit-field-block">
							<label htmlFor="create-date">{dateLabel}</label>
							<input
								id="create-date"
								className="doc-edit-control"
								name="doc_at"
								type="date"
								value={docAt}
								required
								onChange={(event) => {
									const value = event.target.value;
									setDocAt(value);
									if (listId !== "credits" && terms) setDueAt(purchaseDueFromTerms(value, terms));
								}}
							/>
						</div>
						{listId === "credits" ? (
							<div className="doc-edit-field-block">
								<label htmlFor="create-extra">{dueLabel}</label>
								<input id="create-extra" className="doc-edit-control" name="extra" type="text" value={dueAt} onChange={(event) => setDueAt(event.target.value)} />
							</div>
						) : (
							<>
								<div className="doc-edit-field-block">
									<HubFieldSelect menuInModal id="create-terms" name="terms-ui" label="Terms" defaultValue={terms} items={PURCHASE_TERMS} onPick={onTermsChange} />
								</div>
								<div className="doc-edit-field-block">
									<label htmlFor="create-extra">{dueLabel}</label>
									<input id="create-extra" className="doc-edit-control" name="extra" type="date" value={dueAt} onChange={(event) => setDueAt(event.target.value)} />
								</div>
							</>
						)}
					</div>
				</section>
				{isSalesOrder ? (
					<SalesOrderLinesEditor
						drafts={lineDrafts}
						onChange={setLineDrafts}
						showTax={sst > 0}
						taxAmount={sst}
						grandTotal={grand}
						formatMoney={docMoney}
						idPrefix="create-so"
					/>
				) : (
					<section className="doc-edit-card" aria-labelledby="doc-create-items">
						<div className="doc-edit-section-head">
							<h2 id="doc-create-items" className="doc-edit-kicker">Items</h2>
							<button className="doc-detail-btn" type="button" disabled title="Additional lines are not available yet.">
								Add line
							</button>
						</div>
						<div className="doc-edit-table-box">
							<table className="doc-edit-table">
								<thead>
									<DocEditItemTableHead showTax rateLabel="Rate" />
								</thead>
								<tbody>
									<tr>
										<td className="center">1</td>
										<td>
											<label className="sr" htmlFor="create-line">Item</label>
											<div className="doc-edit-chip doc-edit-chip-grow">
												<textarea id="create-line" name="line" rows={3} placeholder="Description" required />
											</div>
										</td>
										<td className="center">
											<input className="doc-edit-qty-input" type="number" min={1} step={1} inputMode="numeric" defaultValue={1} aria-label="Quantity" />
										</td>
										<td className="num">
											<label className="sr" htmlFor="create-amount">Unit price</label>
											<input id="create-amount" name="amount" className="doc-edit-money" inputMode="decimal" value={amount} onChange={(event) => setAmount(event.target.value)} required />
										</td>
										<td className="num">
											<p className="doc-edit-tax">{sst > 0 ? `${docMoney(sst)} (8% SST)` : "—"}</p>
										</td>
										<td className="num">
											<p className="doc-edit-line-total">{docMoney(grand)}</p>
											<p className="doc-edit-line-sub">excl. {docMoney(amountNumber)}</p>
										</td>
									</tr>
								</tbody>
							</table>
						</div>
					</section>
				)}
				{isSalesOrder ? null : (
					<section className="doc-edit-card doc-edit-summary" aria-label={`${docKind} summary`}>
						<h2 className="doc-edit-kicker">{docKind} summary</h2>
						<dl className="doc-edit-summary-lines">
							<div><dt>Subtotal</dt><dd>{docMoney(amountNumber)}</dd></div>
							{sst > 0 ? <div><dt>Tax</dt><dd>{docMoney(sst)}</dd></div> : null}
							<div className="grand"><dt>Total</dt><dd>{docMoney(grand)}</dd></div>
						</dl>
					</section>
				)}
				<section className="doc-edit-card" aria-labelledby="doc-create-notes">
					<h2 id="doc-create-notes" className="doc-edit-kicker">Notes</h2>
					<label className="sr" htmlFor="create-note">Notes shown on document</label>
					<textarea id="create-note" className="doc-edit-note" name="note" rows={3} placeholder="Notes shown on document (optional)" />
				</section>
				{listId === "sales-orders" && books ? (
					<section className="doc-edit-card" aria-labelledby="doc-create-work">
						<h2 id="doc-create-work" className="doc-edit-kicker">Work</h2>
						<p className="doc-create-hint">Optional. Link existing work after this sales order is saved.</p>
						{workUnlinkedForNewLink(books.work).length === 0 ? (
							<p className="doc-detail-muted">No unlinked work yet. You can create work from the sales order detail page.</p>
						) : (
							<HubFieldSelect
								menuInModal
								id="create-link-work"
								name="link_work"
								label="Link existing work"
								defaultValue=""
								items={[
									["", "Choose work…"],
									...workUnlinkedForNewLink(books.work).map(
										(row) => [String(row.id ?? ""), row.title] as [string, string],
									),
								]}
							/>
						)}
					</section>
				) : null}
				<section className="doc-edit-card doc-edit-advanced" aria-label="Document details">
					<div><label htmlFor="create-ref">{meta.ref}</label><input id="create-ref" className="doc-edit-control" name="ref_no" defaultValue={suggestedRef} required /></div>
					<HubFieldSelect
						menuInModal
						id="create-status"
						name="status"
						label="Status"
						defaultValue="draft"
						items={purchaseStatusOptions(listId).map((value) => [value, label(value)] as [string, string])}
					/>
					{listId === "invoices" ? (
						<HubFieldSelect menuInModal id="create-mark" name="mark" label="Type" defaultValue={defaultMark} items={INVOICE_MARKS} />
					) : (
						<input type="hidden" name="mark" value="" />
					)}
				</section>
			</div>
		</Modal>
	);
}

function InvoiceReceiptsSection({
	invoice,
	books,
	balanceDue,
	onCreateReceipt,
}: {
	invoice: PurchaseDoc;
	books: Books;
	balanceDue: number;
	onCreateReceipt: () => void;
}) {
	const linked = receiptsForInvoice(books.receipts, invoice);
	const paid = paidTotalForInvoice(books.receipts, invoice);
	const grand = invoiceGrandTotal(invoice);
	return (
		<section className="doc-detail-work" aria-label="Receipts">
			<div className="doc-detail-work-head">
				<h2>Receipts</h2>
				<div className="doc-detail-work-actions">
					<button className="doc-detail-btn" type="button" disabled={balanceDue <= 0} onClick={onCreateReceipt}>Create receipt</button>
				</div>
			</div>
			<p className="doc-detail-muted">
				{docMoney(paid)} received of {docMoney(grand)} · {docMoney(balanceDue)} outstanding
			</p>
			{linked.length === 0 ? (
				<p className="doc-detail-muted">No receipts yet.</p>
			) : (
				<ul className="doc-detail-work-list">
					{linked.map((item) => (
						<li key={item.id ?? item.ref_no}>
							<div>
								<Link className="row-link" to={receiptDetailPath(item)}>{item.ref_no}</Link>
								<span className="doc-detail-muted">{` · ${docLongDate(item.doc_at)} · ${label(item.method)} · ${docMoney(Number(item.amount) || 0)}`}</span>
							</div>
						</li>
					))}
				</ul>
			)}
		</section>
	);
}

function QuotationSalesOrderSection({ quote, books }: { quote: PurchaseDoc; books: Books }) {
	const client = useQueryClient();
	const navigate = useNavigate();
	const [busy, setBusy] = useState(false);
	const [actionError, setActionError] = useState("");
	const salesOrders = books.purchases["sales-orders"] ?? [];
	const linked = linkedSalesOrderForQuotation(quote, salesOrders);
	const blockReason = linked ? null : quotationConversionBlockedReason(quote, salesOrders);
	async function refresh() {
		await client.invalidateQueries({ queryKey: queryKeys.hubRecords });
	}
	async function createSalesOrder() {
		if (quote.id == null || blockReason) return;
		setActionError("");
		setBusy(true);
		try {
			const refNo = suggestPurchaseRef("sales-orders", salesOrders);
			const created = await api.appendHubBook({
				list: "purchases.sales-orders",
				record: buildSalesOrderRecordFromQuotation(quote, refNo, todayIso()),
			});
			const salesOrderId = Number(created?.id);
			if (!Number.isFinite(salesOrderId)) {
				throw new ApiError(500, { code: "internal", message: "The sales order could not be created.", requestId: "" });
			}
			await api.updateHubBook({
				list: "purchases.quotations",
				id: quote.id,
				record: quotationRecordWithSalesOrderLink(quote, salesOrderId),
			});
			await refresh();
			navigate(salesOrderDetailPath({ ref_no: refNo, id: salesOrderId, party: quote.party, doc_at: quote.doc_at, status: "draft", note: quote.note ?? "" }));
		} catch (err) {
			setActionError(presentError(err).title);
		} finally {
			setBusy(false);
		}
	}
	return (
		<section className="doc-detail-work" aria-label="Sales order">
			<div className="doc-detail-work-head">
				<h2>Sales order</h2>
				<div className="doc-detail-work-actions">
					{linked ? null : (
						<button
							className="doc-detail-btn"
							type="button"
							disabled={busy || Boolean(blockReason)}
							onClick={() => void createSalesOrder()}
						>
							Create sales order
						</button>
					)}
				</div>
			</div>
			{linked ? (
				<p>
					<Link className="row-link" to={salesOrderDetailPath(linked)}>Open {linked.ref_no || "sales order"}</Link>
				</p>
			) : blockReason ? (
				<p className="doc-detail-muted">{blockReason}</p>
			) : (
				<p className="doc-detail-muted">Create a sales order from this quotation. Line items and notes copy across.</p>
			)}
			{actionError ? <p className="err">{actionError}</p> : null}
		</section>
	);
}

function SalesOrderQuotationSection({ order, books }: { order: PurchaseDoc; books: Books }) {
	const quotations = books.purchases.quotations ?? [];
	const source = linkedQuotationForSalesOrder(order, quotations);
	if (!source) return null;
	return (
		<section className="doc-detail-work" aria-label="Source quotation">
			<h2>Source quotation</h2>
			<p>
				<Link className="row-link" to={quotationDetailPath(source)}>Open {source.ref_no || "quotation"}</Link>
			</p>
		</section>
	);
}

function SalesOrderInvoiceSection({ order, books }: { order: PurchaseDoc; books: Books }) {
	const client = useQueryClient();
	const navigate = useNavigate();
	const [busy, setBusy] = useState(false);
	const [actionError, setActionError] = useState("");
	const invoices = books.purchases.invoices ?? [];
	const linked = linkedInvoiceForSalesOrder(order, invoices);
	const blockReason = linked ? null : salesOrderInvoiceBlockedReason(order, invoices);
	async function refresh() {
		await client.invalidateQueries({ queryKey: queryKeys.hubRecords });
	}
	async function createInvoice() {
		if (order.id == null || blockReason) return;
		setActionError("");
		setBusy(true);
		try {
			const refNo = suggestPurchaseRef("invoices", invoices);
			const created = await api.appendHubBook({
				list: "purchases.invoices",
				record: buildInvoiceRecordFromSalesOrder(order, refNo, todayIso()),
			});
			const invoiceId = Number(created?.id);
			if (!Number.isFinite(invoiceId)) {
				throw new ApiError(500, { code: "internal", message: "The invoice could not be created.", requestId: "" });
			}
			await api.updateHubBook({
				list: "purchases.sales-orders",
				id: order.id,
				record: salesOrderRecordWithInvoiceLink(order, invoiceId),
			});
			await refresh();
			navigate(invoiceDetailPath({ ref_no: refNo, id: invoiceId, party: order.party, doc_at: todayIso(), status: "draft", note: order.note ?? "" }));
		} catch (err) {
			setActionError(presentError(err).title);
		} finally {
			setBusy(false);
		}
	}
	return (
		<section className="doc-detail-work" aria-label="Invoice">
			<div className="doc-detail-work-head">
				<h2>Invoice</h2>
				<div className="doc-detail-work-actions">
					{linked ? null : (
						<button
							className="doc-detail-btn"
							type="button"
							disabled={busy || Boolean(blockReason)}
							onClick={() => void createInvoice()}
						>
							Create invoice
						</button>
					)}
				</div>
			</div>
			{linked ? (
				<p>
					<Link className="row-link" to={invoiceDetailPath(linked)}>Open {linked.ref_no || "invoice"}</Link>
				</p>
			) : blockReason ? (
				<p className="doc-detail-muted">{blockReason}</p>
			) : (
				<p className="doc-detail-muted">Create an invoice from this sales order. Customer and line items copy across.</p>
			)}
			{actionError ? <p className="err">{actionError}</p> : null}
		</section>
	);
}

function InvoiceSalesOrderSection({ invoice, books }: { invoice: PurchaseDoc; books: Books }) {
	const salesOrders = books.purchases["sales-orders"] ?? [];
	const source = linkedSalesOrderForInvoice(invoice, salesOrders);
	if (!source) return null;
	return (
		<section className="doc-detail-work" aria-label="Source sales order">
			<h2>Source sales order</h2>
			<p>
				<Link className="row-link" to={salesOrderDetailPath(source)}>Open {source.ref_no || "sales order"}</Link>
			</p>
		</section>
	);
}

function SalesOrderWorkSection({ order, books }: { order: PurchaseDoc; books: Books }) {
	const client = useQueryClient();
	const [linkWorkId, setLinkWorkId] = useState("");
	const [busy, setBusy] = useState(false);
	const [actionError, setActionError] = useState("");
	const salesOrderId = order.id;
	const linked = salesOrderId != null ? worksForSalesOrder(books.work, salesOrderId) : [];
	const linkChoices = salesOrderId != null ? workUnlinkedForNewLink(books.work) : [];
	const templateLinked = workTemplateAlreadyLinked(order, linked);
	async function refresh() {
		await client.invalidateQueries({ queryKey: queryKeys.hubRecords });
	}
	async function run(action: () => Promise<void>) {
		setActionError("");
		setBusy(true);
		try {
			await action();
			await refresh();
		} catch (err) {
			setActionError(presentError(err).title);
		} finally {
			setBusy(false);
		}
	}
	async function createWork() {
		if (salesOrderId == null || templateLinked) return;
		await api.appendHubBook({
			list: "work",
			record: {
				title: defaultWorkTitleFromSalesOrder(order),
				projectId: "",
				status: DEFAULT_WORK_STATUS,
				due: defaultWorkDueFromSalesOrder(order),
				start: "",
				salesOrderId,
			},
		});
	}
	async function linkExisting() {
		if (salesOrderId == null || !linkWorkId) return;
		const workRow = books.work.find((row) => String(row.id) === linkWorkId);
		if (!workRow?.id) return;
		if (salesOrderIdOf(workRow.salesOrderId) != null && !workMatchesSalesOrder(workRow, salesOrderId)) return;
		await api.updateHubBook({
			list: "work",
			id: workRow.id,
			record: workRecordWithSalesOrder(workRow, salesOrderId),
		});
		setLinkWorkId("");
	}
	async function unlink(workRow: WorkRow) {
		if (workRow.id == null) return;
		await api.updateHubBook({
			list: "work",
			id: workRow.id,
			record: workRecordWithSalesOrder(workRow, null),
		});
	}
	if (salesOrderId == null) {
		return (
			<section className="doc-detail-work" aria-label="Work">
				<h2>Work</h2>
				<p className="doc-detail-muted">Save this sales order before linking work.</p>
			</section>
		);
	}
	return (
		<section className="doc-detail-work" aria-label="Work">
			<div className="doc-detail-work-head">
				<h2>Work</h2>
				<div className="doc-detail-work-actions">
					<button className="doc-detail-btn" type="button" disabled={busy || templateLinked} onClick={() => void run(createWork)}>Create work</button>
				</div>
			</div>
			{templateLinked ? (
				<p className="doc-detail-muted">Work for the default line title is already linked. Create a differently titled job from the Work tab, or link existing work below.</p>
			) : null}
			{linked.length === 0 ? <p className="doc-detail-muted">No work linked yet.</p> : (
				<ul className="doc-detail-work-list">
					{linked.map((row) => (
						<li key={row.id ?? row.title}>
							<div>
								<Link className="row-link" to={`/hub/work?work=${row.id}`}>{row.title}</Link>
								<span className="doc-detail-muted">{row.due ? ` · ${row.due.slice(0, 10)}` : ""}</span>
							</div>
							<button className="btn ghost" type="button" disabled={busy} onClick={() => void run(() => unlink(row))}>Unlink</button>
						</li>
					))}
				</ul>
			)}
			{linkChoices.length ? (
				<div className="doc-detail-work-link">
					<label htmlFor="so-link-work">Link existing work</label>
					<div className="doc-detail-work-link-row">
						<Select id="so-link-work" value={linkWorkId} onChange={(event) => setLinkWorkId(event.target.value)}>
							<option value="">Choose work</option>
							{linkChoices.map((row) => (
								<option key={row.id ?? row.title} value={String(row.id ?? "")}>{row.title}</option>
							))}
						</Select>
						<button className="btn" type="button" disabled={busy || !linkWorkId} onClick={() => void run(linkExisting)}>Link</button>
					</div>
				</div>
			) : null}
			{actionError ? <p className="err">{actionError}</p> : null}
		</section>
	);
}

function PurchaseDetail({
	listId,
	meta,
	row,
	books,
	brands,
	listPath,
	tab,
}: {
	listId: string;
	meta: PurchaseMeta;
	row: PurchaseDoc;
	books: Books;
	brands: HubBrandDto[];
	listPath: string;
	tab: string;
}) {
	const navigate = useNavigate();
	const client = useQueryClient();
	const customer = organisationByName(books, row.party, "customer") ?? organisationByName(books, row.party);
	const issuer = issuerOrganisation(books, brands, customer);
	const billed = docPartyBlock(customer, row.party);
	const from = docPartyBlock(issuer, brands[0]?.name ?? "");
	const lines = row.lines?.length ? row.lines : [{ description: "—", amount: "0" }];
	const total = docTotal(row);
	const receipts = listId === "invoices"
		? receiptsForInvoice(books.receipts, row)
		: books.receipts.filter((item) => item.against === row.ref_no);
	const paidAmount = listId === "invoices"
		? paidTotalForInvoice(books.receipts, row)
		: receipts.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
	const [paymentOpen, setPaymentOpen] = useState(false);
	const [copyNote, setCopyNote] = useState("");
	const [detailActionError, setDetailActionError] = useState("");
	const suggestedReceiptRef = useMemo(() => suggestReceiptRef(books.receipts), [books.receipts, paymentOpen]);
	useEffect(() => {
		setDetailActionError("");
		setCopyNote("");
	}, [row.id, listId]);
	const paymentForm = useHubSave(async (fields) => {
		const amountRaw = textOf(fields, "amount");
		if (listId === "invoices") {
			const refNo = textOf(fields, "ref_no");
			const message = validateReceiptPaymentInput({
				amount: amountRaw,
				refNo,
				receipts: books.receipts,
				invoice: row,
			});
			if (message) throw new ApiError(422, { code: "validation_failed", message, requestId: "" });
			await api.appendHubBook({
				list: "receipts",
				record: buildReceiptRecordFromInvoice(row, {
					docAt: textOf(fields, "doc_at") || todayIso(),
					refNo,
					method: textOf(fields, "method") || "transfer",
					amount: amountRaw,
					note: textOf(fields, "note"),
				}),
			});
			if (row.id != null) {
				const nextReceipts: HubReceiptRow[] = [
					...receiptsForInvoice(books.receipts, row),
					{
						amount: formatReceiptAmount(amountRaw),
						invoiceId: row.id,
						against: row.ref_no,
						party: row.party,
						doc_at: textOf(fields, "doc_at") || todayIso(),
						ref_no: refNo,
						method: textOf(fields, "method") || "transfer",
						note: textOf(fields, "note"),
					},
				];
				const status = invoiceStatusAfterReceipts(row, nextReceipts);
				if (status !== row.status) {
					await api.updateHubBook({
						list: "purchases.invoices",
						id: row.id,
						record: { ...row, status },
					});
				}
			}
		} else {
			const amount = Number(amountRaw) || 0;
			await api.appendHubBook({
				list: "receipts",
				record: {
					doc_at: textOf(fields, "doc_at") || todayIso(),
					party: row.party,
					ref_no: textOf(fields, "ref_no"),
					against: row.ref_no,
					method: textOf(fields, "method") || "transfer",
					amount: String(amount),
					note: textOf(fields, "note"),
				},
			});
			if (row.id != null && amount + paidAmount >= purchaseTax(listId, total, row.mark).grand) {
				await api.updateHubBook({
					list: `purchases.${listId}`,
					id: row.id,
					record: {
						...row,
						status: meta.done,
					},
				});
			}
		}
		setPaymentOpen(false);
		await client.invalidateQueries({ queryKey: queryKeys.hubRecords });
	});
	const docKind = purchaseDocKind(listId, meta);
	const dueLabel = listId === "credits" ? "Against" : "Due Date";
	const statusLabel = `${docKind} Status`;
	const dateLabel = `${docKind} Date`;
	const refLabel = row.ref_no?.trim();
	const docNumber = refLabel
		? (/^\d+$/.test(refLabel) ? `#${refLabel}` : refLabel)
		: row.id != null
			? `#${row.id}`
			: "—";
	const { sst, grand } = purchaseTax(listId, total, listId === "invoices" ? row.mark : undefined);
	const balanceDue = listId === "invoices"
		? invoiceBalanceDue(books.receipts, row)
		: Math.max(0, grand - paidAmount);
	async function removeDoc() {
		if (row.id == null) return;
		const blocked =
			listId === "invoices"
				? invoiceDeletionBlockedReason(row, books.receipts)
				: listId === "sales-orders"
					? salesOrderDeletionBlockedReason(row, books.purchases.invoices ?? [])
					: listId === "quotations"
						? quotationDeletionBlockedReason(row, books.purchases["sales-orders"] ?? [])
						: null;
		if (blocked) {
			setDetailActionError(blocked);
			setCopyNote("");
			return;
		}
		setDetailActionError("");
		if (!window.confirm(`Remove this ${meta.one}?`)) return;
		if (listId === "sales-orders") {
			const linked = worksForSalesOrder(books.work, row.id);
			await Promise.all(linked.map((workRow) => {
				if (workRow.id == null) return Promise.resolve();
				return api.updateHubBook({
					list: "work",
					id: workRow.id,
					record: workRecordWithSalesOrder(workRow, null),
				});
			}));
			const qid = quotationIdOf(row.quotationId);
			if (qid != null) {
				const quote = findQuotationById(books.purchases.quotations ?? [], qid);
				if (quote?.id != null) {
					await api.updateHubBook({
						list: "purchases.quotations",
						id: quote.id,
						record: quotationRecordWithSalesOrderLink(quote, null),
					});
				}
			}
		}
		if (listId === "invoices") {
			const soId = salesOrderIdOf(row.salesOrderId);
			if (soId != null) {
				const salesOrder = findSalesOrderById(books.purchases["sales-orders"] ?? [], soId);
				if (salesOrder?.id != null) {
					await api.updateHubBook({
						list: "purchases.sales-orders",
						id: salesOrder.id,
						record: salesOrderRecordWithInvoiceLink(salesOrder, null),
					});
				}
			}
		}
		await api.deleteHubBook({ list: `purchases.${listId}`, id: row.id });
		await client.invalidateQueries({ queryKey: queryKeys.hubRecords });
		navigate(listPath);
	}
	function sendDoc() {
		const email = customer?.email?.trim() || (billed.contact.includes("@") ? billed.contact.split(" · ")[0]?.trim() : "");
		const subject = `${meta.title} ${row.ref_no}`;
		const body = `${docKind} ${row.ref_no} for ${docMoney(grand)} is attached as a PDF.`;
		if (email) window.location.href = `mailto:${encodeURIComponent(email)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
		const nextStatus = purchaseStatusAfterSend(listId, row.status);
		if (row.id != null && nextStatus) {
			void api.updateHubBook({
				list: `purchases.${listId}`,
				id: row.id,
				record: { ...row, status: nextStatus },
			}).then(() => client.invalidateQueries({ queryKey: queryKeys.hubRecords }));
		}
	}
	function pdfDoc() {
		const kind = purchaseDocKind(listId, meta);
		const { sst, grand } = purchaseTax(listId, total, listId === "invoices" ? row.mark : undefined);
		const fileName = purchasePdfFileName(kind, docNumber);
		void downloadPurchaseDocumentPdf({
			kind,
			docNumber,
			from,
			billed,
			statusLabel,
			status: label(row.status),
			tone: DOC_TONE[row.status] ?? "idle",
			dateLabel,
			docDate: docLongDate(row.doc_at),
			dueLabel,
			dueDate: listId === "credits" ? row.extra || "—" : docLongDate(row.extra ?? ""),
			lines,
			subtotal: total,
			sst,
			grand,
			balanceDue,
			note: row.note?.trim() ?? "",
		}, fileName);
	}
	async function copyLink() {
		try {
			await navigator.clipboard.writeText(window.location.href);
			setCopyNote("Link copied.");
		} catch {
			setCopyNote("Could not copy the link.");
		}
	}
	const showPayment = listId === "invoices" || listId === "bills";
	return (
		<DocDetailShell
			stack={(
				<>
					<DocDetailToolbarFrame
						onBack={() => navigate(listPath)}
						error={detailActionError || undefined}
						actions={(
							<DocDetailPurchaseActionBar
								onPdf={pdfDoc}
								onSend={sendDoc}
								onEdit={() => navigate(purchaseDocEditPath(listId, row, tab))}
								onDelete={removeDoc}
								editDisabled={row.id == null}
								deleteDisabled={row.id == null}
								paymentAction={showPayment ? (
									<DocDetailIconButton icon="wallet" onClick={() => setPaymentOpen(true)}>Add Payment</DocDetailIconButton>
								) : undefined}
							/>
						)}
					/>
					<DocDetailDocumentCard ariaLabel={`${meta.title} ${row.ref_no}`}>
						<DocDetailIssuerHeader from={from} docKind={docKind} docNumber={docNumber} />
						<DocDetailBilledBand
							billed={billed}
							meta={(
								<>
									<dt>{statusLabel}</dt>
									<dd><span className={`doc-pill ${DOC_TONE[row.status] ?? "idle"}`}>{label(row.status)}</span></dd>
									<dt>{dateLabel}</dt>
									<dd>{docLongDate(row.doc_at)}</dd>
									<dt>{dueLabel}</dt>
									<dd>{listId === "credits" ? row.extra || "—" : docLongDate(row.extra ?? "")}</dd>
								</>
							)}
						/>
						<DocDetailBody>
						<DocDetailItemsSection lines={purchaseDetailItemLines(listId, lines)} />
						<DocDetailAmountSummary subtotal={total} sst={sst} grand={grand} balanceDue={balanceDue} />
						{row.note ? <p className="doc-detail-note">{row.note}</p> : null}
						{listId === "bills" && receipts.length ? (
							<section className="doc-detail-payments" aria-label="Payments">
								<h2>Payments</h2>
								<ul>
									{receipts.map((item, index) => (
										<li key={`${item.ref_no}-${index}`}>{docLongDate(item.doc_at)} · {label(item.method)} · {docMoney(Number(item.amount) || 0)}</li>
									))}
								</ul>
							</section>
						) : null}
						{listId === "quotations" ? <QuotationSalesOrderSection quote={row} books={books} /> : null}
						{listId === "invoices" ? (
							<>
								<InvoiceReceiptsSection invoice={row} books={books} balanceDue={balanceDue} onCreateReceipt={() => setPaymentOpen(true)} />
								<InvoiceSalesOrderSection invoice={row} books={books} />
							</>
						) : null}
						{listId === "sales-orders" ? (
							<>
								<SalesOrderQuotationSection order={row} books={books} />
								<SalesOrderInvoiceSection order={row} books={books} />
								<SalesOrderWorkSection order={row} books={books} />
							</>
						) : null}
						</DocDetailBody>
					</DocDetailDocumentCard>
					<DocDetailBottomBar
						copyNote={copyNote}
						onCopyLink={copyLink}
						trailing={(
							<button className="doc-detail-btn pay" type="button" onClick={() => setPaymentOpen(true)} disabled={!showPayment || balanceDue <= 0}>
								Pay Now
							</button>
						)}
					/>
				</>
			)}
			after={(
				<Modal
					open={paymentOpen}
					title={listId === "invoices" ? "Create receipt" : "Add payment"}
					error={paymentForm.error}
					pending={paymentForm.pending}
					onClose={() => setPaymentOpen(false)}
					onSubmit={paymentForm.save}
				>
					{listId === "invoices" ? (
						<p className="doc-create-hint">Payment for {row.ref_no} · {row.party}. Outstanding: {docMoney(balanceDue)}.</p>
					) : null}
					<div className="grid two">
						<div><label htmlFor="pay-date">Date</label><input id="pay-date" name="doc_at" type="date" defaultValue={todayIso()} required /></div>
						<div><label htmlFor="pay-ref">Receipt no.</label><input id="pay-ref" name="ref_no" placeholder="RC-1001" defaultValue={listId === "invoices" ? suggestedReceiptRef : undefined} required /></div>
						<div><label htmlFor="pay-amount">Amount</label><input id="pay-amount" name="amount" inputMode="decimal" defaultValue={String(balanceDue)} required /></div>
						<HubFieldSelect id="pay-method" name="method" label="Method" defaultValue="transfer" items={PAYMENT_METHODS} />
					</div>
					<label htmlFor="pay-note">Note</label>
					<textarea id="pay-note" name="note" rows={2} />
				</Modal>
			)}
		/>
	);
}

function Purchase({ id, books, brands, tab, onTab }: { id: string; books: Books; brands: HubBrandDto[]; tab: string; onTab: (value: string) => void }) {
	const meta = PURCHASE_COPY[id];
	const { docId } = useParams();
	const location = useLocation();
	const navigate = useNavigate();
	const listPath = `/hub/${id}${tab ? `?tab=${encodeURIComponent(tab)}` : ""}`;
	const editing = location.pathname.endsWith("/edit");
	const all = books.purchases[id] ?? [];
	const [query, setQuery] = useState("");
	const [searching, setSearching] = useState(false);
	const [sort, setSort] = useState<DocSort | "">("");
	const [dir, setDir] = useState<"asc" | "desc">("asc");
	const searchRef = useRef<HTMLInputElement>(null);
	const tabbed = all.filter((row) => !tab || row.mark === tab);
	const needle = query.trim().toLowerCase();
	const found = needle
		? tabbed.filter((row) => [row.party, row.ref_no, row.note, ...(row.lines ?? []).map((line) => line.description)].some((value) => (value ?? "").toLowerCase().includes(needle)))
		: tabbed;
	const sortValue = (row: PurchaseDoc): string | number => sort === "amount" ? docTotal(row) : sort === "ref" ? row.ref_no : sort === "party" ? row.party : sort === "extra" ? row.extra ?? "" : row.status;
	const sign = dir === "asc" ? 1 : -1;
	const rows = sort
		? [...found].sort((a, b) => {
			const left = sortValue(a);
			const right = sortValue(b);
			if (typeof left === "number" && typeof right === "number") return (left - right) * sign;
			return String(left).localeCompare(String(right), "en", { numeric: true }) * sign;
		})
		: found;
	function sortBy(column: DocSort) {
		if (sort === column) setDir(dir === "asc" ? "desc" : "asc");
		else {
			setSort(column);
			setDir("asc");
		}
	}
	const form = useHubSave(async (fields) => {
		await syncPurchasePartyContact(books, id, fields);
		const savedLines = purchaseLinesForSave(id, fields);
		if (id === "sales-orders") assertSalesOrderValid(fields, savedLines);
		const created = await api.appendHubBook({
			list: `purchases.${id}`,
			record: {
				party: textOf(fields, "party"),
				doc_at: textOf(fields, "doc_at"),
				extra: textOf(fields, "extra"),
				ref_no: textOf(fields, "ref_no"),
				status: textOf(fields, "status"),
				mark: textOf(fields, "mark"),
				note: textOf(fields, "note"),
				lines: purchaseLinesToRecords(savedLines),
			},
		});
		if (id === "sales-orders" && created?.id != null) {
			const salesOrderId = Number(created.id);
			if (Number.isFinite(salesOrderId)) {
				const linkWork = textOf(fields, "link_work");
				const chosen = linkWork ? [linkWork] : [];
				for (const workId of chosen) {
					const workRow = books.work.find((row) => String(row.id) === workId);
					if (!workRow?.id || salesOrderIdOf(workRow.salesOrderId) != null) continue;
					await api.updateHubBook({
						list: "work",
						id: workRow.id,
						record: workRecordWithSalesOrder(workRow, salesOrderId),
					});
				}
			}
		}
	});
	useEffect(() => {
		if (searching) searchRef.current?.focus();
	}, [searching]);
	if (!meta) return null;
	if (docId) {
		const selected = findPurchaseDoc(all, docId);
		if (!selected) {
			return (
				<div className="doc-screen">
					<button className="doc-detail-back" type="button" onClick={() => navigate(listPath)}>Back</button>
					<Empty>{`${meta.title} not found.`}</Empty>
				</div>
			);
		}
		if (editing) {
			return <PurchaseEdit listId={id} meta={meta} row={selected} books={books} tab={tab} />;
		}
		return <PurchaseDetail listId={id} meta={meta} row={selected} books={books} brands={brands} listPath={listPath} tab={tab} />;
	}
	const typed = id === "invoices";
	const done = tabbed.filter((row) => row.status === meta.done);
	const open = tabbed.filter((row) => row.status !== meta.done);
	const sum = (list: PurchaseDoc[]) => list.reduce((total, row) => total + docTotal(row), 0);
	const count = (n: number) => `${n} ${n === 1 ? meta.one : meta.many}`;
	return (
		<div className="doc-screen">
			{meta.tabs ? (
				<DocTypeTabs items={meta.tabs} current={tab} onPick={onTab} label={`${meta.title} type`} />
			) : null}
			<div className="doc-body">
				<section className="doc-summary" aria-label={`${meta.title} summary`}>
					<dl>
						<div><dt>{`Total ${meta.many} created`}</dt><dd>{tabbed.length}</dd></div>
						<div><dt>{`Total ${meta.many} amount`}</dt><dd>{docMoney(sum(tabbed))}</dd></div>
						<div><dt>{`Total ${meta.doneLabel.toLowerCase()} ${meta.many}`}</dt><dd>{docMoney(sum(done))}<span>{count(done.length)}</span></dd></div>
						<div><dt>{`Total ${meta.openLabel.toLowerCase()} ${meta.many}`}</dt><dd>{docMoney(sum(open))}<span>{count(open.length)}</span></dd></div>
					</dl>
				</section>
				<div className="doc-toolbar">
					<div className="doc-search-group">
						<div className={searching ? "doc-search-wrap is-open" : "doc-search-wrap"}>
							<div className="doc-search-field">
								<label className="sr" htmlFor={`doc-search-${id}`}>{`Search ${meta.many}…`}</label>
								<input
									ref={searchRef}
									id={`doc-search-${id}`}
									className="doc-search-input"
									type="search"
									placeholder={`Search ${meta.many}…`}
									aria-label={`Search ${meta.many}…`}
									value={query}
									tabIndex={searching ? 0 : -1}
									onChange={(event) => setQuery(event.target.value)}
									onKeyDown={(event) => {
										if (event.key === "Escape") {
											setQuery("");
											setSearching(false);
										}
									}}
								/>
							</div>
						</div>
						<button
							className="doc-icon-btn doc-search-toggle"
							type="button"
							aria-label={`Search ${meta.many}…`}
							aria-expanded={searching}
							onClick={() => { if (searching) setQuery(""); setSearching(!searching); }}
						>
							<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>
						</button>
					</div>
					<button className="doc-create" type="button" onClick={() => form.setOpen(true)}>
						<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M12 5v14M5 12h14" /></svg>
						{`New ${meta.one}`}
					</button>
				</div>
				<section className={typed ? "doc-list has-type" : "doc-list"} aria-label={meta.title}>
					<div className="doc-head">
						<DocSortHead column="ref" label={meta.ref} sort={sort} dir={dir} onSort={sortBy} />
						<DocSortHead column="party" label={meta.party} sort={sort} dir={dir} onSort={sortBy} />
						{typed ? <span className="center">Type</span> : null}
						<DocSortHead column="extra" label={id === "credits" ? "Against" : "Due date"} sort={sort} dir={dir} onSort={sortBy} center />
						<DocSortHead column="amount" label="Amount" sort={sort} dir={dir} onSort={sortBy} center />
						<DocSortHead column="status" label="Status" sort={sort} dir={dir} onSort={sortBy} center />
					</div>
					{rows.length === 0 ? (
						<p className="doc-empty">{all.length === 0 ? `No ${meta.many} yet.` : needle ? `No ${meta.many} match "${query.trim()}".` : `No ${meta.many} here yet.`}</p>
					) : (
						<div className="doc-rows">
							{rows.map((row, index) => {
								const detail = [(row.lines ?? []).map((line) => line.description).filter(Boolean).join(", "), row.note].filter(Boolean).join(" · ");
								const target = row.id != null ? String(row.id) : encodeURIComponent(row.ref_no);
								function openDoc() {
									const key = row.ref_no?.trim() ? encodeURIComponent(row.ref_no) : target;
									navigate(`/hub/${id}/${key}${tab ? `?tab=${encodeURIComponent(tab)}` : ""}`);
								}
								return (
									<div
										className="doc-row clickable"
										key={`${row.ref_no}-${index}`}
										role="button"
										tabIndex={0}
										onClick={openDoc}
										onKeyDown={(event) => {
											if (event.key === "Enter" || event.key === " ") {
												event.preventDefault();
												openDoc();
											}
										}}
									>
										<div className="doc-ref">
											<span>{row.ref_no || "—"}</span>
											<small>{docDate(row.doc_at)}</small>
										</div>
										<div className="doc-party">
											<strong>{row.party || "—"}</strong>
											{detail ? <small>{detail}</small> : null}
										</div>
										{typed ? <span className="doc-type">{row.mark ? label(row.mark) : "—"}</span> : null}
										<span className="doc-due" data-label={id === "credits" ? "Against" : "Due"}>{(id === "credits" ? row.extra : docDate(row.extra ?? "")) || "—"}</span>
										<span className="doc-amount">{docMoney(docTotal(row))}</span>
										<span className="doc-status"><span className={`doc-pill ${DOC_TONE[row.status] ?? "idle"}`}>{label(row.status) || "—"}</span></span>
									</div>
								);
							})}
						</div>
					)}
				</section>
			</div>
			{form.open ? (
				<PurchaseCreateModal
					key={`${id}-${tab}`}
					listId={id}
					meta={meta}
					tab={tab}
					rows={all}
					books={books}
					open={form.open}
					error={form.error}
					pending={form.pending}
					onClose={() => form.setOpen(false)}
					onSubmit={form.save}
				/>
			) : null}
		</div>
	);
}

function Expenses({ data }: { data: HubRecordsDto }) {
	const form = useHubSave(async (fields) => {
		await api.createHubExpense({
			spentOn: textOf(fields, "spentOn"),
			description: textOf(fields, "description"),
			category: textOf(fields, "category"),
			amount: textOf(fields, "amount"),
			payee: textOf(fields, "payee"),
		});
	});
	return (
		<div>
			<Bar sub={`${data.expenses.length} ${data.expenses.length === 1 ? "expense" : "expenses"}`}>
				<button className="btn" type="button" onClick={() => form.setOpen(true)}>New</button>
			</Bar>
			<Modal open={form.open} title="New expense" error={form.error} pending={form.pending} onClose={() => form.setOpen(false)} onSubmit={form.save}>
				<div className="grid two">
					<div><label htmlFor="ex-date">Date</label><input id="ex-date" name="spentOn" type="date" required /></div>
					<div><label htmlFor="ex-desc">Expense</label><input id="ex-desc" name="description" required /></div>
					<div>
						<label htmlFor="ex-cat">Category</label>
						<Select id="ex-cat" name="category">{Object.entries(EXPENSE_CATEGORY).map(([value, name]) => <option key={value} value={value}>{name}</option>)}</Select>
					</div>
					<div><label htmlFor="ex-amount">Amount</label><input id="ex-amount" name="amount" inputMode="decimal" required /></div>
					<div><label htmlFor="ex-payee">Payee</label><input id="ex-payee" name="payee" /></div>
				</div>
			</Modal>
			{data.expenses.length === 0 ? <Empty>No expenses yet.</Empty> : (
				<Grid
					columns={["Date", "Expense", "Category", "Amount"]}
					rows={data.expenses.map((row) => [row.spentOn, row.description, EXPENSE_CATEGORY[row.category] ?? row.category, money(Number(row.amount) || 0)])}
				/>
			)}
		</div>
	);
}

function Leave({ books, tab, onTab }: { books: Books; tab: string; onTab: (value: string) => void }) {
	const rows = books.leave;
	const form = useHubSave(async (fields) => {
		await api.appendHubBook({
			list: "leave",
			record: {
				mine: false,
				person: textOf(fields, "person"),
				kind: textOf(fields, "kind"),
				from: textOf(fields, "from"),
				to: textOf(fields, "to"),
				days: Number(textOf(fields, "days")) || 1,
				status: "pending",
				reason: textOf(fields, "reason"),
			},
		});
	});
	const shown = tab === "approval" ? rows.filter((row) => row.status === "pending") : rows;
	const people = [...new Set(rows.map((row) => row.person))].sort((a, b) => a.localeCompare(b));
	const balances = people.flatMap((person) =>
		books.leavePolicy.map((policy) => {
			const used = rows
				.filter((row) => row.person === person && row.kind === policy.kind && row.status === "approved")
				.reduce((sum, row) => sum + row.days, 0);
			return [person, label(policy.kind), String(policy.days), String(used), String(policy.days - used)];
		}),
	);
	return (
		<div>
			<Bar sub={tab === "approval" ? "Waiting for approval" : tab === "balance" ? "Days left this year" : tab === "settings" ? "Types and how many days" : `${shown.length} leave`}>
				{tab === "all" ? <button className="btn" type="button" onClick={() => form.setOpen(true)}>New</button> : null}
			</Bar>
			<Modal open={form.open} title="New leave" error={form.error} pending={form.pending} onClose={() => form.setOpen(false)} onSubmit={form.save}>
				<div className="grid two">
					<div><label htmlFor="lv-person">Person</label><input id="lv-person" name="person" required /></div>
					<div>
						<label htmlFor="lv-kind">Type</label>
						<Select id="lv-kind" name="kind">{books.leavePolicy.map((row) => <option key={row.kind} value={row.kind}>{label(row.kind)}</option>)}</Select>
					</div>
					<div><label htmlFor="lv-from">From</label><input id="lv-from" name="from" type="date" required /></div>
					<div><label htmlFor="lv-to">To</label><input id="lv-to" name="to" type="date" required /></div>
					<div><label htmlFor="lv-days">Days</label><input id="lv-days" name="days" inputMode="numeric" required /></div>
				</div>
				<label htmlFor="lv-reason">Reason</label>
				<input id="lv-reason" name="reason" />
			</Modal>
			<Chips current={tab} onPick={onTab} items={[["all", "All leave"], ["approval", "Approval"], ["balance", "Leave balance"], ["settings", "Settings"]]} />
			{tab === "settings" ? (
				<Grid columns={["Type", "Days", "Paid", "Needs approval"]} rows={books.leavePolicy.map((row) => [label(row.kind), String(row.days), yesNo(row.paid), yesNo(row.approval)])} />
			) : tab === "balance" ? (
				balances.length ? <Grid columns={["Person", "Type", "Entitled", "Used", "Left"]} rows={balances} /> : <Empty>No leave balance yet.</Empty>
			) : shown.length === 0 ? <Empty>{tab === "approval" ? "Nothing is waiting for approval." : "No leave yet."}</Empty> : (
				<Grid columns={["From", "To", "Person", "Type", "Days", "Status", "Reason"]} rows={shown.map((row) => [row.from, row.to, row.person, label(row.kind), String(row.days), label(row.status), row.reason])} />
			)}
		</div>
	);
}

function Attendance({ books, tab, onTab }: { books: Books; tab: string; onTab: (value: string) => void }) {
	const rows = books.attendance;
	const form = useHubSave(async (fields) => {
		await api.appendHubBook({
			list: "attendance",
			record: {
				mine: false,
				person: textOf(fields, "person"),
				day: textOf(fields, "day"),
				shift: textOf(fields, "shift") || "day",
				inAt: textOf(fields, "inAt"),
				outAt: textOf(fields, "outAt"),
				status: textOf(fields, "status") || "present",
				note: textOf(fields, "note"),
			},
		});
	});
	const shifts = books.attendanceSettings.shifts;
	return (
		<div>
			<Bar sub={tab === "shifts" ? "Who works which days" : tab === "settings" ? `Late after ${books.attendanceSettings.lateMinutes || 0} minutes` : `${rows.length} records`}>
				{tab === "all" ? <button className="btn" type="button" onClick={() => form.setOpen(true)}>New</button> : null}
			</Bar>
			<Modal open={form.open} title="New attendance" error={form.error} pending={form.pending} onClose={() => form.setOpen(false)} onSubmit={form.save}>
				<div className="grid two">
					<div><label htmlFor="at-person">Person</label><input id="at-person" name="person" required /></div>
					<div><label htmlFor="at-day">Date</label><input id="at-day" name="day" type="date" required /></div>
					<div>
						<label htmlFor="at-shift">Shift</label>
						<Select id="at-shift" name="shift">{(books.attendanceSettings.shifts.length ? books.attendanceSettings.shifts : [{ name: "Day", id: "day" }]).map((row) => <option key={row.name} value={row.name.toLowerCase()}>{row.name}</option>)}</Select>
					</div>
					<div>
						<label htmlFor="at-status">Status</label>
						<Select id="at-status" name="status"><option value="present">Present</option><option value="late">Late</option><option value="absent">Absent</option><option value="leave">Leave</option></Select>
					</div>
					<div><label htmlFor="at-in">In</label><input id="at-in" name="inAt" placeholder="09:00" /></div>
					<div><label htmlFor="at-out">Out</label><input id="at-out" name="outAt" placeholder="18:00" /></div>
				</div>
				<label htmlFor="at-note">Note</label>
				<input id="at-note" name="note" />
			</Modal>
			<Chips current={tab} onPick={onTab} items={[["all", "All records"], ["shifts", "Shift schedule"], ["settings", "Settings"]]} />
			{tab === "settings" ? (
				shifts.length ? <Grid columns={["Shift", "Start", "End"]} rows={shifts.map((row) => [row.name, row.start, row.end])} /> : <Empty>No shifts yet.</Empty>
			) : tab === "shifts" ? (
				books.attendanceSlots.length ? (
					<Grid columns={["Person", "Days", "Shift"]} rows={books.attendanceSlots.map((row) => [row.person, row.days.map((day) => label(day)).join(", "), label(row.shift)])} />
				) : <Empty>No shift schedule yet.</Empty>
			) : rows.length === 0 ? <Empty>No attendance records yet.</Empty> : (
				<Grid columns={["Date", "Person", "Shift", "In", "Out", "Status", "Note"]} rows={rows.map((row) => [row.day, row.person, label(row.shift), row.inAt, row.outAt, label(row.status), row.note])} />
			)}
		</div>
	);
}

function Claims({ books, tab, onTab }: { books: Books; tab: string; onTab: (value: string) => void }) {
	const rows = books.claims;
	const form = useHubSave(async (fields) => {
		await api.appendHubBook({
			list: "claims",
			record: {
				mine: false,
				day: textOf(fields, "day"),
				person: textOf(fields, "person"),
				kind: textOf(fields, "kind"),
				amount: textOf(fields, "amount"),
				status: "pending",
				note: textOf(fields, "note"),
			},
		});
	});
	const shown = tab === "approval" ? rows.filter((row) => row.status === "pending") : rows;
	return (
		<div>
			<Bar sub={tab === "settings" ? "Limits for each claim" : `${shown.length} claims`}>
				{tab === "all" ? <button className="btn" type="button" onClick={() => form.setOpen(true)}>New</button> : null}
			</Bar>
			<Modal open={form.open} title="New claim" error={form.error} pending={form.pending} onClose={() => form.setOpen(false)} onSubmit={form.save}>
				<div className="grid two">
					<div><label htmlFor="cl-day">Date</label><input id="cl-day" name="day" type="date" required /></div>
					<div><label htmlFor="cl-person">Person</label><input id="cl-person" name="person" required /></div>
					<div>
						<label htmlFor="cl-kind">Type</label>
						<Select id="cl-kind" name="kind">{(books.claimPolicy.length ? books.claimPolicy : [{ kind: "fuel" }]).map((row) => <option key={row.kind} value={row.kind}>{label(row.kind)}</option>)}</Select>
					</div>
					<div><label htmlFor="cl-amount">Amount</label><input id="cl-amount" name="amount" inputMode="decimal" required /></div>
				</div>
				<label htmlFor="cl-note">Note</label>
				<input id="cl-note" name="note" />
			</Modal>
			<Chips current={tab} onPick={onTab} items={[["all", "All claim"], ["approval", "Approval"], ["settings", "Settings"]]} />
			{tab === "settings" ? (
				<Grid columns={["Type", "Limit", "Needs approval"]} rows={books.claimPolicy.map((row) => [label(row.kind), money(Number(row.limit) || 0), yesNo(row.approval)])} />
			) : shown.length === 0 ? <Empty>{tab === "approval" ? "Nothing is waiting for approval." : "No claims yet."}</Empty> : (
				<Grid columns={["Date", "Person", "Type", "Amount", "Status", "Note"]} rows={shown.map((row) => [row.day, row.person, label(row.kind), money(Number(row.amount) || 0), label(row.status), row.note])} />
			)}
		</div>
	);
}

function Payroll({ books, tab, onTab }: { books: Books; tab: string; onTab: (value: string) => void }) {
	const people = books.employees;
	const runs = tab === "approval" ? books.payrollRuns.filter((run) => run.status === "pending") : books.payrollRuns;
	const settings = books.payrollSettings;
	const lines = runs.flatMap((run) =>
		run.lines.map((line) => [run.month, label(run.status), run.note, line.person, money(Number(line.basic) || 0), money(Number(line.allowance) || 0), money(Number(line.epf) || 0), money(Number(line.socso) || 0), money(Number(line.eis) || 0), money(Number(line.net) || 0)]),
	);
	return (
		<div>
			<Bar sub={tab === "employees" ? `${people.length} employees` : tab === "settings" ? "Pay day and statutory" : `${runs.length} payroll runs`} />
			<Chips current={tab} onPick={onTab} items={[["employees", "Employees"], ["run", "Run payroll"], ["approval", "Approval payroll"], ["settings", "Settings"]]} />
			{tab === "employees" ? (people.length ? <Grid columns={["Name", "Role", "Basic", "Allowance", "Status"]} rows={people.map((row) => [row.name, row.role, money(Number(row.basic) || 0), money(Number(row.allowance) || 0), row.active ? "Active" : "Inactive"])} /> : <Empty>No employees yet.</Empty>) : null}
			{tab === "run" || tab === "approval" ? (lines.length ? <Grid columns={["Month", "Status", "Note", "Person", "Basic", "Allowance", "EPF", "SOCSO", "EIS", "Net"]} rows={lines} /> : <Empty>{tab === "approval" ? "Nothing is waiting for approval." : "No payroll has been run yet."}</Empty>) : null}
			{tab === "settings" && settings.payDay ? (
				<div className="panel grid two">
					<p><span className="quiet">Pay day</span><br />{settings.payDay}</p>
					<p><span className="quiet">EPF employee</span><br />{settings.epf}%</p>
					<p><span className="quiet">SOCSO</span><br />{money(Number(settings.socso) || 0)}</p>
					<p><span className="quiet">EIS</span><br />{money(Number(settings.eis) || 0)}</p>
				</div>
			) : null}
			{tab === "settings" && !settings.payDay ? <Empty>No payroll settings yet.</Empty> : null}
		</div>
	);
}

function nextJournalId(date: string, rows: Array<{ ref: string }>): string {
	const match = /^(\d{4})-(\d{2})/.exec(date);
	const year = match?.[1]?.slice(-2) ?? "00";
	const month = match?.[2] ?? "00";
	const prefix = `JE-${year}-${month}-`;
	let max = 0;
	for (const row of rows) {
		if (!row.ref.startsWith(prefix)) continue;
		const number = Number(row.ref.slice(prefix.length));
		if (Number.isFinite(number)) max = Math.max(max, number);
	}
	return `${prefix}${String(max + 1).padStart(2, "0")}`;
}

function dayFirst(date: string): string {
	const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
	if (!match?.[3] || !match[2] || !match[1]) return date || "—";
	return `${match[3]}/${match[2]}/${match[1]}`;
}

function isoFromDay(value: string): string {
	const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value);
	if (!match?.[1] || !match[2] || !match[3]) return "";
	return `${match[3]}-${match[2]}-${match[1]}`;
}

function monthOf(date: string): string {
	const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
	if (!match?.[1] || !match[2] || !match[3]) return "—";
	const day = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
	return new Intl.DateTimeFormat("en-GB", { month: "short", year: "numeric", timeZone: "UTC" }).format(day);
}

function Internal({ books }: { books: Books }) {
	const rows = [...books.internal].sort((a, b) => a.date.localeCompare(b.date) || a.ref.localeCompare(b.ref));
	const [date, setDate] = useState("");
	const form = useHubSave(async (fields) => {
		const chosen = isoFromDay(textOf(fields, "date"));
		await api.appendHubBook({
			list: "internal",
			record: {
				date: chosen,
				remarks: textOf(fields, "remarks"),
				payee: textOf(fields, "payee"),
				ref: nextJournalId(chosen, books.internal),
				amount: textOf(fields, "amount"),
				via: textOf(fields, "via"),
				payment: textOf(fields, "payment") || "transfer",
			},
		});
		setDate("");
	});
	const paymentLabel = (value: string) => PAYMENT_TYPES.find(([item]) => item === value)?.[1] ?? label(value);
	return (
		<div>
			<Bar sub={`${rows.length} ${rows.length === 1 ? "payment" : "payments"}`}>
				<button className="btn" type="button" onClick={() => { setDate(""); form.setOpen(true); }}>Add</button>
			</Bar>
			{rows.length === 0 ? <Empty>No internal payments yet.</Empty> : (
				<div className="table-wrap">
					<table>
						<thead>
							<tr>
								<th>Month</th>
								<th>Date</th>
								<th>Remarks</th>
								<th>Payee</th>
								<th>ID</th>
								<th>Amount</th>
								<th>Pay via</th>
								<th>Type of payment</th>
							</tr>
						</thead>
						<tbody>
							{rows.map((row) => (
								<tr key={row.id ?? row.ref}>
									<td data-label="Month">{monthOf(row.date)}</td>
									<td data-label="Date">{row.date ? dayFirst(row.date) : "—"}</td>
									<td data-label="Remarks">{row.remarks || "—"}</td>
									<td data-label="Payee">{row.payee || "—"}</td>
									<td data-label="ID">{row.ref}</td>
									<td data-label="Amount">{money(Number(row.amount) || 0)}</td>
									<td data-label="Pay via">{row.via || "—"}</td>
									<td data-label="Type of payment">{paymentLabel(row.payment)}</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}
			<Modal open={form.open} title="Add payment" error={form.error} pending={form.pending} onClose={() => form.setOpen(false)} onSubmit={(fields) => void form.save(fields)}>
				<div className="grid two">
					<div>
						<label htmlFor="in-date">Date</label>
						<input id="in-date" name="date" placeholder="dd/mm/yyyy" inputMode="numeric" pattern="\d{2}/\d{2}/\d{4}" required value={date} onChange={(event) => setDate(event.target.value)} />
					</div>
					<div>
						<label htmlFor="in-id">ID</label>
						<input id="in-id" value={isoFromDay(date) ? nextJournalId(isoFromDay(date), books.internal) : "JE-YY-MM-NN"} readOnly />
					</div>
				</div>
				<label htmlFor="in-payee">Payee</label>
				<input id="in-payee" name="payee" required />
				<label htmlFor="in-remarks">Remarks</label>
				<textarea id="in-remarks" name="remarks" rows={3} placeholder="Optional" />
				<div className="grid two">
					<div>
						<label htmlFor="in-amount">Amount</label>
						<input id="in-amount" name="amount" inputMode="decimal" required />
					</div>
					<div>
						<label htmlFor="in-via">Pay via</label>
						<input id="in-via" name="via" placeholder="Maybank" />
					</div>
				</div>
				<label htmlFor="in-payment">Type of payment</label>
				<Select id="in-payment" name="payment" defaultValue="transfer">
					{PAYMENT_TYPES.map(([value, name]) => <option key={value} value={value}>{name}</option>)}
				</Select>
			</Modal>
		</div>
	);
}

function Transactions({ books }: { books: Books }) {
	const names = new Map(books.banks.map((bank) => [bank.id, bank.name]));
	const rows = [
		...books.banks.map((bank) => ["", bank.name, "Opening balance", "", money(bank.opening)]),
		...books.moves.map((row) => [row.day, names.get(row.bank) ?? row.bank, row.party, label(row.kind), `${row.kind === "out" ? "−" : "+"}${money(Number(row.amount) || 0)}`]),
		...books.transfers.map((row) => [row.day, `${names.get(row.from) ?? row.from} → ${names.get(row.to) ?? row.to}`, row.note, "Transfer", money(Number(row.amount) || 0)]),
	];
	const count = books.moves.length + books.transfers.length;
	return (
		<div>
			<Bar sub={`${count} ${count === 1 ? "transaction" : "transactions"}`} />
			{rows.length === 0 ? <Empty>No transactions yet.</Empty> : <Grid columns={["Date", "Account", "Detail", "Type", "Amount"]} rows={rows} />}
		</div>
	);
}

function Journals({ books }: { books: Books }) {
	const names = new Map(books.accounts.map((account) => [account.id, `${account.code} ${account.name}`]));
	const form = useHubSave(async (fields) => {
		const amount = textOf(fields, "amount");
		const debit = textOf(fields, "debit");
		const credit = textOf(fields, "credit");
		await api.appendHubBook({
			list: "journals",
			record: {
				day: textOf(fields, "day"),
				ref: textOf(fields, "ref"),
				note: textOf(fields, "note"),
				lines: [
					{ account: debit, description: textOf(fields, "line"), debit: amount, credit: "" },
					{ account: credit, description: textOf(fields, "line"), debit: "", credit: amount },
				],
			},
		});
	});
	const rows = books.journals.flatMap((journal) =>
		journal.lines.map((line) => [journal.day, journal.ref, journal.note, names.get(line.account) ?? line.account, line.description, line.debit ? money(Number(line.debit) || 0) : "", line.credit ? money(Number(line.credit) || 0) : ""]),
	);
	return (
		<div>
			<Bar sub={`${books.journals.length} ${books.journals.length === 1 ? "journal" : "journals"}`}>
				<button className="btn" type="button" onClick={() => form.setOpen(true)}>New</button>
			</Bar>
			<Modal open={form.open} title="New journal" error={form.error} pending={form.pending} onClose={() => form.setOpen(false)} onSubmit={form.save}>
				<div className="grid two">
					<div><label htmlFor="je-day">Date</label><input id="je-day" name="day" type="date" required /></div>
					<div><label htmlFor="je-ref">Reference</label><input id="je-ref" name="ref" required /></div>
					<div>
						<label htmlFor="je-debit">Debit</label>
						<Select id="je-debit" name="debit">{books.accounts.map((row) => <option key={row.id} value={row.id}>{row.code} {row.name}</option>)}</Select>
					</div>
					<div>
						<label htmlFor="je-credit">Credit</label>
						<Select id="je-credit" name="credit">{books.accounts.map((row) => <option key={row.id} value={row.id}>{row.code} {row.name}</option>)}</Select>
					</div>
					<div><label htmlFor="je-amount">Amount</label><input id="je-amount" name="amount" inputMode="decimal" required /></div>
					<div><label htmlFor="je-line">Line</label><input id="je-line" name="line" /></div>
				</div>
				<label htmlFor="je-note">Description</label>
				<input id="je-note" name="note" required />
			</Modal>
			{rows.length === 0 ? <Empty>No journals yet.</Empty> : <Grid columns={["Date", "Reference", "Description", "Account", "Line", "Debit", "Credit"]} rows={rows} />}
		</div>
	);
}

function Accounts({ books }: { books: Books }) {
	const [query, setQuery] = useState("");
	const form = useHubSave(async (fields) => {
		await api.appendHubBook({
			list: "accounts",
			record: { code: textOf(fields, "code"), name: textOf(fields, "name"), type: textOf(fields, "type") },
		});
	});
	const rows = books.accounts.filter((row) => `${row.code} ${row.name} ${row.type}`.toLowerCase().includes(query.trim().toLowerCase()));
	return (
		<div>
			<Bar sub={`${rows.length} ${rows.length === 1 ? "account" : "accounts"}`}>
				<button className="btn" type="button" onClick={() => form.setOpen(true)}>New</button>
			</Bar>
			<Modal open={form.open} title="New account" error={form.error} pending={form.pending} onClose={() => form.setOpen(false)} onSubmit={form.save}>
				<div className="grid two">
					<div><label htmlFor="ac-code">Code</label><input id="ac-code" name="code" required /></div>
					<div><label htmlFor="ac-name">Name</label><input id="ac-name" name="name" required /></div>
					<div>
						<label htmlFor="ac-type">Type</label>
						<Select id="ac-type" name="type"><option value="asset">Asset</option><option value="liability">Liability</option><option value="equity">Equity</option><option value="income">Income</option><option value="expense">Expense</option></Select>
					</div>
				</div>
			</Modal>
			<form className="list-search" role="search" onSubmit={(event) => event.preventDefault()}>
				<input type="search" value={query} placeholder="Search" aria-label="Search" onChange={(event) => setQuery(event.target.value)} />
			</form>
			{rows.length === 0 ? <Empty>No accounts yet.</Empty> : <Grid columns={["Code", "Name", "Type"]} rows={rows.map((row) => [row.code, row.name, label(row.type)])} />}
		</div>
	);
}

function Tax({ books, tab, onTab }: { books: Books; tab: string; onTab: (value: string) => void }) {
	const chargeable = (row: Computation) => (Number(row.profit) || 0) + (Number(row.addback) || 0) - (Number(row.deduction) || 0);
	return (
		<div>
			<Bar sub="Year of assessment" />
			<Chips
				current={tab}
				onPick={onTab}
				items={[
					["documents", "Tax Documents"],
					["computation", "Tax Computation"],
					["cp204", "CP204"],
					["form-c", "Form C"],
					["form-e", "Form E"],
					["ea", "Form EA"],
				]}
			/>
			{tab === "documents" ? (books.taxDocuments.length ? <Grid columns={["Year", "Type", "Date", "Note"]} rows={books.taxDocuments.map((row) => [row.year, label(row.kind), row.day, row.note])} /> : <Empty>No tax documents yet.</Empty>) : null}
			{tab === "computation" ? (books.computations.length ? <Grid columns={["Year", "Profit", "Add back", "Deduction", "Chargeable", "Rate"]} rows={books.computations.map((row) => [row.year, money(Number(row.profit) || 0), money(Number(row.addback) || 0), money(Number(row.deduction) || 0), money(chargeable(row)), `${row.rate}%`])} /> : <Empty>No tax computation yet.</Empty>) : null}
			{tab === "cp204" ? (books.cp204.length ? <Grid columns={["Year", "Estimate", "Revision", "Note"]} rows={books.cp204.map((row) => [row.year, money(Number(row.estimate) || 0), label(row.revision), row.note])} /> : <Empty>No CP204 yet.</Empty>) : null}
			{tab === "form-c" ? (books.formC.length ? <Grid columns={["Year", "Chargeable", "Rate", "Status"]} rows={books.formC.map((row) => [row.year, money(Number(row.chargeable) || 0), `${row.rate}%`, label(row.status)])} /> : <Empty>No Form C yet.</Empty>) : null}
			{tab === "form-e" ? ((books.formE ?? []).length ? <Grid columns={["Year", "Employees", "Remuneration", "Status"]} rows={(books.formE ?? []).map((row) => [row.year, row.employees, money(Number(row.remuneration) || 0), label(row.status)])} /> : <Empty>No Form E yet.</Empty>) : null}
			{tab === "ea" ? (books.eaForms.length ? <Grid columns={["Year", "Employee", "Gross", "EPF", "PCB"]} rows={books.eaForms.map((row) => [row.year, row.employee, money(Number(row.gross) || 0), money(Number(row.epf) || 0), money(Number(row.pcb) || 0)])} /> : <Empty>No Form EA yet.</Empty>) : null}
		</div>
	);
}


const AUDIT_FOLDERS = [
	["management", "Management Accounts"],
	["supporting", "Supporting Documents"],
] as const;

function twelveMonthPeriod(start: string, end: string): boolean {
	const from = new Date(`${start}T00:00:00`);
	const to = new Date(`${end}T00:00:00`);
	if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return false;
	const expected = new Date(from);
	expected.setFullYear(expected.getFullYear() + 1);
	expected.setDate(expected.getDate() - 1);
	return expected.getTime() === to.getTime();
}

function Audit({ books, tab, onTab }: { books: Books; tab: string; onTab: (value: string) => void }) {
	const client = useQueryClient();
	const [fileError, setFileError] = useState("");
	const [viewing, setViewing] = useState<{ name: string; url: string; kind: "pdf" | "image" | "other" } | null>(null);
	const [report, setReport] = useState<PnlSnapshot | null>(null);
	const [renaming, setRenaming] = useState<number | null>(null);
	const [renameDraft, setRenameDraft] = useState("");
	const years = (Array.isArray(books.auditYears) ? books.auditYears : []).filter((row) => row?.end).sort((a, b) => b.end.localeCompare(a.end));
	const files = Array.isArray(books.auditFiles) ? books.auditFiles : [];
	const form = useHubSave(async (fields) => {
		const start = textOf(fields, "start");
		const end = textOf(fields, "end");
		if (!twelveMonthPeriod(start, end)) throw new ApiError(422, { code: "validation_failed", message: "Enter a 12-month period.", requestId: "" });
		await api.appendHubBook({ list: "auditYears", record: { start, end } });
	});
	const folderForm = useHubSave(async (fields) => {
		const name = textOf(fields, "name");
		if (!name) throw new ApiError(422, { code: "validation_failed", message: "Enter a folder name.", requestId: "" });
		if (tab !== "query" && tab !== "adjustment" && tab !== "letter" && tab !== "report") return;
		await api.appendHubBook({ list: "auditFiles", record: { yearId: tab, folder: "folder", name, type: "text/plain", data: "" } });
	});
	const closeView = () => {
		if (viewing?.url) URL.revokeObjectURL(viewing.url);
		setViewing(null);
	};
	const upload = async (yearId: string, folder: string, file: File) => {
		setFileError("");
		const fields = new FormData();
		fields.set("file", file);
		const stored = await fileDataOf(fields, "file");
		if (!stored) return;
		try {
			await api.appendHubBook({
				list: "auditFiles",
				record: { yearId, folder, name: stored.name, type: file.type || "application/octet-stream", data: stored.data },
			});
			await client.invalidateQueries({ queryKey: queryKeys.hubRecords });
		} catch (err) {
			setFileError(presentError(err).title);
		}
	};
	const removePeriod = async (year: AuditYear) => {
		if (year.id == null || !window.confirm(`Remove ${year.end.slice(0, 4)}?`)) return;
		const yearId = String(year.id);
		setFileError("");
		try {
			await Promise.all([
				api.deleteHubBook({ list: "auditYears", id: year.id }),
				...files.filter((row) => row.yearId === yearId && row.id != null).map((row) => api.deleteHubBook({ list: "auditFiles", id: row.id! })),
			]);
			await client.invalidateQueries({ queryKey: queryKeys.hubRecords });
		} catch (err) {
			setFileError(presentError(err).title);
		}
	};
	const removeFile = async (row: AuditFile) => {
		if (row.id == null || !window.confirm("Remove this file?")) return;
		setFileError("");
		try {
			await api.deleteHubBook({ list: "auditFiles", id: row.id });
			await client.invalidateQueries({ queryKey: queryKeys.hubRecords });
		} catch (err) {
			setFileError(presentError(err).title);
		}
	};
	const renameFolder = async (row: AuditFile) => {
		const name = renameDraft.trim();
		setRenaming(null);
		if (row.id == null || !name || name === row.name) return;
		setFileError("");
		try {
			await api.updateHubBook({
				list: "auditFiles",
				id: row.id,
				record: { yearId: row.yearId, folder: "folder", name, type: row.type || "text/plain", data: row.data || "" },
			});
			await client.invalidateQueries({ queryKey: queryKeys.hubRecords });
		} catch (err) {
			setFileError(presentError(err).title);
		}
	};
	const removeQueryFolder = async (row: AuditFile) => {
		if (row.id == null || !window.confirm(`Remove ${row.name}?`)) return;
		const folderId = String(row.id);
		setFileError("");
		try {
			await Promise.all([
				api.deleteHubBook({ list: "auditFiles", id: row.id }),
				...files.filter((file) => file.yearId === folderId && file.folder === row.yearId && file.id != null).map((file) => api.deleteHubBook({ list: "auditFiles", id: file.id! })),
			]);
			await client.invalidateQueries({ queryKey: queryKeys.hubRecords });
		} catch (err) {
			setFileError(presentError(err).title);
		}
	};
	const viewFile = (row: AuditFile) => {
		if (!row.data) return;
		const type = row.type || "application/octet-stream";
		const url = attachmentUrl(row.data, type);
		const kind = type === "application/pdf" ? "pdf" : type.startsWith("image/") ? "image" : "other";
		setViewing({ name: row.name, url, kind });
	};
	return (
		<div>
			<Bar sub="Audit file">
				{tab === "documents" ? <button className="btn" type="button" onClick={() => form.setOpen(true)}>Add year</button> : null}
				{tab === "query" || tab === "adjustment" || tab === "letter" || tab === "report" ? <button className="btn" type="button" onClick={() => folderForm.setOpen(true)}>Add folder</button> : null}
			</Bar>
			<Chips current={tab} onPick={onTab} items={[["documents", "Audit Documents"], ["query", "Audit Query"], ["adjustment", "Audit Adjustments"], ["letter", "Audit Letter"], ["report", "Audit Report"]]} />
			{tab === "documents" ? (
				<>
					{fileError ? <p className="err">{fileError}</p> : null}
					{years.length === 0 ? <Empty>No audit years yet.</Empty> : years.map((year) => (
						<section className="audit-year" key={year.id ?? year.end}>
							<div className="row">
								<h2>{year.end.slice(0, 4)}</h2>
								<button className="btn ghost" type="button" onClick={() => void removePeriod(year)}>Remove</button>
							</div>
							<div className="audit-folders">
								{AUDIT_FOLDERS.map(([folder, title]) => {
									const folderFiles = files.filter((row) => row.yearId === String(year.id ?? "") && row.folder === folder);
									return (
										<div className="audit-folder" key={folder}>
											<div className="row tight">
												<h3>{title}</h3>
												{folder === "supporting" ? (
													<label className="btn ghost">
														Add
														<input className="sr" type="file" onChange={(event) => {
															const file = event.currentTarget.files?.[0];
															event.currentTarget.value = "";
															if (file && year.id != null) void upload(String(year.id), folder, file);
														}} />
													</label>
												) : null}
											</div>
											{folderFiles.length === 0 ? <p className="quiet">No files yet.</p> : folderFiles.map((row) => (
												<div className="audit-file" key={row.id ?? row.name}>
													<button className="file-name" type="button" onClick={() => {
														const saved = pnlSnapshot(row);
														if (saved) setReport(saved);
														else viewFile(row);
													}}>{row.name}</button>
													<button className="file-x" type="button" aria-label="Remove" onClick={() => void removeFile(row)}>×</button>
												</div>
											))}
										</div>
									);
								})}
							</div>
						</section>
					))}
				</>
			) : null}
			{tab === "query" || tab === "adjustment" || tab === "letter" || tab === "report" ? (
				<>
					{fileError ? <p className="err">{fileError}</p> : null}
					{files.filter((row) => row.yearId === tab && row.folder === "folder").length === 0 ? <Empty>No folders yet.</Empty> : (
						<div className="audit-folders">
							{files.filter((row) => row.yearId === tab && row.folder === "folder").map((folder) => {
								const folderFiles = files.filter((row) => row.yearId === String(folder.id ?? "") && row.folder === tab);
								return (
									<div className="audit-folder" key={folder.id ?? folder.name}>
										<div className="row tight">
											{renaming === folder.id ? (
												<input
													aria-label="Folder name"
													value={renameDraft}
													autoFocus
													onChange={(event) => setRenameDraft(event.target.value)}
													onBlur={() => void renameFolder(folder)}
													onKeyDown={(event) => {
														if (event.key === "Enter") event.currentTarget.blur();
														if (event.key === "Escape") setRenaming(null);
													}}
												/>
											) : (
												<h3 onDoubleClick={() => { setRenaming(folder.id ?? null); setRenameDraft(folder.name); }}>{folder.name}</h3>
											)}
											<details className="select menu">
												<summary aria-label="Folder actions"><svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="12" cy="5" r="1.8" /><circle cx="12" cy="12" r="1.8" /><circle cx="12" cy="19" r="1.8" /></svg></summary>
												<div className="select-pop">
													<label className="select-item">
														Add PDF
														<input className="sr" type="file" accept="application/pdf,.pdf" onChange={(event) => {
															const file = event.currentTarget.files?.[0];
															event.currentTarget.value = "";
															if (!file || folder.id == null) return;
															if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
																setFileError("Upload a PDF.");
																return;
															}
															void upload(String(folder.id), tab, file);
														}} />
													</label>
													<button className="select-item" type="button" onClick={() => void removeQueryFolder(folder)}>Remove</button>
												</div>
											</details>
										</div>
										{folderFiles.length === 0 ? <p className="quiet">No files yet.</p> : folderFiles.map((row) => (
											<div className="audit-file" key={row.id ?? row.name}>
												<button className="file-name" type="button" onClick={() => viewFile(row)}>{row.name}</button>
												<button className="file-x" type="button" aria-label="Remove" onClick={() => void removeFile(row)}>×</button>
											</div>
										))}
									</div>
								);
							})}
						</div>
					)}
				</>
			) : null}
			<Modal open={folderForm.open} title="Add folder" error={folderForm.error} pending={folderForm.pending} onClose={() => folderForm.setOpen(false)} onSubmit={(fields) => void folderForm.save(fields)}>
				<label htmlFor="query-folder">Name</label>
				<input id="query-folder" name="name" required />
			</Modal>
			<Modal open={form.open} title="Add year" error={form.error} pending={form.pending} onClose={() => form.setOpen(false)} onSubmit={(fields) => void form.save(fields)}>
				<div className="grid two">
					<div>
						<label htmlFor="audit-start">From</label>
						<input id="audit-start" name="start" type="date" required />
					</div>
					<div>
						<label htmlFor="audit-end">To</label>
						<input id="audit-end" name="end" type="date" required />
					</div>
				</div>
			</Modal>
			{report ? <ProfitLoss books={books} snapshot={report} onClose={() => setReport(null)} /> : null}
			<dialog className="modal preview" open={viewing !== null} onClose={closeView}>
				<div className="row tight"><h1>{viewing?.name || "File"}</h1></div>
				{viewing?.kind === "pdf" ? <iframe title={viewing.name} src={viewing.url} /> : null}
				{viewing?.kind === "image" ? <img alt="" src={viewing.url} /> : null}
				{viewing?.kind === "other" ? <p><a className="btn" href={viewing.url} download={viewing.name}>Download</a></p> : null}
				<p className="actions"><button className="btn ghost" type="button" onClick={closeView}>Close</button></p>
			</dialog>
		</div>
	);
}

function letterStamp(value: string): string {
	const raw = value.trim();
	if (!raw) return "—";
	const iso = raw.includes("T") ? raw : raw.replace(" ", "T");
	const dated = new Date(/Z$|[+-]\d{2}:\d{2}$/.test(iso) ? iso : `${iso}+08:00`);
	if (Number.isNaN(dated.getTime())) return raw;
	return new Intl.DateTimeFormat("en-GB", {
		timeZone: "Asia/Kuala_Lumpur",
		day: "2-digit",
		month: "short",
		year: "numeric",
		hour: "2-digit",
		minute: "2-digit",
		hour12: false,
	}).format(dated);
}

async function fileDataOf(fields: FormData, name: string): Promise<{ name: string; data: string } | null> {
	const file = fields.get(name);
	if (!(file instanceof File) || file.size === 0) return null;
	const bytes = new Uint8Array(await file.arrayBuffer());
	let binary = "";
	for (let index = 0; index < bytes.length; index += 8192) {
		const slice = bytes.subarray(index, index + 8192);
		binary += String.fromCharCode(...slice);
	}
	return { name: file.name, data: btoa(binary) };
}

function attachmentUrl(data: string, type: string): string {
	const binary = atob(data);
	const bytes = new Uint8Array(binary.length);
	for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
	return URL.createObjectURL(new Blob([bytes], { type }));
}

function Letters({ books, tab, onTab }: { books: Books; tab: string; onTab: (value: string) => void }) {
	const templates = books.letterTemplates;
	const saved = books.savedLetters;
	const savedTab = tab === "saved";
	const client = useQueryClient();
	const [preparing, setPreparing] = useState<LetterTemplate | null>(null);
	const [editing, setEditing] = useState<LetterTemplate | null>(null);
	const [editingSaved, setEditingSaved] = useState<SavedLetter | null>(null);
	const [viewing, setViewing] = useState<{ name: string; url: string; kind: "pdf" | "docx"; missing: boolean } | null>(null);
	const form = useHubSave(async (fields) => {
		if (savedTab) {
			const docx = await fileDataOf(fields, "docx");
			const pdf = await fileDataOf(fields, "pdf");
			const record = {
				title: textOf(fields, "title"),
				template: textOf(fields, "template"),
				recipient: textOf(fields, "recipient"),
				saved_at: editingSaved?.saved_at || new Date().toISOString(),
				docx: docx?.name || editingSaved?.docx || "",
				pdf: pdf?.name || editingSaved?.pdf || "",
				docx_data: docx?.data || editingSaved?.docx_data || "",
				pdf_data: pdf?.data || editingSaved?.pdf_data || "",
			};
			if (editingSaved?.id != null) await api.updateHubBook({ list: "savedLetters", id: editingSaved.id, record });
			else await api.appendHubBook({ list: "savedLetters", record });
			setEditingSaved(null);
			return;
		}
		const record = {
			title: textOf(fields, "title"),
			type: textOf(fields, "type"),
			body: editing ? editing.body : textOf(fields, "body"),
			updated_at: new Date().toISOString(),
		};
		if (editing?.id) await api.updateHubBook({ list: "letterTemplates", id: editing.id, record });
		else await api.appendHubBook({ list: "letterTemplates", record });
		setEditing(null);
	});
	async function removeTemplate(id: number | string) {
		if (!window.confirm("Remove this template?")) return;
		await api.deleteHubBook({ list: "letterTemplates", id });
		await client.invalidateQueries({ queryKey: queryKeys.hubRecords });
	}
	async function removeSaved(id: number | string) {
		if (!window.confirm("Remove this letter?")) return;
		await api.deleteHubBook({ list: "savedLetters", id });
		await client.invalidateQueries({ queryKey: queryKeys.hubRecords });
	}
	function viewFile(name: string | undefined, data: string | undefined, kind: "pdf" | "docx") {
		if (!name) return;
		if (viewing?.url) URL.revokeObjectURL(viewing.url);
		if (!data) {
			setViewing({ name, url: "", kind, missing: true });
			return;
		}
		const type = kind === "pdf" ? "application/pdf" : "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
		setViewing({ name, url: attachmentUrl(data, type), kind, missing: false });
	}
	return (
		<div>
			<Bar sub="Letters and official correspondence.">
				<button className="btn" type="button" onClick={() => { setEditing(null); setEditingSaved(null); form.setOpen(true); }}>{savedTab ? "Save" : "New"}</button>
			</Bar>
			<Chips current={savedTab ? "saved" : "template"} onPick={onTab} items={[["template", "Template"], ["saved", "Saved"]]} />
			<p className="hint">{savedTab ? "Store the finished letter as DOCX and PDF." : "Prepare a letter from a template, then save it as DOCX and PDF."}</p>
			{savedTab ? (saved.length === 0 ? <Empty>No saved letters yet.</Empty> : (
				<div className="table-wrap">
					<table>
						<thead><tr><th>Letter</th><th>Template</th><th>DOCX</th><th>PDF</th><th>Saved</th><th className="tight"></th></tr></thead>
						<tbody>
							{saved.map((row) => (
								<tr key={row.id ?? row.title}>
									<td data-label="Letter">{row.title}{row.recipient ? <span className="quiet"> {row.recipient}</span> : null}</td>
									<td data-label="Template">{row.template || "—"}</td>
									<td data-label="DOCX"><div className="file-cell">{row.docx ? <button className="file-name" type="button" onClick={() => viewFile(row.docx, row.docx_data, "docx")}>{row.docx}</button> : <span className="quiet">No docx</span>}</div></td>
									<td data-label="PDF"><div className="file-cell">{row.pdf ? <button className="file-name" type="button" onClick={() => viewFile(row.pdf, row.pdf_data, "pdf")}>{row.pdf}</button> : <span className="quiet">No pdf</span>}</div></td>
									<td data-label="Saved"><span className="time">{letterStamp(row.saved_at)}</span></td>
									<td className="tight">
										<details className="select menu">
											<summary aria-label="Letter actions"><svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="12" cy="5" r="1.8" /><circle cx="12" cy="12" r="1.8" /><circle cx="12" cy="19" r="1.8" /></svg></summary>
											<div className="select-pop">
												<button className="select-item" type="button" onClick={() => { setEditing(null); setEditingSaved(row); form.setOpen(true); }}>Edit</button>
												<button className="select-item" type="button" onClick={() => { if (row.id != null) void removeSaved(row.id); }}>Remove</button>
											</div>
										</details>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)) : templates.length === 0 ? <Empty>No templates yet.</Empty> : (
				<div className="table-wrap">
					<table>
						<thead><tr><th>Template</th><th>Type</th><th>Updated</th><th className="tight"></th></tr></thead>
						<tbody>
							{templates.map((row) => (
								<tr key={row.id ?? row.title} className="letter-row" onClick={(event) => { if (event.target instanceof Element && event.target.closest("details, button, summary")) return; setPreparing(row); }}>
									<td data-label="Template">{row.title}</td>
									<td data-label="Type">{row.type ? <span className="pill">{row.type}</span> : "—"}</td>
									<td data-label="Updated"><span className="time">{letterStamp(row.updated_at)}</span></td>
									<td className="tight">
										<details className="select menu">
											<summary aria-label="Template actions"><svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="12" cy="5" r="1.8" /><circle cx="12" cy="12" r="1.8" /><circle cx="12" cy="19" r="1.8" /></svg></summary>
											<div className="select-pop">
												<button className="select-item" type="button" onClick={() => { setEditing(row); form.setOpen(true); }}>Edit</button>
												<button className="select-item" type="button" onClick={() => setPreparing(row)}>Prepare</button>
												<button className="select-item" type="button" onClick={() => { if (row.id != null) void removeTemplate(row.id); }}>Remove</button>
											</div>
										</details>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}
			<Modal open={form.open} title={savedTab ? (editingSaved ? "Edit letter" : "Save letter") : editing ? "Edit template" : "New template"} error={form.error} pending={form.pending} action={savedTab || editing ? "Save" : "Add"} onClose={() => { form.setOpen(false); setEditing(null); setEditingSaved(null); }} onSubmit={(fields) => void form.save(fields)}>
				<div key={editingSaved?.id ?? editing?.id ?? "new"}>
				<label htmlFor="letter-title">Title</label>
				<input id="letter-title" name="title" placeholder={savedTab ? "Letter of offer — name" : "Letter of offer"} defaultValue={editingSaved?.title ?? editing?.title ?? ""} required />
				{savedTab ? (
					<>
						<label htmlFor="letter-template">Template</label>
						<Select id="letter-template" name="template" defaultValue={editingSaved?.template ?? ""}>
							<option value="">No template</option>
							{templates.map((row) => <option key={row.id ?? row.title} value={row.title}>{row.title}</option>)}
						</Select>
						<label htmlFor="letter-recipient">Recipient</label>
						<input id="letter-recipient" name="recipient" placeholder="Name" defaultValue={editingSaved?.recipient ?? ""} />
						<label htmlFor="letter-docx">DOCX</label>
						<input id="letter-docx" name="docx" type="file" accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document" />
						{editingSaved?.docx ? <p className="hint">{editingSaved.docx}</p> : null}
						<label htmlFor="letter-pdf">PDF</label>
						<input id="letter-pdf" name="pdf" type="file" accept="application/pdf,.pdf" />
						{editingSaved?.pdf ? <p className="hint">{editingSaved.pdf}</p> : null}
					</>
				) : (
					<>
						<label htmlFor="letter-type">Type</label>
						<input id="letter-type" name="type" placeholder="HR, Board, Official" defaultValue={editing?.type ?? ""} />
						{editing ? null : (
							<>
								<label htmlFor="letter-body">Letter</label>
								<textarea id="letter-body" name="body" rows={8} placeholder="Write the template here. Use [brackets] for fill-in fields." />
							</>
						)}
					</>
				)}
				</div>
			</Modal>
			<dialog className="modal preview" open={viewing !== null} onClose={() => { if (viewing?.url) URL.revokeObjectURL(viewing.url); setViewing(null); }}>
				<div className="row tight"><h1>{viewing?.name || "File"}</h1></div>
				{viewing?.missing ? <p>This file was saved by name only. Edit the letter and upload it again to open it.</p> : null}
				{viewing && !viewing.missing && viewing.kind === "pdf" ? <iframe title={viewing.name} src={viewing.url} /> : null}
				{viewing && !viewing.missing && viewing.kind === "docx" ? <p><a className="btn" href={viewing.url} download={viewing.name}>Download</a></p> : null}
				<p className="actions"><button className="btn ghost" type="button" onClick={() => { if (viewing?.url) URL.revokeObjectURL(viewing.url); setViewing(null); }}>Close</button></p>
			</dialog>
			<DocumentDialog
				title={preparing?.title ?? "Document"}
				html={letterDocument(preparing)}
				open={preparing !== null}
				onClose={() => setPreparing(null)}
				onSave={preparing?.id ? async (html) => {
					await api.updateHubBook({
						list: "letterTemplates",
						id: preparing.id!,
						record: {
							title: preparing.title,
							type: preparing.type,
							updated_at: new Date().toISOString(),
							body: html,
						},
					});
					await client.invalidateQueries({ queryKey: queryKeys.hubRecords });
				} : undefined}
			/>
		</div>
	);
}

const DOC_KIND_LABEL: Record<string, string> = {
	agreements: "Agreements",
	"registration-initial": "Initial documents",
	"registration-additional": "Additional documents",
	"secretary-returns": "Annual returns",
	"secretary-resolutions": "Resolutions",
	"secretary-lodgements": "Lodgements",
};
const DOC_HINT: Record<string, string> = {
	"registration-initial": "First SSM papers from when the company was created.",
	"registration-additional": "Later filings such as a change of company name or transfer of shares.",
	"secretary-returns": "Yearly returns lodged with SSM.",
	"secretary-resolutions": "Board and member resolutions.",
	"secretary-lodgements": "Other filings lodged with SSM or kept by the secretary.",
};
const REGISTRATION_KINDS = ["registration-initial", "registration-additional"] as const;
const SECRETARY_KINDS = ["secretary-returns", "secretary-resolutions", "secretary-lodgements"] as const;
const DOC_SORTS = ["document", "type", "latest", "updated", "status"] as const;

function filterDocuments(rows: DocumentItem[], kind: string): DocumentItem[] {
	if (!kind) return rows;
	if (kind === "registration") return rows.filter((item) => item.kind === "registration-initial" || item.kind === "registration-additional");
	if (kind === "secretary") return rows.filter((item) => item.kind.startsWith("secretary-"));
	return rows.filter((item) => item.kind === kind);
}

function sortDocuments(rows: DocumentItem[], sort: string, dir: string): DocumentItem[] {
	const sign = dir === "desc" ? -1 : 1;
	return [...rows].sort((a, b) => {
		const av = a.versions[0];
		const bv = b.versions[0];
		let cmp = 0;
		if (sort === "type") cmp = a.subtype.localeCompare(b.subtype) || (DOC_KIND_LABEL[a.kind] ?? a.kind).localeCompare(DOC_KIND_LABEL[b.kind] ?? b.kind);
		else if (sort === "latest") cmp = (av?.n ?? 0) - (bv?.n ?? 0);
		else if (sort === "updated") cmp = (av?.uploaded_at ?? "").localeCompare(bv?.uploaded_at ?? "");
		else if (sort === "status") cmp = (a.draft ? "Draft" : "Current").localeCompare(b.draft ? "Draft" : "Current");
		else cmp = a.title.localeCompare(b.title);
		return (cmp || a.title.localeCompare(b.title)) * sign;
	});
}

function Documents({ books }: { books: Books }) {
	const [params, setParams] = useSearchParams();
	const kind = params.get("kind") ?? "";
	const sort = params.get("sort") ?? "";
	const dir = params.get("dir") === "desc" ? "desc" : "asc";
	const [openId, setOpenId] = useState("");
	const [addOpen, setAddOpen] = useState(false);
	const [upload, setUpload] = useState<DocumentItem | null>(null);
	const filtered = filterDocuments(books.documents, kind);
	const rows = sort && (DOC_SORTS as readonly string[]).includes(sort) ? sortDocuments(filtered, sort, dir) : filtered;
	const grouped = kind === "registration" ? REGISTRATION_KINDS : kind === "secretary" ? SECRETARY_KINDS : null;

	function setKind(value: string) {
		const next = new URLSearchParams(params);
		if (value) next.set("kind", value);
		else next.delete("kind");
		next.delete("sort");
		next.delete("dir");
		setParams(next);
		setOpenId("");
	}

	function setSort(column: string, direction: string) {
		const next = new URLSearchParams(params);
		next.set("sort", column);
		next.set("dir", direction);
		setParams(next);
	}

	function toggleRow(id: string, event: { currentTarget: HTMLTableRowElement; target: EventTarget }) {
		const target = event.target;
		if (!(target instanceof Element) || target.closest("a, button, details, summary")) return;
		setOpenId(openId === id ? "" : id);
	}

	return (
		<div>
			<Bar sub={`Agreements, company registration, and company secretary files. ${filtered.length} shown.`}>
				<button className="btn" type="button" onClick={() => setAddOpen(true)}>Add</button>
			</Bar>
			<div className="filters">
				<button type="button" className={kind ? "chip" : "chip active"} onClick={() => setKind("")}>All</button>
				<button type="button" className={kind === "agreements" ? "chip active" : "chip"} onClick={() => setKind("agreements")}>Agreements</button>
				<DocFilter label="Company Registration" active={kind === "registration" || kind.startsWith("registration-")} current={kind} items={REGISTRATION_KINDS} onPick={setKind} />
				<DocFilter label="Company Secretary" active={kind === "secretary" || kind.startsWith("secretary-")} current={kind} items={SECRETARY_KINDS} onPick={setKind} />
			</div>
			{DOC_HINT[kind] ? <p className="hint">{DOC_HINT[kind]}</p> : null}
			{grouped ? grouped.map((group) => {
				const groupRows = rows.filter((item) => item.kind === group);
				if (!groupRows.length) return null;
				return (
					<section className="doc-group" key={group}>
						<h2>{DOC_KIND_LABEL[group]}</h2>
						<p className="hint">{DOC_HINT[group]}</p>
						<DocTable rows={groupRows} openId={openId} onToggle={toggleRow} onOpen={setOpenId} onUpload={setUpload} />
					</section>
				);
			}) : rows.length === 0 ? <Empty>No documents in this type.</Empty> : (
				<DocTable rows={rows} sortable sort={sort} dir={dir} onSort={setSort} openId={openId} onToggle={toggleRow} onOpen={setOpenId} onUpload={setUpload} />
			)}
			<Modal open={addOpen} title="Add document" error="" pending={false} action="Add" onClose={() => setAddOpen(false)} onSubmit={() => setAddOpen(false)}>
				<p className="hint">Mock only. Nothing is stored yet.</p>
				<div>
					<label htmlFor="doc-kind">Type</label>
					<Select id="doc-kind" name="kind" defaultValue={kind && DOC_KIND_LABEL[kind] ? kind : "agreements"}>
						{Object.entries(DOC_KIND_LABEL).map(([value, name]) => <option key={value} value={value}>{name}</option>)}
					</Select>
				</div>
				<div>
					<label htmlFor="doc-subtype">Kind</label>
					<input id="doc-subtype" name="subtype" placeholder="Loan, NDA, Name change, Minutes" />
				</div>
				<div>
					<label htmlFor="doc-title">Title</label>
					<input id="doc-title" name="title" placeholder="Maybank revolving credit facility" required />
				</div>
				<div>
					<label htmlFor="doc-party">Party</label>
					<input id="doc-party" name="party" placeholder="Counterparty or issuer" />
				</div>
				<div>
					<label htmlFor="doc-file">File</label>
					<input id="doc-file" name="file" type="file" />
				</div>
				<div>
					<label htmlFor="doc-note">Note</label>
					<input id="doc-note" name="note" placeholder="What changed in this version" />
				</div>
			</Modal>
			<Modal open={upload !== null} title="Upload version" error="" pending={false} action="Upload version" onClose={() => setUpload(null)} onSubmit={() => setUpload(null)}>
				<p className="hint">Mock only. Nothing is stored yet.</p>
				{upload ? <div className="tag-row"><span>Document</span><span>{upload.title}</span></div> : null}
				<div>
					<label htmlFor="ver-file">File</label>
					<input id="ver-file" name="file" type="file" />
				</div>
				<div>
					<label htmlFor="ver-note">Note</label>
					<input id="ver-note" name="note" placeholder="What changed in this version" />
				</div>
			</Modal>
		</div>
	);
}

function DocFilter({ label, active, current, items, onPick }: { label: string; active: boolean; current: string; items: readonly string[]; onPick: (value: string) => void }) {
	return (
		<details
			className={active ? "select active" : "select"}
			onToggle={(event) => closeSiblingSelectMenus(event.currentTarget)}
		>
			<summary>
				<span>{label}</span>
				<ChevronDown size={12} aria-hidden="true" />
			</summary>
			<div className="select-pop">
				{items.map((value) => (
					<button type="button" key={value} className={current === value ? "select-item active" : "select-item"} onClick={(event) => { onPick(value); event.currentTarget.closest("details")?.removeAttribute("open"); }}>
						{DOC_KIND_LABEL[value] ?? value}
						{current === value ? <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5" /></svg> : null}
					</button>
				))}
			</div>
		</details>
	);
}

function DocTable({
	rows,
	sortable = false,
	sort = "",
	dir = "asc",
	onSort,
	openId,
	onToggle,
	onOpen,
	onUpload,
}: {
	rows: DocumentItem[];
	sortable?: boolean;
	sort?: string;
	dir?: string;
	onSort?: (column: string, direction: string) => void;
	openId: string;
	onToggle: (id: string, event: { currentTarget: HTMLTableRowElement; target: EventTarget }) => void;
	onOpen: (id: string) => void;
	onUpload: (item: DocumentItem) => void;
}) {
	return (
		<div className="table-wrap">
			<table className="doc-table">
				<thead>
					<tr>
						{sortable ? DOC_SORTS.map((column) => (
							<th key={column}>
								<span className="th-sort">{column === "document" ? "Document" : column === "type" ? "Type" : column === "latest" ? "Latest" : column === "updated" ? "Updated" : "Status"}
									<span className="th-arrows">
										<button type="button" className={sort === column && dir === "asc" ? "th-arrow active" : "th-arrow"} aria-label={`Sort ${column} up`} onClick={() => onSort?.(column, "asc")}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m18 15-6-6-6 6" /></svg></button>
										<button type="button" className={sort === column && dir === "desc" ? "th-arrow active" : "th-arrow"} aria-label={`Sort ${column} down`} onClick={() => onSort?.(column, "desc")}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m6 9 6 6 6-6" /></svg></button>
									</span>
								</span>
							</th>
						)) : (
							<>
								<th>Document</th><th>Type</th><th>Latest</th><th>Updated</th><th>Status</th>
							</>
						)}
						<th className="tight"></th>
					</tr>
				</thead>
				<tbody>
					{rows.map((item, index) => {
						const id = item.id || item.title || String(index);
						const latest = item.versions[0];
						return (
							<Fragment key={id}>
								<tr className={openId === id ? "lead-row is-open" : "lead-row"} onClick={(event) => onToggle(id, event)}>
									<td data-label="Document">{item.title}{item.party ? <span className="quiet"> {item.party}</span> : null}</td>
									<td data-label="Type"><span className="pill">{item.subtype}</span> {DOC_KIND_LABEL[item.kind] ?? item.kind}</td>
									<td data-label="Latest">{latest ? `v${latest.n}` : "—"}</td>
									<td data-label="Updated">{latest ? <span className="time">{stamp(latest.uploaded_at)}</span> : "—"}</td>
									<td data-label="Status">{item.draft ? "Draft" : "Current"}</td>
									<td className="tight" data-label="">
										<details className="select menu">
											<summary aria-label="Document actions">
												<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="12" cy="5" r="1.8" /><circle cx="12" cy="12" r="1.8" /><circle cx="12" cy="19" r="1.8" /></svg>
											</summary>
											<div className="select-pop">
												<button className="select-item" type="button" onClick={() => onOpen(openId === id ? "" : id)}>Versions</button>
												<button className="select-item" type="button" onClick={() => onUpload(item)}>New version</button>
											</div>
										</details>
									</td>
								</tr>
								<tr className="lead-detail">
									<td colSpan={6}>
										<div className="versions">
											{item.versions.map((version, versionIndex) => (
												<div className={versionIndex === 0 ? "version is-latest" : "version"} key={version.n}>
													<div className="version-main">
														<div className="version-title">
															v{version.n}
															<span className="pill">{versionIndex === 0 ? "Latest" : "Previous"}</span>
															{item.draft && versionIndex === 0 ? <span className="pill">Draft</span> : null}
														</div>
														<div className="version-meta">{stamp(version.uploaded_at)} · {version.uploaded_by} · {version.file}</div>
														<div className="version-meta">{version.note}</div>
													</div>
													<div className="actions">
														<button className="btn ghost" type="button" disabled>View</button>
														<button className="btn ghost" type="button" disabled>Download</button>
													</div>
												</div>
											))}
										</div>
									</td>
								</tr>
							</Fragment>
						);
					})}
				</tbody>
			</table>
		</div>
	);
}

const VEHICLE_FILES = [
	["grant", "Grant"],
	["insurance", "Insurance"],
	["roadtax", "Road tax"],
] as const;

function vehicleSummary(row: Vehicle): string {
	return [row.make, row.model, row.year, row.colour].filter(Boolean).join(" · ");
}

function latestVehicleFile(row: Vehicle, kind: string): VehicleFile | undefined {
	return row.files.find((file) => file.kind === kind);
}

function Vehicles({ books }: { books: Books }) {
	const rows = books.vehicles;
	const today = new Date().toISOString().slice(0, 10);
	const [editing, setEditing] = useState<Vehicle | null>(null);
	const [upload, setUpload] = useState<{ row: Vehicle; kind: string } | null>(null);
	const form = useHubSave(async (fields) => {
		const record = {
			plate: textOf(fields, "plate"),
			make: textOf(fields, "make"),
			model: textOf(fields, "model"),
			year: textOf(fields, "year"),
			colour: textOf(fields, "colour"),
			note: textOf(fields, "note"),
			created_at: editing?.created_at ?? new Date().toISOString(),
			files: editing?.files ?? [],
		};
		if (editing?.id) await api.updateHubBook({ list: "vehicles", id: editing.id, record });
		else await api.appendHubBook({ list: "vehicles", record });
		setEditing(null);
	});
	const fileForm = useHubSave(async (fields) => {
		if (!upload?.row.id) return;
		const picked = fields.get("file");
		const filename = picked instanceof File ? picked.name : "";
		const next: VehicleFile = {
			kind: upload.kind,
			filename,
			expires_at: textOf(fields, "expires"),
			note: textOf(fields, "note"),
			uploaded_by: "member",
			created_at: new Date().toISOString(),
		};
		await api.updateHubBook({
			list: "vehicles",
			id: upload.row.id,
			record: { ...upload.row, files: [next, ...upload.row.files.filter((file) => !(file.kind === upload.kind && file.filename === filename))] },
		});
		setUpload(null);
	});
	function openAdd() {
		setEditing(null);
		form.setOpen(true);
	}
	return (
		<div>
			<Bar sub={`${rows.length} ${rows.length === 1 ? "vehicle" : "vehicles"}`}>
				<button className="btn" type="button" onClick={openAdd}>Add</button>
			</Bar>
			{rows.length === 0 ? <Empty>No vehicles yet.</Empty> : (
				<div className="table-wrap">
					<table>
						<thead>
							<tr>
								<th className="tight"></th>
								<th>Vehicle information</th>
								<th>Grant</th>
								<th>Insurance</th>
								<th>Road tax</th>
								<th className="tight"></th>
							</tr>
						</thead>
						<tbody>
							{rows.map((row) => {
								const summary = vehicleSummary(row);
								return (
									<Fragment key={row.id ?? row.plate}>
										<tr className="lead-row" onClick={(event) => event.currentTarget.classList.toggle("is-open")}>
											<td className="tight"><details className="expand"><summary aria-label="Show details"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="m6 9 6 6 6-6" /></svg></summary></details></td>
											<td data-label="Vehicle information">{row.plate}{summary ? <span className="quiet"> {summary}</span> : null}</td>
											{VEHICLE_FILES.map(([kind]) => {
												const file = latestVehicleFile(row, kind);
												const expired = Boolean(file?.expires_at && file.expires_at < today);
												return (
													<td key={kind} data-label={VEHICLE_FILES.find(([value]) => value === kind)?.[1]}>
														<div className="file-cell">
															{file ? <span className="file-name">{file.filename}</span> : <span className="quiet">No PDF</span>}
															{file?.expires_at ? <span className={expired ? "expired" : "quiet"}>{expired ? "Expired" : "Expires"} {file.expires_at}</span> : null}
															{row.id ? <button className="quiet" type="button" onClick={(event) => { event.stopPropagation(); setUpload({ row, kind }); fileForm.setOpen(true); }}>{file ? "Replace" : "Upload"}</button> : null}
														</div>
													</td>
												);
											})}
											<td className="tight" onClick={(event) => event.stopPropagation()}>
												<details className="select menu">
													<summary aria-label="Actions"><svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="12" cy="5" r="1.8" /><circle cx="12" cy="12" r="1.8" /><circle cx="12" cy="19" r="1.8" /></svg></summary>
													<div className="select-pop">
														<button className="select-item" type="button" onClick={() => { setEditing(row); form.setOpen(true); }}>Edit</button>
														{VEHICLE_FILES.map(([kind, name]) => (
															<button key={kind} className="select-item" type="button" onClick={() => { setUpload({ row, kind }); fileForm.setOpen(true); }}>Upload {name.toLowerCase()}</button>
														))}
													</div>
												</details>
											</td>
										</tr>
										<tr className="lead-detail">
											<td colSpan={6}>
												<div className="lead-detail-inner"><span>Note</span>{row.note || "—"}<span>Added</span>{row.created_at ? stamp(row.created_at) : "—"}</div>
												<div className="versions">
													{VEHICLE_FILES.map(([kind, name]) => {
														const files = row.files.filter((file) => file.kind === kind);
														if (files.length === 0) {
															return (
																<div className="version" key={kind}>
																	<div className="version-main"><div className="version-title">{name}</div><div className="version-meta">No PDF yet.</div></div>
																</div>
															);
														}
														return files.map((file, index) => {
															const expired = Boolean(file.expires_at && file.expires_at < today);
															return (
																<div className={index === 0 ? "version is-latest" : "version"} key={`${kind}-${file.filename}-${index}`}>
																	<div className="version-main">
																		<div className="version-title">{name} <span className="pill">{index === 0 ? "Latest" : "Previous"}</span>{file.expires_at ? <span className={expired ? "pill expired" : "pill"}>{expired ? "Expired" : file.expires_at}</span> : null}</div>
																		<div className="version-meta">{file.filename}{file.created_at ? ` · ${stamp(file.created_at)}` : ""}{file.uploaded_by ? ` · ${file.uploaded_by}` : ""}</div>
																		{file.note ? <div className="version-meta">{file.note}</div> : null}
																	</div>
																</div>
															);
														});
													})}
												</div>
											</td>
										</tr>
									</Fragment>
								);
							})}
						</tbody>
					</table>
				</div>
			)}
			<Modal open={form.open} title={editing ? "Edit vehicle" : "Add vehicle"} error={form.error} pending={form.pending} onClose={() => { form.setOpen(false); setEditing(null); }} onSubmit={(fields) => void form.save(fields)}>
				<div key={editing?.id ?? "new"}>
				<label htmlFor="veh-plate">Plate</label>
				<input id="veh-plate" name="plate" placeholder="WXY 1234" defaultValue={editing?.plate ?? ""} required />
				<div className="grid two">
					<div><label htmlFor="veh-make">Make</label><input id="veh-make" name="make" placeholder="Perodua" defaultValue={editing?.make ?? ""} /></div>
					<div><label htmlFor="veh-model">Model</label><input id="veh-model" name="model" placeholder="Bezza" defaultValue={editing?.model ?? ""} /></div>
				</div>
				<div className="grid two">
					<div><label htmlFor="veh-year">Year</label><input id="veh-year" name="year" inputMode="numeric" placeholder="2022" defaultValue={editing?.year ?? ""} /></div>
					<div><label htmlFor="veh-colour">Colour</label><input id="veh-colour" name="colour" placeholder="White" defaultValue={editing?.colour ?? ""} /></div>
				</div>
				<label htmlFor="veh-note">Note</label>
				<textarea id="veh-note" name="note" rows={3} placeholder="Optional" defaultValue={editing?.note ?? ""} />
				</div>
			</Modal>
			<Modal open={fileForm.open} title={`Upload ${VEHICLE_FILES.find(([value]) => value === upload?.kind)?.[1] ?? "file"}`} error={fileForm.error} pending={fileForm.pending} onClose={() => { fileForm.setOpen(false); setUpload(null); }} onSubmit={(fields) => void fileForm.save(fields)}>
				<p className="hint">{upload?.row.plate} · PDF up to 1.8 MB.</p>
				<label htmlFor="veh-file">PDF</label>
				<input id="veh-file" name="file" type="file" accept="application/pdf,.pdf" required />
				{upload?.kind === "insurance" || upload?.kind === "roadtax" ? (
					<>
						<label htmlFor="veh-expires">Expiry</label>
						<input id="veh-expires" name="expires" type="date" />
					</>
				) : null}
				<label htmlFor="veh-file-note">Note</label>
				<textarea id="veh-file-note" name="note" rows={2} placeholder="Optional" />
			</Modal>
		</div>
	);
}

type PnlLine = { label: string; amount: number };

function profitAndLoss(books: Books, from: string, to: string) {
	const accounts = Array.isArray(books.accounts) ? books.accounts : [];
	const journals = (Array.isArray(books.journals) ? books.journals : []).filter((row) => {
		if (from && row.day < from) return false;
		if (to && row.day > to) return false;
		return true;
	});
	const totals = new Map<string, { debit: number; credit: number }>();
	for (const journal of journals) {
		for (const line of journal.lines ?? []) {
			const current = totals.get(line.account) ?? { debit: 0, credit: 0 };
			current.debit += Number(line.debit) || 0;
			current.credit += Number(line.credit) || 0;
			totals.set(line.account, current);
		}
	}
	const bucket = (account: Account): "revenue" | "other" | "cost" | "operating" | "finance" | "tax" => {
		const name = `${account.code} ${account.name}`.toLowerCase();
		if (account.type === "income") return /other income|interest income|finance income/.test(name) ? "other" : "revenue";
		if (/cost of sales|cost of goods|cogs|purchases|direct cost/.test(name)) return "cost";
		if (/interest|finance cost|bank charge/.test(name)) return "finance";
		if (/\btax\b|taxation/.test(name)) return "tax";
		return "operating";
	};
	const linesFor = (kind: "revenue" | "other" | "cost" | "operating" | "finance" | "tax"): PnlLine[] => {
		const rows = accounts.flatMap((account) => {
			if (account.type !== "income" && account.type !== "expense") return [];
			if (bucket(account) !== kind) return [];
			const row = totals.get(account.id) ?? { debit: 0, credit: 0 };
			const amount = account.type === "expense" ? row.debit - row.credit : row.credit - row.debit;
			if (amount === 0) return [];
			return [{ label: `${account.code} ${account.name}`.trim(), amount }];
		});
		const known = new Set(accounts.map((account) => account.id));
		if (kind === "revenue" || kind === "operating") {
			for (const [id, row] of totals) {
				if (known.has(id)) continue;
				const net = row.credit - row.debit;
				if (net === 0) continue;
				if (kind === "revenue" && net > 0) rows.push({ label: id || "Uncoded", amount: net });
				if (kind === "operating" && net < 0) rows.push({ label: id || "Uncoded", amount: -net });
			}
		}
		return rows.sort((a, b) => a.label.localeCompare(b.label));
	};
	const revenue = linesFor("revenue");
	const cost = linesFor("cost");
	const other = linesFor("other");
	const operating = linesFor("operating");
	const finance = linesFor("finance");
	const tax = linesFor("tax");
	const sum = (rows: PnlLine[]) => rows.reduce((total, row) => total + row.amount, 0);
	const totalRevenue = sum(revenue);
	const totalCost = sum(cost);
	const gross = totalRevenue - totalCost;
	const totalOther = sum(other);
	const totalOperating = sum(operating);
	const operatingProfit = gross + totalOther - totalOperating;
	const totalFinance = sum(finance);
	const beforeTax = operatingProfit - totalFinance;
	const totalTax = sum(tax);
	return {
		revenue,
		cost,
		other,
		operating,
		finance,
		tax,
		totalRevenue,
		totalCost,
		gross,
		totalOther,
		totalOperating,
		operatingProfit,
		totalFinance,
		beforeTax,
		totalTax,
		profit: beforeTax - totalTax,
	};
}

function StatementBlock({ title, lines, totalLabel, total }: { title: string; lines: PnlLine[]; totalLabel: string; total: number }) {
	return (
		<>
			<tr className="section"><td colSpan={2}>{title}</td></tr>
			{lines.length === 0 ? (
				<tr><td className="quiet">None</td><td className="num quiet">—</td></tr>
			) : lines.map((line) => (
				<tr key={line.label}><td>{line.label}</td><td className="num">{money(line.amount)}</td></tr>
			))}
			<tr className="total"><td>{totalLabel}</td><td className="num">{money(total)}</td></tr>
		</>
	);
}

type PnlReport = ReturnType<typeof profitAndLoss>;
type PnlSnapshot = PnlReport & { kind: "pnl"; from: string; to: string; period: string };

function pnlSnapshot(row: AuditFile): PnlSnapshot | null {
	if (!row.stamp) return null;
	try {
		const parsed = JSON.parse(row.stamp) as PnlSnapshot;
		if (parsed?.kind !== "pnl" || !Array.isArray(parsed.revenue)) return null;
		return parsed;
	} catch {
		return null;
	}
}

function auditYearFor(books: Books, from: string, to: string): AuditYear | null {
	const years = (Array.isArray(books.auditYears) ? books.auditYears : []).filter((row) => row?.start && row.end && row.id != null);
	if (from && to) return years.find((year) => year.start <= from && year.end >= to) ?? null;
	if (to) return years.find((year) => year.start <= to && year.end >= to) ?? null;
	if (from) return years.find((year) => year.start <= from && year.end >= from) ?? null;
	return [...years].sort((a, b) => b.end.localeCompare(a.end))[0] ?? null;
}

function ProfitLoss({ books, snapshot, onClose }: { books: Books; snapshot?: PnlSnapshot | null; onClose: () => void }) {
	const ref = useRef<HTMLDialogElement>(null);
	const client = useQueryClient();
	const [from, setFrom] = useState(snapshot?.from ?? "");
	const [to, setTo] = useState(snapshot?.to ?? "");
	const [note, setNote] = useState("");
	const [pending, setPending] = useState(false);
	const live = profitAndLoss(books, from, to);
	const report = snapshot ?? live;
	const period = snapshot?.period ?? (from || to ? `${from || "Start"} – ${to || "Today"}` : "All activity");
	useEffect(() => {
		const dialog = ref.current;
		if (!dialog) return;
		if (!dialog.open) dialog.showModal();
	}, []);
	const downloadPdf = () => {
		const binary = atob(pnlPdf(report, period));
		const bytes = new Uint8Array(binary.length);
		for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
		downloadFile("Profit & Loss.pdf", new Blob([bytes], { type: "application/pdf" }));
	};
	const save = async () => {
		setNote("");
		const year = auditYearFor(books, from, to);
		if (year?.id == null) {
			setNote(from || to ? "This period is not in an audit year." : "Add an audit year before saving this report.");
			return;
		}
		setPending(true);
		const stored: PnlSnapshot = { kind: "pnl", from, to, period, ...live };
		const record = {
			yearId: String(year.id),
			folder: "management",
			name: "Profit & Loss.pdf",
			type: "application/pdf",
			data: pnlPdf(live, period),
			stamp: JSON.stringify(stored),
		};
		const existing = (Array.isArray(books.auditFiles) ? books.auditFiles : []).find((row) => row.yearId === String(year.id) && row.folder === "management" && row.name === record.name);
		try {
			if (existing?.id != null) await api.updateHubBook({ list: "auditFiles", id: existing.id, record });
			else await api.appendHubBook({ list: "auditFiles", record });
			await client.invalidateQueries({ queryKey: queryKeys.hubRecords });
			setNote("Saved to Management Accounts.");
		} catch (err) {
			setNote(presentError(err).title);
		} finally {
			setPending(false);
		}
	};
	return (
		<dialog ref={ref} className="modal report" onClose={onClose}>
			<div className="sheet">
				<div className="row">
					<div>
						<h1>Profit & Loss</h1>
						<p className="sub">{period}</p>
					</div>
					<div className="actions">
						{snapshot ? null : <button className="btn" type="button" disabled={pending} onClick={() => void save()}>Save</button>}
						<button className="btn ghost icon" type="button" aria-label="PDF" onClick={downloadPdf}>
							<svg viewBox="0 0 24 24" aria-hidden="true">
								<path d="M7 3h7l5 5v13H7z" fill="none" stroke="currentColor" strokeWidth="1.6" />
								<path d="M14 3v5h5" fill="none" stroke="currentColor" strokeWidth="1.6" />
								<path d="M8.2 18.2v-4.2h1.15c.85 0 1.35.45 1.35 1.15s-.5 1.15-1.35 1.15H9.1v1.9H8.2zm.9-2.55h.25c.4 0 .6-.18.6-.5s-.2-.5-.6-.5H9.1v1zm2.15 2.55v-4.2h1.7v.75h-.8v.85h.7v.75h-.7v1.1h.85v.75h-1.75zm3.05 0v-4.2H17v.75h-1.05v.8h.95v.7h-.95v1.2h1.1v.75h-2z" fill="currentColor" />
							</svg>
						</button>
						<button className="btn ghost" type="button" onClick={onClose}>Close</button>
					</div>
				</div>
				{note ? <p className={note.startsWith("Saved") ? "quiet" : "err"}>{note}</p> : null}
				{snapshot ? null : (
				<div className="grid two">
					<div>
						<label htmlFor="pnl-from">From</label>
						<input id="pnl-from" type="date" value={from} onChange={(event) => setFrom(event.target.value)} />
					</div>
					<div>
						<label htmlFor="pnl-to">To</label>
						<input id="pnl-to" type="date" value={to} onChange={(event) => setTo(event.target.value)} />
					</div>
				</div>
				)}
				<div className="table-wrap">
					<table className="statement">
						<thead>
							<tr><th>Account</th><th className="num">Amount</th></tr>
						</thead>
						<tbody>
							<StatementBlock title="Revenue" lines={report.revenue} totalLabel="Total revenue" total={report.totalRevenue} />
							<StatementBlock title="Cost of sales" lines={report.cost} totalLabel="Total cost of sales" total={report.totalCost} />
							<tr className="result"><td>Gross profit</td><td className="num">{money(report.gross)}</td></tr>
							<StatementBlock title="Other income" lines={report.other} totalLabel="Total other income" total={report.totalOther} />
							<StatementBlock title="Operating expenses" lines={report.operating} totalLabel="Total operating expenses" total={report.totalOperating} />
							<tr className="result"><td>Operating profit</td><td className="num">{money(report.operatingProfit)}</td></tr>
							<StatementBlock title="Finance costs" lines={report.finance} totalLabel="Total finance costs" total={report.totalFinance} />
							<tr className="result"><td>Profit before tax</td><td className="num">{money(report.beforeTax)}</td></tr>
							<StatementBlock title="Tax" lines={report.tax} totalLabel="Tax expense" total={report.totalTax} />
							<tr className="result"><td>Profit for the period</td><td className="num">{money(report.profit)}</td></tr>
						</tbody>
					</table>
				</div>
			</div>
		</dialog>
	);
}

function Reports({ data }: { data: HubRecordsDto }) {
	const [pnl, setPnl] = useState(false);
	const books = booksOf(data);
	const sections: Array<[string, string, string[]]> = [
		["Sales", "Leads, quotations, sales orders, invoices, and collections.", ["Leads by source", "Leads by site", "Conversion", "Quotations", "Sales orders", "Invoices", "Receipts", "Sales by item", "Outstanding invoices"]],
		["Purchases", "Spend, vendors, and bills to pay.", ["Expenses", "Bills", "Purchase orders", "Credits", "Spend by vendor", "Outstanding bills"]],
		["Operations", "Work, stock, and jobs on the floor.", ["Work", "Work by status", "Inventory", "Consumables", "Stock movement", "Register", "Sites", "Projects"]],
		["Finance", "Books and statements for this workspace.", ["Profit & Loss", "Balance sheet", "General ledger", "Trial balance", "Cash flow", "Tax", "Audit", "Asset"]],
	];
	const ready = new Set(["Leads by source", "Leads by site", "Expenses"]);
	return (
		<div>
			<Bar sub="Sales, purchases, operations, and finance." />
			<div className="report-grid">
				{sections.map(([title, sub, items]) => (
					<section className="panel" key={title}>
						<h2>{title}</h2>
						<p className="sub">{sub}</p>
						{items.map((item) => item === "Profit & Loss" ? (
							<button className="tag-row" type="button" key={item} onClick={() => setPnl(true)}>
								<span>Profit & Loss</span>
								<span className="quiet">View</span>
							</button>
						) : (
							<div className="tag-row" key={item}>
								<span>{item}</span>
								<span className="quiet">{item === "Leads by source" ? String(data.leads.length) : item === "Expenses" ? String(data.expenses.length) : item === "Leads by site" ? String(new Set(data.leads.map((lead) => lead.site)).size) : ready.has(item) ? "0" : "Soon"}</span>
							</div>
						))}
					</section>
				))}
			</div>
			{pnl ? <ProfitLoss books={books} onClose={() => setPnl(false)} /> : null}
		</div>
	);
}

function Activity({ data }: { data: HubRecordsDto }) {
	return (
		<div>
			<Bar sub={data.activity.length ? `1–${Math.min(data.activity.length, 100)} of ${data.activity.length}` : "0 records"} />
			{data.activity.length === 0 ? <Empty>No activity yet.</Empty> : (
				<Grid
					columns={["Time", "User", "Workspace", "Action", "Detail"]}
					rows={data.activity.slice(0, 100).map((row) => [when(row.createdAt), row.username, row.brand || "—", ACTIVITY_LABEL[row.action] ?? row.action, row.detail])}
				/>
			)}
		</div>
	);
}

function nextAssetNumber(rows: Array<{ number: string }>): string {
	let max = 0;
	for (const row of rows) {
		const number = Number(row.number.replace(/^FA-/, ""));
		if (Number.isFinite(number)) max = Math.max(max, number);
	}
	return `FA-${String(max + 1).padStart(4, "0")}`;
}

function nextRegisterNumber(rows: Array<{ number?: string }>): string {
	let max = 0;
	for (const row of rows) {
		const number = Number((row.number ?? "").replace(/^IA-/, ""));
		if (Number.isFinite(number)) max = Math.max(max, number);
	}
	return `IA-${String(max + 1).padStart(3, "0")}`;
}

function depreciationOf(row: FixedAsset, asOf = new Date()): { thisYear: number; accumulated: number; book: number } {
	const cost = Number(row.cost) || 0;
	const residual = Math.min(Number(row.residual) || 0, cost);
	const rate = Number(row.rate) || 0;
	const depreciable = Math.max(0, cost - residual);
	const start = /^\d{4}-\d{2}-\d{2}$/.test(row.purchased) ? new Date(`${row.purchased}T00:00:00`) : null;
	if (row.status !== "registered" || row.method === "none" || rate <= 0 || !start || Number.isNaN(start.getTime())) {
		return { thisYear: 0, accumulated: 0, book: cost };
	}
	let years = asOf.getFullYear() - start.getFullYear();
	if (asOf.getMonth() < start.getMonth() || (asOf.getMonth() === start.getMonth() && asOf.getDate() < start.getDate())) years -= 1;
	const periods = Math.max(0, years) + 1;
	if (row.method === "straight") {
		const annual = depreciable * (rate / 100);
		const prior = Math.min(depreciable, annual * Math.max(0, periods - 1));
		const thisYear = Math.min(annual, depreciable - prior);
		const accumulated = prior + thisYear;
		return { thisYear, accumulated, book: cost - accumulated };
	}
	let book = cost;
	let thisYear = 0;
	let accumulated = 0;
	for (let period = 0; period < periods; period += 1) {
		thisYear = Math.min(Math.max(0, book - residual) * (rate / 100), Math.max(0, book - residual));
		book -= thisYear;
		accumulated += thisYear;
	}
	return { thisYear, accumulated, book };
}

const SCHEDULE_HEAD = ["Number", "Category", "Description", "Year", "Rate", "Cost b/f", "Addition", "Cost c/f", "Accum. b/f", "Depreciation", "Accum. c/f", "Net book value"];

function cents(value: number): number {
	return Math.round(value * 100) / 100;
}

function depreciationSchedule(row: FixedAsset, count: number) {
	const cost = cents(Number(row.cost) || 0);
	const residual = cents(Math.min(Number(row.residual) || 0, cost));
	const rate = Number(row.rate) || 0;
	const start = /^\d{4}/.test(row.purchased) ? Number(row.purchased.slice(0, 4)) : new Date().getFullYear();
	const month = /^\d{4}-\d{2}-\d{2}$/.test(row.purchased) ? Number(row.purchased.slice(5, 7)) : 1;
	const firstFraction = month >= 1 && month <= 12 ? (12 - month) / 12 : 1;
	const lines = [];
	let accum = 0;
	let book = cost;
	for (let index = 0; index < count; index += 1) {
		const room = Math.max(0, cents(book - residual));
		let charge = 0;
		if (row.status === "registered" && row.method !== "none" && rate > 0 && room > 0) {
			const base = row.method === "diminishing" ? room : Math.max(0, cost - residual);
			const fraction = index === 0 ? firstFraction : 1;
			charge = cents(Math.min(room, base * (rate / 100) * fraction));
		}
		const closingAccum = cents(accum + charge);
		const closingNbv = cents(cost - closingAccum);
		lines.push({
			number: index === 0 ? row.number : "",
			category: index === 0 ? (row.assetType || "—") : "",
			name: index === 0 ? row.name : "",
			year: start + index,
			rate: index === 0 && rate > 0 ? `${rate}%` : index === 0 ? "—" : "",
			costBf: index === 0 ? 0 : cost,
			addition: index === 0 ? cost : 0,
			costCf: cost,
			accumBf: accum,
			charge,
			accumCf: closingAccum,
			nbv: closingNbv,
		});
		accum = closingAccum;
		book = closingNbv;
	}
	return lines;
}

function scheduleCells(line: ReturnType<typeof depreciationSchedule>[number]): string[] {
	const blank = (value: number, hide: boolean) => hide || value === 0 ? "" : money(value);
	return [
		line.number,
		line.category,
		line.name,
		String(line.year),
		line.rate,
		blank(line.costBf, line.number !== ""),
		line.addition ? money(line.addition) : "",
		money(line.costCf),
		line.accumBf ? money(line.accumBf) : "",
		line.charge ? money(line.charge) : "",
		line.accumCf ? money(line.accumCf) : "",
		money(line.nbv),
	];
}

function downloadFile(name: string, blob: Blob) {
	const url = URL.createObjectURL(blob);
	const link = document.createElement("a");
	link.href = url;
	link.download = name;
	link.click();
	URL.revokeObjectURL(url);
}

function xmlCell(value: string) {
	return value.replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;");
}

function exportDepreciationExcel(rows: FixedAsset[], years: number) {
	const sheets = Array.from({ length: years }, (_, index) => {
		const header = SCHEDULE_HEAD.map((cell) => `<Cell><Data ss:Type="String">${xmlCell(cell)}</Data></Cell>`).join("");
		const body = rows.map((row) => {
			const line = depreciationSchedule(row, years)[index];
			if (!line) return "";
			const cells = scheduleCells(line).map((cell) => `<Cell><Data ss:Type="String">${xmlCell(cell)}</Data></Cell>`).join("");
			return `<Row>${cells}</Row>`;
		}).join("");
		return `<Worksheet ss:Name="${index + 1}"><Table><Row>${header}</Row>${body}</Table></Worksheet>`;
	}).join("");
	const xml = `<?xml version="1.0"?><?mso-application progid="Excel.Sheet"?><Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet" xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">${sheets}</Workbook>`;
	downloadFile("depreciation.xls", new Blob([xml], { type: "application/vnd.ms-excel" }));
}

function pdfSafe(value: string) {
	return value.replace(/[^\x20-\x7E]/g, "?").replaceAll("\\", "\\\\").replaceAll("(", "\\(").replaceAll(")", "\\)");
}

function pnlPdf(report: PnlReport, period: string): string {
	const lines = [
		period,
		"Revenue",
		...report.revenue.map((line) => `${line.label}  ${money(line.amount)}`),
		`Total revenue  ${money(report.totalRevenue)}`,
		"Cost of sales",
		...report.cost.map((line) => `${line.label}  ${money(line.amount)}`),
		`Total cost of sales  ${money(report.totalCost)}`,
		`Gross profit  ${money(report.gross)}`,
		"Other income",
		...report.other.map((line) => `${line.label}  ${money(line.amount)}`),
		`Total other income  ${money(report.totalOther)}`,
		"Operating expenses",
		...report.operating.map((line) => `${line.label}  ${money(line.amount)}`),
		`Total operating expenses  ${money(report.totalOperating)}`,
		`Operating profit  ${money(report.operatingProfit)}`,
		"Finance costs",
		...report.finance.map((line) => `${line.label}  ${money(line.amount)}`),
		`Total finance costs  ${money(report.totalFinance)}`,
		`Profit before tax  ${money(report.beforeTax)}`,
		"Tax",
		...report.tax.map((line) => `${line.label}  ${money(line.amount)}`),
		`Tax expense  ${money(report.totalTax)}`,
		`Profit for the period  ${money(report.profit)}`,
	];
	const pages: string[] = [];
	let body: string[] = [];
	let y = 0;
	const startPage = () => {
		body = [`BT /F1 14 Tf 36 800 Td (${pdfSafe("Profit & Loss")}) Tj ET`];
		y = 778;
	};
	startPage();
	for (const line of lines) {
		if (y < 48) {
			pages.push(body.join("\n"));
			startPage();
		}
		body.push(`BT /F1 9 Tf 36 ${y} Td (${pdfSafe(line)}) Tj ET`);
		y -= 14;
	}
	pages.push(body.join("\n"));
	let text = "";
	const offsets: number[] = [0];
	const add = (content: string) => {
		offsets.push(text.length);
		text += `${offsets.length - 1} 0 obj\n${content}\nendobj\n`;
	};
	add("<< /Type /Catalog /Pages 2 0 R >>");
	add(`<< /Type /Pages /Count ${pages.length} /Kids [${pages.map((_, index) => `${index + 4} 0 R`).join(" ")}] >>`);
	add("<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>");
	pages.forEach((_page, index) => {
		const contentId = 4 + pages.length + index;
		add(`<< /Type /Page /Parent 2 0 R /MediaBox [0 0 595 842] /Contents ${contentId} 0 R /Resources << /Font << /F1 3 0 R >> >> >>`);
	});
	pages.forEach((page) => add(`<< /Length ${page.length} >>\nstream\n${page}\nendstream`));
	const header = "%PDF-1.4\n";
	let file = header + text;
	const xref = file.length;
	file += `xref\n0 ${offsets.length}\n0000000000 65535 f \n`;
	for (const offset of offsets.slice(1)) file += `${String(header.length + offset).padStart(10, "0")} 00000 n \n`;
	file += `trailer\n<< /Size ${offsets.length} /Root 1 0 R >>\nstartxref\n${header.length + xref}\n%%EOF`;
	return btoa(file);
}

function exportDepreciationPdf(rows: FixedAsset[], years: number) {
	const pages: string[] = [];
	for (let index = 0; index < years; index += 1) {
		const lines = [
			`BT /F1 14 Tf 36 560 Td (${pdfSafe(`Year ${index + 1}`)}) Tj ET`,
			`BT /F1 7 Tf 36 540 Td (${pdfSafe(SCHEDULE_HEAD.join(" | "))}) Tj ET`,
		];
		let y = 524;
		for (const row of rows) {
			const line = depreciationSchedule(row, years)[index];
			if (!line) continue;
			lines.push(`BT /F1 7 Tf 36 ${y} Td (${pdfSafe(scheduleCells(line).join(" | "))}) Tj ET`);
			y -= 12;
		}
		pages.push(lines.join("\n"));
	}
	if (pages.length === 0) pages.push("BT /F1 12 Tf 36 560 Td (No depreciation yet.) Tj ET");
	let body = "";
	const objects: string[] = [];
	const offsets: number[] = [0];
	function add(content: string) {
		offsets.push(body.length);
		body += `${objects.length + 1} 0 obj\n${content}\nendobj\n`;
		objects.push(content);
	}
	add("<< /Type /Catalog /Pages 2 0 R >>");
	add(`<< /Type /Pages /Count ${pages.length} /Kids [${pages.map((_, index) => `${index + 4} 0 R`).join(" ")}] >>`);
	add("<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>");
	pages.forEach((_page, index) => {
		const contentId = 4 + pages.length + index;
		add(`<< /Type /Page /Parent 2 0 R /MediaBox [0 0 842 595] /Contents ${contentId} 0 R /Resources << /Font << /F1 3 0 R >> >> >>`);
	});
	pages.forEach((page) => {
		add(`<< /Length ${page.length} >>\nstream\n${page}\nendstream`);
	});
	const header = "%PDF-1.4\n";
	let file = header + body;
	const xref = file.length;
	file += `xref\n0 ${offsets.length}\n0000000000 65535 f \n`;
	for (const offset of offsets.slice(1)) file += `${String(header.length + offset).padStart(10, "0")} 00000 n \n`;
	file += `trailer\n<< /Size ${offsets.length} /Root 1 0 R >>\nstartxref\n${header.length + xref}\n%%EOF`;
	downloadFile("depreciation.pdf", new Blob([file], { type: "application/pdf" }));
}

function Assets({ books }: { books: Books }) {
	const rows = books.assets;
	const client = useQueryClient();
	const [status, setStatus] = useState("");
	const [editing, setEditing] = useState<FixedAsset | null>(null);
	const [addingYear, setAddingYear] = useState(false);
	const registered = rows.filter((row) => row.status === "registered");
	const opened = registered.reduce((max, row) => Math.max(max, row.openedYears || 1), 1);
	const [years, setYears] = useState(opened);
	const [numberDir, setNumberDir] = useState<"" | "asc" | "desc">("");
	const [hiddenCategories, setHiddenCategories] = useState<string[]>([]);
	const [draftHidden, setDraftHidden] = useState<string[]>([]);
	const [categoryQuery, setCategoryQuery] = useState("");
	useEffect(() => { setYears(opened); }, [opened]);
	const visible = status === "draft" || status === "registered" ? rows.filter((row) => row.status === status) : rows;
	const form = useHubSave(async (fields) => {
		const detail = {
			name: textOf(fields, "name"),
			purchased: textOf(fields, "purchased"),
			cost: textOf(fields, "cost"),
			assetType: textOf(fields, "assetType"),
			description: textOf(fields, "description"),
			serial: textOf(fields, "serial"),
			warranty: textOf(fields, "warranty"),
			method: textOf(fields, "method") || "straight",
			rate: textOf(fields, "rate"),
			averaging: textOf(fields, "averaging") || "full-month",
			residual: textOf(fields, "residual"),
		};
		if (editing?.id) {
			await api.updateHubBook({
				list: "assets",
				id: editing.id,
				record: { ...editing, ...detail, number: editing.number, status: editing.status },
			});
			for (const item of books.register) {
				const linked = (item.financeNumber && item.financeNumber === editing.number) || (item.assetId !== "" && item.assetId === String(editing.id));
				if (linked && item.id) {
					await api.updateHubBook({
						list: "register",
						id: item.id,
						record: { ...item, name: detail.name, assetType: detail.assetType, purchased: detail.purchased, cost: detail.cost, serial: detail.serial, warranty: detail.warranty, description: detail.description },
					});
				}
			}
		} else {
			await api.appendHubBook({
				list: "assets",
				record: { number: nextAssetNumber(books.assets), ...detail, status: "draft" },
			});
		}
		setEditing(null);
	});
	async function remove(row: FixedAsset) {
		if (!row.id || !window.confirm("Remove this asset?")) return;
		await api.deleteHubBook({ list: "assets", id: row.id });
		for (const item of books.register) {
			const linked = (item.financeNumber && item.financeNumber === row.number) || (item.assetId !== "" && item.assetId === String(row.id));
			if (linked && item.id) await api.deleteHubBook({ list: "register", id: item.id });
		}
		await client.invalidateQueries({ queryKey: queryKeys.hubRecords });
	}
	async function addYear() {
		const next = years + 1;
		setYears(next);
		setAddingYear(true);
		try {
			await Promise.all(registered.flatMap((row) => row.id ? [api.updateHubBook({ list: "assets", id: row.id, record: { ...row, openedYears: next } })] : []));
			await client.invalidateQueries({ queryKey: queryKeys.hubRecords });
		} finally {
			setAddingYear(false);
		}
	}
	const categories = [...new Set(registered.map((row) => row.assetType || "—"))].sort((a, b) => a.localeCompare(b));
	const shownAssets = registered
		.filter((row) => !hiddenCategories.includes(row.assetType || "—"))
		.sort((a, b) => {
			if (!numberDir) return 0;
			const order = a.number.localeCompare(b.number, undefined, { numeric: true });
			return numberDir === "asc" ? order : -order;
		});
	function toggleDraft(value: string) {
		setDraftHidden((current) => current.includes(value) ? current.filter((item) => item !== value) : [...current, value]);
	}
	const register = useHubSave(async (fields) => {
		const id = Number(textOf(fields, "id"));
		const row = books.assets.find((item) => item.id === id);
		if (!row?.id) return;
		await api.updateHubBook({ list: "assets", id: row.id, record: { ...row, status: "registered" } });
	});
	return (
		<div>
			<Bar sub={status === "depreciation" ? `${registered.length} registered` : `${visible.length} ${visible.length === 1 ? "asset" : "assets"}${status ? " · filtered" : ""}`}>
				{status === "depreciation" ? <button className="btn ghost" type="button" onClick={() => exportDepreciationPdf(registered, years)}>PDF</button> : null}
				{status === "depreciation" ? <button className="btn ghost" type="button" onClick={() => exportDepreciationExcel(registered, years)}>Excel</button> : null}
				{status === "depreciation" ? <button className="btn ghost" type="button" disabled={addingYear || registered.length === 0} onClick={() => void addYear()}>Add year</button> : null}
				<button className="btn" type="button" onClick={() => { setEditing(null); form.setOpen(true); }}>New asset</button>
			</Bar>
			<Chips
				current={status}
				onPick={setStatus}
				items={[
					["", `All · ${rows.length}`],
					["draft", `Draft · ${rows.filter((row) => row.status === "draft").length}`],
					["registered", `Registered · ${rows.filter((row) => row.status === "registered").length}`],
					["depreciation", "Depreciation"],
				]}
			/>
			{status === "depreciation" ? (
				<>
					{registered.length === 0 ? <Empty>No depreciation yet. Register an asset to start.</Empty> : (
						<div className="table-wrap">
							<table className="depreciation schedule">
								<thead>
									<tr>
										{SCHEDULE_HEAD.map((column) => column === "Number" ? (
											<th key={column}>
												<span className="th-sort">Number
													<span className="th-arrows">
														<button type="button" className={numberDir === "asc" ? "th-arrow active" : "th-arrow"} aria-label="Sort number up" onClick={() => setNumberDir("asc")}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m18 15-6-6-6 6" /></svg></button>
														<button type="button" className={numberDir === "desc" ? "th-arrow active" : "th-arrow"} aria-label="Sort number down" onClick={() => setNumberDir("desc")}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m6 9 6 6 6-6" /></svg></button>
													</span>
												</span>
											</th>
										) : column === "Category" ? (
											<th key={column} className="col-filter">
												<span>Category</span>
												<details className={hiddenCategories.length > 0 ? "select menu active" : "select menu"} onToggle={(event) => { if (event.currentTarget.open) { setDraftHidden(hiddenCategories); setCategoryQuery(""); } }}>
													<summary aria-label="Filter category"><svg viewBox="0 0 16 12" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" aria-hidden="true"><path d="M1 1.5h14" /><path d="M3.5 6h9" /><path d="M6 10.5h4" /></svg></summary>
													<div className="select-pop sheet-filter">
														<div className="filter-links">
															<button type="button" onClick={() => setDraftHidden([])}>Select all</button>
															<span className="dash">-</span>
															<button type="button" onClick={() => setDraftHidden(categories)}>Clear</button>
														</div>
														<div className="filter-search">
															<input aria-label="Search categories" value={categoryQuery} onChange={(event) => setCategoryQuery(event.target.value)} />
															<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>
														</div>
														<div className="filter-values">
															{categories.filter((value) => value.toLowerCase().includes(categoryQuery.trim().toLowerCase())).map((value) => (
																<label key={value}>
																	<input type="checkbox" checked={!draftHidden.includes(value)} onChange={() => toggleDraft(value)} />
																	{value}
																</label>
															))}
														</div>
														<div className="filter-actions">
															<button className="cancel" type="button" onClick={(event) => event.currentTarget.closest("details")?.removeAttribute("open")}>Cancel</button>
															<button className="ok" type="button" onClick={(event) => { setHiddenCategories(draftHidden); event.currentTarget.closest("details")?.removeAttribute("open"); }}>OK</button>
														</div>
													</div>
												</details>
											</th>
										) : <th key={column}>{column}</th>)}
									</tr>
								</thead>
								<tbody>
									{shownAssets.length === 0 ? (
										<tr><td colSpan={SCHEDULE_HEAD.length}>No assets in the selected categories.</td></tr>
									) : shownAssets.flatMap((row) => {
										const lines = depreciationSchedule(row, years);
										const sum = (pick: (line: (typeof lines)[number]) => number) => cents(lines.reduce((total, line) => total + pick(line), 0));
										const total = ["", "", "Total", "", "", money(sum((line) => line.costBf)), money(sum((line) => line.addition)), money(sum((line) => line.costCf)), money(sum((line) => line.accumBf)), money(sum((line) => line.charge)), money(sum((line) => line.accumCf)), money(sum((line) => line.nbv))];
										return [
											...lines.map((line) => (
												<tr key={`${row.id ?? row.number}-${line.year}`}>
													{scheduleCells(line).map((cell, cellIndex) => <td key={SCHEDULE_HEAD[cellIndex] ?? cell} data-label={SCHEDULE_HEAD[cellIndex]}>{cell}</td>)}
												</tr>
											)),
											<tr key={`${row.id ?? row.number}-total`} className="total">
												{total.map((cell, cellIndex) => <td key={SCHEDULE_HEAD[cellIndex] ?? cellIndex} data-label={SCHEDULE_HEAD[cellIndex]}>{cell}</td>)}
											</tr>,
											<tr key={`${row.id ?? row.number}-gap`} className="gap">
												{SCHEDULE_HEAD.map((column) => <td key={column} />)}
											</tr>,
										];
									})}
								</tbody>
							</table>
						</div>
					)}
				</>
			) : visible.length === 0 ? <Empty>{status ? "No assets with that status." : "No fixed assets yet."}</Empty> : (
				<div className="table-wrap">
					<table className="assets">
						<thead>
							<tr>
								<th>Number</th>
								<th>Register</th>
								<th>Name</th>
								<th>Type</th>
								<th>Purchase date</th>
								<th>Cost</th>
								<th>Book value</th>
								<th>Status</th>
								<th className="tight"></th>
							</tr>
						</thead>
						<tbody>
							{visible.map((row) => (
								<tr key={row.id ?? row.number}>
									<td data-label="Number">{row.number}</td>
									<td data-label="Register">{row.registerNumber || "—"}</td>
									<td data-label="Name">{row.name}</td>
									<td data-label="Type">{row.assetType || "—"}</td>
									<td data-label="Purchase date">{row.purchased || "—"}</td>
									<td data-label="Cost">{money(Number(row.cost) || 0)}</td>
									<td data-label="Book value">{money(depreciationOf(row).book)}</td>
									<td data-label="Status">{label(row.status)}</td>
									<td className="tight">
										<div className="actions">
											{row.status === "draft" && row.id ? (
												<form onSubmit={(event) => { event.preventDefault(); void register.save(new FormData(event.currentTarget)); }}>
													<input type="hidden" name="id" value={row.id} />
													<button className="btn ghost" type="submit" disabled={register.pending}>Register</button>
												</form>
											) : null}
											{row.id ? (
												<details className="select menu">
													<summary aria-label="Actions"><svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="12" cy="5" r="1.8" /><circle cx="12" cy="12" r="1.8" /><circle cx="12" cy="19" r="1.8" /></svg></summary>
													<div className="select-pop">
														<button className="select-item" type="button" onClick={() => { setEditing(row); form.setOpen(true); }}>Edit</button>
														<button className="select-item" type="button" onClick={() => void remove(row)}>Remove</button>
													</div>
												</details>
											) : null}
										</div>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}
			{register.error ? <p className="err">{register.error}</p> : null}
			<Modal open={form.open} title={editing ? "Edit asset" : "New asset"} error={form.error} pending={form.pending} onClose={() => { form.setOpen(false); setEditing(null); }} onSubmit={(fields) => void form.save(fields)}>
				<div key={editing?.id ?? "new"}>
				<label htmlFor="asset-name">Asset name</label>
				<input id="asset-name" name="name" placeholder="Delivery van" defaultValue={editing?.name ?? ""} required />
				<div className="grid two">
					<div>
						<label htmlFor="asset-number">Asset number</label>
						<input id="asset-number" value={editing?.number || nextAssetNumber(books.assets)} readOnly />
					</div>
					<div>
						<label htmlFor="asset-type">Asset type</label>
						<Select id="asset-type" name="assetType" defaultValue={editing?.assetType || "Computer equipment"}>
							{ASSET_TYPES.map((item) => <option key={item} value={item}>{item}</option>)}
						</Select>
					</div>
				</div>
				<div className="grid two">
					<div>
						<label htmlFor="asset-purchased">Purchase date</label>
						<input id="asset-purchased" name="purchased" type="date" defaultValue={editing?.purchased ?? ""} required />
					</div>
					<div>
						<label htmlFor="asset-cost">Purchase price</label>
						<input id="asset-cost" name="cost" inputMode="decimal" defaultValue={editing?.cost ?? ""} required />
					</div>
				</div>
				<div className="grid two">
					<div>
						<label htmlFor="asset-serial">Serial number</label>
						<input id="asset-serial" name="serial" defaultValue={editing?.serial ?? ""} />
					</div>
					<div>
						<label htmlFor="asset-warranty">Warranty expiry</label>
						<input id="asset-warranty" name="warranty" type="date" defaultValue={editing?.warranty ?? ""} />
					</div>
				</div>
				<label htmlFor="asset-description">Description</label>
				<textarea id="asset-description" name="description" rows={3} placeholder="Optional" defaultValue={editing?.description ?? ""} />
				<div className="grid two">
					<div>
						<label htmlFor="asset-method">Depreciation method</label>
						<Select id="asset-method" name="method" defaultValue={editing?.method || "straight"}>
							{DEPRECIATION_METHODS.map(([value, name]) => <option key={value} value={value}>{name}</option>)}
						</Select>
					</div>
					<div>
						<label htmlFor="asset-rate">Rate %</label>
						<input id="asset-rate" name="rate" inputMode="decimal" placeholder="20" defaultValue={editing?.rate ?? ""} />
					</div>
				</div>
				<div className="grid two">
					<div>
						<label htmlFor="asset-averaging">Averaging method</label>
						<Select id="asset-averaging" name="averaging" defaultValue={editing?.averaging || "full-month"}>
							{AVERAGING_METHODS.map(([value, name]) => <option key={value} value={value}>{name}</option>)}
						</Select>
					</div>
					<div>
						<label htmlFor="asset-residual">Residual value</label>
						<input id="asset-residual" name="residual" inputMode="decimal" placeholder="0" defaultValue={editing?.residual ?? ""} />
					</div>
				</div>
				</div>
			</Modal>
		</div>
	);
}

function Register({ books }: { books: Books }) {
	const rows = books.register;
	const client = useQueryClient();
	const [linked, setLinked] = useState(false);
	const [editing, setEditing] = useState<(typeof rows)[number] | null>(null);
	const [number, setNumber] = useState(() => nextRegisterNumber(rows));
	const form = useHubSave(async (fields) => {
		const assetNumber = textOf(fields, "number") || nextRegisterNumber(rows);
		const name = textOf(fields, "name");
		const detail = {
			name,
			assetType: textOf(fields, "assetType"),
			purchased: textOf(fields, "purchased"),
			cost: textOf(fields, "cost"),
			serial: textOf(fields, "serial"),
			warranty: textOf(fields, "warranty"),
			description: textOf(fields, "description"),
		};
		const depreciation = {
			method: textOf(fields, "method") || "straight",
			rate: textOf(fields, "rate"),
			averaging: textOf(fields, "averaging") || "full-month",
			residual: textOf(fields, "residual"),
		};
		let financeNumber = editing?.financeNumber || "";
		let assetId = editing?.assetId || "";
		const linkedAsset = books.assets.find((item) => (financeNumber && item.number === financeNumber) || (assetId !== "" && String(item.id ?? "") === assetId));
		if (linked && linkedAsset?.id) {
			await api.updateHubBook({
				list: "assets",
				id: linkedAsset.id,
				record: { ...linkedAsset, ...detail, ...depreciation, registerNumber: assetNumber, number: linkedAsset.number, status: "registered" },
			});
			financeNumber = linkedAsset.number;
			assetId = String(linkedAsset.id);
		} else if (linked) {
			financeNumber = nextAssetNumber(books.assets);
			const created = await api.appendHubBook({
				list: "assets",
				record: { number: financeNumber, registerNumber: assetNumber, ...detail, ...depreciation, status: "registered" },
			});
			assetId = String(created.id);
		}
		const record = { number: assetNumber, ...detail, assetId, financeNumber, note: editing?.note || "" };
		if (editing?.id) await api.updateHubBook({ list: "register", id: editing.id, record });
		else await api.appendHubBook({ list: "register", record });
		setEditing(null);
		setLinked(false);
	});
	function openNew() {
		setEditing(null);
		setLinked(false);
		setNumber(nextRegisterNumber(rows));
		form.setOpen(true);
	}
	function openEdit(row: (typeof rows)[number]) {
		setEditing(row);
		setLinked(Boolean(row.financeNumber || row.assetId));
		setNumber(row.number || "");
		form.setOpen(true);
	}
	async function remove(row: (typeof rows)[number]) {
		if (!row.id || !window.confirm("Remove this asset?")) return;
		await api.deleteHubBook({ list: "register", id: row.id });
		if (row.assetId) await api.deleteHubBook({ list: "assets", id: row.assetId });
		await client.invalidateQueries({ queryKey: queryKeys.hubRecords });
	}
	return (
		<div>
			<Bar sub={`${rows.length} ${rows.length === 1 ? "asset" : "assets"}`}>
				<button className="btn" type="button" onClick={openNew}>New</button>
			</Bar>
			{rows.length === 0 ? <Empty>No internal assets yet.</Empty> : (
				<div className="table-wrap">
					<table>
						<thead>
							<tr>
								<th>Number</th>
								<th>Finance asset</th>
								<th>Name</th>
								<th>Type</th>
								<th>Purchase date</th>
								<th>Cost</th>
								<th className="tight"></th>
							</tr>
						</thead>
						<tbody>
							{rows.map((row) => {
								const asset = books.assets.find((item) => (row.financeNumber && item.number === row.financeNumber) || (row.assetId !== "" && String(item.id ?? "") === row.assetId));
								return (
									<tr key={row.id ?? row.number ?? row.name}>
										<td data-label="Number">{row.number || "—"}</td>
										<td data-label="Finance asset">{row.financeNumber || asset?.number || "—"}</td>
										<td data-label="Name">{row.name}</td>
										<td data-label="Type">{asset?.assetType || row.assetType || "—"}</td>
										<td data-label="Purchase date">{asset?.purchased || row.purchased || "—"}</td>
										<td data-label="Cost">{money(Number(asset?.cost || row.cost) || 0)}</td>
										<td className="tight">
											{row.id ? (
												<details className="select menu">
													<summary aria-label="Actions"><svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="12" cy="5" r="1.8" /><circle cx="12" cy="12" r="1.8" /><circle cx="12" cy="19" r="1.8" /></svg></summary>
													<div className="select-pop">
														<button className="select-item" type="button" onClick={() => openEdit(row)}>Edit</button>
														<button className="select-item" type="button" onClick={() => void remove(row)}>Remove</button>
													</div>
												</details>
											) : null}
										</td>
									</tr>
								);
							})}
						</tbody>
					</table>
				</div>
			)}
			<Modal open={form.open} title={editing ? "Edit asset" : "New asset"} error={form.error} pending={form.pending} action="Save" onClose={() => { form.setOpen(false); setLinked(false); setEditing(null); }} onSubmit={(fields) => void form.save(fields)}>
				<div key={editing?.id ?? "new"}>
				<label htmlFor="reg-name">Asset name</label>
				<input id="reg-name" name="name" placeholder="Delivery van" defaultValue={editing?.name ?? ""} required />
				<label htmlFor="reg-number">Asset number</label>
				<input id="reg-number" name="number" value={number} onChange={(event) => setNumber(event.target.value)} required />
				<div className="grid two">
					<div>
						<label htmlFor="reg-type">Asset type</label>
						<Select id="reg-type" name="assetType" defaultValue={editing?.assetType || "Computer equipment"}>
							{ASSET_TYPES.map((item) => <option key={item} value={item}>{item}</option>)}
						</Select>
					</div>
					<div>
						<label htmlFor="reg-purchased">Purchase date</label>
						<input id="reg-purchased" name="purchased" type="date" defaultValue={editing?.purchased ?? ""} required />
					</div>
				</div>
				<div className="grid two">
					<div>
						<label htmlFor="reg-cost">Purchase price</label>
						<input id="reg-cost" name="cost" inputMode="decimal" defaultValue={editing?.cost ?? ""} required />
					</div>
					<div>
						<label htmlFor="reg-serial">Serial number</label>
						<input id="reg-serial" name="serial" defaultValue={editing?.serial ?? ""} />
					</div>
				</div>
				<label htmlFor="reg-warranty">Warranty expiry</label>
				<input id="reg-warranty" name="warranty" type="date" defaultValue={editing?.warranty ?? ""} />
				<label htmlFor="reg-description">Description</label>
				<textarea id="reg-description" name="description" rows={3} placeholder="Optional" defaultValue={editing?.description ?? ""} />
				<div className="link-asset">
					<button className={linked ? "btn" : "btn ghost"} type="button" onClick={() => setLinked((value) => !value)}>{linked ? "Linked to asset" : "Link to asset"}</button>
					<p>{linked ? "This asset will also be added in Finance, and each record will keep the other’s number." : "Optional. Leave this off to keep the asset on Register only."}</p>
				</div>
				{linked ? (
					<>
						<div className="grid two">
							<div>
								<label htmlFor="reg-method">Depreciation method</label>
								<Select id="reg-method" name="method" defaultValue="straight">
									{DEPRECIATION_METHODS.map(([value, name]) => <option key={value} value={value}>{name}</option>)}
								</Select>
							</div>
							<div>
								<label htmlFor="reg-rate">Rate %</label>
								<input id="reg-rate" name="rate" inputMode="decimal" placeholder="20" />
							</div>
						</div>
						<div className="grid two">
							<div>
								<label htmlFor="reg-averaging">Averaging method</label>
								<Select id="reg-averaging" name="averaging" defaultValue="full-month">
									{AVERAGING_METHODS.map(([value, name]) => <option key={value} value={value}>{name}</option>)}
								</Select>
							</div>
							<div>
								<label htmlFor="reg-residual">Residual value</label>
								<input id="reg-residual" name="residual" inputMode="decimal" placeholder="0" />
							</div>
						</div>
					</>
				) : null}
				</div>
			</Modal>
		</div>
	);
}

function Phones({ books }: { books: Books }) {
	const rows = books.phones;
	const client = useQueryClient();
	const [editing, setEditing] = useState<(typeof rows)[number] | null>(null);
	const form = useHubSave(async (fields) => {
		const record = {
			telco: textOf(fields, "telco"),
			plan: textOf(fields, "plan"),
			phone: textOf(fields, "phone"),
			type: textOf(fields, "type"),
			user: textOf(fields, "user"),
		};
		if (editing?.id) await api.updateHubBook({ list: "phones", id: editing.id, record });
		else await api.appendHubBook({ list: "phones", record });
		setEditing(null);
	});
	async function remove(id: number) {
		if (!window.confirm("Remove this phone number?")) return;
		await api.deleteHubBook({ list: "phones", id });
		await client.invalidateQueries({ queryKey: queryKeys.hubRecords });
	}
	return (
		<div>
			<Bar sub={`${rows.length} ${rows.length === 1 ? "number" : "numbers"}`}>
				<button className="btn" type="button" onClick={() => { setEditing(null); form.setOpen(true); }}>New</button>
			</Bar>
			{rows.length === 0 ? <Empty>No phone numbers yet.</Empty> : (
				<div className="table-wrap">
					<table>
						<thead><tr><th>Telco</th><th>Plan</th><th>Phone number</th><th>Phone types</th><th>User</th><th className="tight"></th></tr></thead>
						<tbody>
							{rows.map((row) => (
								<tr key={row.id ?? row.phone}>
									<td data-label="Telco">{row.telco || row.line || "—"}</td>
									<td data-label="Plan">{row.plan || "—"}</td>
									<td data-label="Phone number">{row.phone || "—"}</td>
									<td data-label="Phone types">{row.type || "—"}</td>
									<td data-label="User">{row.user || "—"}</td>
									<td className="tight">
										{row.id ? (
											<details className="select menu">
												<summary aria-label="Actions"><svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="12" cy="5" r="1.8" /><circle cx="12" cy="12" r="1.8" /><circle cx="12" cy="19" r="1.8" /></svg></summary>
												<div className="select-pop">
													<button className="select-item" type="button" onClick={() => { setEditing(row); form.setOpen(true); }}>Edit</button>
													<button className="select-item" type="button" onClick={() => { if (row.id) void remove(row.id); }}>Remove</button>
												</div>
											</details>
										) : null}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}
			<Modal open={form.open} title={editing ? "Edit phone number" : "New phone number"} error={form.error} pending={form.pending} onClose={() => { form.setOpen(false); setEditing(null); }} onSubmit={(fields) => void form.save(fields)}>
				<div key={editing?.id ?? "new"}>
				<label htmlFor="phone-telco">Telco</label>
				<input id="phone-telco" name="telco" placeholder="Maxis" defaultValue={editing?.telco || editing?.line || ""} required />
				<label htmlFor="phone-plan">Plan</label>
				<input id="phone-plan" name="plan" placeholder="Business 50" defaultValue={editing?.plan ?? ""} required />
				<label htmlFor="phone-number">Phone number</label>
				<input id="phone-number" name="phone" placeholder="012-345 6789" defaultValue={editing?.phone ?? ""} required />
				<label htmlFor="phone-type">Phone types</label>
				<input id="phone-type" name="type" placeholder="Mobile" defaultValue={editing?.type ?? ""} required />
				<label htmlFor="phone-user">User</label>
				<input id="phone-user" name="user" placeholder="Name" defaultValue={editing?.user ?? ""} required />
				</div>
			</Modal>
		</div>
	);
}

function Repairs({ books }: { books: Books }) {
	const rows = books.repairs;
	const client = useQueryClient();
	const [status, setStatus] = useState("");
	const [editing, setEditing] = useState<(typeof rows)[number] | null>(null);
	const visible = status ? rows.filter((row) => row.status === status) : rows;
	const form = useHubSave(async (fields) => {
		const record = {
			item: textOf(fields, "item"),
			required: textOf(fields, "required"),
			place: textOf(fields, "place"),
			date: textOf(fields, "date"),
			status: textOf(fields, "status") || "reported",
			note: textOf(fields, "note"),
		};
		if (editing?.id) await api.updateHubBook({ list: "repairs", id: editing.id, record });
		else await api.appendHubBook({ list: "repairs", record });
		setEditing(null);
	});
	async function remove(id: number) {
		if (!window.confirm("Remove this repair?")) return;
		await api.deleteHubBook({ list: "repairs", id });
		await client.invalidateQueries({ queryKey: queryKeys.hubRecords });
	}
	return (
		<div>
			<Bar sub={`${visible.length} ${visible.length === 1 ? "repair" : "repairs"}${status ? " · filtered" : ""}`}>
				<button className="btn" type="button" onClick={() => { setEditing(null); form.setOpen(true); }}>New</button>
			</Bar>
			<Chips
				current={status}
				onPick={setStatus}
				items={[
					["", `All · ${rows.length}`],
					...REPAIR_STATUSES.map(([value, name]) => [value, `${name} · ${rows.filter((row) => row.status === value).length}`] as [string, string]),
				]}
			/>
			{visible.length === 0 ? <Empty>{status ? "No repairs with that status." : "No repairs yet."}</Empty> : (
				<div className="table-wrap">
					<table>
						<thead><tr><th>Item</th><th>Required</th><th>Place</th><th>Date</th><th>Status</th><th>Note</th><th className="tight"></th></tr></thead>
						<tbody>
							{visible.map((row) => (
								<tr key={row.id ?? row.item}>
									<td data-label="Item">{row.item}</td>
									<td data-label="Required">{row.required || "—"}</td>
									<td data-label="Place">{row.place || "—"}</td>
									<td data-label="Date">{row.date ? dayFirst(row.date) : "—"}</td>
									<td data-label="Status">{REPAIR_STATUSES.find(([value]) => value === row.status)?.[1] ?? row.status}</td>
									<td data-label="Note">{row.note || "—"}</td>
									<td className="tight">
										{row.id ? (
											<details className="select menu">
												<summary aria-label="Actions"><svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="12" cy="5" r="1.8" /><circle cx="12" cy="12" r="1.8" /><circle cx="12" cy="19" r="1.8" /></svg></summary>
												<div className="select-pop">
													<button className="select-item" type="button" onClick={() => { setEditing(row); form.setOpen(true); }}>Edit</button>
													<button className="select-item" type="button" onClick={() => { if (row.id) void remove(row.id); }}>Remove</button>
												</div>
											</details>
										) : null}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}
			<Modal open={form.open} title={editing ? "Edit repair" : "New repair"} error={form.error} pending={form.pending} onClose={() => { form.setOpen(false); setEditing(null); }} onSubmit={(fields) => void form.save(fields)}>
				<div key={editing?.id ?? "new"}>
					<label htmlFor="repair-item">Item</label>
					<input id="repair-item" name="item" placeholder="Door lock" defaultValue={editing?.item ?? ""} required />
					<label htmlFor="repair-required">Required</label>
					<input id="repair-required" name="required" placeholder="Replacement lock, contractor" defaultValue={editing?.required ?? ""} required />
					<div className="grid two">
						<div>
							<label htmlFor="repair-place">Place</label>
							<input id="repair-place" name="place" placeholder="Main office" defaultValue={editing?.place ?? ""} required />
						</div>
						<div>
							<label htmlFor="repair-status">Status</label>
							<Select id="repair-status" name="status" defaultValue={editing?.status ?? (status || "reported")}>
								{REPAIR_STATUSES.map(([value, name]) => <option key={value} value={value}>{name}</option>)}
							</Select>
						</div>
					</div>
					<label htmlFor="repair-date">Date</label>
					<input id="repair-date" name="date" type="date" defaultValue={editing?.date ?? ""} required />
					<label htmlFor="repair-note">Note</label>
					<textarea id="repair-note" name="note" rows={3} placeholder="Optional" defaultValue={editing?.note ?? ""} />
				</div>
			</Modal>
		</div>
	);
}

function Maintenance({ books }: { books: Books }) {
	const rows = books.maintenance;
	const client = useQueryClient();
	const [status, setStatus] = useState("");
	const [editing, setEditing] = useState<(typeof rows)[number] | null>(null);
	const visible = status ? rows.filter((row) => row.status === status) : rows;
	const form = useHubSave(async (fields) => {
		const record = {
			purpose: textOf(fields, "purpose") || "Cleaning",
			place: textOf(fields, "place"),
			date: textOf(fields, "date"),
			status: textOf(fields, "status") || "scheduled",
			note: textOf(fields, "note"),
		};
		if (editing?.id) await api.updateHubBook({ list: "maintenance", id: editing.id, record });
		else await api.appendHubBook({ list: "maintenance", record });
		setEditing(null);
	});
	async function remove(id: number) {
		if (!window.confirm("Remove this maintenance record?")) return;
		await api.deleteHubBook({ list: "maintenance", id });
		await client.invalidateQueries({ queryKey: queryKeys.hubRecords });
	}
	return (
		<div>
			<Bar sub={`${visible.length} ${visible.length === 1 ? "record" : "records"}${status ? " · filtered" : ""}`}>
				<button className="btn" type="button" onClick={() => { setEditing(null); form.setOpen(true); }}>New</button>
			</Bar>
			<Chips
				current={status}
				onPick={setStatus}
				items={[
					["", `All · ${rows.length}`],
					...MAINTENANCE_STATUSES.map(([value, name]) => [value, `${name} · ${rows.filter((row) => row.status === value).length}`] as [string, string]),
				]}
			/>
			{visible.length === 0 ? <Empty>{status ? "No maintenance with that status." : "No maintenance yet."}</Empty> : (
				<div className="table-wrap">
					<table>
						<thead><tr><th>Purpose</th><th>Place</th><th>Date</th><th>Status</th><th>Note</th><th className="tight"></th></tr></thead>
						<tbody>
							{visible.map((row) => (
								<tr key={row.id ?? row.purpose}>
									<td data-label="Purpose">{row.purpose}</td>
									<td data-label="Place">{row.place || "—"}</td>
									<td data-label="Date">{row.date ? dayFirst(row.date) : "—"}</td>
									<td data-label="Status">{MAINTENANCE_STATUSES.find(([value]) => value === row.status)?.[1] ?? row.status}</td>
									<td data-label="Note">{row.note || "—"}</td>
									<td className="tight">
										{row.id ? (
											<details className="select menu">
												<summary aria-label="Actions"><svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="12" cy="5" r="1.8" /><circle cx="12" cy="12" r="1.8" /><circle cx="12" cy="19" r="1.8" /></svg></summary>
												<div className="select-pop">
													<button className="select-item" type="button" onClick={() => { setEditing(row); form.setOpen(true); }}>Edit</button>
													<button className="select-item" type="button" onClick={() => { if (row.id) void remove(row.id); }}>Remove</button>
												</div>
											</details>
										) : null}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}
			<Modal open={form.open} title={editing ? "Edit maintenance" : "New maintenance"} error={form.error} pending={form.pending} onClose={() => { form.setOpen(false); setEditing(null); }} onSubmit={(fields) => void form.save(fields)}>
				<div key={editing?.id ?? "new"}>
					<div className="grid two">
						<div>
							<label htmlFor="maint-purpose">Purpose</label>
							<Select id="maint-purpose" name="purpose" defaultValue={editing?.purpose ?? "Cleaning"}>
								{MAINTENANCE_PURPOSES.map((item) => <option key={item} value={item}>{item}</option>)}
							</Select>
						</div>
						<div>
							<label htmlFor="maint-status">Status</label>
							<Select id="maint-status" name="status" defaultValue={editing?.status ?? (status || "scheduled")}>
								{MAINTENANCE_STATUSES.map(([value, name]) => <option key={value} value={value}>{name}</option>)}
							</Select>
						</div>
					</div>
					<label htmlFor="maint-place">Place</label>
					<input id="maint-place" name="place" placeholder="Office, lobby" defaultValue={editing?.place ?? ""} required />
					<label htmlFor="maint-date">Date</label>
					<input id="maint-date" name="date" type="date" defaultValue={editing?.date ?? ""} required />
					<label htmlFor="maint-note">Note</label>
					<textarea id="maint-note" name="note" rows={3} placeholder="Optional" defaultValue={editing?.note ?? ""} />
				</div>
			</Modal>
		</div>
	);
}

function Credentials({ books }: { books: Books }) {
	const rows = books.credentials;
	const client = useQueryClient();
	const [editing, setEditing] = useState<(typeof rows)[number] | null>(null);
	const [shown, setShown] = useState<number | null>(null);
	const form = useHubSave(async (fields) => {
		const record = {
			name: textOf(fields, "name"),
			userId: textOf(fields, "userId"),
			password: textOf(fields, "password"),
			link: textOf(fields, "link"),
		};
		if (editing?.id) await api.updateHubBook({ list: "credentials", id: editing.id, record });
		else await api.appendHubBook({ list: "credentials", record });
		setEditing(null);
	});
	async function remove(id: number) {
		if (!window.confirm("Remove this credential?")) return;
		await api.deleteHubBook({ list: "credentials", id });
		await client.invalidateQueries({ queryKey: queryKeys.hubRecords });
	}
	return (
		<div>
			<Bar sub={`${rows.length} ${rows.length === 1 ? "credential" : "credentials"}`}>
				<button className="btn" type="button" onClick={() => { setEditing(null); form.setOpen(true); }}>New</button>
			</Bar>
			{rows.length === 0 ? <Empty>No credentials yet.</Empty> : (
				<div className="table-wrap">
					<table className="credentials">
						<thead><tr><th>Name</th><th>User ID</th><th>Password</th><th>Link</th><th className="tight"></th></tr></thead>
						<tbody>
							{rows.map((row) => (
								<tr key={row.id ?? row.name}>
									<td data-label="Name">{row.name}</td>
									<td data-label="User ID">{row.userId || "—"}</td>
									<td className="secret-cell" data-label="Password">
										{row.password ? (
											<span className="secret">
												<span className="value">{shown === row.id ? row.password : "••••••••"}</span>
												{row.id ? (
													<button className="btn icon" type="button" aria-label={shown === row.id ? "Hide password" : "Show password"} onClick={() => setShown((current) => current === row.id ? null : row.id ?? null)}>
														{shown === row.id ? <EyeSlashIcon /> : <EyeIcon />}
													</button>
												) : null}
											</span>
										) : "—"}
									</td>
									<td data-label="Link">{row.link ? <a href={row.link} target="_blank" rel="noreferrer">{row.link}</a> : "—"}</td>
									<td className="tight">
										{row.id ? (
											<details className="select menu">
												<summary aria-label="Actions"><svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="12" cy="5" r="1.8" /><circle cx="12" cy="12" r="1.8" /><circle cx="12" cy="19" r="1.8" /></svg></summary>
												<div className="select-pop">
													<button className="select-item" type="button" onClick={() => { setEditing(row); form.setOpen(true); }}>Edit</button>
													<button className="select-item" type="button" onClick={() => { if (row.id) void remove(row.id); }}>Remove</button>
												</div>
											</details>
										) : null}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}
			<Modal open={form.open} title={editing ? "Edit credential" : "New credential"} error={form.error} pending={form.pending} onClose={() => { form.setOpen(false); setEditing(null); }} onSubmit={(fields) => void form.save(fields)}>
				<div key={editing?.id ?? "new"}>
					<label htmlFor="cred-name">Website or app</label>
					<input id="cred-name" name="name" placeholder="Supplier portal" defaultValue={editing?.name ?? ""} required />
					<label htmlFor="cred-user">User ID</label>
					<input id="cred-user" name="userId" placeholder="name@company.com" defaultValue={editing?.userId ?? ""} required />
					<label htmlFor="cred-password">Password</label>
					<input id="cred-password" name="password" type="password" autoComplete="new-password" defaultValue={editing?.password ?? ""} required />
					<label htmlFor="cred-link">Link</label>
					<input id="cred-link" name="link" type="url" placeholder="https://" defaultValue={editing?.link ?? ""} />
				</div>
			</Modal>
		</div>
	);
}

const OFFER_LETTER_HTML = `<img class="letter-logo" src="${logo}" alt="Jages"><p>Muhammad (020901-10-0800)</p>
<p>2-1 Sinar Magna,</p>
<p>Jalan Prima 12, Metro Prima Kepong,</p>
<p>52100 Kuala Lumpur.</p>
<p><br></p>
<p>Dear Muhammad,</p>
<p><u>Letter of Offer – Member of Technical Staff</u></p>
<p>We are pleased to offer you employment with Jages Sdn Bhd (“the Company”) on the following terms:</p>
<p><br></p>
<p>Position: Member of Technical Staff</p>
<p>Employment Status: Fixed-Term Contract</p>
<p>Commencement Date: 5 August 2026</p>
<p>Probation Expiry Date: 4 November 2026</p>
<p>Contract Expiry Date: 4 August 2027</p>
<p><br></p>
<p>Based on business requirements, you may be assigned to another supervisor or required to work at any Company office, customer premises, project site or other location within Malaysia.</p>
<p><br></p>
<p><u>Salary</u></p>
<p>Your basic salary will be RM2,300 per month, paid monthly by bank transfer and subject to all statutory deductions. Salary for any incomplete month will be prorated in accordance with applicable law.</p>
<p><br></p>
<p><u>Shift Allowance</u></p>
<p>You will receive a monthly shift allowance of RM600 while formally assigned to the Company’s rotational shift schedule. The allowance will commence from the effective date of assignment, may be prorated for an incomplete month and will cease when you are no longer assigned to shift duties. It will continue during approved paid leave, subject to Company policy and applicable law. This allowance does not form part of your basic salary.</p>
<p><br></p>
<p><u>Leave Entitlement</u></p>
<p>You will be entitled to 12 days of annual leave, 14 days of paid medical leave, 60 days of hospitalisation leave, 98 days of maternity leave, 7 consecutive days of paternity leave and 3 days of marriage leave, subject to eligibility requirements and applicable law.</p>
<p><u>Termination &amp; Notice</u></p>
<p>Either party may terminate the employment by giving one month’s written notice during probation and two months’ written notice after confirmation, or payment in lieu of notice where permitted by law. The Company may take disciplinary action, including dismissal, for misconduct, negligence or breach of employment terms, subject to due process and applicable law.</p>
<p><br></p>
<p>Upon termination, you must complete a proper handover and return all Company or client property, documents, records and information, including electronic copies.</p>
<p><u>Entire Agreement</u></p>
<p>This Letter of Offer, together with all expressly incorporated documents, constitutes the entire employment agreement and supersedes all prior discussions or representations. Any amendment to its contractual terms must be agreed in writing by both parties. The Company may reasonably amend its non-contractual policies, procedures, reporting lines and operational arrangements.</p>
<p><br></p>
<p><u>Acceptance of Offer</u></p>
<p>This offer must be signed and returned by 1 August 2026, failing which the Company may withdraw or allow it to lapse unless an extension is agreed in writing.</p>
<p><br></p>
<p>By signing, you confirm that you have read, understood and accepted this Letter of Offer, the applicable Job Description and the Company’s Code of Conduct.</p>
<p><br></p>
<p>We look forward to welcoming you to Jages Sdn Bhd.</p>
<p><br></p>
<p>Yours sincerely,</p>
<p><br></p>
<p>Mohamad Syahir Bin Mohamad Zaini</p>
<p>Chief Executive Officer</p>
<p>Jages Sdn Bhd</p>
<p><br></p>
<p>I, Muhammad, confirm that:</p>
<p>a. I have read and understood this Letter of Offer;</p>
<p>b. I accept the terms and conditions of employment stated in this Letter of Offer;</p>
<p>c. the information and documents I have provided to the Company are true and accurate; and</p>
<p>d. I will commence employment as a Member of Technical Staff on 5 August 2026.</p>
<p><br></p>
<p>_______________________</p>
<p>Name:</p>
<p>ID No:</p>
<p>Date:</p>`;

function letterFileName(title: string, ext: string): string {
	const name = title.replace(/[^\w .()-]+/g, "").trim() || "letter";
	return `${name}.${ext}`;
}

function xmlText(value: string): string {
	return value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function crc32(data: Uint8Array): number {
	let crc = 0xffffffff;
	for (const byte of data) {
		crc ^= byte;
		for (let bit = 0; bit < 8; bit += 1) crc = (crc >>> 1) ^ (crc & 1 ? 0xedb88320 : 0);
	}
	return (crc ^ 0xffffffff) >>> 0;
}

function zipStore(files: Array<{ name: string; data: Uint8Array }>): Blob {
	const chunks: Uint8Array[] = [];
	const central: Uint8Array[] = [];
	let offset = 0;
	for (const file of files) {
		const name = new TextEncoder().encode(file.name);
		const crc = crc32(file.data);
		const local = new Uint8Array(30 + name.length);
		const view = new DataView(local.buffer);
		view.setUint32(0, 0x04034b50, true);
		view.setUint16(4, 20, true);
		view.setUint32(14, crc, true);
		view.setUint32(18, file.data.length, true);
		view.setUint32(22, file.data.length, true);
		view.setUint16(26, name.length, true);
		local.set(name, 30);
		chunks.push(local, file.data);
		const entry = new Uint8Array(46 + name.length);
		const head = new DataView(entry.buffer);
		head.setUint32(0, 0x02014b50, true);
		head.setUint16(4, 20, true);
		head.setUint16(6, 20, true);
		head.setUint32(16, crc, true);
		head.setUint32(20, file.data.length, true);
		head.setUint32(24, file.data.length, true);
		head.setUint16(28, name.length, true);
		head.setUint32(42, offset, true);
		entry.set(name, 46);
		central.push(entry);
		offset += local.length + file.data.length;
	}
	const centralSize = central.reduce((sum, part) => sum + part.length, 0);
	const end = new Uint8Array(22);
	const tail = new DataView(end.buffer);
	tail.setUint32(0, 0x06054b50, true);
	tail.setUint16(8, files.length, true);
	tail.setUint16(10, files.length, true);
	tail.setUint32(12, centralSize, true);
	tail.setUint32(16, offset, true);
	const out = new Uint8Array(offset + centralSize + end.length);
	let cursor = 0;
	for (const part of [...chunks, ...central, end]) {
		out.set(part, cursor);
		cursor += part.length;
	}
	return new Blob([out], { type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document" });
}

function letterRuns(node: Node): string {
	let xml = "";
	node.childNodes.forEach((child) => {
		if (child.nodeType === Node.TEXT_NODE) {
			const text = child.textContent ?? "";
			if (text) xml += `<w:r><w:rPr><w:rFonts w:ascii="Inter" w:hAnsi="Inter"/><w:sz w:val="24"/><w:szCs w:val="24"/></w:rPr><w:t xml:space="preserve">${xmlText(text)}</w:t></w:r>`;
			return;
		}
		if (!(child instanceof HTMLElement)) return;
		const marked = child.tagName === "B" || child.tagName === "STRONG" || child.tagName === "U";
		const size = Number.parseFloat(child.style.fontSize) || 14;
		const points = String(Math.round(size * 2));
		const bold = child.tagName === "B" || child.tagName === "STRONG" ? "<w:b/>" : "";
		const underline = child.tagName === "U" ? `<w:u w:val="single"/>` : "";
		if (marked && child.childNodes.length === 1 && child.firstChild?.nodeType === Node.TEXT_NODE) {
			xml += `<w:r><w:rPr><w:rFonts w:ascii="Inter" w:hAnsi="Inter"/><w:sz w:val="${points}"/><w:szCs w:val="${points}"/>${bold}${underline}</w:rPr><w:t xml:space="preserve">${xmlText(child.textContent ?? "")}</w:t></w:r>`;
			return;
		}
		xml += letterRuns(child);
	});
	return xml;
}

async function downloadLetter(title: string, page: HTMLElement, kind: "pdf" | "docx") {
	const paragraphs = [...page.children].filter((node) => node.tagName !== "IMG");
	if (kind === "docx") {
		const body = paragraphs.map((node) => {
			const align = node instanceof HTMLElement ? node.style.textAlign : "";
			const jc = align === "center" ? "center" : align === "right" ? "right" : align === "left" ? "left" : "both";
			return `<w:p><w:pPr><w:jc w:val="${jc}"/></w:pPr>${letterRuns(node) || `<w:r><w:t></w:t></w:r>`}</w:p>`;
		}).join("");
		const documentXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><w:document xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main"><w:body>${body}<w:sectPr><w:pgSz w:w="11906" w:h="16838"/><w:pgMar w:top="1134" w:right="1134" w:bottom="1134" w:left="1134"/></w:sectPr></w:body></w:document>`;
		const types = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Override PartName="/word/document.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml"/></Types>`;
		const rels = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="word/document.xml"/></Relationships>`;
		const encode = (value: string) => new TextEncoder().encode(value);
		downloadFile(letterFileName(title, "docx"), zipStore([
			{ name: "[Content_Types].xml", data: encode(types) },
			{ name: "_rels/.rels", data: encode(rels) },
			{ name: "word/document.xml", data: encode(documentXml) },
		]));
		return;
	}
	const lines: string[] = [];
	for (const node of paragraphs) {
		const text = (node.textContent ?? "").replace(/\s+/g, " ").trim();
		if (!text) {
			lines.push("");
			continue;
		}
		let rest = text;
		while (rest.length > 90) {
			const cut = rest.lastIndexOf(" ", 90);
			const at = cut > 40 ? cut : 90;
			lines.push(rest.slice(0, at));
			rest = rest.slice(at).trim();
		}
		lines.push(rest);
	}
	const pages: string[][] = [];
	for (let index = 0; index < lines.length; index += 48) pages.push(lines.slice(index, index + 48));
	if (pages.length === 0) pages.push([""]);
	const objects: string[] = [];
	const add = (body: string) => { objects.push(body); return objects.length; };
	const font = add("<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>");
	const pageIds: number[] = [];
	const contentIds: number[] = [];
	for (const chunk of pages) {
		let stream = "BT /F1 12 Tf 50 780 Td 14 TL\n";
		chunk.forEach((line, index) => {
			const safe = line.replace(/[()\\]/g, (char) => `\\${char}`).replace(/[^\x20-\x7e]/g, "");
			stream += index === 0 ? `(${safe}) Tj\n` : `T* (${safe}) Tj\n`;
		});
		stream += "ET";
		contentIds.push(add(`<< /Length ${stream.length} >>\nstream\n${stream}\nendstream`));
	}
	for (const contentId of contentIds) pageIds.push(add(`<< /Type /Page /Parent 0 0 R /MediaBox [0 0 595 842] /Contents ${contentId} 0 R /Resources << /Font << /F1 ${font} 0 R >> >> >>`));
	const kids = pageIds.map((id) => `${id} 0 R`).join(" ");
	const pagesId = add(`<< /Type /Pages /Count ${pageIds.length} /Kids [${kids}] >>`);
	const catalog = add(`<< /Type /Catalog /Pages ${pagesId} 0 R >>`);
	for (const pageId of pageIds) {
		const body = objects[pageId - 1];
		if (body) objects[pageId - 1] = body.replace("/Parent 0 0 R", `/Parent ${pagesId} 0 R`);
	}
	let file = "%PDF-1.4\n";
	const offsets = [0];
	objects.forEach((body, index) => {
		offsets.push(file.length);
		file += `${index + 1} 0 obj\n${body}\nendobj\n`;
	});
	const xref = file.length;
	file += `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`;
	for (const offset of offsets.slice(1)) file += `${String(offset).padStart(10, "0")} 00000 n \n`;
	file += `trailer\n<< /Size ${objects.length + 1} /Root ${catalog} 0 R >>\nstartxref\n${xref}\n%%EOF`;
	downloadFile(letterFileName(title, "pdf"), new Blob([file], { type: "application/pdf" }));
}

function refreshLogo(html: string): string {
	return html.replace(/<img\b[^>]*class="letter-logo"[^>]*>/i, (tag) => {
		const style = tag.match(/\sstyle="[^"]*"/)?.[0] ?? "";
		return `<img class="letter-logo" src="${logo}" alt="Jages"${style}>`;
	});
}

function letterDocument(row: LetterTemplate | null): string {
	if (!row) return "";
	if (row.body.includes("letter-logo")) return refreshLogo(row.body);
	if (/letter of offer/i.test(row.title)) return OFFER_LETTER_HTML;
	return row.body;
}

function DocumentDialog({ title, html = "", open, onClose, onSave }: { title: string; html?: string; open: boolean; onClose: () => void; onSave?: (html: string) => Promise<void> }) {
	const ref = useRef<HTMLDialogElement>(null);
	const pageRef = useRef<HTMLDivElement>(null);
	const seen = useRef(false);
	const loaded = useRef(false);
	const caret = useRef<Range | null>(null);
	const saveTimer = useRef<number | null>(null);
	const [error, setError] = useState("");
	function store() {
		const page = pageRef.current;
		if (!page || !onSave) return;
		void onSave(page.innerHTML).catch((reason: unknown) => setError(presentError(reason).title));
	}
	function rememberSelection() {
		const page = pageRef.current;
		const selection = window.getSelection();
		if (!page || !selection || selection.rangeCount === 0) return;
		const range = selection.getRangeAt(0);
		if (page.contains(range.commonAncestorContainer)) caret.current = range.cloneRange();
	}
	useEffect(() => {
		const dialog = ref.current;
		if (!dialog) return;
		if (open && !dialog.open) {
			dialog.showModal();
			seen.current = true;
		}
		if (!open && dialog.open) dialog.close();
	}, [open]);
	useEffect(() => {
		if (!open) {
			loaded.current = false;
			return;
		}
		if (loaded.current || !pageRef.current) return;
		pageRef.current.innerHTML = html;
		loaded.current = true;
	}, [open, html]);
	useEffect(() => () => {
		if (saveTimer.current !== null) window.clearTimeout(saveTimer.current);
	}, []);
	function keepSelection(event: { preventDefault: () => void }) {
		rememberSelection();
		event.preventDefault();
	}
	function format(command: string, value?: string) {
		pageRef.current?.focus();
		document.execCommand(command, false, value);
	}
	function setSize(px: string) {
		const page = pageRef.current;
		const selection = window.getSelection();
		if (!page || !selection) return;
		const saved = caret.current;
		if (saved && page.contains(saved.commonAncestorContainer)) {
			selection.removeAllRanges();
			selection.addRange(saved);
		}
		page.focus();
		if (selection.rangeCount === 0) return;
		const range = selection.getRangeAt(0);
		if (!page.contains(range.commonAncestorContainer)) return;
		const span = document.createElement("span");
		span.style.fontSize = `${px}px`;
		if (range.collapsed) {
			const marker = document.createTextNode("\u200b");
			span.appendChild(marker);
			range.insertNode(span);
			const next = document.createRange();
			next.setStart(marker, 1);
			next.collapse(true);
			selection.removeAllRanges();
			selection.addRange(next);
			caret.current = next.cloneRange();
			return;
		}
		span.appendChild(range.extractContents());
		range.insertNode(span);
		const after = document.createRange();
		after.selectNodeContents(span);
		selection.removeAllRanges();
		selection.addRange(after);
		caret.current = after.cloneRange();
	}
	function scheduleSave() {
		if (saveTimer.current !== null) window.clearTimeout(saveTimer.current);
		saveTimer.current = window.setTimeout(() => {
			saveTimer.current = null;
			store();
		}, 400);
	}
	function resizeLogo(event: { clientX: number; target: EventTarget | null; preventDefault: () => void }) {
		const image = event.target;
		if (!(image instanceof HTMLImageElement) || !image.classList.contains("letter-logo")) return;
		const logoImage = image;
		event.preventDefault();
		const startX = event.clientX;
		const startWidth = logoImage.getBoundingClientRect().width;
		const ratio = logoImage.naturalWidth > 0 ? logoImage.naturalHeight / logoImage.naturalWidth : logoImage.getBoundingClientRect().height / startWidth;
		function move(ev: PointerEvent) {
			const width = Math.max(48, Math.min(480, startWidth + ev.clientX - startX));
			logoImage.style.width = `${Math.round(width)}px`;
			logoImage.style.height = `${Math.round(width * ratio)}px`;
		}
		function up() {
			window.removeEventListener("pointermove", move);
			window.removeEventListener("pointerup", up);
			store();
		}
		window.addEventListener("pointermove", move);
		window.addEventListener("pointerup", up);
	}
	return (
		<dialog ref={ref} className="modal doc" onClose={() => { if (seen.current) store(); seen.current = false; onClose(); }}>
			<div className="row tight">
				<h1>{title}</h1>
				<div className="actions">
					{onSave ? (
						<details className="select menu save-menu">
							<summary className="btn ghost">Save</summary>
							<div className="select-pop">
								<button className="select-item" type="button" onClick={() => {
									const page = pageRef.current;
									if (!page) return;
									void downloadLetter(title, page, "pdf");
								}}>PDF</button>
								<button className="select-item" type="button" onClick={() => {
									const page = pageRef.current;
									if (!page) return;
									void downloadLetter(title, page, "docx");
								}}>DOCX</button>
							</div>
						</details>
					) : null}
					<button className="btn ghost" type="button" onClick={onClose}>Close</button>
				</div>
			</div>
			{error ? <p className="err">{error}</p> : null}
			<div className="doc-tools">
				<label htmlFor="doc-size">Size</label>
				<Select id="doc-size" defaultValue="14" onMouseDown={rememberSelection} onChange={(event) => setSize(event.target.value)}>
					{["12", "14", "16", "18", "20", "24"].map((size) => <option key={size} value={size}>{size}</option>)}
				</Select>
				<button className="btn ghost" type="button" onMouseDown={keepSelection} onClick={() => format("justifyLeft")}>Left</button>
				<button className="btn ghost" type="button" onMouseDown={keepSelection} onClick={() => format("justifyCenter")}>Centre</button>
				<button className="btn ghost" type="button" onMouseDown={keepSelection} onClick={() => format("justifyRight")}>Right</button>
				<button className="btn ghost" type="button" onMouseDown={keepSelection} onClick={() => format("justifyFull")}>Justify</button>
				<button className="btn ghost" type="button" onMouseDown={keepSelection} onClick={() => format("bold")}>Bold</button>
				<button className="btn ghost" type="button" onMouseDown={keepSelection} onClick={() => format("underline")}>Underline</button>
				<button className="btn ghost" type="button" onMouseDown={keepSelection} onClick={() => format("undo")}>Undo</button>
				<button className="btn ghost" type="button" onMouseDown={keepSelection} onClick={() => format("redo")}>Redo</button>
			</div>
			<div ref={pageRef} className="word-page" contentEditable suppressContentEditableWarning role="textbox" aria-label="Document" onPointerDown={resizeLogo} onInput={scheduleSave} />
		</dialog>
	);
}

function Coming({ sub }: { sub: string }) {
	return (
		<div>
			<Bar sub={sub} />
			<Empty>This page is not built yet.</Empty>
		</div>
	);
}
